import React, { useState, useEffect, useLayoutEffect, useRef, useContext, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useSearchParams } from 'react-router-dom';
import api from '../../services/api';
import visitService from '../../services/visitService';
import auditService from '../../services/auditService';
import stageService from '../../services/stageService';
import { selectReviewerTierStages } from '../../utils/crfReviewerStages';
import Spinner from '../Common/Spinner';
import Alert from '../Common/Alert';
import { Modal } from '../Common/Modal';
import ActionItemReplyThread from '../Common/ActionItemReplyThread';
import { ElectronicSign } from '../Common/ElectronicSign';
import { AuthContext } from '../../context/AuthContext';
import { useSession } from '../../context/SessionContext';
import { useProject } from '../../context/ProjectContext';
import CreateActionItemModal from '../ActionItems/CreateActionItemModal';
import { FiPlus, FiFileText } from 'react-icons/fi';
import { report as reportMessages, dcf as dcfMessages, common as commonMessages, actionItems as actionItemMessages, stsAutoCapture as stsAutoCaptureMessages } from '../../utils/validationMessages';
import {
  assertVisitReportPdfBlob,
  getVisitReportPdfErrorMessage,
  VISIT_REPORT_PDF_ERROR_CODES,
} from '../../utils/visitReportPdfErrors';
import {
  formatActionItemStatusForDisplay,
  isIssueCategoryOthers,
  normalizeActionItemStatusSelectValue,
} from '../../utils/actionItemUi';
import {
  formatDateDDMMMYYYY,
  formatDateTimeDDMMMYYYY,
  formatIsoDateCalendarDDMMMYYYY,
  formatOffsetForDisplay,
  formatPerformedOnUtcToLocal,
  formatPerformedOnUtcToLocalWithOffset,
  todayIsoDateLocal,
  apiDateTimeToIsoDateLocal,
  getActionItemClosingDateBounds,
  clampIsoDateToMinMax,
} from '../../utils/dateFormat';
import { dispatchNotificationsRefresh } from '../../utils/constants';
import { isFirstReviewStageGate } from '../../utils/stageReviewTierUtils';
import { formatReplyRemarkPlainForAuditDisplay } from '../../utils/actionItemReplyThread';
import { PDFDocument } from 'pdf-lib';
import './CRFForm.css';
import DcfVisitReportModal from './Dcf/DcfVisitReportModal';
import SubmitAllDcfButton from './Dcf/SubmitAllDcfButton';
import { fetchVisitTypes } from '../../studyConfiguration/studyConfigurationApi';
import {
  isFieldDcfOpen,
  openDcfResolutionAllowedAtHeaderStage,
  dcfCloseAllowedAtViewingStage,
  openDcfRowSubmitSatisfiedClient,
} from './Dcf/dcfHighlight';
import { useDcf } from './Dcf/useDcf';

/** Matches server-side CRF upload path check in populateSavedFieldValues (file fields). */
function isCrfServerUploadPath(path) {
  const p = String(path || '').trim();
  if (!p) return false;
  return /^[A-Za-z0-9_-]+(\/[^\\]*)?$/i.test(p) && (p.startsWith('CRFUploads') || p.includes('/'));
}

/** Deduplicated uploaded files from Visit Report file inputs (DOM order). */
function collectCrfUploadedAttachmentDescriptors(content) {
  if (!content) return [];
  const map = new Map();
  content.querySelectorAll('input[type="file"][data-uploaded-path]').forEach((inp) => {
    const path = (inp.getAttribute('data-uploaded-path') || '').trim();
    if (!isCrfServerUploadPath(path)) return;
    const filename = (inp.getAttribute('data-saved-filename') || path.split(/[/\\]/).pop() || 'attachment').trim();
    if (!map.has(path)) map.set(path, { path, filename });
  });
  return Array.from(map.values());
}

async function fetchCrfFileBlob(path) {
  const base = (typeof window !== 'undefined' && (sessionStorage.getItem('ctms_api_base_url') || '')) || '';
  const baseNorm = base.replace(/\/$/, '');
  const urlPath = baseNorm.toLowerCase().endsWith('/api') ? 'crf/file' : 'api/crf/file';
  const res = await api.get(urlPath, {
    params: { path: String(path || '').trim() },
    responseType: 'blob',
  });
  return res.data;
}

function classifyAttachmentForPreview(blob, filename) {
  const mime = (blob && blob.type) || '';
  const lower = (filename || '').toLowerCase();
  if (mime.startsWith('image/')) return 'image';
  if (mime === 'application/pdf' || lower.endsWith('.pdf')) return 'pdf';
  if (/\.(png|jpe?g|gif|webp)$/i.test(lower)) return 'image';
  return 'other';
}

async function appendPdfBlobsToPdfBytes(mainBytes, pdfBlobs) {
  if (!pdfBlobs || pdfBlobs.length === 0) return mainBytes;
  const merged = await PDFDocument.create();
  const mainDoc = await PDFDocument.load(mainBytes);
  const mainCopied = await merged.copyPages(mainDoc, mainDoc.getPageIndices());
  mainCopied.forEach((p) => merged.addPage(p));
  for (let i = 0; i < pdfBlobs.length; i += 1) {
    const blob = pdfBlobs[i];
    const ab = await blob.arrayBuffer();
    const att = await PDFDocument.load(ab, { ignoreEncryption: true });
    const copied = await merged.copyPages(att, att.getPageIndices());
    copied.forEach((p) => merged.addPage(p));
  }
  return merged.save({ useObjectStreams: false });
}

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function collectVisitReportPdfCss() {
  if (typeof document === 'undefined') return '';
  const cssParts = [];
  document.querySelectorAll('style').forEach((styleEl) => {
    const text = styleEl.textContent || '';
    if (text.trim()) cssParts.push(text);
  });
  Array.from(document.styleSheets || []).forEach((sheet) => {
    try {
      const rules = sheet.cssRules;
      if (!rules) return;
      const text = Array.from(rules).map((rule) => rule.cssText).join('\n');
      if (text.trim()) cssParts.push(text);
    } catch (_) {
      // Cross-origin stylesheets cannot be read; Visit Report CSS is bundled same-origin.
    }
  });
  return cssParts.join('\n');
}

async function renderVisitReportPdfOnServer(element) {
  const html = element?.outerHTML || '';
  if (!html.trim()) {
    const e = new Error('Visit Report preview HTML is empty.');
    e.code = VISIT_REPORT_PDF_ERROR_CODES.EMPTY_PREVIEW;
    throw e;
  }
  const response = await api.post('/pdf/visit-report', {
    html,
    css: collectVisitReportPdfCss(),
    title: 'Visit Report Preview',
  }, {
    responseType: 'blob',
    timeout: 120000,
  });
  const blob = response.data instanceof Blob
    ? response.data
    : new Blob([response.data], { type: 'application/pdf' });
  return assertVisitReportPdfBlob(blob);
}

// Read value from object using first matching key (handles API naming convention differences)
const getAnyCase = (obj, ...keys) => {
  if (obj == null) return undefined;
  for (const k of keys) {
    if (Object.prototype.hasOwnProperty.call(obj, k) && obj[k] != null) return obj[k];
  }
  return undefined;
};

/** Collapse whitespace + case for comparing StageMst display names (e.g. First Review vs first  review). */
const normalizeStageLabelForMatch = (s) =>
  (s ?? '')
    .toString()
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');

/** From GET /crf/page* or /actions when API sends isReviewStage (dynamic review stages from StageMst). */
const readPageIsReviewStage = (pageData) => {
  if (!pageData || typeof pageData !== 'object') return null;
  if (typeof pageData.isReviewStage === 'boolean') return pageData.isReviewStage;
  if (typeof pageData.IsReviewStage === 'boolean') return pageData.IsReviewStage;
  return null;
};

/** From GET /crf/page* when API sends isHeaderStageInProgress (workflow In Progress, display-aware). Undefined = property absent. */
const readPageHeaderStageInProgress = (pageData) => {
  if (!pageData || typeof pageData !== 'object') return undefined;
  if (Object.prototype.hasOwnProperty.call(pageData, 'isHeaderStageInProgress'))
    return pageData.isHeaderStageInProgress === true;
  if (Object.prototype.hasOwnProperty.call(pageData, 'IsHeaderStageInProgress'))
    return pageData.IsHeaderStageInProgress === true;
  return undefined;
};

/** Numeric StageMst.iStageCode from GET /crf/page* (optional). */
const readCurrentStageCodeFromPage = (pageData) => {
  if (!pageData || typeof pageData !== 'object') return null;
  const v = getAnyCase(pageData, 'currentStageCode', 'CurrentStageCode');
  if (v == null || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

/** Undefined = property absent (do not clear state on partial payloads). */
const pickCurrentStageCodeUpdate = (pageData) => {
  if (!pageData || typeof pageData !== 'object') return undefined;
  if (
    !Object.prototype.hasOwnProperty.call(pageData, 'currentStageCode')
    && !Object.prototype.hasOwnProperty.call(pageData, 'CurrentStageCode')
  ) {
    return undefined;
  }
  return readCurrentStageCodeFromPage(pageData);
};

/** CRFHdr.CSaveType from GET /crf/page*, /actions (backend uses E after review-tier Submit). */
const readCSaveTypeFromPage = (pageData) => {
  if (!pageData || typeof pageData !== 'object') return null;
  const v = getAnyCase(pageData, 'cSaveType', 'CSaveType');
  if (v == null || v === '') return null;
  return String(v).trim();
};

/** Escape selector attribute fragments (prefer CSS.escape). */
function cssEscapeAttr(value) {
  const s = String(value ?? '');
  if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') return CSS.escape(s);
  return s.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
}

function inferAssociatedDriverUiKind(driverType, driverValues, assocDomHint = null) {
  const dt = String(driverType || '').trim().toLowerCase();
  if (dt === 'radio' || dt === 'checkbox' || dt === 'combobox' || dt === 'dropdown') return dt;
  const parts = String(driverValues || '')
    .split(',')
    .map((x) => x.trim())
    .filter(Boolean);
  if (parts.length >= 2 && parts.some((p) => /^yes$/i.test(p) || /^no$/i.test(p))) return 'radio';
  // Template JSON sometimes omits driverType or omits Yes/No in driverValues; visit report still emits radios inside the Associated row.
  if (
    assocDomHint &&
    (assocDomHint.querySelector('.crf-associated-driver-wrap input[type="radio"]') ||
      assocDomHint.querySelector('.crf-associated-driver-only input[type="radio"]') ||
      assocDomHint.querySelector('.crf-answer-cell input[type="radio"]'))
  ) {
    return 'radio';
  }
  return dt || '';
}

function associatedBranchValueToList(v) {
  if (Array.isArray(v)) return v.map((x) => String(x || '').trim()).filter(Boolean);
  if (v == null || v === '') return [];
  if (typeof v === 'object' && v !== null) {
    return Object.values(v).flatMap((x) => associatedBranchValueToList(x));
  }
  return [String(v).trim()].filter(Boolean);
}

/** Align with API CRFPageService.ResolveAssociatedActiveBranchKey — allowed driverValues match ignoring case. */
function driverOptionMatchesAllowed(val, rawVals) {
  const list = Array.isArray(rawVals) ? rawVals : [];
  if (!list.length) return true;
  const v = String(val ?? '').trim();
  if (!v) return false;
  const lc = v.toLowerCase();
  return list.some((rv) => String(rv ?? '').trim().toLowerCase() === lc);
}

/** Aliases for numeric MedEx codes (e.g. API/detail uses 00002; DOM id parsing may yield 2). */
function collectMedExCodeLookupVariants(codeRaw) {
  const s = String(codeRaw || '').trim();
  if (!s) return [];
  const set = new Set([s]);
  if (/^\d+$/.test(s)) {
    const n = parseInt(s, 10);
    if (!Number.isNaN(n)) {
      set.add(String(n));
      set.add(String(n).padStart(5, '0'));
      const p4 = String(n).padStart(4, '0');
      if (p4 !== String(n).padStart(5, '0')) set.add(p4);
    }
  }
  return [...set];
}

/**
 * Whether the saved CRF payload already has a non-empty answer or comment for this field code.
 * Used so visit-report rows stay visible when the API lists them as Associated-inactive for the
 * current workflow stage but the same page load already includes persisted values from an earlier tier.
 */
function savedFieldRowHasPersistedDisplayData(savedFields, codeRaw) {
  const want = new Set(
    collectMedExCodeLookupVariants(codeRaw).map((v) => String(v).trim().toLowerCase()).filter(Boolean)
  );
  if (want.size === 0) return false;
  const list = Array.isArray(savedFields) ? savedFields : [];
  for (let i = 0; i < list.length; i += 1) {
    const f = list[i];
    const id = String(f?.vFieldId ?? f?.VFieldId ?? '').trim();
    if (!id) continue;
    const idLc = id.toLowerCase();
    let match = want.has(idLc);
    if (!match) {
      const idVars = collectMedExCodeLookupVariants(id);
      for (let j = 0; j < idVars.length; j += 1) {
        if (want.has(String(idVars[j]).trim().toLowerCase())) {
          match = true;
          break;
        }
      }
    }
    if (!match) continue;
    const val = String(f?.vFieldValue ?? f?.VFieldValue ?? '').trim();
    const cmt = String(f?.vComment ?? f?.VComment ?? '').trim();
    if (val || cmt) return true;
  }
  return false;
}

/**
 * True when {@code codeRaw} is not a branch child of any Associated block, or it is on the active driver branch.
 * Prevents serverInactive "persisted re-show" from overriding branch hide (e.g. ABC1 only on Yes while driver is No).
 */
function isAssociatedBranchChildShownForCurrentDriver(content, savedFields, codeRaw) {
  if (!content || codeRaw == null || codeRaw === '') return true;
  const wantLc = new Set(
    collectMedExCodeLookupVariants(codeRaw).map((v) => String(v || '').trim().toLowerCase()).filter(Boolean)
  );
  if (wantLc.size === 0) return true;

  const fieldCodeMatchesWant = (code) => {
    const c = String(code || '').trim();
    if (!c) return false;
    if (wantLc.has(c.toLowerCase())) return true;
    const vars = collectMedExCodeLookupVariants(c);
    for (let i = 0; i < vars.length; i += 1) {
      if (wantLc.has(String(vars[i] || '').trim().toLowerCase())) return true;
    }
    return false;
  };

  const markers = queryCrfAssociatedMarkerElements(content);
  let childDefinedInAnyAssociation = false;
  let onActiveBranchInDefiningAssociation = false;

  markers.forEach((wrap) => {
    const raw = wrap.getAttribute('data-associated-config') || '';
    let cfg = {};
    try {
      cfg = JSON.parse(decodeAssociatedConfigAttribute(raw) || '{}');
    } catch {
      cfg = {};
    }
    const branchesRaw = cfg.branches ?? cfg.Branches ?? {};
    const branches = typeof branchesRaw === 'object' && branchesRaw !== null ? branchesRaw : {};
    const allCodes = new Set();
    Object.keys(branches).forEach((k) => {
      associatedBranchValueToList(branches[k]).forEach((c) => {
        const t = String(c || '').trim();
        if (t) allCodes.add(t);
      });
    });
    const touches = Array.from(allCodes).some((c) => fieldCodeMatchesWant(c));
    if (!touches) return;
    childDefinedInAnyAssociation = true;
    const branchKey = resolveAssociatedBranchKey(content, cfg, savedFields, wrap);
    const activeList = getAssociatedBranchList(branches, branchKey);
    const arr = Array.isArray(activeList) ? activeList.map((c) => String(c || '').trim()) : [];
    if (branchKey != null && arr.some((code) => fieldCodeMatchesWant(code))) {
      onActiveBranchInDefiningAssociation = true;
    }
  });

  if (!childDefinedInAnyAssociation) return true;
  return onActiveBranchInDefiningAssociation;
}

/**
 * False when {@code codeRaw} is a branch child of an Associated block whose {@code branchStageCode} / {@code stageCode}
 * does not match the current visit/CRF stage — blocks serverInactive persisted re-show in In Progress when the child is
 * assigned to First Review only.
 */
function isAssociatedBranchChildAllowedAtAssignedStage(
  content,
  stageList,
  crfStage,
  crfCurrentStageCode,
  codeRaw
) {
  if (!content || codeRaw == null || codeRaw === '') return true;
  const list = Array.isArray(stageList) ? stageList : [];
  const wantLc = new Set(
    collectMedExCodeLookupVariants(codeRaw).map((v) => String(v || '').trim().toLowerCase()).filter(Boolean)
  );
  if (wantLc.size === 0) return true;

  const fieldCodeMatchesWant = (code) => {
    const c = String(code || '').trim();
    if (!c) return false;
    if (wantLc.has(c.toLowerCase())) return true;
    const vars = collectMedExCodeLookupVariants(c);
    for (let i = 0; i < vars.length; i += 1) {
      if (wantLc.has(String(vars[i] || '').trim().toLowerCase())) return true;
    }
    return false;
  };

  const markers = queryCrfAssociatedMarkerElements(content);
  let isBranchChild = false;
  let blockedByStageGate = false;

  markers.forEach((wrap) => {
    const raw = wrap.getAttribute('data-associated-config') || '';
    let cfg = {};
    try {
      cfg = JSON.parse(decodeAssociatedConfigAttribute(raw) || '{}');
    } catch {
      cfg = {};
    }
    const branchesRaw = cfg.branches ?? cfg.Branches ?? {};
    const branches = typeof branchesRaw === 'object' && branchesRaw !== null ? branchesRaw : {};
    const allCodes = new Set();
    Object.keys(branches).forEach((k) => {
      associatedBranchValueToList(branches[k]).forEach((c) => {
        const t = String(c || '').trim();
        if (t) allCodes.add(t);
      });
    });
    const touches = Array.from(allCodes).some((c) => fieldCodeMatchesWant(c));
    if (!touches) return;
    isBranchChild = true;
    const wantStage = resolveAssociatedStageGate(cfg);
    if (wantStage == null || wantStage === '') return;
    if (!isWorkflowAtOrAfterAssociatedStageGate(wantStage, list, crfCurrentStageCode, crfStage)) {
      blockedByStageGate = true;
    }
  });

  if (!isBranchChild) return true;
  return !blockedByStageGate;
}

/** Section wrapper for a CRF field code (Associated visibility). */
function findCrfFieldSection(content, fieldCode) {
  const code = String(fieldCode || '').trim();
  if (!code || !content) return null;
  const reportRow = findCrfReportRowForFieldId(content, code);
  if (reportRow) return reportRow;
  const byMedVisit = content.querySelector(`.crf-report-row[data-medex-code="${cssEscapeAttr(code)}"]`);
  if (byMedVisit) return byMedVisit;
  const esc = typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(code) : code.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
  const byFieldId = content.querySelector(
    `.crf-report-row .crf-answer-cell[data-field-id="${esc}"], .crf-report-row .crf-answer-col[data-field-id="${esc}"], .crf-report-row [data-field-id="${esc}"]`
  );
  if (byFieldId) {
    const row = byFieldId.closest('.crf-report-row');
    if (row) return row;
  }
  const el = content.querySelector(
    `input[id$="${code}"], select[id$="${code}"], textarea[id$="${code}"]`
  );
  if (!el) return null;
  return (
    el.closest('.crf-report-row') ||
    el.closest('.form-section-box') ||
    el.closest('.CRFControl')
  );
}

/**
 * Active branch key from driver control (must match association JSON branches).
 * optionalAssociatedWrap: visit-report row / node with [data-crf-associated="1"] (driver radios live in that row after transform).
 */
function getDriverBranchKeyForAssociated(content, driverFieldCode, driverType, driverValues, optionalAssociatedWrap = null) {
  const code = String(driverFieldCode || '').trim();
  if (!code || !content) return null;

  let assocWrap = optionalAssociatedWrap;
  if (assocWrap && assocWrap.getAttribute && assocWrap.getAttribute('data-crf-associated') !== '1') {
    assocWrap = assocWrap.closest('[data-crf-associated="1"]');
  }

  const searchRoots = [];
  if (assocWrap?.querySelectorAll) searchRoots.push(assocWrap);
  const medRow = content.querySelector(`.crf-report-row[data-medex-code="${cssEscapeAttr(code)}"]`);
  if (medRow && !searchRoots.includes(medRow)) searchRoots.push(medRow);
  if (!searchRoots.includes(content)) searchRoots.push(content);

  const rawVals = String(driverValues || '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);

  // Read driver value from radios actually rendered on the Associated/driver row FIRST. This avoids sticking to
  // stale savedFields "Yes" when driverFieldCode / radio names don't match naive selectors (ABC1 stays visible bug).
  const readScopedAssociatedRadioBranch = () => {
    const tryRoot = (root) => {
      if (!root || root === content || typeof root.querySelector !== 'function') return null;
      const scoped =
        root.querySelector('.crf-associated-driver-wrap input[type="radio"]:checked') ||
        root.querySelector('.crf-associated-driver-only input[type="radio"]:checked') ||
        root.querySelector('.crf-answer-cell input[type="radio"]:checked');
      if (!scoped) return null;
      const val = (scoped.value || '').trim();
      if (rawVals.length && val && !driverOptionMatchesAllowed(val, rawVals)) return '__reject__';
      return val !== '' ? val : '__empty_checked__';
    };
    const ordered = [];
    if (assocWrap) ordered.push(assocWrap);
    if (medRow && medRow !== assocWrap) ordered.push(medRow);
    let v = null;
    for (let i = 0; i < ordered.length; i += 1) {
      const r = tryRoot(ordered[i]);
      if (r === '__reject__') return null;
      if (r != null && r !== '__empty_checked__') {
        v = r;
        break;
      }
    }
    return v;
  };
  const scopedBranch = readScopedAssociatedRadioBranch();
  if (scopedBranch != null) return scopedBranch;

  const assocHint = assocWrap || medRow || null;
  const dtLower = inferAssociatedDriverUiKind(driverType, driverValues, assocHint);
  const esc = cssEscapeAttr(code);

  const findRadioChecked = (root) => {
    if (!root?.querySelector) return null;
    let el =
      root.querySelector(`input[type="radio"][name="radio_${esc}"]:checked`) ||
      root.querySelector(`input[type="radio"][name^="radio_${esc}_"]:checked`) ||
      root.querySelector(`input[type="radio"][name$="_${esc}"]:checked`);
    if (el) return el;
    const driverWrap = root.querySelector('.crf-associated-driver-wrap');
    if (driverWrap) {
      el = driverWrap.querySelector('input[type="radio"]:checked');
      if (el) return el;
    }
    const allChecked = root.querySelectorAll('input[type="radio"]:checked');
    for (let i = 0; i < allChecked.length; i += 1) {
      const inp = allChecked[i];
      const n = (inp.name || '').trim();
      if (!n) continue;
      if (n === `radio_${code}` || n.endsWith(`_${code}`)) return inp;
    }
    return null;
  };

  if (dtLower !== 'radio' && dtLower !== 'checkbox') {
    for (let i = 0; i < searchRoots.length; i += 1) {
      const root = searchRoots[i];
      const el =
        root.querySelector(`select#${esc}`) ||
        root.querySelector(`select[id="${code}"]`) ||
        root.querySelector(`select[id$="${esc}"]`);
      if (el?.tagName === 'SELECT') {
        const v = (el.value || '').trim();
        if (!v) continue;
        if (rawVals.length && !driverOptionMatchesAllowed(v, rawVals)) continue;
        return v;
      }
    }
  }

  if (dtLower === 'radio') {
    for (let i = 0; i < searchRoots.length; i += 1) {
      const checked = findRadioChecked(searchRoots[i]);
      if (!checked) continue;
      const val = (checked.value || '').trim();
      if (rawVals.length && val && !driverOptionMatchesAllowed(val, rawVals)) return null;
      return val || null;
    }
    return null;
  }

  if (dtLower === 'checkbox') {
    for (let i = 0; i < searchRoots.length; i += 1) {
      const root = searchRoots[i];
      if (rawVals.length >= 2) {
        const cbs = root.querySelectorAll(
          `input[type="checkbox"][id*="checkbox_${code}"], input[type="checkbox"][name*="checkbox_${code}"]`
        );
        for (const cb of cbs) {
          if (!cb.checked) continue;
          const val = (cb.value || '').trim();
          if (driverOptionMatchesAllowed(val, rawVals)) return val;
        }
        continue;
      }
      const cb =
        root.querySelector(`input[type="checkbox"][id*="checkbox_${code}"]`) ||
        root.querySelector(`input[type="checkbox"][id$="${esc}"]`);
      if (cb) return cb.checked ? '__checked__' : '__unchecked__';
    }
    return '__default__';
  }

  const elFallback = content.querySelector(
    `input[id$="${esc}"], select[id$="${esc}"], textarea[id$="${esc}"]`
  );
  if (elFallback?.tagName === 'SELECT') {
    const v = (elFallback.value || '').trim();
    if (!v) return null;
    if (rawVals.length && !driverOptionMatchesAllowed(v, rawVals)) return null;
    return v;
  }
  for (let i = 0; i < searchRoots.length; i += 1) {
    const root = searchRoots[i];
    const inp =
      root.querySelector(`input#${esc}`) ||
      root.querySelector(`textarea#${esc}`) ||
      root.querySelector(`input[id$="${esc}"], textarea[id$="${esc}"]`);
    if (inp && (inp.tagName === 'INPUT' || inp.tagName === 'TEXTAREA')) {
      const v = (inp.value || '').trim();
      if (v) return v;
    }
  }
  return null;
}

/** Branch map lookup that tolerates case/whitespace differences in keys. */
function getAssociatedBranchList(branches, branchKey) {
  if (!branches || typeof branches !== 'object' || branchKey == null) return null;
  let raw = null;
  if (Object.prototype.hasOwnProperty.call(branches, branchKey)) {
    raw = branches[branchKey];
  } else {
    const want = String(branchKey).trim().toLowerCase();
    for (const key of Object.keys(branches)) {
      if (String(key || '').trim().toLowerCase() === want) {
        raw = branches[key];
        break;
      }
    }
  }
  if (raw == null) return null;
  if (Array.isArray(raw)) return raw.map((x) => String(x || '').trim()).filter(Boolean);
  return associatedBranchValueToList(raw);
}

/** Fallback: read driver value from savedFields when DOM has no current selection (e.g. previous stage answer). */
function getSavedDriverBranchKeyForAssociated(savedFields, driverFieldCode, driverValues) {
  const code = String(driverFieldCode || '').trim();
  if (!code || !Array.isArray(savedFields) || savedFields.length === 0) return null;
  const row = savedFields.find((f) => String(f?.vFieldId ?? f?.VFieldId ?? '').trim() === code);
  if (!row) return null;
  const raw = String(row?.vFieldValue ?? row?.VFieldValue ?? '').trim();
  if (!raw) return null;
  const allowed = String(driverValues || '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
  if (allowed.length === 0) return raw;
  if (driverOptionMatchesAllowed(raw, allowed)) return raw;
  const parts = raw.split(',').map((s) => s.trim()).filter(Boolean);
  return parts.find((p) => driverOptionMatchesAllowed(p, allowed)) || null;
}

/**
 * Prefer live driver value from DOM when available so Yes-branch children hide after CRA changes Associated driver to No
 * (workflow: Submit Yes → First Review → Send back → In Progress No → Submit skips PM → Second Review).
 */
function resolveAssociatedBranchKey(content, cfg, savedFields, optionalAssociatedWrap = null) {
  const driverCode = String(cfg?.driverFieldCode ?? cfg?.DriverFieldCode ?? '').trim();
  if (!driverCode) return null;
  const driverType = cfg?.driverType ?? cfg?.DriverType ?? '';
  const driverValues = cfg?.driverValues ?? cfg?.DriverValues ?? '';
  const fromDom = getDriverBranchKeyForAssociated(content, driverCode, driverType, driverValues, optionalAssociatedWrap);
  if (fromDom != null && String(fromDom).trim() !== '') return String(fromDom).trim();
  return getSavedDriverBranchKeyForAssociated(savedFields, driverCode, driverValues);
}

/**
 * Prefer `.crf-report-row[data-crf-associated="1"]` so a duplicate hidden template block with the same marker
 * does not run visibility twice (second pass could re-show Yes-only children from savedFields).
 */
function queryCrfAssociatedMarkerElements(content) {
  if (!content?.querySelectorAll) return [];
  const reportRows = content.querySelectorAll('.crf-report-row[data-crf-associated="1"]');
  if (reportRows.length > 0) return reportRows;
  return content.querySelectorAll('[data-crf-associated="1"]');
}

function decodeAssociatedConfigAttribute(raw) {
  if (raw == null) return '';
  if (typeof document === 'undefined') return String(raw);
  const t = document.createElement('textarea');
  t.innerHTML = String(raw);
  return t.value;
}

/** Child codes editable at current stage for active associated branch. */
function getEditableAssociatedChildCodes(content, savedFields, crfCurrentStageCode, crfStage, stageList = []) {
  const editable = new Set();
  if (!content) return editable;
  const markers = queryCrfAssociatedMarkerElements(content);
  markers.forEach((wrap) => {
    const raw = wrap.getAttribute('data-associated-config') || '';
    let cfg = {};
    try {
      cfg = JSON.parse(decodeAssociatedConfigAttribute(raw) || '{}');
    } catch {
      cfg = {};
    }
    const driverCode = String(cfg.driverFieldCode || cfg.DriverFieldCode || '').trim();
    const branchesRaw = cfg.branches ?? cfg.Branches ?? {};
    const branches = typeof branchesRaw === 'object' && branchesRaw !== null ? branchesRaw : {};

    const wantStage = resolveAssociatedStageGate(cfg);
    const stageBlocked = isAssociatedStageMismatch(
      wantStage,
      Array.isArray(stageList) ? stageList : [],
      crfCurrentStageCode,
      crfStage
    );
    if (stageBlocked) return;

    const branchKey = resolveAssociatedBranchKey(content, cfg, savedFields, wrap);
    const activeList = getAssociatedBranchList(branches, branchKey);
    if (!Array.isArray(activeList)) return;
    activeList.forEach((code) => {
      const c = String(code || '').trim();
      if (!c || c === driverCode) return;
      editable.add(c);
    });
  });
  return editable;
}

/** Union of all Associated branch child field codes (every branch value list), excluding the driver — for DCF/visibility overrides. */
function getAllAssociatedBranchChildCodes(content) {
  const all = new Set();
  if (!content) return all;
  const markers = queryCrfAssociatedMarkerElements(content);
  markers.forEach((wrap) => {
    const raw = wrap.getAttribute('data-associated-config') || '';
    let cfg = {};
    try {
      cfg = JSON.parse(decodeAssociatedConfigAttribute(raw) || '{}');
    } catch {
      cfg = {};
    }
    const driverCode = String(cfg.driverFieldCode || cfg.DriverFieldCode || '').trim();
    const branchesRaw = cfg.branches ?? cfg.Branches ?? {};
    const branches = typeof branchesRaw === 'object' && branchesRaw !== null ? branchesRaw : {};
    Object.keys(branches).forEach((k) => {
      associatedBranchValueToList(branches[k]).forEach((c) => {
        const t = String(c || '').trim();
        if (t && t !== driverCode) all.add(t);
      });
    });
  });
  return all;
}

/** Add Associated branch field codes that have Open DCF + IsEditable (server) — for reviewer enable before/after stage fetch. */
function mergeOpenDcfEditableAssociatedBranchChildCodes(
  content,
  savedFields,
  hasOpenDCF,
  targetSet,
  headerStageLabel,
  headerCurrentStageCode,
  apiHeaderStageInProgress
) {
  if (!hasOpenDCF || !content || !(targetSet instanceof Set)) return;
  const allBranch = getAllAssociatedBranchChildCodes(content);
  const hdr = headerStageLabel != null ? String(headerStageLabel).trim() : '';
  (Array.isArray(savedFields) ? savedFields : []).forEach((f) => {
    if (!isFieldDcfOpen(f)) return;
    const isEditable = f.isEditable === true || f.IsEditable === true;
    if (!isEditable) return;
    if (!openDcfResolutionAllowedAtHeaderStage(f, hdr, headerCurrentStageCode, apiHeaderStageInProgress)) return;
    const id = String(f?.vFieldId ?? f?.VFieldId ?? '').trim();
    if (id && allBranch.has(id)) targetSet.add(id);
  });
}

/** After inactive hide: re-show branch rows that have Open DCF and server IsEditable (reviewer/CRA payload). */
function forceShowAssociatedBranchRowsWithOpenEditableDcf(content, savedFields, branchChildCodes) {
  if (!content || !Array.isArray(savedFields) || savedFields.length === 0) return;
  const branchSet = branchChildCodes instanceof Set ? branchChildCodes : new Set(branchChildCodes);
  if (branchSet.size === 0) return;
  savedFields.forEach((f) => {
    if (!isFieldDcfOpen(f)) return;
    const isEditable = f.isEditable === true || f.IsEditable === true;
    if (!isEditable) return;
    const id = String(f?.vFieldId ?? f?.VFieldId ?? '').trim();
    if (!id || !branchSet.has(id)) return;
    const variants = collectMedExCodeLookupVariants(id);
    for (let vi = 0; vi < variants.length; vi += 1) {
      const sec = findCrfFieldSection(content, variants[vi]);
      if (!sec) continue;
      if (sec.classList?.contains('crf-report-row')) {
        sec.classList.add('crf-report-row-associated-branch');
      }
      setVisitReportSectionDisplay(sec, true);
      break;
    }
  });
}

/** Optional stage gate: branchStageCode, else legacy stageCode. Ignores driverStageCode. */
function resolveAssociatedStageGate(cfg) {
  if (!cfg || typeof cfg !== 'object') return null;
  const b = getAnyCase(cfg, 'branchStageCode', 'BranchStageCode');
  if (b != null && b !== '') return b;
  const s = getAnyCase(cfg, 'stageCode', 'StageCode');
  if (s != null && s !== '') return s;
  return null;
}

function isPmReviewerProfile(userProfile, profileAliases) {
  const p = String(userProfile || '').trim().toUpperCase();
  if (p === 'PM') return true;
  if (profileAliases instanceof Set && profileAliases.has('pm')) return true;
  return false;
}

/** Unblinded ≡ Open Label (same as StudyDesignVisitLaneHelper). */
function normalizeStudyDesignLane(desc) {
  const s = (desc || '').toString().trim().toLowerCase();
  if (!s) return '';
  if (s === 'unblinded' || s.includes('open label')) return 'open label';
  return s;
}

/** True when MedExAssociationTemplateRule branch stage gate is the project's first review tier (StageMst ordered[0]). */
function isFirstReviewAssociatedStageGate(wantStageCode, stageList) {
  return isFirstReviewStageGate(wantStageCode, stageList);
}

/**
 * branchStageCode / stageCode from the Associated block whose active branch contains {@code fieldId}.
 * Matches server DcfAssociatedResolver (iBranchStageCode on MedExAssociationTemplateRule).
 */
function resolveActiveAssociatedBranchStageGate(content, savedFields, fieldId) {
  if (!content || fieldId == null || fieldId === '') return null;
  const wantLc = new Set(
    collectMedExCodeLookupVariants(fieldId).map((v) => String(v || '').trim().toLowerCase()).filter(Boolean)
  );
  if (wantLc.size === 0) return null;

  const fieldCodeMatchesWant = (code) => {
    const c = String(code || '').trim();
    if (!c) return false;
    if (wantLc.has(c.toLowerCase())) return true;
    const vars = collectMedExCodeLookupVariants(c);
    for (let i = 0; i < vars.length; i += 1) {
      if (wantLc.has(String(vars[i] || '').trim().toLowerCase())) return true;
    }
    return false;
  };

  const markers = queryCrfAssociatedMarkerElements(content);
  for (let mi = 0; mi < markers.length; mi += 1) {
    const wrap = markers[mi];
    const raw = wrap.getAttribute('data-associated-config') || '';
    let cfg = {};
    try {
      cfg = JSON.parse(decodeAssociatedConfigAttribute(raw) || '{}');
    } catch {
      cfg = {};
    }
    const branchesRaw = cfg.branches ?? cfg.Branches ?? {};
    const branches = typeof branchesRaw === 'object' && branchesRaw !== null ? branchesRaw : {};
    const branchKey = resolveAssociatedBranchKey(content, cfg, savedFields, wrap);
    const activeList = getAssociatedBranchList(branches, branchKey);
    const arr = Array.isArray(activeList) ? activeList : [];
    if (!arr.some((code) => fieldCodeMatchesWant(code))) continue;
    const branchStageCode = resolveAssociatedStageGate(cfg);
    if (branchStageCode == null || branchStageCode === '') continue;
    return branchStageCode;
  }
  return null;
}

/**
 * PM must not raise a Reviewer's Comment on its own Associated-branch fields
 * (branchStageCode → First Review, or visit header already at that assigned stage).
 */
function getPmOwnFieldDcfRaiseBlockMessage(
  content,
  fieldId,
  savedFields,
  stageList,
  crfStage,
  crfCurrentStageCode,
  userProfile
) {
  if (!isPmReviewerProfile(userProfile)) return null;
  const branchStageCode = resolveActiveAssociatedBranchStageGate(content, savedFields, fieldId);
  if (branchStageCode == null) return null;
  const list = Array.isArray(stageList) ? stageList : [];
  const isFirstReviewBranch = isFirstReviewAssociatedStageGate(branchStageCode, list);
  const headerAtAssignedBranch = !isAssociatedStageMismatch(
    branchStageCode,
    list,
    crfCurrentStageCode,
    crfStage
  );
  if (isFirstReviewBranch) return dcfMessages.raiseBlockedPmOwnAssignedBranchField;
  if (headerAtAssignedBranch) return dcfMessages.raiseBlockedPmOwnAssignedBranchField;
  return null;
}

function isReviewerProfile(userProfile, profileAliases) {
  const p = String(userProfile || '').trim().toUpperCase();
  if (p === 'PM' || p === 'SRM') return true;
  if (profileAliases instanceof Set) {
    if (profileAliases.has('pm') || profileAliases.has('srm')) return true;
  }
  return false;
}

function isOpenUnreleasedDcfFieldRow(f) {
  const status = (f?.vDCFStatus ?? f?.VDCFStatus ?? '').toString().trim().toLowerCase();
  if (status !== 'open') return false;
  const submitCra = f?.iSubmitDCFToCRA ?? f?.ISubmitDCFToCRA ?? f?.isubmitDCFToCRA;
  return !(submitCra === 1 || submitCra === '1' || submitCra === true);
}

/** Saved CRF row for DCF open/raise by detail no and/or MedEx field code. */
function findSavedFieldForDcfActions(savedFields, fieldId, nCRFDtlNo) {
  const list = savedFields || [];
  if (nCRFDtlNo != null && nCRFDtlNo !== '' && !Number.isNaN(Number(nCRFDtlNo))) {
    const no = Number(nCRFDtlNo);
    const byDtl = list.find((x) => Number(x.nCRFDtlNo ?? x.NCRFDtlNo ?? x.ncrfDtlNo ?? 0) === no);
    if (byDtl) return byDtl;
  }
  const trimmed = (fieldId ?? '').toString().trim();
  if (!trimmed) return null;
  return (
    list.find((x) => {
      const fid = (x.vFieldId || x.VFieldId || '').trim();
      if (!fid) return false;
      if (fid.toLowerCase() === trimmed.toLowerCase()) return true;
      return crfFieldCodesEquivalent(fid, trimmed);
    }) ?? null
  );
}

/** Open or Resolved Reviewer's Comment already exists on this field (view/respond only — not a new raise). */
function fieldHasActiveDcfThread(savedFields, fieldId, nCRFDtlNo) {
  const f = findSavedFieldForDcfActions(savedFields, fieldId, nCRFDtlNo);
  if (!f) return false;
  const status = (f.vDCFStatus ?? f.VDCFStatus ?? '').toString().trim().toLowerCase();
  return status === 'open' || status === 'resolved';
}

function crfFieldCodesEquivalent(a, b) {
  const aVars = collectMedExCodeLookupVariants(a)
    .map((v) => String(v || '').trim().toLowerCase())
    .filter(Boolean);
  const bVars = collectMedExCodeLookupVariants(b)
    .map((v) => String(v || '').trim().toLowerCase())
    .filter(Boolean);
  if (!aVars.length || !bVars.length) return false;
  const bSet = new Set(bVars);
  return aVars.some((v) => bSet.has(v));
}

/** Associated branch child on the active driver branch assigned to this reviewer's tier (e.g. PM → First Review). */
function isBranchFieldAssignedToReviewerTier(
  content,
  savedFields,
  fieldId,
  stageList,
  crfStage,
  crfCurrentStageCode,
  userProfile
) {
  const branchGate = resolveActiveAssociatedBranchStageGate(content, savedFields, fieldId);
  if (branchGate == null) return false;
  if (isPmReviewerProfile(userProfile) && isFirstReviewAssociatedStageGate(branchGate, stageList)) {
    return true;
  }
  return !isAssociatedStageMismatch(branchGate, stageList, crfCurrentStageCode, crfStage);
}

/**
 * All branch child codes under associations whose branchStageCode matches {@code gateMatcher}
 * (any driver branch — used for pending DCF gate when raiser may be another profile, e.g. SRM → PM tier).
 */
function getAssociatedBranchChildCodesForStageGate(content, stageList, gateMatcher) {
  const codes = new Set();
  if (!content || typeof gateMatcher !== 'function') return codes;
  const markers = queryCrfAssociatedMarkerElements(content);
  markers.forEach((wrap) => {
    const raw = wrap.getAttribute('data-associated-config') || '';
    let cfg = {};
    try {
      cfg = JSON.parse(decodeAssociatedConfigAttribute(raw) || '{}');
    } catch {
      cfg = {};
    }
    const gate = resolveAssociatedStageGate(cfg);
    if (!gateMatcher(gate, stageList)) return;
    const driverCode = String(cfg.driverFieldCode || cfg.DriverFieldCode || '').trim();
    const branchesRaw = cfg.branches ?? cfg.Branches ?? {};
    const branches = typeof branchesRaw === 'object' && branchesRaw !== null ? branchesRaw : {};
    Object.keys(branches).forEach((k) => {
      associatedBranchValueToList(branches[k]).forEach((c) => {
        const t = String(c || '').trim();
        if (t && t !== driverCode) codes.add(t);
      });
    });
  });
  return codes;
}

function isPmFirstReviewStageGate(gate, stageList) {
  return isFirstReviewAssociatedStageGate(gate, stageList);
}

/** True when {@code fieldId} is a branch child under an Associated block gated to First Review (any active branch). */
function isFieldCodeInAssociatedBranchStageGate(content, stageList, fieldId, gateMatcher) {
  if (!content || fieldId == null || fieldId === '' || typeof gateMatcher !== 'function') return false;
  const codes = getAssociatedBranchChildCodesForStageGate(content, stageList, gateMatcher);
  return Array.from(codes).some((c) => crfFieldCodesEquivalent(fieldId, c));
}

/** Parse CRF HTML when live .crf-generated-content is not mounted yet (modal / early click). */
function resolveCrfAssociationLookupContent(liveContent, htmlContent, parsedCacheRef) {
  if (liveContent) return liveContent;
  const html = (htmlContent ?? '').toString();
  if (!html.trim()) return null;
  const cache = parsedCacheRef?.current;
  if (cache && cache.html === html && cache.el) return cache.el;
  const wrap = document.createElement('d' + 'iv');
  wrap.innerHTML = html;
  const el = wrap.querySelector('.crf-generated-content') || wrap;
  if (parsedCacheRef) {
    parsedCacheRef.current = { html, el };
  }
  return el;
}

/** True when tier display/code is First Review (PM completion lane), from saved row routing — any raiser profile. */
function isDcfRowRoutedToPmFirstReviewTier(f, stageList) {
  const sentTo = (f?.vDCFSentToReviewStage ?? f?.VDCFSentToReviewStage ?? '').trim();
  const resolveStage = (f?.vDCFResolveStage ?? f?.VDCFResolveStage ?? '').trim();
  const tiers = [sentTo, resolveStage].filter(
    (t) => t && normalizeStageLabelForMatch(t) !== normalizeStageLabelForMatch('In Progress')
  );
  return tiers.some((t) => isFirstReviewAssociatedStageGate(t, stageList));
}

/**
 * Open unreleased DCF on PM's First Review lane (branch config and/or sent-to tier).
 * Includes SRM-raised DCF on PM branch fields — does not require vDCFRaisedByProfileCode = PM.
 */
function isFieldCodeInPmFirstReviewBranchSet(fieldId, cachedPmBranchCodes, content, stageList) {
  if (!fieldId) return false;
  const cached = Array.isArray(cachedPmBranchCodes) ? cachedPmBranchCodes : [];
  if (cached.some((c) => crfFieldCodesEquivalent(fieldId, c))) return true;
  return isFieldCodeInAssociatedBranchStageGate(content, stageList, fieldId, isPmFirstReviewStageGate);
}

function isOpenDcfPendingForPmReviewerLane(
  f,
  content,
  savedFields,
  stageList,
  crfStage,
  crfCurrentStageCode,
  cachedPmBranchCodes
) {
  if (!isOpenUnreleasedDcfFieldRow(f)) return false;
  const fieldId = f?.vFieldId ?? f?.VFieldId ?? '';
  if (isDcfRowRoutedToPmFirstReviewTier(f, stageList)) return true;
  if (isFieldCodeInPmFirstReviewBranchSet(fieldId, cachedPmBranchCodes, content, stageList)) return true;
  return isBranchFieldAssignedToReviewerTier(
    content,
    savedFields,
    fieldId,
    stageList,
    crfStage,
    crfCurrentStageCode,
    'PM'
  );
}

/** General CRA field (not PM First Review Associated branch child). */
function isGeneralCraFieldForPmDcfRaise(
  content,
  fieldId,
  savedFields,
  stageList,
  cachedPmBranchCodes
) {
  if (!fieldId) return true;
  if (isFieldCodeInPmFirstReviewBranchSet(fieldId, cachedPmBranchCodes, content, stageList)) return false;
  if (resolveActiveAssociatedBranchStageGate(content, savedFields, fieldId) != null) return false;
  return true;
}

/** Visit-report row is an Associated branch child (not the driver control row). */
function isAssociatedBranchChildReportRow(row) {
  if (!row) return false;
  const associated =
    row.getAttribute?.('data-crf-associated') === '1'
    || row.classList?.contains('crf-report-row-associated');
  if (!associated) return false;
  const driverCode = (row.getAttribute?.('data-associated-field-code') || '').trim();
  const medex = (row.getAttribute?.('data-medex-code') || '').trim();
  if (driverCode && medex && crfFieldCodesEquivalent(medex, driverCode)) return false;
  return true;
}

function isGeneralVisitReportRowForPmDcf(row) {
  return !isAssociatedBranchChildReportRow(row);
}

function isSavedRowOpenUnreleasedDcf(f) {
  if (isOpenUnreleasedDcfFieldRow(f)) return true;
  if (!isFieldDcfOpen(f)) return false;
  const submitCra = f?.iSubmitDCFToCRA ?? f?.ISubmitDCFToCRA ?? f?.isubmitDCFToCRA;
  return !(submitCra === 1 || submitCra === '1' || submitCra === true);
}

function collectOpenUnreleasedPmBranchDcfFieldIds(
  content,
  savedFields,
  stageList,
  crfStage,
  crfCurrentStageCode,
  cachedPmBranchCodes
) {
  const ids = new Set();
  const addId = (fieldId) => {
    collectMedExCodeLookupVariants(fieldId)
      .map((v) => String(v || '').trim().toLowerCase())
      .filter(Boolean)
      .forEach((v) => ids.add(v));
  };

  (savedFields || []).forEach((f) => {
    if (!isSavedRowOpenUnreleasedDcf(f)) return;
    const fieldId = f?.vFieldId ?? f?.VFieldId ?? '';
    if (!fieldId) return;
    if (
      isOpenDcfPendingForPmReviewerLane(
        f,
        content,
        savedFields,
        stageList,
        crfStage,
        crfCurrentStageCode,
        cachedPmBranchCodes
      )
    ) {
      addId(fieldId);
    }
  });

  if (content?.querySelectorAll) {
    const rows = content.querySelectorAll(
      '.crf-report-row[data-crf-associated="1"], .crf-report-row-associated'
    );
    rows.forEach((row) => {
      if (!isAssociatedBranchChildReportRow(row)) return;
      const sf = findSavedFieldForCrfReportRow(row, savedFields);
      if (!sf || !isSavedRowOpenUnreleasedDcf(sf)) return;
      const medex = (row.getAttribute?.('data-medex-code') || sf?.vFieldId || sf?.VFieldId || '').trim();
      if (medex) addId(medex);
    });
  }

  return ids;
}

function fieldIdMatchesOpenPmBranchDcfSet(targetFieldId, openBranchIds) {
  if (!targetFieldId || !(openBranchIds instanceof Set) || openBranchIds.size === 0) return false;
  return collectMedExCodeLookupVariants(targetFieldId)
    .map((v) => String(v || '').trim().toLowerCase())
    .filter(Boolean)
    .some((v) => openBranchIds.has(v));
}

/** Visit-report row shows Open DCF (red icon and/or yellow answer/comments from processContent). */
function reportRowShowsOpenDcfMarker(row, savedFields) {
  if (!row) return false;
  const sf = findSavedFieldForCrfReportRow(row, savedFields);
  if (sf && isSavedRowOpenUnreleasedDcf(sf)) return true;
  const dcfEl = row.querySelector('[data-crf-action="dcf"], .fieldDcfBtn, [class*="fieldDcfBtn"]');
  if (dcfEl && (dcfEl.classList.contains('text-danger') || dcfEl.querySelector('.text-danger'))) return true;
  if (row.querySelector('.crf-comments-dcf-highlight')) return true;
  const answerCell = row.querySelector('.crf-answer-cell, .crf-answer-col');
  const bg = (answerCell?.style?.backgroundColor || '').replace(/\s/g, '').toLowerCase();
  if (bg.includes('255,249,196') || bg.includes('fff9c4')) return true;
  return false;
}

/** Another report row (not target) has Open DCF on PM Associated branch / First Review lane. */
function pmHasOpenDcfOnOtherBranchRow(
  content,
  targetRow,
  targetFieldId,
  savedFields,
  stageList,
  crfStage,
  crfCurrentStageCode,
  cachedPmBranchCodes,
  hasUnreleasedOpenDcfFlag
) {
  if (content?.querySelectorAll) {
    const rows = content.querySelectorAll('.crf-report-row');
    for (let i = 0; i < rows.length; i += 1) {
      const row = rows[i];
      if (row === targetRow) continue;
      if (!reportRowShowsOpenDcfMarker(row, savedFields)) continue;
      if (isAssociatedBranchChildReportRow(row)) return true;
      const sf = findSavedFieldForCrfReportRow(row, savedFields);
      if (
        sf
        && isOpenDcfPendingForPmReviewerLane(
          sf,
          content,
          savedFields,
          stageList,
          crfStage,
          crfCurrentStageCode,
          cachedPmBranchCodes
        )
      ) {
        return true;
      }
    }
  }

  return (savedFields || []).some((f) => {
    if (!isSavedRowOpenUnreleasedDcf(f)) return false;
    const fid = f?.vFieldId ?? f?.VFieldId ?? '';
    if (targetFieldId && crfFieldCodesEquivalent(targetFieldId, fid)) return false;
    return isOpenDcfPendingForPmReviewerLane(
      f,
      content,
      savedFields,
      stageList,
      crfStage,
      crfCurrentStageCode,
      cachedPmBranchCodes
    );
  });
}

/**
 * PM DCF icon gate: while Open unreleased DCF exists on Associated branch rows, block the icon on general CRA rows.
 * Uses live visit-report row markers (data-crf-associated) so it works even when saved-field routing is masked.
 */
function getPmDcfIconBlockMessage(
  targetFieldId,
  targetRow,
  content,
  savedFields,
  stageList,
  crfStage,
  crfCurrentStageCode,
  userProfile,
  profileAliases,
  cachedPmBranchCodes,
  hasUnreleasedOpenDcfFlag
) {
  if (!isPmReviewerProfile(userProfile, profileAliases)) return null;

  const targetIsGeneral = targetRow
    ? isGeneralVisitReportRowForPmDcf(targetRow)
    : isGeneralCraFieldForPmDcfRaise(content, targetFieldId, savedFields, stageList, cachedPmBranchCodes);
  if (!targetIsGeneral) return null;

  if (
    pmHasOpenDcfOnOtherBranchRow(
      content,
      targetRow,
      targetFieldId,
      savedFields,
      stageList,
      crfStage,
      crfCurrentStageCode,
      cachedPmBranchCodes,
      hasUnreleasedOpenDcfFlag
    )
  ) {
    return dcfMessages.raiseBlockedPendingAssignedTierDcf;
  }

  const openBranchIds = collectOpenUnreleasedPmBranchDcfFieldIds(
    content,
    savedFields,
    stageList,
    crfStage,
    crfCurrentStageCode,
    cachedPmBranchCodes
  );

  if (openBranchIds.size === 0 && hasUnreleasedOpenDcfFlag === true && content) {
    content
      .querySelectorAll('.crf-report-row[data-crf-associated="1"], .crf-report-row-associated')
      .forEach((row) => {
        if (!isAssociatedBranchChildReportRow(row)) return;
        if (!reportRowShowsOpenDcfMarker(row, savedFields)) return;
        const medex = (
          row.getAttribute?.('data-medex-code')
          || findSavedFieldForCrfReportRow(row, savedFields)?.vFieldId
          || findSavedFieldForCrfReportRow(row, savedFields)?.VFieldId
          || ''
        ).trim();
        if (medex) {
          collectMedExCodeLookupVariants(medex)
            .map((v) => String(v || '').trim().toLowerCase())
            .filter(Boolean)
            .forEach((v) => openBranchIds.add(v));
        }
      });
  }

  if (openBranchIds.size === 0) return null;
  if (fieldIdMatchesOpenPmBranchDcfSet(targetFieldId, openBranchIds)) return null;
  return dcfMessages.raiseBlockedPendingAssignedTierDcf;
}

/** Open, unreleased DCF on a branch field assigned to the reviewer's review stage. */
function isOpenDcfOnReviewerAssignedField(
  f,
  content,
  savedFields,
  stageList,
  crfStage,
  crfCurrentStageCode,
  userProfile,
  visitLaneLabel,
  profileAliases,
  cachedPmBranchCodes
) {
  if (!isOpenUnreleasedDcfFieldRow(f)) return false;
  const fieldId = f?.vFieldId ?? f?.VFieldId ?? '';

  if (isPmReviewerProfile(userProfile, profileAliases) && isOpenDcfPendingForPmReviewerLane(
    f,
    content,
    savedFields,
    stageList,
    crfStage,
    crfCurrentStageCode,
    cachedPmBranchCodes
  )) {
    return true;
  }

  if (
    isBranchFieldAssignedToReviewerTier(
      content,
      savedFields,
      fieldId,
      stageList,
      crfStage,
      crfCurrentStageCode,
      userProfile
    )
  ) {
    return true;
  }
  const sentTo = (f?.vDCFSentToReviewStage ?? f?.VDCFSentToReviewStage ?? '').trim();
  if (!sentTo || normalizeStageLabelForMatch(sentTo) === normalizeStageLabelForMatch('In Progress')) {
    return false;
  }
  if (resolveActiveAssociatedBranchStageGate(content, savedFields, fieldId) == null) {
    return false;
  }
  const lane = (visitLaneLabel || crfStage || '').trim();
  if (!lane) return false;
  return (
    normalizeStageLabelForMatch(sentTo) === normalizeStageLabelForMatch(lane)
    || crfFieldCodesEquivalent(sentTo, lane)
  );
}

function getReviewerPendingAssignedOpenDcfFields(
  savedFields,
  content,
  stageList,
  crfStage,
  crfCurrentStageCode,
  userProfile,
  visitLaneLabel,
  profileAliases,
  cachedPmBranchCodes
) {
  return (savedFields || []).filter((f) =>
    isOpenDcfOnReviewerAssignedField(
      f,
      content,
      savedFields,
      stageList,
      crfStage,
      crfCurrentStageCode,
      userProfile,
      visitLaneLabel,
      profileAliases,
      cachedPmBranchCodes
    )
  );
}

/**
 * SRM (and other reviewers): while Open unreleased DCF exists on tier-assigned branch fields, block raising on another field.
 * PM may raise multiple DCFs before Submit All; PM general-vs-branch rules are in getPmDcfIconBlockMessage only.
 */
function getReviewerPendingAssignedDcfRaiseBlockMessage(
  targetFieldId,
  savedFields,
  content,
  stageList,
  crfStage,
  crfCurrentStageCode,
  userProfile,
  visitLaneLabel,
  isReviewStage,
  isReviewerActor,
  profileAliases,
  cachedPmBranchCodes,
  hasUnreleasedOpenDcfFlag
) {
  if (isPmReviewerProfile(userProfile, profileAliases)) return null;

  const reviewerUser = isReviewerProfile(userProfile, profileAliases);
  const inReviewerContext =
    isReviewStage
    || (isReviewerActor && reviewerUser)
    || (String(visitLaneLabel || '').trim().length > 0 && reviewerUser);
  if (!inReviewerContext || !isReviewerActor || !reviewerUser) return null;
  let pending = getReviewerPendingAssignedOpenDcfFields(
    savedFields,
    content,
    stageList,
    crfStage,
    crfCurrentStageCode,
    userProfile,
    visitLaneLabel,
    profileAliases,
    cachedPmBranchCodes
  );
  if (pending.length === 0) return null;
  const onPendingField = pending.some((f) =>
    crfFieldCodesEquivalent(targetFieldId, f?.vFieldId ?? f?.VFieldId ?? '')
  );
  if (onPendingField) return null;
  return dcfMessages.raiseBlockedPendingAssignedTierDcf;
}

/** PM own-field rules, then reviewer pending-tier gate (frontend; API also enforces). */
function getDcfRaiseBlockMessageForField(
  content,
  fieldId,
  savedFields,
  stageList,
  crfStage,
  crfCurrentStageCode,
  userProfile,
  visitLaneLabel,
  isReviewStage,
  isReviewerActor,
  profileAliases,
  cachedPmBranchCodes,
  hasUnreleasedOpenDcfFlag,
  targetRow
) {
  const pmBlock = getPmOwnFieldDcfRaiseBlockMessage(
    content,
    fieldId,
    savedFields,
    stageList,
    crfStage,
    crfCurrentStageCode,
    userProfile
  );
  if (pmBlock) return pmBlock;
  const pmIconBlock = getPmDcfIconBlockMessage(
    fieldId,
    targetRow,
    content,
    savedFields,
    stageList,
    crfStage,
    crfCurrentStageCode,
    userProfile,
    profileAliases,
    cachedPmBranchCodes,
    hasUnreleasedOpenDcfFlag
  );
  if (pmIconBlock) return pmIconBlock;
  return getReviewerPendingAssignedDcfRaiseBlockMessage(
    fieldId,
    savedFields,
    content,
    stageList,
    crfStage,
    crfCurrentStageCode,
    userProfile,
    visitLaneLabel,
    isReviewStage,
    isReviewerActor,
    profileAliases,
    cachedPmBranchCodes,
    hasUnreleasedOpenDcfFlag
  );
}

/** True when current study stage does not match the configured gate. */
function isAssociatedStageMismatch(wantStageCode, stageList, crfCurrentStageCode, crfStage) {
  if (wantStageCode == null || wantStageCode === '') return false;
  const wantNum = Number(wantStageCode);
  const curNum = crfCurrentStageCode != null ? Number(crfCurrentStageCode) : NaN;
  if (crfCurrentStageCode != null && !Number.isNaN(curNum) && !Number.isNaN(wantNum)) {
    return wantNum !== curNum;
  }
  const row = Array.isArray(stageList)
    ? stageList.find((s) => String(s.stageCode ?? s.StageCode) === String(wantStageCode))
    : null;
  if (row) {
    const want = (row.label ?? row.Label ?? row.stageName ?? row.StageName ?? '').trim();
    const vs = (crfStage || '').trim();
    if (!want) return true;
    if (!vs) return true;
    return want.toLowerCase() !== vs.toLowerCase();
  }
  const ws = String(wantStageCode ?? '').trim();
  const vsLbl = (crfStage || '').trim();
  if (ws && vsLbl && Number.isNaN(Number(ws))) {
    return ws.toLowerCase() !== vsLbl.toLowerCase();
  }
  // Numeric branchStageCode but CurrentStageCode not yet usable and no StageMst list → do not treat as match (avoids enabling First Review branch at Second Review before stages hydrate).
  if (!Number.isNaN(wantNum) && (crfCurrentStageCode == null || Number.isNaN(curNum))) {
    if (!Array.isArray(stageList) || stageList.length === 0) return true;
  }
  if (!Array.isArray(stageList) || stageList.length === 0) return false;
  return true;
}

function readCrfAssociationStageOrderNo(row) {
  if (!row || typeof row !== 'object') return null;
  const o = row.orderNo ?? row.OrderNo ?? row.iOrderNo;
  if (o == null || o === '') return null;
  const n = Number(o);
  return Number.isNaN(n) ? null : n;
}

function readCrfAssociationStageCodeNum(row) {
  if (!row || typeof row !== 'object') return null;
  const c = row.stageCode ?? row.StageCode;
  if (c == null || c === '') return null;
  const n = Number(c);
  return Number.isNaN(n) ? null : n;
}

/** OrderNo for the Associated branchStageCode / stageCode gate (StageMst list from /stage/crf-association). */
function resolveOrderNoForAssociatedGateStage(stageList, wantStageCode) {
  const list = Array.isArray(stageList) ? stageList : [];
  if (list.length === 0) return null;
  const ws = String(wantStageCode ?? '').trim();
  if (!ws) return null;
  const wantNum = Number(ws);
  if (!Number.isNaN(wantNum)) {
    const hit = list.find((s) => readCrfAssociationStageCodeNum(s) === wantNum);
    if (hit != null) return readCrfAssociationStageOrderNo(hit);
  }
  const wl = ws.toLowerCase();
  const hit2 = list.find((s) => {
    const label = String(s.label ?? s.Label ?? '').trim().toLowerCase();
    const name = String(s.stageName ?? s.StageName ?? '').trim().toLowerCase();
    return label === wl || name === wl;
  });
  if (hit2 != null) return readCrfAssociationStageOrderNo(hit2);
  return null;
}

/** OrderNo for the current CRF / visit workflow stage in the same ordered list. */
function resolveOrderNoForCurrentCrfStage(stageList, crfCurrentStageCode, crfStageLabel) {
  const list = Array.isArray(stageList) ? stageList : [];
  if (list.length === 0) return null;
  const curNum =
    crfCurrentStageCode != null && crfCurrentStageCode !== '' && !Number.isNaN(Number(crfCurrentStageCode))
      ? Number(crfCurrentStageCode)
      : null;
  if (curNum != null) {
    const hit = list.find((s) => readCrfAssociationStageCodeNum(s) === curNum);
    if (hit != null) return readCrfAssociationStageOrderNo(hit);
  }
  const vs = String(crfStageLabel || '').trim().toLowerCase();
  if (vs) {
    const hit2 = list.find((s) => {
      const label = String(s.label ?? s.Label ?? '').trim().toLowerCase();
      const name = String(s.stageName ?? s.StageName ?? '').trim().toLowerCase();
      return label === vs || name === vs;
    });
    if (hit2 != null) return readCrfAssociationStageOrderNo(hit2);
  }
  return null;
}

/**
 * True when workflow is at the gated stage or a later StageMst tier (iOrderNo).
 * Keeps First Review–gated branch children visible in Second Review+ when driver=Yes; In Progress stays before gate.
 */
function isWorkflowAtOrAfterAssociatedStageGate(wantStageCode, stageList, crfCurrentStageCode, crfStage) {
  if (wantStageCode == null || wantStageCode === '') return true;
  const list = Array.isArray(stageList) ? stageList : [];
  const wantOrder = resolveOrderNoForAssociatedGateStage(list, wantStageCode);
  const curOrder = resolveOrderNoForCurrentCrfStage(list, crfCurrentStageCode, crfStage);
  if (wantOrder != null && curOrder != null) {
    return curOrder >= wantOrder;
  }
  return !isAssociatedStageMismatch(wantStageCode, list, crfCurrentStageCode, crfStage);
}

/** True if branch child list from JSON includes field code (MedEx code variants). */
function associatedBranchActiveListIncludesCode(activeList, fieldCode) {
  if (fieldCode == null || fieldCode === '') return false;
  const arr = Array.isArray(activeList) ? activeList : [];
  const variants = collectMedExCodeLookupVariants(fieldCode);
  const want = new Set(variants.map((v) => String(v || '').trim().toLowerCase()).filter(Boolean));
  if (want.size === 0) return false;
  return arr.some((x) => want.has(String(x || '').trim().toLowerCase()));
}

/**
 * Visit Report rows are CSS-table rows ({@link transformToReportFormat} sets display:table-row).
 * Clearing inline display (removeProperty / '') makes a div default to block and breaks Question/Answer/Comments columns.
 * Branch rows may carry class `crf-report-row-associated-branch`; CRFForm.css forces `display: table-row !important` on it,
 * so we remove that class when hiding — inline `display: none` alone loses to that rule.
 */
function setVisitReportSectionDisplay(el, visible) {
  if (!el) return;
  if (!visible) {
    el.classList?.remove('crf-report-row-associated-branch');
    el.style.display = 'none';
    return;
  }
  if (el.classList?.contains('crf-report-row')) {
    el.style.display = 'table-row';
    return;
  }
  el.style.removeProperty('display');
}

/**
 * Backend HTML uses Bootstrap-style class `collapse` on subgroup panels. Tailwind also emits a `.collapse` utility
 * (from scanning this file), which applies `visibility: collapse` and hides injected panels. Use a CRF-only class
 * for user-driven hide; never add the literal `collapse` class in JS.
 */
const CRF_TEMPLATE_SUBGROUP_COLLAPSED_CLASS = 'crf-template-subgroup-collapsed';

/** Open all subgroup panels when the Visit Report loads (Start / Review / View). */
function expandVisitReportTemplateSubGroups(content) {
  if (!content?.querySelectorAll) return;
  const panels = content.querySelectorAll(
    '.templateSubGroup, [class*="templateSubGroup"], [class*="template-subgroup"]'
  );
  panels.forEach((panel) => {
    panel.classList.remove('collapse', CRF_TEMPLATE_SUBGROUP_COLLAPSED_CLASS);
    panel.classList.add('show');
    panel.style.display = 'block';
  });
  content.querySelectorAll('.subGroupDiv').forEach((header) => {
    header.setAttribute('aria-expanded', 'true');
  });
}

const ASSOCIATED_STAGE_LOCK_ATTR = 'data-crf-associated-stage-lock';

/** Clear inputs locked for Associated branch stage mismatch (In Progress vs First Review, etc.). */
function clearAssociatedStageBranchLocks(content) {
  if (!content?.querySelectorAll) return;
  content.querySelectorAll(`[${ASSOCIATED_STAGE_LOCK_ATTR}="1"]`).forEach((el) => {
    el.removeAttribute(ASSOCIATED_STAGE_LOCK_ATTR);
    el.removeAttribute('disabled');
    el.removeAttribute('readonly');
  });
}

/** Visit Report Clear: only wipe controls the user can edit (skip disabled/read-only/transaction-view-locked). */
function isCrfFormControlClearable(el) {
  if (!el) return false;
  if (el.closest?.('.crf-row-actions, [data-crf-action]')) return false;
  if (el.disabled || el.readOnly) return false;
  if (el.getAttribute?.('data-sts-autocapture') === 'true') return false;
  if (el.dataset?.crfTransactionViewLock === '1' || el.getAttribute?.('data-crf-transaction-view-lock') === '1') {
    return false;
  }
  return true;
}

function clearCrfFileInputAndDisplay(input) {
  if (!input) return;
  input.removeAttribute('data-saved-filename');
  input.removeAttribute('data-uploaded-path');
  input.value = '';
  const answerCell = input.closest?.('.form-section-box, .crf-report-row, .row') || input.parentElement;
  const fileNamesEl = answerCell?.querySelector('.file-names-list');
  const savedSpan = answerCell?.querySelector('.crf-saved-filename');
  [fileNamesEl, savedSpan].filter(Boolean).forEach((node) => {
    node.innerHTML = '';
    node.textContent = '';
  });
}

function setBranchRowInputsAssociatedStageLocked(row, locked) {
  if (!row?.querySelectorAll) return;
  row.querySelectorAll('input:not([type="hidden"]), select, textarea').forEach((el) => {
    if (el.closest?.('.crf-row-actions, [data-crf-action]')) return;
    if (el.getAttribute?.('data-sts-autocapture') === 'true') return;
    if (locked) {
      el.setAttribute(ASSOCIATED_STAGE_LOCK_ATTR, '1');
      el.setAttribute('disabled', 'disabled');
      if ((el.tagName || '').toUpperCase() !== 'SELECT') {
        el.setAttribute('readonly', 'readonly');
      } else {
        el.removeAttribute('readonly');
      }
    } else if (el.getAttribute(ASSOCIATED_STAGE_LOCK_ATTR) === '1') {
      el.removeAttribute(ASSOCIATED_STAGE_LOCK_ATTR);
      el.removeAttribute('disabled');
      el.removeAttribute('readonly');
    }
  });
}

const DCF_WRONG_STAGE_LOCK_ATTR = 'data-crf-dcf-wrong-stage-lock';

function clearDcfWrongStageLocks(content) {
  if (!content?.querySelectorAll) return;
  content.querySelectorAll(`[${DCF_WRONG_STAGE_LOCK_ATTR}="1"]`).forEach((el) => {
    el.removeAttribute(DCF_WRONG_STAGE_LOCK_ATTR);
    el.removeAttribute('disabled');
    el.removeAttribute('readonly');
  });
}

/** Lock row inputs when Open DCF is routed to another stage (independent of Associated stage list hydration). */
function setReportRowInputsDcfWrongStageLocked(row, locked) {
  if (!row?.querySelectorAll) return;
  row.querySelectorAll('input:not([type="hidden"]), select, textarea').forEach((el) => {
    if (el.closest?.('.crf-row-actions, [data-crf-action]')) return;
    if (el.getAttribute?.('data-sts-autocapture') === 'true') return;
    if (locked) {
      el.setAttribute(DCF_WRONG_STAGE_LOCK_ATTR, '1');
      el.setAttribute('disabled', 'disabled');
      if ((el.tagName || '').toUpperCase() !== 'SELECT') {
        el.setAttribute('readonly', 'readonly');
      } else {
        el.removeAttribute('readonly');
      }
    } else if (el.getAttribute(DCF_WRONG_STAGE_LOCK_ATTR) === '1') {
      el.removeAttribute(DCF_WRONG_STAGE_LOCK_ATTR);
      el.removeAttribute('disabled');
      el.removeAttribute('readonly');
    }
  });
}

/**
 * Force-enable inputs for Open DCF rows where the server explicitly marked IsEditable=true.
 * The server's IsEditable flag is the master control - it determines editability based on:
 * - DCF release status (iSubmitDCFToCRA)
 * - User profile (CRA vs Reviewer)
 * - Stage routing rules
 * Additionally checks openDcfResolutionAllowedAtHeaderStage for UI stage matching.
 */
function applyOpenDcfEditableRowUnlocks(
  content,
  savedFields,
  headerStageLabel,
  headerCurrentStageCode,
  apiHeaderStageInProgress
) {
  if (!content || !Array.isArray(savedFields) || savedFields.length === 0) return;
  savedFields.forEach((f) => {
    if (!isFieldDcfOpen(f)) return;
    // Master logic: server's IsEditable flag must be true
    const isEditable = f.isEditable === true || f.IsEditable === true;
    if (!isEditable) return;
    // Additionally check stage matching for UI consistency
    if (
      !openDcfResolutionAllowedAtHeaderStage(
        f,
        headerStageLabel,
        headerCurrentStageCode,
        apiHeaderStageInProgress
      )
    )
      return;
    const dtlNo = Number(f.nCRFDtlNo ?? f.NCRFDtlNo ?? f.ncrfDtlNo ?? 0);
    if (!dtlNo) return;
    const row = content.querySelector(`.crf-report-row[data-n-crf-dtl-no="${dtlNo}"]`);
    if (!row?.querySelectorAll) return;
    row.querySelectorAll('input:not([type="hidden"]), select, textarea').forEach((el) => {
      if (el.closest?.('.crf-row-actions, [data-crf-action]')) return;
      if (el.getAttribute?.('data-sts-autocapture') === 'true') return;
      el.removeAttribute('disabled');
      el.removeAttribute('readonly');
    });
  });
}

function applyOpenDcfRoutedWrongStageLocks(
  content,
  savedFields,
  headerStageLabel,
  headerCurrentStageCode,
  apiHeaderStageInProgress
) {
  if (!content || !Array.isArray(savedFields)) return;
  clearDcfWrongStageLocks(content);
  savedFields.forEach((f) => {
    if (!isFieldDcfOpen(f)) return;
    if (openDcfResolutionAllowedAtHeaderStage(f, headerStageLabel, headerCurrentStageCode, apiHeaderStageInProgress))
      return;
    const id = (f.vFieldId || f.VFieldId || '').trim();
    if (!id) return;
    const variants = collectMedExCodeLookupVariants(id);
    for (let vi = 0; vi < variants.length; vi += 1) {
      const sec = findCrfFieldSection(content, variants[vi]);
      if (!sec || !sec.classList?.contains('crf-report-row')) continue;
      setReportRowInputsDcfWrongStageLocked(sec, true);
      break;
    }
  });
}

/**
 * In Progress: branch children gated to a later review stage (e.g. First Review) must stay non-editable
 * even when rows stay visible (persisted data / server inactive re-show) or after blanket setFieldsEnabled(true).
 */
function applyInProgressAssociatedStageBranchLocks(
  content,
  stageList,
  savedFields,
  crfStage,
  crfCurrentStageCode,
  isReviewStage,
  readOnly,
  isViewOnly
) {
  if (!content) return;
  clearAssociatedStageBranchLocks(content);
  if (readOnly || isViewOnly || isReviewStage) return;
  const list = Array.isArray(stageList) ? stageList : [];
  const markers = queryCrfAssociatedMarkerElements(content);
  markers.forEach((wrap) => {
    const raw = wrap.getAttribute('data-associated-config') || '';
    let cfg = {};
    try {
      cfg = JSON.parse(decodeAssociatedConfigAttribute(raw) || '{}');
    } catch {
      cfg = {};
    }
    const driverCode = String(cfg.driverFieldCode || cfg.DriverFieldCode || '').trim();
    const branchesRaw = cfg.branches ?? cfg.Branches ?? {};
    const branches = typeof branchesRaw === 'object' && branchesRaw !== null ? branchesRaw : {};
    const wantStage = resolveAssociatedStageGate(cfg);
    const stageBlocked = isAssociatedStageMismatch(wantStage, list, crfCurrentStageCode, crfStage);
    if (!stageBlocked) return;
    const allCodes = new Set();
    Object.keys(branches).forEach((k) => {
      associatedBranchValueToList(branches[k]).forEach((c) => {
        const t = String(c || '').trim();
        if (t) allCodes.add(t);
      });
    });
    allCodes.forEach((code) => {
      if (code === driverCode) return;
      const sec = findCrfFieldSection(content, code);
      if (!sec) return;
      setBranchRowInputsAssociatedStageLocked(sec, true);
    });
  });
}

/**
 * Associated-field visibility: optional stage gate + show branch child rows only when driver value matches.
 * Must run after {@link transformToReportFormat} replaces DOM (visit report table); otherwise markers/blocks are stale.
 */
function applyAssociatedFieldVisibility(
  content,
  stageList,
  {
    savedFields,
    crfStage,
    crfCurrentStageCode,
    isReviewStage,
    readOnly,
    isViewOnly,
    allowReviewAssociatedEdit,
    enableFieldsByCodes,
    serverInactiveChildFieldCodes,
    hasOpenDCF = false,
    headerStageForOpenDcf,
    pageHeaderStageInProgress,
  }
) {
  if (!content) return;
  const list = Array.isArray(stageList) ? stageList : [];
  // Completed Visit Report (read-only / view-only): show Associated branch rows by driver value like a review stage,
  // instead of hiding all branch children when stageBlocked (gate no longer applies once workflow is Completed).
  const completedReadOnlyArchive =
    String(crfStage || '').trim().toLowerCase() === 'completed' && (readOnly || isViewOnly);
  const markers = queryCrfAssociatedMarkerElements(content);
  markers.forEach((wrap) => {
    const raw = wrap.getAttribute('data-associated-config') || '';
    let cfg = {};
    try {
      cfg = JSON.parse(decodeAssociatedConfigAttribute(raw) || '{}');
    } catch {
      cfg = {};
    }
    const driverCode = String(cfg.driverFieldCode || cfg.DriverFieldCode || '').trim();
    const branchesRaw = cfg.branches ?? cfg.Branches ?? {};
    const branches = typeof branchesRaw === 'object' && branchesRaw !== null ? branchesRaw : {};

    const wantStage = resolveAssociatedStageGate(cfg);
    const stageBlocked = isAssociatedStageMismatch(
      wantStage,
      list,
      crfCurrentStageCode,
      crfStage
    );

    if (driverCode) {
      const driverSec = findCrfFieldSection(content, driverCode);
      if (driverSec) setVisitReportSectionDisplay(driverSec, true);
    }

    const allCodes = new Set();
    Object.keys(branches).forEach((k) => {
      associatedBranchValueToList(branches[k]).forEach((c) => {
        const t = String(c || '').trim();
        if (t) allCodes.add(t);
      });
    });

    if (stageBlocked) {
      // Stage gate applies to branch child fields only — keep the Associated marker row visible in CRA/In Progress
      // so users still see the association block; hide only configured branch attributes (e.g. ABC1 until First Review).
      {
        const ar = wrap.closest('.crf-report-row');
        if (ar) setVisitReportSectionDisplay(ar, true);
      }
      setVisitReportSectionDisplay(wrap, true);
      if (isReviewStage || completedReadOnlyArchive) {
        // In later review tiers (e.g. Second Review), keep previously captured Associated branch data visible
        // so reviewers can verify what was submitted in earlier review stage.
        const branchKey = resolveAssociatedBranchKey(content, cfg, savedFields, wrap);
        const activeList = getAssociatedBranchList(branches, branchKey);
        const arr = Array.isArray(activeList) ? activeList.map((c) => String(c || '').trim()) : [];
        allCodes.forEach((code) => {
          if (code === driverCode) return;
          const sec = findCrfFieldSection(content, code);
          if (!sec) return;
        if (sec.classList?.contains('crf-report-row')) {
          sec.classList.add('crf-report-row-associated-branch');
        }
          const show = branchKey != null && associatedBranchActiveListIncludesCode(arr, code);
          setVisitReportSectionDisplay(sec, show);
        });
        return;
      }
      allCodes.forEach((code) => {
        if (code === driverCode) return;
        const sec = findCrfFieldSection(content, code);
        if (sec) setVisitReportSectionDisplay(sec, false);
      });
      return;
    }
    {
      const assocRow = wrap.closest('.crf-report-row');
      if (assocRow) setVisitReportSectionDisplay(assocRow, true);
    }
    setVisitReportSectionDisplay(wrap, true);

    const branchKey = resolveAssociatedBranchKey(content, cfg, savedFields, wrap);
    const activeList = getAssociatedBranchList(branches, branchKey);
    const arr = Array.isArray(activeList) ? activeList.map((c) => String(c || '').trim()) : [];

    allCodes.forEach((code) => {
      if (code === driverCode) return;
      const sec = findCrfFieldSection(content, code);
      if (!sec) return;
      const show = branchKey != null && associatedBranchActiveListIncludesCode(arr, code);
      if (sec.classList?.contains('crf-report-row')) {
        sec.classList.add('crf-report-row-associated-branch');
      }
      setVisitReportSectionDisplay(sec, show);
    });
  });

  // Authoritative hide list from API (same exclusions as submit required-field validation); wins over DOM/template branch quirks.
  const serverInactive = Array.isArray(serverInactiveChildFieldCodes) ? serverInactiveChildFieldCodes : [];
  serverInactive.forEach((codeRaw) => {
    const variants = collectMedExCodeLookupVariants(codeRaw);
    for (let vi = 0; vi < variants.length; vi += 1) {
      const sec = findCrfFieldSection(content, variants[vi]);
      if (!sec) continue;
      if (sec.classList?.contains('crf-report-row')) sec.classList.add('crf-report-row-associated-branch');
      setVisitReportSectionDisplay(sec, false);
      break;
    }
  });

  // API inactive list matches required-field exclusions for the *current* stage; do not hide rows
  // that already hold persisted answer/comment data (same pattern as Open DCF force-show below).
  serverInactive.forEach((codeRaw) => {
    if (!savedFieldRowHasPersistedDisplayData(savedFields, codeRaw)) return;
    if (!isAssociatedBranchChildShownForCurrentDriver(content, savedFields, codeRaw)) return;
    // Completed Visit Report: stage list often omits Completed (9999), so order-based "at or after gate" fails and
    // would keep PM-tier serverInactive rows hidden despite persisted data; driver match + persisted data suffice.
    if (
      !completedReadOnlyArchive &&
      !isAssociatedBranchChildAllowedAtAssignedStage(content, list, crfStage, crfCurrentStageCode, codeRaw)
    ) {
      return;
    }
    const variants = collectMedExCodeLookupVariants(codeRaw);
    for (let vi = 0; vi < variants.length; vi += 1) {
      const sec = findCrfFieldSection(content, variants[vi]);
      if (!sec) continue;
      if (sec.classList?.contains('crf-report-row')) sec.classList.add('crf-report-row-associated-branch');
      setVisitReportSectionDisplay(sec, true);
      break;
    }
  });

  const allAssociatedBranchChildCodes = getAllAssociatedBranchChildCodes(content);
  forceShowAssociatedBranchRowsWithOpenEditableDcf(content, savedFields, allAssociatedBranchChildCodes);

  if (!readOnly && !isViewOnly && isReviewStage && allowReviewAssociatedEdit && typeof enableFieldsByCodes === 'function') {
    const editableAssociatedChildCodes = getEditableAssociatedChildCodes(
      content,
      savedFields,
      crfCurrentStageCode,
      crfStage,
      list
    );
    const hdrForOpenDcf = (headerStageForOpenDcf ?? crfStage ?? '').toString().trim();
    const apiHipAssoc = pageHeaderStageInProgress === true;
    mergeOpenDcfEditableAssociatedBranchChildCodes(
      content,
      savedFields,
      hasOpenDCF,
      editableAssociatedChildCodes,
      hdrForOpenDcf,
      crfCurrentStageCode,
      apiHipAssoc
    );
    enableFieldsByCodes(content, editableAssociatedChildCodes);
  }

  applyInProgressAssociatedStageBranchLocks(
    content,
    list,
    savedFields,
    crfStage,
    crfCurrentStageCode,
    isReviewStage,
    readOnly,
    isViewOnly
  );
}

/** Default until GET /crf/page* returns maxCrfUploadFileSizeBytes (matches API CRFUploads:MaxFileSizeBytes fallback). */
const DEFAULT_CRF_MAX_UPLOAD_BYTES = 50 * 1024 * 1024;

// STS AutoCapture: execute external API (via CTMS.API) and render read-only DataTable in full-width block.
const STS_AUTOCAPTURE_SEL = '[data-sts-autocapture="true"][data-sts-api-detail-no]';
const STS_AUTOCAPTURE_BLOCK_SEL = '.crf-sts-autocapture-block';
const STS_AUTOCAPTURE_ERROR_ATTR = 'data-sts-autocapture-error';
const STS_AUTOCAPTURE_LOADING_ATTR = 'data-sts-autocapture-loading';
const STS_AUTOCAPTURE_SOURCE_ATTR = 'data-sts-autocapture-source';
const STS_AUTOCAPTURE_FIELD_VALUE_MAX_LENGTH = 10000000;
const STS_AUTOCAPTURE_SOURCE_DB = 'db';

function isStsAutocaptureSection(section) {
  if (!section) return false;
  try {
    const qs = (sel) => {
      if (section && typeof section.querySelector === 'function') return section.querySelector(sel);
      return null;
    };
    if (qs('[data-sts-autocapture="true"]')) return true;
    if (qs('[id^="stsautocapture_"], [name^="stsautocapture_"]')) return true;
    if (qs('.sts-autocapture-host, .sts-autocapture-value, .sts-autocapture-display')) return true;
  } catch (_) { /* ignore */ }
  return false;
}

/** True when DCF raise target is an STS AutoCapture field (DOM markers only). */
function isStsAutoCaptureFieldTarget(content, fieldId, targetRow) {
  if (targetRow) {
    if (targetRow.matches?.(STS_AUTOCAPTURE_BLOCK_SEL)) return true;
    if (targetRow.querySelector?.('[data-sts-autocapture="true"]')) return true;
    if (targetRow.closest?.(STS_AUTOCAPTURE_BLOCK_SEL)) return true;
  }
  const vf = String(fieldId || '').trim();
  if (!content || !vf) return false;
  const vfEsc = cssEscapeAttr(vf);
  const byMedSts = content.querySelector(`${STS_AUTOCAPTURE_BLOCK_SEL}[data-medex-code="${vfEsc}"]`);
  if (byMedSts) return true;
  const byFieldSts = content.querySelector(`${STS_AUTOCAPTURE_BLOCK_SEL}[data-field-id="${vfEsc}"]`);
  if (byFieldSts) return true;
  const stsControl =
    content.querySelector(`[data-sts-autocapture="true"][id="stsautocapture_${vfEsc}"]`)
    || content.querySelector(`[data-sts-autocapture="true"][name="stsautocapture_${vfEsc}"]`)
    || content.querySelector(`[id^="stsautocapture_"][id$="${vfEsc}"]`);
  if (stsControl) return true;
  const section = findCrfFieldSection(content, vf);
  if (section && isStsAutocaptureSection(section)) return true;
  return false;
}

function resolveStsAutoCaptureControl(el) {
  if (!el) return null;
  if (el.getAttribute?.('data-sts-autocapture') === 'true') return el;
  const block = el.closest?.(STS_AUTOCAPTURE_BLOCK_SEL);
  if (block) {
    const hidden = block.querySelector?.('[data-sts-autocapture="true"]');
    if (hidden) return hidden;
  }
  const host = el.closest?.('.sts-autocapture-host, .form-section-box, .inlineWrapper');
  return host?.querySelector?.('[data-sts-autocapture="true"]') || null;
}

function resolveStsAutoCaptureDisplayEl(controlOrBlock) {
  if (!controlOrBlock) return null;
  const block = controlOrBlock.closest?.(STS_AUTOCAPTURE_BLOCK_SEL);
  if (block) {
    const d = block.querySelector?.('[data-sts-autocapture-display="true"], .sts-autocapture-display');
    if (d) return d;
  }
  const host = controlOrBlock.closest?.('.inlineWrapper, .sts-autocapture-host, .form-group');
  return host?.querySelector?.('[data-sts-autocapture-display="true"], .sts-autocapture-display')
    || controlOrBlock.parentElement?.querySelector?.('.sts-autocapture-display');
}

function resolveStsAutoCaptureFieldCode(el) {
  if (!el) return '';
  const dataFieldId = (el.getAttribute?.('data-field-id') || '').trim();
  if (dataFieldId) return dataFieldId;
  const block = el.closest?.(STS_AUTOCAPTURE_BLOCK_SEL);
  const fromBlock = (block?.getAttribute?.('data-field-id') || block?.getAttribute?.('data-medex-code') || '').trim();
  if (fromBlock) return fromBlock;
  const rawId = (el.id || el.name || '').trim();
  if (!rawId) return '';
  const parts = rawId.split('_');
  const five = parts.find((p) => p.length === 5 && /^[A-Za-z0-9]+$/.test(p));
  return five || '';
}

function findStsAutoCaptureSavedField(el, savedFields) {
  if (!el || !Array.isArray(savedFields) || savedFields.length === 0) return null;
  const row = el.closest?.('.crf-report-row') || el.closest?.(STS_AUTOCAPTURE_BLOCK_SEL);
  let field = row ? findSavedFieldForCrfReportRow(row, savedFields) : null;
  if (!field) {
    const code = resolveStsAutoCaptureFieldCode(el);
    if (code) {
      const nField = normalizeCrfFieldCodeForMatch(code);
      field = savedFields.find((f) => {
        const fId = (f?.vFieldId || f?.VFieldId || '').trim();
        if (!fId) return false;
        if (fId.toLowerCase() === code.toLowerCase()) return true;
        return normalizeCrfFieldCodeForMatch(fId) === nField;
      }) || null;
    }
  }
  return field;
}

function isStsAutoCaptureRowOpenDcf(el, savedFields) {
  const field = findStsAutoCaptureSavedField(el, savedFields);
  if (!field) return false;
  const status = (field?.vDCFStatus ?? field?.VDCFStatus ?? '').toString().trim().toLowerCase();
  return status === 'open';
}

function resolveStsOpenDcfHeaderStageLabel(visitUiStageLabel, workflowHeader, crfStage) {
  return String(
    visitUiStageLabel ||
    getAnyCase(workflowHeader, 'vStage', 'VStage', 'stage', 'Stage') ||
    crfStage || ''
  ).trim();
}

function resolveStsApiHeaderStageLabel(
  visitUiStageLabel,
  workflowHeader,
  crfStage,
  { embedInModal = false, reviewMode = false, readOnly = false } = {}
) {
  const ui = String(visitUiStageLabel || '').trim();
  if (ui) return ui;
  // Visit Report from In Progress tab: CRA lane (not reviewMode); CRFHdr may still show a review stage after DCF handoff.
  if (embedInModal && !reviewMode && !readOnly) {
    return 'In Progress';
  }
  const crf = String(crfStage || '').trim();
  if (normalizeStageLabelForMatch(crf) === normalizeStageLabelForMatch('In Progress')) {
    return crf;
  }
  return resolveStsOpenDcfHeaderStageLabel(visitUiStageLabel, workflowHeader, crfStage);
}

function isStsOpenDcfAtSentToStage(el, savedFields, headerStageLabel) {
  const field = findStsAutoCaptureSavedField(el, savedFields);
  if (!field) return false;
  const status = (field?.vDCFStatus ?? field?.VDCFStatus ?? '').toString().trim().toLowerCase();
  if (status !== 'open') return false;
  const sentTo = (
    field?.vDCFSentToReviewStage ??
    field?.VDCFSentToReviewStage ??
    field?.vDCFResolveStage ??
    field?.VDCFResolveStage ??
    ''
  ).toString().trim();
  if (!sentTo) return false;
  return normalizeStageLabelForMatch(headerStageLabel) === normalizeStageLabelForMatch(sentTo);
}

function isStsOpenDcfCorrectionAllowed(el, savedFields, headerStageLabel) {
  return isStsOpenDcfAtSentToStage(el, savedFields, headerStageLabel);
}

function stsAutoCaptureExecutionKey(detailNoRaw, el, headerStageLabel) {
  const stagePart = normalizeStageLabelForMatch(headerStageLabel || '');
  const elPart = el?.id || el?.name || '';
  return `${detailNoRaw}::${elPart}::${stagePart}`;
}

let stsRowSelectionContext = { readOnly: false, isViewOnly: false, crfStage: null };

function setStsRowSelectionContext(ctx = {}) {
  stsRowSelectionContext = {
    readOnly: !!ctx.readOnly,
    isViewOnly: !!ctx.isViewOnly,
    crfStage: ctx.crfStage != null ? ctx.crfStage : null,
  };
}

function shouldStsShowRowSelectionMode(ctx = stsRowSelectionContext) {
  return !ctx.readOnly && !ctx.isViewOnly && String(ctx.crfStage || '').trim() === 'In Progress';
}

function shouldStsEnableEditableCells(ctx = stsRowSelectionContext) {
  return shouldStsShowRowSelectionMode(ctx);
}

function stsRowContentKey(row, columns = null, enableFields = null) {
  const cells = row || [];
  if (!Array.isArray(columns) || columns.length === 0) {
    return cells.map((c) => String(c ?? '').trim()).join('\x1e');
  }
  const enabledIndices = buildStsEnabledColumnIndexSet(columns, enableFields || []);
  if (enabledIndices.size === 0) {
    return cells.map((c) => String(c ?? '').trim()).join('\x1e');
  }
  return columns.map((_, i) => (
    enabledIndices.has(i) ? '' : String(cells[i] ?? '').trim()
  )).join('\x1e');
}

function isStsSourceRowSelected(sourceRow, selectedRows, columns = null, enableFields = null) {
  const key = stsRowContentKey(sourceRow, columns, enableFields);
  return (selectedRows || []).some((r) => stsRowContentKey(r, columns, enableFields) === key);
}

function findStsMatchingSavedRow(sourceRow, savedRows, columns, enableFields) {
  const key = stsRowContentKey(sourceRow, columns, enableFields);
  return (savedRows || []).find((r) => stsRowContentKey(r, columns, enableFields) === key) || null;
}

function stsSavedNormHasEnableFieldData(norm) {
  if (!norm?.enableFields?.length || !norm?.rows?.length) return false;
  const enabledIndices = buildStsEnabledColumnIndexSet(norm.columns, norm.enableFields);
  if (enabledIndices.size === 0) return false;
  return norm.rows.some((row) => {
    for (const i of enabledIndices) {
      if (String(row[i] ?? '').trim()) return true;
    }
    return false;
  });
}

function countStsEnableFieldPopulated(norm) {
  if (!norm?.enableFields?.length || !norm?.rows?.length) return 0;
  const enabledIndices = buildStsEnabledColumnIndexSet(norm.columns, norm.enableFields);
  if (enabledIndices.size === 0) return 0;
  let count = 0;
  norm.rows.forEach((row) => {
    enabledIndices.forEach((i) => {
      if (String(row[i] ?? '').trim()) count += 1;
    });
  });
  return count;
}

function resolveStsAutoCaptureSavedNorm(el, savedFields) {
  const fromDb = normalizeStsAutoCaptureDataTable(getStsAutoCaptureSavedFieldValue(el, savedFields));
  const fromHidden = normalizeStsAutoCaptureDataTable(el?.value);
  if (!fromDb) return fromHidden;
  if (!fromHidden) return fromDb;
  const dbHas = stsSavedNormHasEnableFieldData(fromDb);
  const hiddenHas = stsSavedNormHasEnableFieldData(fromHidden);
  if (dbHas && !hiddenHas) return fromDb;
  if (hiddenHas && !dbHas) return fromHidden;
  if (dbHas && hiddenHas) {
    return countStsEnableFieldPopulated(fromDb) >= countStsEnableFieldPopulated(fromHidden)
      ? fromDb
      : fromHidden;
  }
  return (fromDb.rows?.length || 0) >= (fromHidden.rows?.length || 0) ? fromDb : fromHidden;
}

function stsDisplayHasRowSelectionTable(el) {
  const display = resolveStsAutoCaptureDisplayEl(el);
  const table = display?.querySelector('.sts-autocapture-table');
  return !!table?.querySelector('.sts-autocapture-row-select');
}

function stsDisplayHasEnableFieldData(el, savedNorm) {
  const display = resolveStsAutoCaptureDisplayEl(el);
  const table = display?.querySelector('.sts-autocapture-table');
  if (!table || !savedNorm?.enableFields?.length) return false;
  const enabledIndices = buildStsEnabledColumnIndexSet(savedNorm.columns, savedNorm.enableFields);
  if (enabledIndices.size === 0) return false;
  let found = false;
  table.querySelectorAll('tbody tr').forEach((tr) => {
    if (found) return;
    const tds = [...tr.querySelectorAll('td')].filter((td) => !td.classList.contains('sts-autocapture-select-col'));
    enabledIndices.forEach((colIdx) => {
      const td = tds[colIdx];
      if (td && String(getStsTableCellDisplayValue(td)).trim()) found = true;
    });
  });
  return found;
}

function mergeStsSavedEnableFieldValues(apiRow, savedRows, columns, enableFields) {
  const merged = [...(apiRow || [])];
  if (!Array.isArray(columns) || !Array.isArray(enableFields) || enableFields.length === 0) {
    return merged;
  }
  const savedRow = findStsMatchingSavedRow(apiRow, savedRows, columns, enableFields);
  if (!savedRow) return merged;
  const enabledIndices = buildStsEnabledColumnIndexSet(columns, enableFields);
  enabledIndices.forEach((i) => {
    merged[i] = savedRow[i] != null ? String(savedRow[i]) : '';
  });
  return merged;
}

function serializeStsAutoCapturePersistedValue(norm) {
  if (!norm?.columns?.length) return '';
  const payload = {
    columns: norm.columns,
    rows: Array.isArray(norm.rows) ? norm.rows : [],
  };
  if (Array.isArray(norm.enableFields) && norm.enableFields.length > 0) {
    payload.enablefields = norm.enableFields;
  }
  return JSON.stringify(payload);
}

function stsAutoCaptureSerializedValueExceedsMax(serialized) {
  return String(serialized ?? '').length > STS_AUTOCAPTURE_FIELD_VALUE_MAX_LENGTH;
}

function resolveStsAutoCaptureApiErrorMessage(err) {
  const apiError = err?.response?.data?.error;
  if (apiError === stsAutoCaptureMessages.dataTooLarge || apiError === stsAutoCaptureMessages.loadFailed) {
    return apiError;
  }
  return stsAutoCaptureMessages.loadFailed;
}

/** View Audit: comma-separated lines for STS AutoCapture vFieldValue (columns header + data rows). */
function formatStsAutoCaptureAuditValueForDisplay(value) {
  const norm = normalizeStsAutoCaptureDataTable(value);
  if (!norm?.columns?.length) return null;
  const header = norm.columns.join(', ');
  const lines = (norm.rows || []).map((row) =>
    norm.columns.map((_, i) => String(row[i] ?? '')).join(', ') + ','
  );
  return [header, ...lines].join('\n');
}

function stsSavedNormNeedsDraftApiSource(savedNorm, el = null, savedFields = null, headerStageLabel = null) {
  if (!savedNorm?.columns?.length || !Array.isArray(savedNorm.rows) || savedNorm.rows.length === 0) {
    return false;
  }
  if (shouldStsShowRowSelectionMode()) return true;
  // Open DCF: re-fetch API only when viewing stage matches vDCFSentToReviewStage.
  if (el && headerStageLabel && isStsOpenDcfAtSentToStage(el, savedFields, headerStageLabel)) {
    return true;
  }
  return false;
}

function showStsAutoCaptureLoadingState(el) {
  if (!el) return;
  el.setAttribute(STS_AUTOCAPTURE_LOADING_ATTR, 'true');
  el.removeAttribute(STS_AUTOCAPTURE_ERROR_ATTR);
  const stsDisplay = resolveStsAutoCaptureDisplayEl(el);
  if (stsDisplay) {
    stsDisplay.innerHTML = '';
    const loading = document.createElement('div');
    loading.className = 'sts-autocapture-loading';
    loading.textContent = 'Loading...';
    stsDisplay.appendChild(loading);
  } else if (!el.value) {
    el.setAttribute('placeholder', 'Loading...');
  }
}

function showStsAutoCaptureMessage(el, message) {
  if (!el) return;
  el.removeAttribute(STS_AUTOCAPTURE_LOADING_ATTR);
  el.setAttribute(STS_AUTOCAPTURE_ERROR_ATTR, 'true');
  const stsDisplay = resolveStsAutoCaptureDisplayEl(el);
  if (stsDisplay) {
    stsDisplay.innerHTML = '';
    const msg = document.createElement('div');
    msg.className = 'sts-autocapture-error';
    msg.textContent = message;
    stsDisplay.appendChild(msg);
  } else {
    el.setAttribute('placeholder', message);
  }
}

function showStsAutoCaptureBlankAnswer(el) {
  if (!el) return;
  el.value = '';
  el.removeAttribute(STS_AUTOCAPTURE_ERROR_ATTR);
  el.removeAttribute(STS_AUTOCAPTURE_LOADING_ATTR);
  el.removeAttribute('placeholder');
  el.removeAttribute(STS_AUTOCAPTURE_SOURCE_ATTR);
  const stsDisplay = resolveStsAutoCaptureDisplayEl(el);
  if (stsDisplay) stsDisplay.innerHTML = '';
}

function stsAutoCaptureNormHasZeroRows(norm) {
  return !!norm?.columns?.length && (!Array.isArray(norm.rows) || norm.rows.length === 0);
}

function applyStsAutoCaptureApiPayload(el, dataTable, rawValue, stsCellsCanEdit, savedNormForDraft, savedFields = null, headerStageLabel = null) {
  const apiNorm = normalizeStsAutoCaptureDataTable(dataTable ?? rawValue);
  const resolvedSaved = resolveStsAutoCaptureSavedNorm(el, savedFields) || savedNormForDraft;
  if (stsSavedNormNeedsDraftApiSource(resolvedSaved, el, savedFields, headerStageLabel) && apiNorm) {
    if (stsAutoCaptureNormHasZeroRows(apiNorm)) {
      if (resolvedSaved?.rows?.length) {
        applyStsAutoCaptureDbValue(el, serializeStsAutoCapturePersistedValue(resolvedSaved), stsCellsCanEdit);
        return true;
      }
      showStsAutoCaptureBlankAnswer(el);
      return true;
    }
    renderStsAutoCaptureDraftSelectionTable(el, resolvedSaved, apiNorm, stsCellsCanEdit);
    return true;
  }
  if (apiNorm && stsAutoCaptureNormHasZeroRows(apiNorm)) {
    showStsAutoCaptureBlankAnswer(el);
    return true;
  }
  if (dataTable) {
    applyStsAutoCaptureDataTable(el, dataTable, stsCellsCanEdit, true);
    return true;
  }
  if (apiNorm) {
    applyStsAutoCaptureDataTable(el, apiNorm, stsCellsCanEdit, true);
    return true;
  }
  if (rawValue != null && String(rawValue).trim()) {
    el.value = String(rawValue);
    const display = resolveStsAutoCaptureDisplayEl(el);
    if (display) renderStsLegacyTextDisplay(display, String(rawValue));
    return true;
  }
  return false;
}

function fetchStsAutoCaptureForElement(el, detailNoRaw, visitId, stsCellsCanEdit, savedNormForDraft, onCatch, onBeforeApply, savedFields = null, headerStageLabel = null, onSuccess = null, onFailure = null) {
  showStsAutoCaptureLoadingState(el);
  const visitIdNum = parseInt(visitId, 10);
  const stsQueryParams = Number.isFinite(visitIdNum) && visitIdNum > 0
    ? { scheduledVisitId: visitIdNum }
    : undefined;
  return api
    .get(`/sts-auto-capture/execute/${encodeURIComponent(detailNoRaw)}`, {
      ...(stsQueryParams ? { params: stsQueryParams } : undefined),
    })
    .then((res) => {
      if (typeof onBeforeApply === 'function' && onBeforeApply()) {
        if (typeof onSuccess === 'function') onSuccess();
        return;
      }
      const dataTable = res?.data?.dataTable;
      const rawValue = res?.data?.value != null ? String(res.data.value) : '';
      const applied = applyStsAutoCaptureApiPayload(el, dataTable, rawValue, stsCellsCanEdit, savedNormForDraft, savedFields, headerStageLabel);
      if (!applied) {
        showStsAutoCaptureBlankAnswer(el);
        if (typeof onFailure === 'function') onFailure();
        return;
      }
      el.removeAttribute('placeholder');
      el.removeAttribute(STS_AUTOCAPTURE_LOADING_ATTR);
      el.removeAttribute(STS_AUTOCAPTURE_SOURCE_ATTR);
      if (typeof onSuccess === 'function') onSuccess();
    })
    .catch((err) => {
      if (typeof onCatch === 'function') onCatch(err);
    });
}

function renderStsAutoCaptureDraftSelectionTable(control, savedNorm, apiNorm, canEditCells) {
  const el = resolveStsAutoCaptureControl(control) || control;
  if (!el || !savedNorm || !apiNorm) return;
  const columns = savedNorm.columns?.length ? savedNorm.columns : apiNorm.columns;
  const selectedRows = savedNorm.rows || [];
  const enableFields = savedNorm.enableFields?.length
    ? savedNorm.enableFields
    : (apiNorm.enableFields || []);
  const beforeSyncValue = el.value;
  const displayNorm = {
    columns,
    rows: selectedRows,
    enableFields,
  };
  el.value = serializeStsAutoCapturePersistedValue({ columns, rows: selectedRows, enableFields });
  const display = resolveStsAutoCaptureDisplayEl(el);
  if (display) {
    renderStsAutoCaptureTable(display, displayNorm, el, {
      rowSelectionMode: true,
      sourceRows: apiNorm.rows || [],
      selectedRows,
      renderEditableCells: !!canEditCells,
    });
    const table = display.querySelector('.sts-autocapture-table');
    if (table) syncStsAutoCaptureHiddenFromTable(el, table);
    const beforeSyncNorm = normalizeStsAutoCaptureDataTable(beforeSyncValue);
    const afterSyncNorm = normalizeStsAutoCaptureDataTable(el.value);
    if (
      beforeSyncNorm
      && stsSavedNormHasEnableFieldData(beforeSyncNorm)
      && !stsSavedNormHasEnableFieldData(afterSyncNorm)
    ) {
      el.value = beforeSyncValue;
    }
    const scope = el.closest('.crf-generated-content') || display;
    applyStsAutoCaptureTableCellEditability(scope, !!canEditCells);
  }
  el.removeAttribute('placeholder');
  el.removeAttribute(STS_AUTOCAPTURE_LOADING_ATTR);
  el.removeAttribute(STS_AUTOCAPTURE_ERROR_ATTR);
  el.setAttribute(STS_AUTOCAPTURE_SOURCE_ATTR, STS_AUTOCAPTURE_SOURCE_DB);
}

function mapStsAutoCaptureRowsFromRaw(rowsRaw, columns) {
  if (!Array.isArray(rowsRaw)) return [];
  return rowsRaw.map((r) => {
    if (Array.isArray(r)) return r.map((c) => String(c ?? ''));
    if (r && typeof r === 'object') return columns.map((c) => String(r[c] ?? ''));
    return [];
  });
}

function normalizeStsAutoCaptureDataTable(raw) {
  if (raw == null) return null;
  let obj = raw;
  if (typeof raw === 'string') {
    const t = raw.trim();
    if (!t) return null;
    if (!t.startsWith('{') && !t.startsWith('[')) return null;
    try {
      obj = JSON.parse(t);
    } catch (_) {
      return null;
    }
  }
  if (Array.isArray(obj)) {
    if (obj.length === 0) return null;
    const first = obj[0];
    if (first && typeof first === 'object' && !Array.isArray(first)) {
      const columns = Object.keys(first);
      const rows = obj.map((row) => columns.map((c) => String(row[c] ?? '')));
      return { columns, rows, enableFields: [] };
    }
    return null;
  }
  if (!obj || typeof obj !== 'object') return null;
  const columns = Array.isArray(obj.columns) ? obj.columns.map((c) => String(c ?? ''))
    : Array.isArray(obj.Columns) ? obj.Columns.map((c) => String(c ?? '')) : null;
  const rowsRaw = obj.rows ?? obj.Rows;
  if (!columns || !Array.isArray(rowsRaw)) return null;
  const rows = mapStsAutoCaptureRowsFromRaw(rowsRaw, columns);
  const enableFieldsRaw = obj.enablefields ?? obj.enableFields ?? obj.EnableFields;
  const enableFields = Array.isArray(enableFieldsRaw)
    ? enableFieldsRaw.map((c) => String(c ?? '').trim()).filter(Boolean)
    : [];
  const sourceRowsRaw = obj.sourcerows ?? obj.sourceRows ?? obj.SourceRows;
  const sourceRows = Array.isArray(sourceRowsRaw)
    ? mapStsAutoCaptureRowsFromRaw(sourceRowsRaw, columns)
    : null;
  const result = { columns, rows, enableFields };
  if (sourceRows && sourceRows.length > 0) result.sourceRows = sourceRows;
  return result;
}

function buildStsEnabledColumnIndexSet(columns, enableFields) {
  if (!Array.isArray(columns) || !Array.isArray(enableFields) || enableFields.length === 0) {
    return new Set();
  }
  const nameToIndex = new Map();
  columns.forEach((col, i) => nameToIndex.set(String(col).trim().toLowerCase(), i));
  const indices = new Set();
  enableFields.forEach((name) => {
    const idx = nameToIndex.get(String(name).trim().toLowerCase());
    if (idx != null) indices.add(idx);
  });
  return indices;
}

function getStsTableCellDisplayValue(td) {
  if (td?.classList?.contains('sts-autocapture-select-col')) return '';
  const input = td.querySelector('.sts-autocapture-cell-input');
  if (input) return String(input.value ?? '');
  return String(td.textContent ?? '');
}

function resolveStsNormForTableSync(el, table) {
  const fromValue = normalizeStsAutoCaptureDataTable(el?.value);
  if (fromValue?.columns?.length) return fromValue;
  if (!table) return null;
  const headers = [...table.querySelectorAll('thead th')]
    .filter((th) => !th.classList.contains('sts-autocapture-select-col'))
    .map((th) => String(th.textContent ?? '').trim())
    .filter(Boolean);
  if (!headers.length) return null;
  return {
    columns: headers,
    rows: [],
    enableFields: fromValue?.enableFields || [],
  };
}

function syncStsAutoCaptureHiddenFromTable(hiddenControl, table) {
  const el = resolveStsAutoCaptureControl(hiddenControl) || hiddenControl;
  if (!el || !table) return;
  const norm = resolveStsNormForTableSync(el, table);
  if (!norm) return;
  const hasRowSelection = !!table.querySelector('.sts-autocapture-row-select');
  const sourceRows = [];
  const rows = [];
  table.querySelectorAll('tbody tr').forEach((tr) => {
    const row = [];
    tr.querySelectorAll('td').forEach((td) => {
      if (td.classList.contains('sts-autocapture-select-col')) return;
      row.push(getStsTableCellDisplayValue(td));
    });
    if (hasRowSelection) {
      sourceRows.push(row);
      const rowCb = tr.querySelector('.sts-autocapture-row-select');
      if (rowCb && rowCb.checked) rows.push(row);
    } else {
      rows.push(row);
    }
  });
  norm.rows = rows;
  const serialized = serializeStsAutoCapturePersistedValue(norm);
  el.value = serialized;
}

function flushStsAutoCaptureHiddenFieldsFromTables(content) {
  if (!content?.querySelectorAll) return;
  content.querySelectorAll(STS_AUTOCAPTURE_BLOCK_SEL).forEach((block) => {
    const control = block.querySelector('[data-sts-autocapture="true"]');
    const table = block.querySelector('.sts-autocapture-table');
    if (control && table?.querySelector('.sts-autocapture-row-select')) {
      syncStsAutoCaptureHiddenFromTable(control, table);
    }
  });
}

/** Returns { ok: true } or { ok: false, message } — call before Draft Save / Submit (after flush). */
function validateStsAutoCaptureFieldsBeforeSave(content) {
  if (!content?.querySelectorAll) return { ok: true };
  flushStsAutoCaptureHiddenFieldsFromTables(content);
  const controls = content.querySelectorAll('[data-sts-autocapture="true"]');
  for (const el of controls) {
    if (el.getAttribute(STS_AUTOCAPTURE_LOADING_ATTR) === 'true') continue;
    if (el.getAttribute(STS_AUTOCAPTURE_ERROR_ATTR) === 'true') continue;
    const value = getStsAutoCapturePersistedValue(el);
    if (!value) continue;
    if (stsAutoCaptureSerializedValueExceedsMax(value)) {
      showStsAutoCaptureMessage(el, stsAutoCaptureMessages.dataTooLarge);
      return { ok: false, message: stsAutoCaptureMessages.dataTooLarge };
    }
  }
  return { ok: true };
}

function updateStsSelectAllState(table) {
  const selectAll = table?.querySelector('.sts-autocapture-select-all');
  const rowCbs = table ? [...table.querySelectorAll('.sts-autocapture-row-select')] : [];
  if (!selectAll || rowCbs.length === 0) return;
  const checkedCount = rowCbs.filter((cb) => cb.checked).length;
  selectAll.checked = checkedCount === rowCbs.length;
  selectAll.indeterminate = checkedCount > 0 && checkedCount < rowCbs.length;
}

function wireStsAutoCaptureRowSelection(table, hiddenControl) {
  if (!table || !hiddenControl || !table.querySelector('.sts-autocapture-row-select')) return;
  const sync = () => syncStsAutoCaptureHiddenFromTable(hiddenControl, table);
  const selectAll = table.querySelector('.sts-autocapture-select-all');
  if (selectAll) {
    selectAll.addEventListener('change', () => {
      const checked = selectAll.checked;
      selectAll.indeterminate = false;
      table.querySelectorAll('.sts-autocapture-row-select').forEach((cb) => {
        cb.checked = checked;
      });
      sync();
    });
  }
  table.querySelectorAll('.sts-autocapture-row-select').forEach((cb) => {
    cb.addEventListener('change', () => {
      updateStsSelectAllState(table);
      sync();
    });
  });
}

function wireStsAutoCaptureTableCellInputs(table, hiddenControl) {
  if (!table || !hiddenControl) return;
  const handler = () => syncStsAutoCaptureHiddenFromTable(hiddenControl, table);
  table.querySelectorAll('.sts-autocapture-cell-input').forEach((input) => {
    input.addEventListener('input', handler);
    input.addEventListener('change', handler);
  });
}

function resolveStsAutoCaptureCommentsEl(content, row, vFieldId, popCommentsTaSel) {
  if (row?.matches?.(STS_AUTOCAPTURE_BLOCK_SEL)) {
    const stsTa = row.querySelector('.crf-sts-comments-field');
    if (stsTa) return stsTa;
  }
  const vf = String(vFieldId || '').trim();
  if (vf && content?.querySelector) {
    const vfEsc = cssEscapeAttr(vf);
    const stsBlock = content.querySelector(`${STS_AUTOCAPTURE_BLOCK_SEL}[data-field-id="${vfEsc}"]`)
      || content.querySelector(`${STS_AUTOCAPTURE_BLOCK_SEL}[data-medex-code="${vfEsc}"]`);
    const stsTa = stsBlock?.querySelector('.crf-sts-comments-field');
    if (stsTa) return stsTa;
  }
  return (row ? row.querySelector(popCommentsTaSel) : null) ||
    (vf && content?.querySelector(`[id="reportfield_${vf}_comments"]`)) ||
    (vf && content?.querySelector(`[id="textbox_${vf}_comments"]`)) ||
    (vf && content?.querySelector(`[id="textarea_${vf}_comments"]`)) ||
    (vf && content?.querySelector(`[name="reportfield_${vf}_comments"]`)) ||
    (vf && content?.querySelector(`[name="textarea_${vf}_comments"]`)) ||
    (vf && content?.querySelector(`[id$="_comments"][id*="${vf}"]`)) ||
    null;
}

function applyStsAutoCaptureCommentsView(content, editable) {
  if (!content?.querySelectorAll) return;
  const blocks = content.matches?.(STS_AUTOCAPTURE_BLOCK_SEL)
    ? [content]
    : [...content.querySelectorAll(STS_AUTOCAPTURE_BLOCK_SEL)];
  blocks.forEach((block) => {
    const ta = block.querySelector('.crf-sts-comments-field');
    if (!ta) return;
    block.querySelector('[data-sts-comments-static="1"]')?.remove();
    ta.style.removeProperty('display');
    if (editable) {
      ta.removeAttribute('disabled');
      ta.removeAttribute('readonly');
      if (!ta.getAttribute('placeholder')) {
        ta.setAttribute('placeholder', 'Enter the Comments');
      }
    } else {
      ta.setAttribute('disabled', 'disabled');
      ta.setAttribute('readonly', 'readonly');
      ta.removeAttribute('placeholder');
    }
  });
}

function applyStsAutoCaptureTableCellEditability(content, canEdit, savedFields = null) {
  if (!content?.querySelectorAll) return;
  if (!canEdit) {
    content.querySelectorAll('.sts-autocapture-cell-input').forEach((input) => {
      const block = input.closest(STS_AUTOCAPTURE_BLOCK_SEL);
      const control = block?.querySelector('[data-sts-autocapture="true"]');
      if (control && stsDisplayHasRowSelectionTable(control)) return;
      const td = input.closest('td');
      if (!td) return;
      td.textContent = String(input.value ?? '');
    });
  } else {
    content.querySelectorAll('.sts-autocapture-cell-input').forEach((input) => {
      input.removeAttribute('disabled');
      input.removeAttribute('readonly');
    });
  }
  content.querySelectorAll('.sts-autocapture-select-all, .sts-autocapture-row-select').forEach((input) => {
    if (canEdit) {
      input.removeAttribute('disabled');
    } else {
      input.setAttribute('disabled', 'disabled');
    }
  });
  applyStsAutoCaptureCommentsView(content, !!canEdit);
}

function appendStsAutoCaptureDataCells(tr, columns, row, enabledColIndices, renderEditableCells = true) {
  columns.forEach((col, i) => {
    const td = document.createElement('td');
    const cellValue = row[i] != null ? String(row[i]) : '';
    if (renderEditableCells && enabledColIndices.has(i)) {
      const input = document.createElement('input');
      input.type = 'text';
      input.className = 'sts-autocapture-cell-input';
      input.setAttribute('data-sts-column', col);
      input.value = cellValue;
      input.setAttribute('disabled', 'disabled');
      input.setAttribute('readonly', 'readonly');
      td.appendChild(input);
    } else {
      td.textContent = cellValue;
    }
    tr.appendChild(td);
  });
}

function buildStsAutoCaptureTableElement(dataTable, options = {}) {
  const {
    rowSelectionMode = false,
    sourceRows = null,
    selectedRows = null,
    renderEditableCells = true,
  } = options;
  const norm = normalizeStsAutoCaptureDataTable(dataTable);
  const table = document.createElement('table');
  table.className = 'sts-autocapture-table';
  if (!norm || !norm.columns?.length) {
    const tbody = document.createElement('tbody');
    const tr = document.createElement('tr');
    const td = document.createElement('td');
    td.className = 'sts-autocapture-empty';
    td.textContent = '\u00A0';
    tr.appendChild(td);
    tbody.appendChild(tr);
    table.appendChild(tbody);
    return table;
  }
  const enabledColIndices = buildStsEnabledColumnIndexSet(norm.columns, norm.enableFields);
  const displayRows = rowSelectionMode && Array.isArray(sourceRows) && sourceRows.length > 0
    ? sourceRows
    : (norm.rows || []);
  if (rowSelectionMode) {
    try {
      table.dataset.stsSourceRows = JSON.stringify(displayRows);
    } catch (_) { /* ignore */ }
  }
  const thead = document.createElement('thead');
  const hr = document.createElement('tr');
  if (rowSelectionMode) {
    const selectTh = document.createElement('th');
    selectTh.className = 'sts-autocapture-select-col';
    const selectAllLabel = document.createElement('label');
    selectAllLabel.className = 'sts-autocapture-select-all-label';
    const selectAll = document.createElement('input');
    selectAll.type = 'checkbox';
    selectAll.className = 'sts-autocapture-select-all';
    selectAll.checked = true;
    selectAllLabel.appendChild(selectAll);
    const selectAllText = document.createElement('span');
    selectAllText.className = 'sts-autocapture-select-all-text';
    selectAllText.textContent = 'Select All';
    selectAllLabel.appendChild(selectAllText);
    selectTh.appendChild(selectAllLabel);
    hr.appendChild(selectTh);
  }
  norm.columns.forEach((col) => {
    const th = document.createElement('th');
    th.textContent = col;
    hr.appendChild(th);
  });
  thead.appendChild(hr);
  table.appendChild(thead);
  const tbody = document.createElement('tbody');
  const hasSelectedRows = Array.isArray(selectedRows) && selectedRows.length > 0;
  displayRows.forEach((row) => {
    const tr = document.createElement('tr');
    const displayRow = rowSelectionMode && hasSelectedRows
      ? mergeStsSavedEnableFieldValues(row, selectedRows, norm.columns, norm.enableFields)
      : row;
    if (rowSelectionMode) {
      const selectTd = document.createElement('td');
      selectTd.className = 'sts-autocapture-select-col';
      const rowSelect = document.createElement('input');
      rowSelect.type = 'checkbox';
      rowSelect.className = 'sts-autocapture-row-select';
      rowSelect.checked = hasSelectedRows
        ? isStsSourceRowSelected(row, selectedRows, norm.columns, norm.enableFields)
        : true;
      selectTd.appendChild(rowSelect);
      tr.appendChild(selectTd);
    }
    appendStsAutoCaptureDataCells(tr, norm.columns, displayRow, enabledColIndices, renderEditableCells);
    tbody.appendChild(tr);
  });
  table.appendChild(tbody);
  if (rowSelectionMode) updateStsSelectAllState(table);
  return table;
}

function renderStsLegacyTextDisplay(displayEl, text) {
  if (!displayEl) return;
  displayEl.innerHTML = '';
  const pre = document.createElement('pre');
  pre.className = 'sts-autocapture-legacy-text';
  pre.textContent = text || '';
  displayEl.appendChild(pre);
}

function renderStsAutoCaptureTable(displayEl, dataTable, hiddenControl = null, options = {}) {
  if (!displayEl) return null;
  const {
    rowSelectionMode = false,
    sourceRows = null,
    selectedRows = null,
    renderEditableCells = true,
  } = options;
  displayEl.innerHTML = '';
  const wrap = document.createElement('div');
  wrap.className = 'sts-autocapture-table-wrap';
  const norm = normalizeStsAutoCaptureDataTable(dataTable);
  const resolvedSourceRows = rowSelectionMode
    ? (Array.isArray(sourceRows) ? sourceRows : norm?.rows || [])
    : null;
  const resolvedSelectedRows = rowSelectionMode
    ? (Array.isArray(selectedRows) ? selectedRows : null)
    : null;
  const table = buildStsAutoCaptureTableElement(dataTable, {
    rowSelectionMode,
    sourceRows: resolvedSourceRows,
    selectedRows: resolvedSelectedRows,
    renderEditableCells,
  });
  wrap.appendChild(table);
  displayEl.appendChild(wrap);
  if (hiddenControl) {
    wireStsAutoCaptureTableCellInputs(table, hiddenControl);
    if (rowSelectionMode) wireStsAutoCaptureRowSelection(table, hiddenControl);
  }
  return table;
}

function getStsAutoCapturePersistedValue(control) {
  const el = resolveStsAutoCaptureControl(control) || control;
  if (!el) return '';
  return String(el.value ?? '').trim();
}

function applyStsAutoCaptureDataTable(control, dataTable, canEditCells = false, rowSelectionMode = false) {
  const el = resolveStsAutoCaptureControl(control) || control;
  if (!el) return;
  const norm = normalizeStsAutoCaptureDataTable(dataTable);
  if (norm) {
    if (stsAutoCaptureNormHasZeroRows(norm)) {
      showStsAutoCaptureBlankAnswer(el);
      return;
    }
    const serialized = serializeStsAutoCapturePersistedValue(norm);
    if (!rowSelectionMode) {
      el.value = serialized;
    } else {
      el.value = serializeStsAutoCapturePersistedValue({
        columns: norm.columns,
        rows: [],
        enableFields: norm.enableFields || [],
      });
    }
    const display = resolveStsAutoCaptureDisplayEl(el);
    if (display) {
      renderStsAutoCaptureTable(display, norm, el, {
        rowSelectionMode,
        sourceRows: rowSelectionMode ? norm.rows : null,
        selectedRows: rowSelectionMode ? norm.rows : null,
        renderEditableCells: !!canEditCells,
      });
      if (rowSelectionMode) {
        const table = display.querySelector('.sts-autocapture-table');
        if (table) syncStsAutoCaptureHiddenFromTable(el, table);
      }
      const scope = el.closest('.crf-generated-content') || display;
      applyStsAutoCaptureTableCellEditability(scope, !!canEditCells);
    }
  }
  el.removeAttribute('placeholder');
  el.removeAttribute(STS_AUTOCAPTURE_LOADING_ATTR);
  el.removeAttribute(STS_AUTOCAPTURE_ERROR_ATTR);
}

/** Persisted CRFDtl vFieldValue for an STS AutoCapture control (after Draft Save / reload). */
function getStsAutoCaptureSavedFieldValue(el, savedFields) {
  if (!el || !Array.isArray(savedFields) || savedFields.length === 0) return '';
  const row = el.closest?.('.crf-report-row') || el.closest?.(STS_AUTOCAPTURE_BLOCK_SEL);
  let field = row ? findSavedFieldForCrfReportRow(row, savedFields) : null;
  if (!field) {
    const code = resolveStsAutoCaptureFieldCode(el);
    if (code) {
      const nField = normalizeCrfFieldCodeForMatch(code);
      field = savedFields.find((f) => {
        const fId = (f.vFieldId || f.VFieldId || '').trim();
        if (!fId) return false;
        if (fId.toLowerCase() === code.toLowerCase()) return true;
        return normalizeCrfFieldCodeForMatch(fId) === nField;
      }) || null;
    }
  }
  if (!field) return '';
  return String(field.vFieldValue ?? field.VFieldValue ?? '').trim();
}

function applyStsAutoCaptureDbValue(el, savedTrim, canEditCells = false) {
  if (!el || !savedTrim) return false;
  const control = resolveStsAutoCaptureControl(el) || el;
  const norm = normalizeStsAutoCaptureDataTable(savedTrim);
  if (norm) {
    const useSelection = shouldStsShowRowSelectionMode();
    control.value = serializeStsAutoCapturePersistedValue(norm) || savedTrim;
    const display = resolveStsAutoCaptureDisplayEl(control);
    if (display && !useSelection) {
      renderStsAutoCaptureTable(display, norm, control, {
        rowSelectionMode: false,
        renderEditableCells: !!canEditCells,
      });
      const scope = control.closest('.crf-generated-content') || display;
      applyStsAutoCaptureTableCellEditability(scope, !!canEditCells);
    }
    return true;
  } else if (control.tagName === 'TEXTAREA' || control.tagName === 'INPUT') {
    if (/^[\[{]/.test(String(savedTrim).trim())) {
      showStsAutoCaptureMessage(control, 'AutoCapture data is invalid. Please refresh or contact administrator.');
      return false;
    }
    control.value = savedTrim;
    const display = resolveStsAutoCaptureDisplayEl(control);
    if (display) renderStsLegacyTextDisplay(display, savedTrim);
    control.removeAttribute('placeholder');
    control.removeAttribute(STS_AUTOCAPTURE_LOADING_ATTR);
    control.removeAttribute(STS_AUTOCAPTURE_ERROR_ATTR);
    control.setAttribute(STS_AUTOCAPTURE_SOURCE_ATTR, STS_AUTOCAPTURE_SOURCE_DB);
    return true;
  }
  return false;
}

function processStsAutocapturePreviewBlocks(clone, blankAnswersAndComments) {
  if (!clone?.querySelectorAll) return;
  clone.querySelectorAll(STS_AUTOCAPTURE_BLOCK_SEL).forEach((block) => {
    const control = block.querySelector('[data-sts-autocapture="true"]');
    const display = block.querySelector('[data-sts-autocapture-display="true"], .sts-autocapture-display');
    const commentsTa = block.querySelector('.crf-sts-comments-field, textarea[id$="_comments"]');
    const persistedRaw = control ? getStsAutoCapturePersistedValue(control) : '';
    if (control) control.remove();
    if (display) {
      display.innerHTML = '';
      if (!blankAnswersAndComments) {
        const norm = normalizeStsAutoCaptureDataTable(persistedRaw);
        if (norm) {
          renderStsAutoCaptureTable(display, norm, null, {
            rowSelectionMode: false,
            renderEditableCells: false,
          });
        } else if (persistedRaw) {
          renderStsLegacyTextDisplay(display, persistedRaw);
        }
      }
    }
    if (commentsTa) {
      const text = blankAnswersAndComments ? '' : (commentsTa.value || '').trim();
      const div = document.createElement('div');
      div.className = 'crf-preview-field-value crf-sts-comments-preview';
      div.textContent = text || '\u00A0';
      commentsTa.parentNode.replaceChild(div, commentsTa);
    }
  });
}

function stsExtractFieldCodeFromControl(input, controlTypes) {
  const rawFieldId = (input?.id || input?.name || '').trim();
  if (!rawFieldId) return '';
  const parts = rawFieldId.split('_');
  const five = parts.find((p) => p.length === 5 && /^[A-Za-z0-9]+$/.test(p));
  if (five && !controlTypes.includes(five.toLowerCase())) return five;
  return five || rawFieldId.substring(0, 5);
}

/** PDF download only: tier STS table font and proportional column widths from header/content length. */
function applyPdfStsTableColumnFit(container) {
  const restore = [];
  if (!container?.querySelectorAll) return restore;
  container.querySelectorAll('.sts-autocapture-table').forEach((table) => {
    const headers = [...table.querySelectorAll('thead th')].filter(
      (th) => !th.classList.contains('sts-autocapture-select-col')
    );
    const colCount = headers.length || 0;
    const prevDataCols = table.getAttribute('data-pdf-sts-cols');
    const prevFontSize = table.style.fontSize;
    const prevTableLayout = table.style.tableLayout;
    const existingColgroup = table.querySelector('colgroup');
    const prevColgroup = existingColgroup ? existingColgroup.cloneNode(true) : null;

    let fontPx = '8.5px';
    if (colCount <= 6) fontPx = '9px';
    else if (colCount <= 9) fontPx = '8.5px';
    else if (colCount > 0) fontPx = '8px';

    restore.push({
      el: table,
      dataPdfStsCols: prevDataCols,
      fontSize: prevFontSize,
      tableLayout: prevTableLayout,
      prevColgroup,
      hadColgroup: !!existingColgroup,
    });

    if (colCount > 0) table.setAttribute('data-pdf-sts-cols', String(colCount));
    else table.removeAttribute('data-pdf-sts-cols');
    table.style.fontSize = fontPx;
    table.style.tableLayout = 'fixed';

    if (headers.length > 0) {
      const weights = headers.map((th) => Math.max((th.textContent || '').trim().length, 4));
      const sampleRow = table.querySelector('tbody tr');
      if (sampleRow) {
        const dataCells = [...sampleRow.children].filter(
          (td) => !td.classList.contains('sts-autocapture-select-col')
        );
        dataCells.forEach((td, i) => {
          if (weights[i] == null) return;
          const len = (td.querySelector('.sts-autocapture-cell-input')?.value ?? td.textContent ?? '').trim().length;
          weights[i] = Math.max(weights[i], Math.min(len, 28));
        });
      }
      const total = weights.reduce((a, b) => a + b, 0) || 1;
      let colgroup = existingColgroup;
      if (!colgroup) {
        colgroup = document.createElement('colgroup');
        const first = table.firstChild;
        if (first) table.insertBefore(colgroup, first);
        else table.appendChild(colgroup);
      }
      colgroup.innerHTML = '';
      weights.forEach((w) => {
        const col = document.createElement('col');
        col.style.width = `${((w / total) * 100).toFixed(2)}%`;
        colgroup.appendChild(col);
      });
    }
  });
  return restore;
}

function restorePdfStsTableColumnFit(restore) {
  if (!restore?.length) return;
  restore.forEach(({ el, dataPdfStsCols, fontSize, tableLayout, prevColgroup, hadColgroup }) => {
    if (!el) return;
    if (dataPdfStsCols == null) el.removeAttribute('data-pdf-sts-cols');
    else el.setAttribute('data-pdf-sts-cols', dataPdfStsCols);
    el.style.fontSize = fontSize;
    el.style.tableLayout = tableLayout;
    const colgroup = el.querySelector('colgroup');
    if (hadColgroup) {
      if (prevColgroup && colgroup) {
        colgroup.replaceWith(prevColgroup);
      } else if (colgroup) {
        colgroup.remove();
      }
    } else if (colgroup) {
      colgroup.remove();
    }
  });
}

/** Visit Report Preview modal: expand STS table wrap (full height in preview). */
function expandStsAutocaptureWrapsForPreview(container) {
  const restore = [];
  if (!container?.querySelectorAll) return restore;
  container.querySelectorAll('.sts-autocapture-table-wrap').forEach((wrap) => {
    restore.push({
      el: wrap,
      maxHeight: wrap.style.maxHeight,
      overflow: wrap.style.overflow,
      overflowX: wrap.style.overflowX,
      overflowY: wrap.style.overflowY,
    });
    wrap.style.maxHeight = 'none';
    wrap.style.overflow = 'visible';
    wrap.style.overflowX = 'visible';
    wrap.style.overflowY = 'visible';
  });
  return restore;
}

function restoreStsAutocaptureWraps(restore) {
  if (!restore?.length) return;
  restore.forEach(({ el, maxHeight, overflow, overflowX, overflowY }) => {
    if (!el) return;
    el.style.maxHeight = maxHeight;
    el.style.overflow = overflow;
    el.style.overflowX = overflowX;
    el.style.overflowY = overflowY;
  });
}

/** Apply STS/layout helpers when Visit Report Preview modal opens (restored on close). */
function prepareVisitReportPreviewLayout(container) {
  return {
    stsWrapRestore: expandStsAutocaptureWrapsForPreview(container),
    stsTableRestore: applyPdfStsTableColumnFit(container),
  };
}

function restoreVisitReportPreviewLayout(state) {
  if (!state) return;
  restorePdfStsTableColumnFit(state.stsTableRestore);
  restoreStsAutocaptureWraps(state.stsWrapRestore);
}

/** Reviewer-by line value: blank when API sent em dash, en dash, or "--" placeholder. */
function formatWorkflowReviewerDisplay(value) {
  if (value == null) return '';
  const t = String(value).trim();
  if (t === '' || t === '--' || t === '\u2014' || t === '\u2013') return '';
  return String(value);
}

/** Dynamic Visit Report reviewer columns from API (StageMst order); each col matches one reviewer tier 201–9999. */
function getWorkflowReviewerColumns(workflowHeader) {
  if (!workflowHeader) return null;
  const cols = getAnyCase(workflowHeader, 'reviewerColumns', 'ReviewerColumns');
  return Array.isArray(cols) && cols.length > 0 ? cols : null;
}

/**
 * Header reviewer columns: prefer API ReviewerColumns (signers + labels); else project StageMst tiers + ReviewedBy/ApprovedBy by order.
 * Returns null when there are no API columns and no project reviewer tiers (nothing to render).
 */
function buildCrfHeaderReviewerColumns(workflowHeader, projectTierLabels) {
  const apiCols = getWorkflowReviewerColumns(workflowHeader);
  if (apiCols && apiCols.length > 0) {
    return apiCols.map((col) => ({
      label: String(getAnyCase(col, 'label', 'Label') ?? '').trim(),
      signedBy: formatWorkflowReviewerDisplay(getAnyCase(col, 'signedBy', 'SignedBy')),
      performedAtUtc: getAnyCase(col, 'performedAtUtc', 'PerformedAtUtc') ?? null,
      performedAtOffset: getAnyCase(col, 'performedAtOffset', 'PerformedAtOffset') ?? null,
    }));
  }
  const tiers = Array.isArray(projectTierLabels) ? projectTierLabels : [];
  if (tiers.length === 0) return null;
  const reviewedBy = formatWorkflowReviewerDisplay(workflowHeader?.reviewedBy ?? workflowHeader?.ReviewedBy);
  const approvedBy = formatWorkflowReviewerDisplay(workflowHeader?.approvedBy ?? workflowHeader?.ApprovedBy);
  const signers = [reviewedBy, approvedBy];
  return tiers.map((t, idx) => {
    const label = typeof t === 'string' ? String(t).trim() : String(t.label ?? t.Label ?? '').trim();
    const signedBy = idx < signers.length ? signers[idx] : '';
    return { label, signedBy, performedAtUtc: null, performedAtOffset: null };
  });
}

/** Parse signedBy string "FirstName LastName (Username)(Profile)" into { name, role }. */
function parseSignerNameAndRole(signedBy) {
  if (!signedBy || typeof signedBy !== 'string') return { name: '', role: '' };
  const s = signedBy.trim();
  if (!s) return { name: '', role: '' };
  const firstParen = s.indexOf('(');
  const name = firstParen > 0 ? s.substring(0, firstParen).trim() : s;
  const matches = s.match(/\(([^)]*)\)/g);
  const role = matches && matches.length > 0
    ? matches[matches.length - 1].replace(/[()]/g, '').trim()
    : '';
  return { name, role };
}

/**
 * Format formula Calc result. When template "decimal places" is 0, toFixed(0) would turn 0.375 into "0";
 * for non-integers we still show a trimmed decimal string.
 */
function formatFormulaCalcResult(num, decimalPlaces) {
  if (num === undefined || Number.isNaN(num) || !Number.isFinite(num)) return '';
  const n = parseFloat(num);
  const places = Math.max(0, Number(decimalPlaces) || 0);
  if (places > 0) {
    return n.toFixed(places);
  }
  const roundedInt = Math.round(n);
  if (Math.abs(n - roundedInt) < 1e-9) {
    return String(roundedInt);
  }
  let s = n.toFixed(10);
  if (s.includes('.')) {
    s = s.replace(/\.?0+$/, '');
  }
  return s;
}

/**
 * Turn math-style implicit multiplication into valid JS for eval, e.g.
 * "( a * b ) / c ( d * 2 )" -> "( a * b ) / c*( d * 2 )"; also ")(" -> ")*(".
 */
function insertImplicitMultiplicationForFormula(expr) {
  if (expr == null || typeof expr !== 'string') return expr;
  let s = expr;
  // )(  ->  )*(
  s = s.replace(/\)\(/g, ')*(');
  // number (  ->  number*(
  s = s.replace(/(-?\d+(?:\.\d+)?)\s+\(/g, '$1*(');
  // number(  ->  number*(  (no space, e.g. "5(10+1)")
  s = s.replace(/(-?\d+(?:\.\d+)?)\(/g, '$1*(');
  // ) (  ->  )*(
  s = s.replace(/\)\s+\(/g, ')*(');
  // ) number  ->  )*number
  s = s.replace(/\)\s+(-?\d+(?:\.\d+)?)/g, ')*$1');
  return s;
}

/**
 * Replace field codes in formula string with values from the report DOM; normalize "1 0 0" → 100; map ^ to ** for JS.
 */
function substituteFieldCodesInFormula(content, formulaStr) {
  if (!content || formulaStr == null) return '';
  const formulaStrClean = String(formulaStr).replace(/"/g, '').replace(/'/g, '');
  const tokens = formulaStrClean
    .replace(/\(|\)|\?/g, ' ')
    .replace(/[+\-*/^]/g, ' ')
    .split(/\s+/)
    .filter(Boolean);
  const isFieldCode = (token) => /^\d+$/.test(token) && token.length >= 5;
  let reduced = formulaStrClean.replace(/\?/g, '');
  for (let i = 0; i < tokens.length; i++) {
    const code = tokens[i];
    if (!isFieldCode(code)) continue;
    const el = content.querySelector(`input[id$="${code}"], select[id$="${code}"], textarea[id$="${code}"]`);
    let val = el ? (el.value || '').trim() : '';
    if (val === '' || val === undefined) val = 0;
    else val = parseFloat(val);
    if (Number.isNaN(val)) val = 0;
    const regex = new RegExp(code.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g');
    reduced = reduced.replace(regex, String(val));
  }
  reduced = reduced.replace(/\s*1\s+0\s+0\s*/g, ' 100 ');
  reduced = reduced.replace(/\^/g, '**');
  let sanitized = reduced.replace(/[^0-9+\-*/.()\s]/g, '');
  sanitized = insertImplicitMultiplicationForFormula(sanitized.trim());
  return sanitized;
}

/**
 * Evaluate sanitized arithmetic (digits, + - * / **, parentheses, spaces). Returns ok + value or error message (never conflate with numeric 0).
 */
function evaluateSanitizedFormula(sanitized) {
  const s = (sanitized || '').trim();
  if (!s) {
    return { ok: false, value: null, message: 'Formula is empty or invalid after replacing field values.' };
  }
  try {
    // eslint-disable-next-line no-eval -- only runs on charset-restricted arithmetic strings built above
    const num = eval(s);
    if (typeof num === 'bigint') {
      return { ok: true, value: Number(num), message: '' };
    }
    if (typeof num !== 'number') {
      return { ok: false, value: null, message: 'Formula did not evaluate to a number.' };
    }
    if (Number.isNaN(num) || !Number.isFinite(num)) {
      const message = Number.isNaN(num)
        ? 'Invalid calculation. Check your inputs.'
        : 'Calculation error (e.g., division by zero).';
      return { ok: false, value: null, message };
    }
    return { ok: true, value: num, message: '' };
  } catch (e) {
    const msg = e instanceof Error ? e.message : String(e);
    return { ok: false, value: null, message: msg ? `Invalid formula: ${msg}` : 'Invalid formula expression.' };
  }
}

/**
 * Audit trail values for CRF date fields commonly arrive as ISO dates (yyyy-mm-dd).
 * Normalize them to the same UI date format (dd-mmm-yyyy) for consistent display.
 */
function formatAuditValueForDisplay(value) {
  if (value == null) return '';
  const raw = String(value).trim();
  if (!raw) return '';

  // Match "2026-04-08" or "2026-04-08T00:00:00" (we only care about date part)
  const m = /^(\d{4})-(\d{2})-(\d{2})(?:[T\s].*)?$/.exec(raw);
  if (!m) return raw;

  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  if (!Number.isFinite(y) || !Number.isFinite(mo) || !Number.isFinite(d)) return raw;
  if (mo < 1 || mo > 12 || d < 1 || d > 31) return raw;

  // Use local date constructor to avoid UTC offset shifting the calendar day.
  const dt = new Date(y, mo - 1, d);
  if (Number.isNaN(dt.getTime())) return raw;
  return formatDateDDMMMYYYY(dt);
}

// Report title by visit type (dynamic per plan)
const reportTitleByVisitType = {
  SSV: 'Site Selection Visit Report (SSV)',
  SIV: 'Site Initiation Visit Report (SIV)',
  SMV: 'Site Monitoring Visit Report (SMV)',
  SCV: 'Site Close-Out Visit Report (SCV)',
};

/**
 * True when Standard Date/DateTime string is only template placeholders (DD, MMM, YYYY, HH, MM).
 * UK/UNK/UKUK/0000 are not placeholders. Empty string returns false (use !val for "missing").
 */
function isPlaceholderStandardDateValue(val) {
  const v = (val || '').trim();
  if (!v) return false;
  if (v.length < 8) return false;
  const dateTimeMatch = v.match(/^([^-]+)-([^-]+)-([^\s-]+)(?:\s+([^:]+):([^:]+))?$/);
  if (!dateTimeMatch) return false;
  const day = (dateTimeMatch[1] || '').trim().toUpperCase();
  const month = (dateTimeMatch[2] || '').trim().toUpperCase();
  const year = (dateTimeMatch[3] || '').trim().toUpperCase();
  if (day === 'DD' || month === 'MMM' || year === 'YYYY') return true;
  if (dateTimeMatch[4] != null) {
    const hour = (dateTimeMatch[4] || '').trim().toUpperCase();
    const minute = (dateTimeMatch[5] || '').trim().toUpperCase();
    if (hour === 'HH' || minute === 'MM') return true;
  }
  return false;
}

/** Same as isPlaceholderStandardDateValue (legacy name for submit validation). */
const isPlaceholderOnlyStandardDateOrDateTime = isPlaceholderStandardDateValue;

/** Report table: Answer vs Comments columns (server `.crf-*-col` + client transform `.crf-*-cell`). */
const CRF_COMMENT_REGION_SEL = '.crf-comments-cell, .crf-comments-col, .crf-sts-autocapture-comments';
const CRF_ANSWER_REGION_SEL = '.crf-answer-cell, .crf-answer-col';
const CRF_COMMENTS_TEXTAREA_SEL = '.crf-comments-cell textarea, .crf-comments-col textarea';
/** Row-scoped primary controls (any type) for DCF/highlight and field id resolution. */
const CRF_ROW_ANSWER_CONTROLS_SEL =
  '.crf-answer-cell [data-field-id], .crf-answer-cell input, .crf-answer-cell select, .crf-answer-cell textarea, .crf-answer-col [data-field-id], .crf-answer-col input, .crf-answer-col select, .crf-answer-col textarea';

/**
 * Primary answer control for DCF highlight / savedFields lookup. Avoids matching a leading hidden input
 * before radio options (querySelector would bind the row to the wrong CRFDtl / DCF state).
 */
function getPrimaryCrfAnswerControlForRow(row) {
  if (!row) return null;
  const sts = row.querySelector?.('[data-sts-autocapture="true"]');
  if (sts) return sts;
  const ac = row.querySelector('.crf-answer-cell, .crf-answer-col');
  if (!ac) return null;
  const radioChecked = ac.querySelector('input[type="radio"]:checked');
  if (radioChecked) return radioChecked;
  const anyRadio = ac.querySelector('input[type="radio"]');
  if (anyRadio) return anyRadio;
  const chk = ac.querySelector('input[type="checkbox"]');
  if (chk) return chk;
  const withData = ac.querySelector(
    'input:not([type="hidden"])[data-field-id], select[data-field-id], textarea[data-field-id]'
  );
  if (withData) return withData;
  return ac.querySelector('input:not([type="hidden"]), select, textarea');
}

function isCrfCommentRegionElement(el) {
  return !!el?.closest?.(CRF_COMMENT_REGION_SEL);
}

function isCrfAnswerRegionElement(el) {
  return !!el?.closest?.(CRF_ANSWER_REGION_SEL);
}

/**
 * True if this DOM control is the Comments-side field (or legacy orphan *_comments), not the primary answer.
 * Used like collectFormData: never rely on id suffix alone for "comment" when the control sits in the Answer column.
 */
function isCrfCommentFieldControl(el) {
  if (!el) return false;
  if (isCrfCommentRegionElement(el)) return true;
  const id = (el.id || '').trim();
  const nm = (el.name || '').trim();
  if (!isCrfAnswerRegionElement(el) && (id.endsWith('_comments') || nm.endsWith('_comments'))) return true;
  return false;
}

/** Normalize MedEx / template field codes so "12" and "00012" match API rows. */
function normalizeCrfFieldCodeForMatch(code) {
  const s = (code || '').trim();
  if (!s) return '';
  if (/^\d+$/.test(s)) return s.padStart(5, '0');
  return s;
}

/** Resolve savedFields row for a transformed report row (detail no, then answer control data-field-id). */
function findSavedFieldForCrfReportRow(row, savedFields) {
  if (!row) return null;
  const list = savedFields || [];
  const rowDtlNo = row.getAttribute('data-n-crf-dtl-no');
  const nCRFDtlNoForRow = rowDtlNo != null && rowDtlNo !== '' ? Number(rowDtlNo) : null;
  if (nCRFDtlNoForRow != null && !Number.isNaN(nCRFDtlNoForRow)) {
    const byDtl = list.find((f) => Number(f.nCRFDtlNo ?? f.NCRFDtlNo ?? f.ncrfDtlNo ?? 0) === nCRFDtlNoForRow);
    if (byDtl) return byDtl;
  }
  const input = getPrimaryCrfAnswerControlForRow(row);
  let fieldId = (input?.getAttribute('data-field-id') || '').trim();
  if (!fieldId) {
    const ac = row.querySelector('.crf-answer-cell[data-field-id], .crf-answer-col[data-field-id]');
    fieldId = (ac?.getAttribute('data-field-id') || '').trim();
  }
  if (fieldId) {
    const nField = normalizeCrfFieldCodeForMatch(fieldId);
    const hit = list.find((f) => {
      const fId = (f.vFieldId || f.VFieldId || '').trim();
      if (!fId) return false;
      if (fId === fieldId || fId.toLowerCase() === fieldId.toLowerCase()) return true;
      return normalizeCrfFieldCodeForMatch(fId) === nField;
    });
    if (hit) return hit;
  }
  const med = (row.getAttribute('data-medex-code') || '').trim();
  if (med && list.length) {
    const nMed = normalizeCrfFieldCodeForMatch(med);
    return (
      list.find((f) => {
        const fId = (f.vFieldId || f.VFieldId || '').trim();
        if (!fId) return false;
        if (fId === med || fId.toLowerCase() === med.toLowerCase()) return true;
        return normalizeCrfFieldCodeForMatch(fId) === nMed;
      }) || null
    );
  }
  return null;
}

/** Visit Report: optional (i) beside the question when master has a fill hint (metadata only; not saved as an answer). */
function applyVisitReportFillHintIcons(content, savedFieldsList) {
  if (!content?.querySelectorAll) return;
  const list = savedFieldsList || [];
  if (!Array.isArray(list)) return;
  content.querySelectorAll('.crf-visit-report-fill-hint-btn').forEach((b) => b.remove());
  if (list.length === 0) return;
  content.querySelectorAll('.crf-report-row:not(.crf-report-row-label-only)').forEach((row) => {
    const field = findSavedFieldForCrfReportRow(row, list);
    const hintRaw =
      field?.vVisitReportFillHint ??
      field?.VVisitReportFillHint ??
      field?.visitReportFillHint ??
      field?.VisitReportFillHint ??
      '';
    const hint = (hintRaw || '').trim();
    if (!hint) return;
    const labelCell =
      row.querySelector('.crf-question-cell') ||
      row.querySelector('td.crf-question-cell') ||
      row.querySelector('.form-group.crf-question-cell');
    let target = labelCell;
    if (!target) {
      const tds = row.querySelectorAll('td');
      if (tds.length) target = tds[0];
    }
    if (!target) return;
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'crf-visit-report-fill-hint-btn';
    btn.setAttribute('title', hint);
    const ariaHint = hint.length > 200 ? `${hint.slice(0, 197)}...` : hint;
    btn.setAttribute('aria-label', `Example suggestion: ${ariaHint}`);
    btn.innerHTML =
      '<svg class="crf-visit-report-fill-hint-svg" width="18" height="18" viewBox="0 0 20 20" aria-hidden="true" focusable="false">' +
      '<circle cx="10" cy="10" r="8.5" fill="none" stroke="currentColor" stroke-width="1.15" />' +
      '<text x="10" y="13.6" text-anchor="middle" font-size="10.5" font-family="Georgia, \'Times New Roman\', Times, serif" font-style="italic" fill="currentColor">i</text>' +
      '</svg>';
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
    });
    const actionsWrap = target.querySelector('.crf-row-actions');
    const labelHost =
      target.querySelector('label.form-label') ||
      target.querySelector('.form-label') ||
      target.querySelector('.crf-question-text') ||
      target.querySelector('label');
    if (labelHost) {
      labelHost.appendChild(btn);
    } else if (actionsWrap) {
      target.insertBefore(btn, actionsWrap);
    } else {
      target.appendChild(btn);
    }
  });
}

/** Display text for one transactional control when DCF / Audit / Issues modals replace inputs with static copy. */
/** Visit Report transform: remove extra stacked text inputs that share the same data-field-id (e.g. duplicate time boxes). */
function pruneDuplicateVisitReportAnswerTextInputs(answerCell, fieldCode) {
  if (!answerCell || !fieldCode) return;
  const fc = String(fieldCode).trim();
  const candidates = Array.from(answerCell.querySelectorAll('input')).filter((el) => {
    const ty = (el.type || '').toLowerCase();
    if (ty && ty !== 'text') return false;
    if (el.closest?.('.crf-row-actions, [data-crf-action]')) return false;
    if (el.classList.contains('standarddate-text') || el.classList.contains('standarddatetime-text')) return false;
    if (el.closest?.('.standarddate-row, .standarddatetime-row')) return false;
    if ((el.getAttribute('data-field-id') || '').trim() !== fc) return false;
    return true;
  });
  if (candidates.length <= 1) return;
  for (let i = 1; i < candidates.length; i += 1) {
    try {
      candidates[i].remove();
    } catch (_) { /* ignore */ }
  }
}

/** Read question label text without visit-report fill-hint icon button content. */
function getCrfQuestionTextWithoutHintIcon(questionEl) {
  if (!questionEl) return '';
  const clone = questionEl.cloneNode(true);
  if (clone?.querySelectorAll) {
    clone.querySelectorAll('.crf-visit-report-fill-hint-btn').forEach((n) => n.remove());
  }
  return (clone?.textContent || '').replace(/\s+/g, ' ').trim();
}

function getCrfTransactionalControlDisplayText(el) {
  if (!el) return '';
  const tag = (el.tagName || '').toUpperCase();
  if (tag === 'BUTTON') return '';
  if (tag === 'INPUT' && (el.type === 'date' || el.type === 'datetime-local')) {
    const raw = (el.value ?? '').toString().trim();
    if (!raw) return '';
    const formatted = formatDateDDMMMYYYY(raw);
    return formatted !== '—' ? formatted : raw;
  }
  if (el.type === 'file') {
    return (
      (el.getAttribute('data-saved-filename') || '').trim() ||
      (el.files && el.files[0] ? el.files[0].name : '') ||
      ''
    ).trim();
  }
  if (el.type === 'checkbox' || el.type === 'radio') {
    if (!el.checked) return '';
    // Prefer text immediately after the input (sibling label span), not a wrapping label that lists every option.
    const sib = el.nextElementSibling;
    if (sib && sib.textContent) {
      const t = (sib.textContent || '').replace(/\s+/g, ' ').trim();
      if (t) return t;
    }
    const lab = el.closest('label');
    if (lab) {
      const inputs = lab.querySelectorAll('input[type="checkbox"], input[type="radio"]');
      if (inputs.length <= 1) return (lab.textContent || '').replace(/\s+/g, ' ').trim();
    }
    return (el.value || '').trim() || 'Yes';
  }
  if (tag === 'SELECT') {
    const i = el.selectedIndex;
    const opt = i >= 0 ? el.options[i] : null;
    const t = (opt?.textContent || opt?.text || '').trim();
    if (t) return t;
    return (el.value || '').trim();
  }
  return (el.value ?? '').toString().trim();
}

/**
 * Visit Report / CRF: bind saved vFieldValue to a combobox (HTML select). Empty or unknown values snap to the template
 * placeholder option (-1, __default_combobox__value__, empty value, or option label "Select") so the closed control
 * does not render blank (invalid selectedIndex).
 */
function applyComboboxOrSelectSavedValue(selectEl, savedValue) {
  if (!selectEl || (selectEl.tagName || '').toUpperCase() !== 'SELECT') return;
  const opts = Array.from(selectEl.options || []);
  if (!opts.length) return;
  const raw = savedValue != null ? String(savedValue).trim() : '';
  const pickPlaceholder = () => {
    const ph = opts.find((o) => {
      const val = (o.value ?? '').toString().trim();
      const txt = (o.textContent ?? '').trim().toLowerCase();
      return val === '' || val === '-1' || val === '__default_combobox__value__' || txt === 'select';
    });
    if (ph) {
      selectEl.value = ph.value;
      return;
    }
    selectEl.selectedIndex = 0;
  };
  if (raw === '') {
    pickPlaceholder();
    return;
  }
  selectEl.value = raw;
  if (selectEl.value === raw) return;
  const byVal = opts.find((o) => (o.value ?? '').toString().trim() === raw);
  if (byVal) {
    selectEl.value = byVal.value;
    return;
  }
  const byText = opts.find((o) => (o.textContent ?? '').trim().toLowerCase() === raw.toLowerCase());
  if (byText) {
    selectEl.value = byText.value;
    return;
  }
  pickPlaceholder();
}

/**
 * Detail row (nCRFDtlNo) while DCF / Audit / Issues / Create Issue modal is open for that row.
 * Shared by the row-lock layout effect and processContent (rAF can run after layout and re-enable inputs).
 */
function getVisitReportCrfRowModalLockDetailNo(modalState) {
  if (!modalState) return null;
  const {
    dcfModalOpen,
    dcfNcrfDtlNo,
    auditModalOpen,
    auditNcrfDtlNo,
    actionItemsModalOpen,
    actionItemsNcrfDtlNo,
    isCreateActionItemModalOpen,
    createActionItemNcrfDtlNo,
  } = modalState;
  if (dcfModalOpen && dcfNcrfDtlNo != null && !Number.isNaN(Number(dcfNcrfDtlNo))) return Number(dcfNcrfDtlNo);
  if (auditModalOpen && auditNcrfDtlNo != null && !Number.isNaN(Number(auditNcrfDtlNo))) return Number(auditNcrfDtlNo);
  if (actionItemsModalOpen && actionItemsNcrfDtlNo != null && !Number.isNaN(Number(actionItemsNcrfDtlNo))) {
    return Number(actionItemsNcrfDtlNo);
  }
  if (isCreateActionItemModalOpen && createActionItemNcrfDtlNo != null && !Number.isNaN(Number(createActionItemNcrfDtlNo))) {
    return Number(createActionItemNcrfDtlNo);
  }
  return null;
}

/**
 * Clears DCF/Audit/Issue modal read-only overlay for one `.crf-report-row` (static snapshot + hidden transactional controls).
 * processContent can call applyCrfReportRowNonTransactionalViewLock again while the modal is open without running React's
 * prior cleanup closure — that orphans extra `.crf-view-only-static` nodes until we sweep the whole row.
 */
function sweepVisitReportRowModalLockOverlay(row) {
  if (!row?.querySelectorAll) return;
  row.querySelectorAll('[data-crf-transaction-view-static]').forEach((el) => el.remove());
  row.querySelectorAll('[data-crf-transaction-view-hide-ro-group="1"]').forEach((g) => {
    g.removeAttribute('data-crf-transaction-view-hide-ro-group');
    g.style.removeProperty('display');
  });
  row.querySelectorAll('[data-crf-transaction-view-lock]').forEach((c) => {
    delete c.dataset.crfTransactionViewLock;
    c.disabled = false;
    c.readOnly = false;
    c.removeAttribute('readonly');
    c.style.removeProperty('display');
  });
}

/**
 * While DCF, View Audit, or Issues UI is open for a report row (or visit-wide read-only snapshot),
 * hide Answer transactional controls and show read-only static text for Answer cells.
 * Comments column: keep the real textarea visible (disabled) so saved remarks and "Enter the Comments" placeholder
 * are not replaced by an em dash static block.
 * @returns cleanup to restore the row
 */
function applyCrfReportRowNonTransactionalViewLock(content, dtlNo) {
  if (!content?.querySelector || dtlNo == null || Number.isNaN(Number(dtlNo))) return () => {};
  const row = content.querySelector(`.crf-report-row[data-n-crf-dtl-no="${String(dtlNo)}"]`);
  if (!row || row.classList.contains('crf-report-row-label-only')) return () => {};

  // Clear any prior overlay on this row so re-lock from processContent cannot stack duplicate static fields.
  sweepVisitReportRowModalLockOverlay(row);

  // Only innermost region cells — nested .crf-answer-cell > .crf-answer-col would otherwise get two static blocks and duplicate text.
  const allRegions = Array.from(row.querySelectorAll('.crf-answer-cell, .crf-answer-col, .crf-comments-cell, .crf-comments-col'));
  const regions = allRegions.filter((cell) => !allRegions.some((other) => other !== cell && cell.contains(other)));

  regions.forEach((cell) => {
    const isCommentsRegion =
      cell.classList.contains('crf-comments-cell') || cell.classList.contains('crf-comments-col');
    if (isCommentsRegion) {
      const ctls = cell.querySelectorAll('input:not([type="hidden"]), select, textarea, button.btnCalc');
      if (!ctls.length) return;
      ctls.forEach((c) => {
        if ((c.tagName || '').toUpperCase() === 'BUTTON' && !c.classList.contains('btnCalc')) return;
        c.dataset.crfTransactionViewLock = '1';
        c.setAttribute('disabled', 'disabled');
        if (c.tagName === 'TEXTAREA' || (c.tagName === 'INPUT' && c.type === 'text')) {
          c.setAttribute('readonly', 'readonly');
          c.readOnly = true;
        }
      });
      return;
    }

    const controls = cell.querySelectorAll(
      'input:not([type="hidden"]), select, textarea, button.btnCalc'
    );
    if (!controls.length) return;

    const parts = [];
    controls.forEach((c) => {
      const piece = getCrfTransactionalControlDisplayText(c);
      if (piece) parts.push(piece);
    });
    // Preserve repeated tokens in StandardDateTime (e.g., day=15 and hour=15) in read-only view.
    const text = parts.join(' · ').replace(/\s+/g, ' ').trim() || '—';

    controls.forEach((c) => {
      if ((c.tagName || '').toUpperCase() === 'BUTTON' && !c.classList.contains('btnCalc')) return;
      c.dataset.crfTransactionViewLock = '1';
      c.disabled = true;
      if (c.tagName === 'TEXTAREA' || (c.tagName === 'INPUT' && c.type === 'text')) {
        c.readOnly = true;
      }
      c.style.display = 'none';
    });

    // Checkbox/radio HTML hides only <input>; option <label> text (Yes / No / Not Required) would still show — hide whole group.
    cell.querySelectorAll('.form-check-group').forEach((group) => {
      group.setAttribute('data-crf-transaction-view-hide-ro-group', '1');
      group.style.display = 'none';
    });

    const staticEl = document.createElement('div');
    staticEl.className = 'crf-view-only-static';
    staticEl.setAttribute('data-crf-transaction-view-static', '1');
    staticEl.textContent = text;
    cell.insertBefore(staticEl, cell.firstChild);
  });

  return () => {
    if (row.isConnected) sweepVisitReportRowModalLockOverlay(row);
  };
}

/** Remove visit-report static overlays (Answer/Comments) when leaving strict read-only mode. */
function clearCrfReportVisitReadOnlyStaticView(content) {
  if (!content?.querySelectorAll) return;
  content.querySelectorAll('[data-crf-transaction-view-static]').forEach((el) => el.remove());
  content.querySelectorAll('[data-crf-transaction-view-hide-ro-group="1"]').forEach((g) => {
    g.removeAttribute('data-crf-transaction-view-hide-ro-group');
    g.style.removeProperty('display');
  });
  content.querySelectorAll('[data-crf-transaction-view-lock="1"]').forEach((c) => {
    delete c.dataset.crfTransactionViewLock;
    c.disabled = false;
    c.readOnly = false;
    c.removeAttribute('readonly');
    c.style.display = '';
  });
}

/** Visit Report / viewer: show every detail row's Answer & Comments as static text only. */
function applyCrfReportAllRowsVisitReadOnlyStaticView(content) {
  if (!content?.querySelectorAll) return;
  const rows = content.querySelectorAll('.crf-report-row:not(.crf-report-row-label-only)[data-n-crf-dtl-no]');
  rows.forEach((row) => {
    const dtl = row.getAttribute('data-n-crf-dtl-no');
    const n = dtl != null && dtl !== '' ? Number(dtl) : NaN;
    if (Number.isNaN(n)) return;
    applyCrfReportRowNonTransactionalViewLock(content, n);
  });
}

/** True when GET /crf/page (or optimistic UI) marks this field as having an Open/Resolved issue. */
function savedFieldHasOpenOrResolvedActionItem(field) {
  if (!field) return false;
  const v = field.hasOpenOrResolvedActionItem ?? field.HasOpenOrResolvedActionItem;
  return v === true || v === 1 || v === '1' || String(v).toLowerCase() === 'true';
}

/**
 * Re-apply Issue / Create Issue icon colors from savedFields whenever the CRF HTML is already transformed.
 * transformToReportFormat skips subgroups with .crf-report-transformed, so without this pass highlights would
 * only appear after a full page reload.
 */
function applyActionItemRowIconHighlights(content, savedFields) {
  if (!content?.querySelectorAll) return;
  const ACTIVE_CLS = 'crf-create-issue-icon--has-active-issue';
  content.querySelectorAll('.crf-report-row:not(.crf-report-row-label-only)').forEach((row) => {
    const field = findSavedFieldForCrfReportRow(row, savedFields);
    const highlight = savedFieldHasOpenOrResolvedActionItem(field);
    row.querySelectorAll('.crf-row-actions [data-crf-action="actionitems"], .crf-row-actions [data-crf-action="createactionitem"]').forEach((btn) => {
      const isOutlineIcon = btn.classList.contains('btn-outline-primary') && btn.classList.contains('crf-action-btn');
      if (highlight) {
        btn.classList.add(ACTIVE_CLS);
        if (!isOutlineIcon) btn.style.color = '#c05621';
        else btn.style.removeProperty('color');
      } else {
        btn.classList.remove(ACTIVE_CLS);
        if (!isOutlineIcon) btn.style.color = '#208094';
        else btn.style.removeProperty('color');
      }
    });
  });
}

/**
 * CRA (First/Second Review only): disable Comments for rows whose DCF is Resolved or Closed so remarks
 * are not edited after the response path, while other fields follow IsEditable from the server.
 * Skipped in In Progress (e.g. after Send Back): CRA must edit the full form again including comments.
 * When no strict Open DCFs remain, the API sets IsEditable on all fields and setFieldsEnabled(true) would re-enable every comment box — this re-applies per-row hiding in review only.
 */
function applyCraResolvedDcfCommentHidingToContent(content, savedFields, isCraUser, readOnly, isViewOnly, isReviewStage) {
  if (!content) return;
  const clearMarkers = () => {
    content.querySelectorAll('.crf-comments-cell.crf-comments-dcf-resolved-cra, .crf-comments-col.crf-comments-dcf-resolved-cra').forEach((commentsCell) => {
      commentsCell.classList.remove('crf-comments-dcf-resolved-cra');
      commentsCell.querySelectorAll('.crf-comments-dcf-resolved-placeholder').forEach((p) => p.remove());
      const ta = commentsCell.querySelector('textarea, input');
      if (ta) ta.style.removeProperty('display');
    });
  };
  if (!isCraUser || readOnly || isViewOnly) {
    clearMarkers();
    return;
  }
  clearMarkers();
  if (!isReviewStage) return;
  const rows = content.querySelectorAll('.crf-report-row:not(.crf-report-row-label-only)');
  rows.forEach((row) => {
    const commentsCell = row.querySelector(CRF_COMMENT_REGION_SEL);
    if (!commentsCell) return;
    const field = findSavedFieldForCrfReportRow(row, savedFields);
    if (!field) return;
    const status = (field.vDCFStatus ?? field.VDCFStatus ?? '').toString().trim().toLowerCase();
    const hide = status === 'resolved' || status === 'closed';
    if (!hide) return;
    // Keep the comment box visible and disabled like other non-DCF rows (no em dash placeholder).
    commentsCell.classList.remove('crf-comments-dcf-resolved-cra');
    commentsCell.querySelectorAll('.crf-comments-dcf-resolved-placeholder').forEach((p) => p.remove());
    const ta = commentsCell.querySelector('textarea, input');
    if (ta) {
      ta.style.removeProperty('display');
      ta.setAttribute('disabled', 'disabled');
      ta.setAttribute('readonly', 'readonly');
      ta.style.removeProperty('background-color');
      ta.style.removeProperty('background-image');
      commentsCell.classList.remove('crf-comments-dcf-highlight');
    }
  });
}

/** Find `.crf-report-row` for a vFieldId (all generator prefixes: textbox_, combobox_, datetime_, time_, etc.). */
function findCrfReportRowForFieldId(content, vFieldId) {
  if (!content || vFieldId == null || vFieldId === '') return null;
  const vf = String(vFieldId).trim();
  if (!vf) return null;
  const byMed = content.querySelector(`.crf-report-row[data-medex-code="${vf}"]`);
  if (byMed) return byMed;
  const fromAnswerCell = content.querySelector(`.crf-answer-cell[data-field-id="${vf}"]`)?.closest('.crf-report-row');
  if (fromAnswerCell) return fromAnswerCell;
  const fromAnswerCol = content.querySelector(`.crf-answer-col [data-field-id="${vf}"]`)?.closest?.('.crf-report-row');
  if (fromAnswerCol) return fromAnswerCol;
  const idAnchors = [
    `textarea_${vf}`,
    `textbox_${vf}`,
    `combobox_${vf}`,
    `datetime_${vf}`,
    `time_${vf}`,
    `file_${vf}`,
    `formula_${vf}`,
    `checkbox_${vf}`,
    `reportfield_${vf}_answer`,
  ];
  for (const aid of idAnchors) {
    const el = content.querySelector(`[id="${aid}"]`);
    const row = el?.closest?.('.crf-report-row');
    if (row) return row;
  }
  const vfEsc = cssEscapeAttr(vf);
  const radioEl =
    content.querySelector(`input[type="radio"][name="radio_${vfEsc}"]`) ||
    content.querySelector(`input[type="radio"][name^="radio_${vfEsc}_"]`) ||
    content.querySelector(`input[type="radio"][name$="_${vfEsc}"]`);
  const radioRow = radioEl?.closest?.('.crf-report-row');
  if (radioRow) return radioRow;
  const stdEl = content.querySelector(`input.standarddate-text[id*="${CSS.escape(vf)}"], input.standarddatetime-text[id*="${CSS.escape(vf)}"]`);
  const stdRow = stdEl?.closest?.('.crf-report-row');
  if (stdRow) return stdRow;
  const byMedSts = content.querySelector(`${STS_AUTOCAPTURE_BLOCK_SEL}[data-medex-code="${vfEsc}"]`);
  if (byMedSts) return byMedSts;
  const byFieldSts = content.querySelector(`${STS_AUTOCAPTURE_BLOCK_SEL}[data-field-id="${vfEsc}"]`);
  if (byFieldSts) return byFieldSts;
  const stsEl =
    content.querySelector(`[data-sts-autocapture="true"][id="stsautocapture_${vfEsc}"]`)
    || content.querySelector(`[data-sts-autocapture="true"][name="stsautocapture_${vfEsc}"]`)
    || content.querySelector(`[id^="stsautocapture_"][id$="${vfEsc}"]`);
  const stsBlock = stsEl?.closest?.(STS_AUTOCAPTURE_BLOCK_SEL);
  if (stsBlock) return stsBlock;
  return null;
}

/**
 * Same as collectFormData fourth pass: dropdowns first, then hidden composite. Returns '' for placeholder-only.
 */
function computeStandardDateFieldValueForSave(content, el, formatStd, formatStdDt) {
  const baseId = el.id || '';
  let value = '';
  const isDateTime = el.classList.contains('standarddatetime-text');
  if (baseId) {
    const dateSel = content.querySelector(`#${CSS.escape(baseId + '_Date')}`);
    const monthSel = content.querySelector(`#${CSS.escape(baseId + '_Month')}`);
    const yearSel = content.querySelector(`#${CSS.escape(baseId + '_Year')}`);
    const dayText = (dateSel?.options[dateSel.selectedIndex]?.text ?? '').trim() || 'DD';
    const monthText = (monthSel?.options[monthSel.selectedIndex]?.text ?? '').trim() || 'MMM';
    const yearText = (yearSel?.options[yearSel.selectedIndex]?.text ?? '').trim() || 'YYYY';
    value = `${dayText}-${monthText}-${yearText}`;
    if (isDateTime) {
      const hourSel = content.querySelector(`#${CSS.escape(baseId + '_Hour')}`);
      const minuteSel = content.querySelector(`#${CSS.escape(baseId + '_Minute')}`);
      const hourText = (hourSel?.options[hourSel?.selectedIndex]?.text ?? '').trim() || 'HH';
      const minuteText = (minuteSel?.options[minuteSel?.selectedIndex]?.text ?? '').trim() || 'MM';
      value += ` ${hourText}:${minuteText}`;
    }
  }
  if (!value) {
    const rawValue = (el.value || '').trim();
    if (rawValue) {
      const alreadySavedFormat = isDateTime
        ? /^(.+)-(.+)-(.+)\s+(.+):(.+)$/.test(rawValue)
        : /^(.+)-(.+)-(.+)$/.test(rawValue);
      value = alreadySavedFormat ? rawValue : (isDateTime ? formatStdDt(rawValue) : formatStd(rawValue));
    }
  }
  if (isPlaceholderStandardDateValue(value)) return '';
  return value;
}

const REVIEWER_COMMENT_TOOLTIP = "Reviewer's Comment";

/** Hover/aria label for DCF icons; also rewrites SVG <title> which wins over the HTML title attribute. */
function setReviewerCommentTooltip(el) {
  if (!el) return;
  el.setAttribute('title', REVIEWER_COMMENT_TOOLTIP);
  el.setAttribute('aria-label', REVIEWER_COMMENT_TOOLTIP);
  const svgTitle =
    el.tagName === 'svg' || el.tagName === 'SVG'
      ? el.querySelector('title')
      : el.querySelector('svg title, title');
  if (svgTitle) svgTitle.textContent = REVIEWER_COMMENT_TOOLTIP;
}

// Inline SVG for CRF row action buttons – use currentColor so CSS can set system primary
const crfActionIconSvg = (name) => {
  const size = 18;
  const stroke = 'currentColor';
  const icons = {
    // Action Items: checklist (check-square) – system primary
    actionitem: `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" stroke="${stroke}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 11 12 14 22 4"></polyline><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"></path></svg>`,
    // Create Action Item: clipboard with plus (single color – system primary)
    plus: `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" stroke="${stroke}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/><rect x="8" y="2" width="8" height="4" rx="1" ry="1"/><line x1="15" y1="17" x2="19" y2="17"/><line x1="17" y1="15" x2="17" y2="19"/></svg>`,
  };
  return icons[name] || '';
};

const CRFForm = (props = {}) => {
  const { user } = useContext(AuthContext);
  const { raise: dcfRaise, resolve: dcfResolve, close: dcfClose, submitAllToCra: dcfSubmitAllToCra, history: dcfHistoryApi } = useDcf();
  const { nRecordedBy: sessionRecordedByMappingId } = useSession();
  const { currentProject, projectInfo } = useProject();
  const auditProjectCode = currentProject && currentProject !== 'all' ? currentProject : undefined;
  const auditProjectId = projectInfo?.projectId ?? projectInfo?.ProjectId;
  const params = useParams();
  const [searchParams] = useSearchParams();
  const templateId = props.templateId ?? params.templateId ?? '';
  const visitId = props.visitId != null ? String(props.visitId) : (params.visitId ?? '');
  const isPreviewInNewTab = searchParams.get('preview') === '1' || (typeof window !== 'undefined' && window.opener && !visitId);
  const visitType = props.visitType ?? params.visitType ?? 'SSV';
  const reportNameFromQuery = (searchParams.get('reportName') || '').trim();
  const reportTitle = reportNameFromQuery || reportTitleByVisitType[visitType] || 'Visit Report';
  const onClose = props.onClose;
  const onDraftSaved = props.onDraftSaved; // (visitId, nCRFHdrNo) => parent can show "Continue" instead of "Start Report"
  const onSubmitSuccess = props.onSubmitSuccess; // optional: parent shows toast after modal closes on Submit
  const onApproveSendbackSuccess = props.onApproveSendbackSuccess; // optional: parent shows "Saved Successfully" when Approve/Send Back succeeds (so toast persists after modal closes)
  const onDcfSaveSuccess = props.onDcfSaveSuccess; // optional: parent toast when DCF resolve/response saves (do not use props.onClose for those — keeps Visit Report open)
  const embedInModal = props.embedInModal === true;
  /** When Visit Report opens from a review lane (e.g. First Review tab), aligns reviewer DCF modal with that lane even if CRFHdr.VStage is still In Progress during CRA handoff. */
  const visitUiStageLabel = (props.visitUiStageLabel ?? '').toString().trim();
  /** GET /crf/page/{hdr}: pass viewingStage when embedded from a review tab so server IsEditable / DCF rows match the user's lane. */
  const buildCrfPageUrl = React.useCallback(
    (hdrNo) => {
      const base = `/crf/page/${hdrNo}`;
      if (!embedInModal || !visitUiStageLabel) return base;
      return `${base}?${new URLSearchParams({ viewingStage: visitUiStageLabel }).toString()}`;
    },
    [embedInModal, visitUiStageLabel]
  );
  const stageVisitsForActionItem = props.stageVisits ?? [];
  const defaultProjectForActionItem = props.defaultProject ?? null;
  const defaultSiteIdForActionItem = props.defaultSiteId ?? '';
  const defaultSiteNameForActionItem = props.defaultSiteName ?? '';
  const onActionItemCreated = props.onActionItemCreated; // optional callback when Create Action Item is saved
  const [resolvedTemplateId, setResolvedTemplateId] = useState(null); // when parent opens without templateId (e.g. First Review), we resolve it from visit
  const [htmlContent, setHtmlContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [nCRFHdrNo, setNCRFHdrNo] = useState(null);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    protocolNo: '',
    siteNumber: '',
    piName: '',
    actualStartDate: '',
    actualEndDate: '',
    visitMode: '',
    sponsor: ''
  });
  const [savedFields, setSavedFields] = useState([]);
  /** Server CRFUploads:MaxFileSizeBytes from GET /crf/page*; used for client pre-check and 413 messaging. */
  const [crfMaxUploadFileSizeBytes, setCrfMaxUploadFileSizeBytes] = useState(DEFAULT_CRF_MAX_UPLOAD_BYTES);
  const [crfStage, setCrfStage] = useState(null);
  /** Numeric StageMst.iStageCode from GET /crf/page (optional); Associated branch stage gate. */
  const [crfCurrentStageCode, setCrfCurrentStageCode] = useState(null);
  /** null = unknown until API; true/false from server (any count of review stages per study). */
  const [pageIsReviewStage, setPageIsReviewStage] = useState(null);
  /** undefined = not yet read from GET /crf/page; authoritative workflow In Progress (display-aware). */
  const [pageHeaderStageInProgress, setPageHeaderStageInProgress] = useState(undefined);
  const [hasOpenDCF, setHasOpenDCF] = useState(false);
  /** True when at least one Open DCF is released to CRA (iSubmitDCFToCRA === 1). */
  const [hasOpenDcfReleasedToCra, setHasOpenDcfReleasedToCra] = useState(false);
  /** True when reviewer has raised Open DCFs not yet batch-released to CRA. */
  const [hasUnreleasedOpenDcf, setHasUnreleasedOpenDcf] = useState(false);
  /** UI-only: hide reviewer Submit immediately after batch release (no reopen required). */
  const [reviewerSubmitHiddenAfterBatchRelease, setReviewerSubmitHiddenAfterBatchRelease] = useState(false);
  const [submitAllDcfSubmitting, setSubmitAllDcfSubmitting] = useState(false);
  /** Open or Resolved on any row — same gate as server HasActiveDCFAsync for Approve/Reject. */
  const hasActiveDCF = useMemo(() => {
    if (!Array.isArray(savedFields) || savedFields.length === 0) return false;
    return savedFields.some((f) => {
      const s = String(f?.vDCFStatus ?? f?.VDCFStatus ?? '').trim().toLowerCase();
      return s === 'open' || s === 'resolved';
    });
  }, [savedFields]);
  const [availableActions, setAvailableActions] = useState([]);
  const [transitioning, setTransitioning] = useState(false);
  const [reviewAssociatedRequiresSaveFlow, setReviewAssociatedRequiresSaveFlow] = useState(false);
  /** Aligns with CRFHdr.CSaveType; E = review Submit completed at current tier (fields locked until Approve/Reject). */
  const [crfCSaveType, setCrfCSaveType] = useState('D');
  const [dcfModalOpen, setDcfModalOpen] = useState(false);
  const [dcfSelected, setDcfSelected] = useState({ nCRFDtlNo: null, fieldId: '', questionText: '' });
  const [auditModalOpen, setAuditModalOpen] = useState(false);
  const [auditSelected, setAuditSelected] = useState({
    fieldId: '',
    questionText: '',
    nCRFDtlNo: null,
    nCRFActionItemNo: null,
    /** When viewing CRFActionItem audit from Issue Review: which auditable column to show (lowercase EF name). */
    actionItemAuditFieldName: null,
  });
  const [auditTrailList, setAuditTrailList] = useState([]);
  const [auditTrailLoading, setAuditTrailLoading] = useState(false);
  const [auditTrailError, setAuditTrailError] = useState(null);
  const [actionItemsModalOpen, setActionItemsModalOpen] = useState(false);
  const [actionItemsSelected, setActionItemsSelected] = useState({ nCRFDtlNo: null, fieldId: '', questionText: '' });
  const [actionItemsList, setActionItemsList] = useState([]);
  const [actionItemsLoading, setActionItemsLoading] = useState(false);
  const [actionItemClosingId, setActionItemClosingId] = useState(null);
  const [isCreateActionItemModalOpen, setIsCreateActionItemModalOpen] = useState(false);
  const [createActionItemFieldCode, setCreateActionItemFieldCode] = useState(null); // when opened from row, pre-select this section attribute
  const [createActionItemNCRFDtlNo, setCreateActionItemNCRFDtlNo] = useState(null); // when opened from row, used to resolve vFieldId for section-attribute matching
  const [selectedActionItemIndex, setSelectedActionItemIndex] = useState(0);
  const [actionDescriptionEdit, setActionDescriptionEdit] = useState('');
  const [actionReplyRemark, setActionReplyRemark] = useState('');
  /** Audit-derived thread rows (user + datetime meta); matches VisitManagement Issue Review / Reply modal. */
  const [actionReplyHistoryEntries, setActionReplyHistoryEntries] = useState([]);
  const [actionReplyDraft, setActionReplyDraft] = useState('');
  /** Issue Review reopen/close workflow: user intent via Status (Open = reopen path, Close = close path). Not API vStatus. */
  const [actionItemReopenIntent, setActionItemReopenIntent] = useState('Closed');
  /** yyyy-MM-dd when closing an issue (Issue Review); shown only when Status intent is Close and user can close. */
  const [actionItemClosingDate, setActionItemClosingDate] = useState('');
  const actionItemReplyRemarkOriginalRef = useRef('');
  const [statusEdit, setStatusEdit] = useState('Closed');
  const [actionItemSaving, setActionItemSaving] = useState(false);
  const [dcfRemark, setDcfRemark] = useState('');
  const [dcfRemarkError, setDcfRemarkError] = useState('');
  const [dcfResponse, setDcfResponse] = useState(''); // CRA response (vDCF Response) when not editing the field
  const [dcfSubmitting, setDcfSubmitting] = useState(false);
  const [dcfHistory, setDcfHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [showESignModal, setShowESignModal] = useState(false);
  const [eSignPendingAction, setESignPendingAction] = useState(null);
  const [toast, setToast] = useState(null);
  const eSignRemarksRef = useRef('');
  const actionItemCloseRef = useRef(null);
  const actionItemReopenRef = useRef(null);
  const actionItemUpdateRef = useRef(null);
  const [isViewOnly, setIsViewOnly] = useState(false);
  const [canApproveReject, setCanApproveReject] = useState(false);
  const [canRaiseDCFFromServer, setCanRaiseDCFFromServer] = useState(false);
  const [canRaiseActionItemFromServer, setCanRaiseActionItemFromServer] = useState(false);
  const [sessionIsCraFamily, setSessionIsCraFamily] = useState(false);
  const [sessionStudyDesign, setSessionStudyDesign] = useState('');
  const [visitTypeStudyDesignByCode, setVisitTypeStudyDesignByCode] = useState({});
  // Increment after DCF refetch so useLayoutEffect re-applies field enable/disable without page refresh
  const [permissionsRefreshKey, setPermissionsRefreshKey] = useState(0);
  // CRF Report Preview: choice modal (With/Without) then display modal
  const [showPreviewChoiceModal, setShowPreviewChoiceModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  /** When true, preview/PDF omits workflow signer lines (Submit / First / Second Review) — blank template only. */
  const [previewOmitWorkflowSignerFields, setPreviewOmitWorkflowSignerFields] = useState(false);
  const [includeOpenIssuesInPreview, setIncludeOpenIssuesInPreview] = useState(false);
  /** When true (filled preview only), show uploaded files after the report and merge PDF attachments into the downloaded PDF. */
  const [includeAttachmentsInPreview, setIncludeAttachmentsInPreview] = useState(false);
  /** Choice modal: include CRF file uploads in preview/PDF ("with") or not ("without"). */
  const [previewAttachmentsChoice, setPreviewAttachmentsChoice] = useState('without');
  const [previewAttachmentAppendixItems, setPreviewAttachmentAppendixItems] = useState([]);
  const [previewAttachmentsLoading, setPreviewAttachmentsLoading] = useState(false);
  const [previewAttachmentsFetchError, setPreviewAttachmentsFetchError] = useState('');
  const previewAttachmentObjectUrlsRef = useRef([]);
  const [openIssuesForReport, setOpenIssuesForReport] = useState([]);
  const [openIssuesLoading, setOpenIssuesLoading] = useState(false);
  /** Site id for visit-report appendix (from visit API or props.defaultSiteId). */
  const [resolvedSiteIdForAppendix, setResolvedSiteIdForAppendix] = useState(null);
  const previewContentRef = useRef(null);
  const previewLayoutRestoreRef = useRef(null);
  const [pdfDownloading, setPdfDownloading] = useState(false);
  const [pdfProgressPercent, setPdfProgressPercent] = useState(0);
  const [pdfProgressLabel, setPdfProgressLabel] = useState('');
  const [requiredFieldCodes, setRequiredFieldCodes] = useState([]);
  const [requiredFieldErrors, setRequiredFieldErrors] = useState([]);
  const [workflowHeader, setWorkflowHeader] = useState(null);
  /** Field codes hidden per Associated rules from GET /crf/page (persisted driver + stage); applied after client branch visibility. */
  const [associatedInactiveChildFieldCodes, setAssociatedInactiveChildFieldCodes] = useState([]);
  /** Latest inactive Associated children from API — refs avoid stale closures when stage fetch resolves out of order. */
  const associatedInactiveChildFieldCodesRef = useRef([]);
  associatedInactiveChildFieldCodesRef.current = associatedInactiveChildFieldCodes;
  /** Latest StageMst list for Associated branch stage gates (kept in sync with association useEffect + page save). */
  const crfAssociationStageListRef = useRef([]);
  /** Parsed htmlContent for Associated lookups when .crf-generated-content is not in the live DOM yet. */
  const associationLookupParsedContentRef = useRef(null);
  /** First Review branch child codes from live Associated config (visit report transform). */
  const crfPmFirstReviewBranchChildCodesRef = useRef([]);
  /** When parent does not pass reviewerStageLabels (or passes []), resolve from project StageMst via protocolNo. */
  const [localReviewerTierLabels, setLocalReviewerTierLabels] = useState([]);
  const [exportedOnDisplay, setExportedOnDisplay] = useState(() => formatDateTimeDDMMMYYYY(new Date()));

  const effectiveTemplateId = templateId || resolvedTemplateId || '';
  const displayedWorkflowDateTime = React.useMemo(() => {
    const dt = workflowHeader?.dateTimeUTC || workflowHeader?.DateTimeUTC;
    const offset = workflowHeader?.dateTimeOffset || workflowHeader?.DateTimeOffset;
    if (!dt) return '';
    return formatPerformedOnUtcToLocal(dt, offset) || '';
  }, [workflowHeader]);

  useEffect(() => {
    const prop = props.reviewerStageLabels;
    if (Array.isArray(prop) && prop.length > 0) {
      setLocalReviewerTierLabels([]);
      return undefined;
    }
    const pc = (formData.protocolNo || '').trim();
    if (!pc) {
      setLocalReviewerTierLabels([]);
      return undefined;
    }
    let cancelled = false;
    stageService.getAllStagesForProject(pc)
      .then((list) => {
        if (cancelled || !Array.isArray(list)) return;
        const tiers = selectReviewerTierStages(list);
        setLocalReviewerTierLabels(tiers.map((t) => ({ label: t.label, stageCode: t.stageCode })));
      })
      .catch(() => {
        if (!cancelled) setLocalReviewerTierLabels([]);
      });
    return () => { cancelled = true; };
  }, [props.reviewerStageLabels, formData.protocolNo]);

  const effectiveReviewerTierLabels = React.useMemo(() => {
    const prop = props.reviewerStageLabels;
    if (Array.isArray(prop) && prop.length > 0) return prop;
    return localReviewerTierLabels;
  }, [props.reviewerStageLabels, localReviewerTierLabels]);

  const headerReviewerCols = React.useMemo(
    () => buildCrfHeaderReviewerColumns(workflowHeader, effectiveReviewerTierLabels),
    [workflowHeader, effectiveReviewerTierLabels],
  );

  // Current user profile and CRA flag (used for DCF modal and field editability; must be defined before any return)
  const currentUserProfile = (
    user?.profileCode ??
    user?.ProfileCode ??
    user?.vUserTypeCode ??
    user?.role ??
    user?.Role ??
    ''
  )
    .toString()
    .trim();
  const currentUserProfileAliases = React.useMemo(() => {
    const vals = [
      user?.profileCode,
      user?.ProfileCode,
      user?.vUserTypeCode,
      user?.VUserTypeCode,
      user?.role,
      user?.Role,
      currentUserProfile,
    ];
    return new Set(
      vals
        .map((v) => (v ?? '').toString().trim().toLowerCase())
        .filter(Boolean)
    );
  }, [user, currentUserProfile]);
  /** Open or Resolved rows raised by this user — treat as not closed until status is Closed (Submit hidden / blocked for that profile). */
  const isNonClosedDcfOwnedByCurrentProfile = React.useCallback((f) => {
    const status = (f?.vDCFStatus ?? f?.VDCFStatus ?? '').toString().trim().toLowerCase();
    if (status !== 'open' && status !== 'resolved') return false;
    const raisedByProfile = (f?.vDCFRaisedByProfileCode ?? f?.VDCFRaisedByProfileCode ?? '').toString().trim().toLowerCase();
    if (!raisedByProfile || currentUserProfileAliases.size === 0) return false;
    return currentUserProfileAliases.has(raisedByProfile);
  }, [currentUserProfileAliases]);
  const submitBlockedByOwnOpenDcf = React.useMemo(
    () => (savedFields || []).some((f) => isNonClosedDcfOwnedByCurrentProfile(f)),
    [savedFields, isNonClosedDcfOwnedByCurrentProfile]
  );
  const isCraUser = React.useMemo(() => {
    const code = (currentUserProfile || '').toString().trim().toUpperCase();
    const craFamily =
      sessionIsCraFamily
      || code === 'CRA'
      || code === 'UCRA';
    if (!craFamily) return false;
    const sessionLane = normalizeStudyDesignLane(sessionStudyDesign);
    const visitCode = (visitType || '').toString().trim().toUpperCase();
    const visitDesignRaw =
      visitTypeStudyDesignByCode[visitCode]
      || visitTypeStudyDesignByCode[visitType]
      || '';
    const visitLane = normalizeStudyDesignLane(visitDesignRaw);
    if (!sessionLane || !visitLane) return true;
    return sessionLane === visitLane;
  }, [currentUserProfile, sessionIsCraFamily, sessionStudyDesign, visitType, visitTypeStudyDesignByCode]);

  // Fetch profile permissions from server (IsViewOnly, iApproveSendback, CanRaiseDCF = reviewer-only, CanRaiseActionItem = create issue)
  useEffect(() => {
    let cancelled = false;
    api.get('/users/me/profile-permissions')
      .then((res) => {
        if (!cancelled && res.data) {
          if (res.data.isViewOnly === 1 || res.data.IsViewOnly === 1) setIsViewOnly(true);
          if (res.data.iApproveSendback === 1 || res.data.iApproveSendback === '1') setCanApproveReject(true);
          if (res.data.canRaiseDCF === 1 || res.data.CanRaiseDCF === 1) setCanRaiseDCFFromServer(true);
          if (res.data.canRaiseActionItem === 1 || res.data.CanRaiseActionItem === 1) setCanRaiseActionItemFromServer(true);
          const family = res.data.isCraFamily === true || res.data.IsCraFamily === true;
          const base = (res.data.baseProfileCode ?? res.data.BaseProfileCode ?? '').toString().trim().toUpperCase();
          const prof = (res.data.profileCode ?? res.data.ProfileCode ?? '').toString().trim().toUpperCase();
          setSessionIsCraFamily(family || prof === 'CRA' || base === 'CRA');
          setSessionStudyDesign((res.data.studyDesignDesc ?? res.data.StudyDesignDesc ?? '').toString());
        }
      })
      .catch(() => { });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    fetchVisitTypes()
      .then((res) => {
        if (cancelled) return;
        const rows = Array.isArray(res.data) ? res.data : [];
        const map = {};
        rows.forEach((v) => {
          const code = (v.visitCode || v.VisitCode || '').toString().trim().toUpperCase();
          if (!code) return;
          map[code] = (v.studyDesignDesc || v.StudyDesignDesc || '').toString();
        });
        setVisitTypeStudyDesignByCode(map);
      })
      .catch(() => {
        if (!cancelled) setVisitTypeStudyDesignByCode({});
      });
    return () => { cancelled = true; };
  }, []);

  const fieldIdToDtlNo = React.useMemo(() => {
    const map = {};
    const toShortCode = (s) => {
      if (!s || s.length <= 5) return s;
      const parts = s.split('_');
      const five = parts.find((p) => p.length === 5 && /^[A-Za-z0-9]+$/.test(p));
      if (five) return five;
      const last = s.replace(/_answer$/i, '').replace(/_comments$/i, '').split('_').pop();
      return last && last.length <= 5 && /^[A-Za-z0-9]+$/.test(last) ? last : s.substring(0, 5);
    };
    (savedFields || []).forEach((f) => {
      const id = (f.vFieldId || f.VFieldId || '').trim();
      const no = f.nCRFDtlNo ?? f.NCRFDtlNo ?? f.ncrfDtlNo;
      if (id && no != null) {
        map[id] = Number(no);
        const short = toShortCode(id);
        if (short && short !== id) map[short] = Number(no);
      }
    });
    return map;
  }, [savedFields]);

  // Memoize so React does not re-apply innerHTML on every re-render (e.g. when opening DCF/Audit modals).
  // Re-applying would wipe out user-entered answers and comments.
  const dangerousContent = React.useMemo(() => ({ __html: htmlContent }), [htmlContent]);

  // Container ref so we transform THIS instance's content only (fixes modal CRF when multiple .crf-generated-content exist)
  const crfContainerRef = useRef(null);
  // Only try once to auto-create detail rows when reviewer opens CRF with no saved details
  const hydratedForReviewRef = useRef(false);
  const prevCrfVisitReportRoRef = useRef(false);

  // Ref for DCF/Audit/ActionItems/CreateActionItem so transform can attach direct click handlers (works when CRF is inside nested modal)
  const crfActionHandlersRef = useRef({
    openDcf: () => { },
    openAudit: () => { },
    openActionItems: () => { },
    openCreateActionItem: () => { }
  });
  // Synchronous payload when opening Create Action Item so modal can read fieldCode/nCRFDtlNo even if state hasn't updated yet
  const createActionItemPayloadRef = useRef(null);
  const isReviewerActorForDcf =
    !isCraUser && (canApproveReject || canRaiseDCFFromServer);

  const resolveAssociationLookupContent = React.useCallback(() => {
    const live = crfContainerRef.current?.querySelector('.crf-generated-content');
    return resolveCrfAssociationLookupContent(live, htmlContent, associationLookupParsedContentRef);
  }, [htmlContent]);

  React.useEffect(() => {
    associationLookupParsedContentRef.current = null;
  }, [htmlContent]);

  const getDcfRaiseBlockMessageForFieldId = (fieldId, targetRow, nCRFDtlNo, forExistingDcfModalOpen = false) => {
    if (forExistingDcfModalOpen && fieldHasActiveDcfThread(savedFields, fieldId, nCRFDtlNo)) {
      return null;
    }
    const content = resolveAssociationLookupContent();
    const stageList = crfAssociationStageListRef.current;
    let cachedPmBranchCodes = crfPmFirstReviewBranchChildCodesRef.current;
    if (content) {
      cachedPmBranchCodes = Array.from(
        getAssociatedBranchChildCodesForStageGate(content, stageList, isPmFirstReviewStageGate)
      );
      crfPmFirstReviewBranchChildCodesRef.current = cachedPmBranchCodes;
    }
    let row = targetRow || null;
    if (!row && content && fieldId) {
      row = findCrfFieldSection(content, fieldId);
    }
    return getDcfRaiseBlockMessageForField(
      content,
      fieldId,
      savedFields,
      stageList,
      crfStage,
      crfCurrentStageCode,
      currentUserProfile,
      visitUiStageLabel,
      isReviewStage,
      isReviewerActorForDcf,
      currentUserProfileAliases,
      cachedPmBranchCodes,
      hasUnreleasedOpenDcf,
      row
    );
  };

  crfActionHandlersRef.current = {
    getDcfRaiseBlockMessage: (fieldId, targetRow, nCRFDtlNo, forExistingDcfModalOpen) =>
      getDcfRaiseBlockMessageForFieldId(fieldId ?? '', targetRow, nCRFDtlNo, forExistingDcfModalOpen),
    showDcfRaiseBlockToast: (message) => {
      if (message) setToast({ type: 'error', message });
    },
    openDcf: (payload) => {
      const fieldId = payload?.fieldId ?? '';
      const dcfRaiseBlock = getDcfRaiseBlockMessageForFieldId(
        fieldId,
        payload?.targetRow ?? null,
        payload?.nCRFDtlNo ?? null,
        true
      );
      if (dcfRaiseBlock) {
        setToast({ type: 'error', message: dcfRaiseBlock });
        return;
      }
      setDcfSelected({ nCRFDtlNo: payload?.nCRFDtlNo ?? null, fieldId, questionText: payload?.questionText ?? '' });
      setDcfModalOpen(true);
    },
    openAudit: (payload) => {
      setAuditSelected({
        fieldId: payload?.fieldId ?? '',
        questionText: payload?.questionText ?? '',
        nCRFDtlNo: payload?.nCRFDtlNo ?? null,
        nCRFActionItemNo: payload?.nCRFActionItemNo ?? null,
        actionItemAuditFieldName: payload?.actionItemAuditFieldName ?? null,
      });
      setAuditModalOpen(true);
    },
    openActionItems: (payload) => {
      setActionItemsSelected({ nCRFDtlNo: payload?.nCRFDtlNo ?? null, fieldId: payload?.fieldId ?? '', questionText: payload?.questionText ?? '' });
      setActionItemsModalOpen(true);
    },
    openCreateActionItem: (payload) => {
      if (props.readOnly || isViewOnly) return;
      if (!canRaiseActionItemFromServer) {
        setToast({ type: 'error', message: actionItemMessages.cannotCreateIssueNoPermission });
        return;
      }
      if (!nCRFHdrNo) {
        setToast({ type: 'error', message: actionItemMessages.pleaseDraftSaveFirst });
        return;
      }
      createActionItemPayloadRef.current = payload ? { fieldCode: payload.fieldCode ?? null, nCRFDtlNo: payload.nCRFDtlNo ?? null } : null;
      setCreateActionItemFieldCode(payload?.fieldCode ?? null);
      setCreateActionItemNCRFDtlNo(payload?.nCRFDtlNo ?? null);
      setIsCreateActionItemModalOpen(true);
    }
  };

  const applyCrfPageDcfFlags = React.useCallback((payload) => {
    if (!payload) return;
    setHasOpenDCF(payload.hasOpenDCF ?? payload.HasOpenDCF ?? false);
    setHasOpenDcfReleasedToCra(payload.hasOpenDcfReleasedToCra ?? payload.HasOpenDcfReleasedToCra ?? false);
    setHasUnreleasedOpenDcf(payload.hasUnreleasedOpenDcf ?? payload.HasUnreleasedOpenDcf ?? false);
    const hip = readPageHeaderStageInProgress(payload);
    if (hip !== undefined) setPageHeaderStageInProgress(hip);
  }, []);

  const applyCrfPageSaveType = React.useCallback((payload) => {
    if (!payload || typeof payload !== 'object') return;
    const has =
      Object.prototype.hasOwnProperty.call(payload, 'cSaveType')
      || Object.prototype.hasOwnProperty.call(payload, 'CSaveType');
    if (!has) return;
    const v = readCSaveTypeFromPage(payload);
    setCrfCSaveType(v != null && v !== '' ? v : 'D');
  }, []);

  const applyCrfPageAssociatedInactiveChildren = React.useCallback((payload) => {
    if (!payload || typeof payload !== 'object') return;
    const raw =
      payload.associatedInactiveChildFieldCodes ??
      payload.AssociatedInactiveChildFieldCodes ??
      undefined;
    if (raw === undefined) return;
    setAssociatedInactiveChildFieldCodes(
      Array.isArray(raw) ? raw.map((c) => String(c || '').trim()).filter(Boolean) : []
    );
  }, []);

  const applyCrfPageUploadLimit = React.useCallback((payload) => {
    if (!payload || typeof payload !== 'object') return;
    const raw =
      payload.maxCrfUploadFileSizeBytes ??
      payload.MaxCrfUploadFileSizeBytes;
    if (raw == null || raw === '') return;
    const n = Number(raw);
    if (Number.isFinite(n) && n > 0) setCrfMaxUploadFileSizeBytes(n);
  }, []);

  /** Refetch CRF page fields so per-row issue (action item) flags stay in sync with Create Issue icon highlight. */
  const refreshCrfPageFieldsForActionItems = React.useCallback(() => {
    if (!nCRFHdrNo) return;
    api.get(buildCrfPageUrl(nCRFHdrNo))
      .then((pageRes) => {
        const fields = pageRes.data?.fields ?? pageRes.data?.Fields ?? [];
        if (Array.isArray(fields)) setSavedFields(fields);
        applyCrfPageDcfFlags(pageRes.data);
        applyCrfPageSaveType(pageRes.data);
        applyCrfPageAssociatedInactiveChildren(pageRes.data);
        applyCrfPageUploadLimit(pageRes.data);
        setWorkflowHeader(pageRes.data?.workflowHeader ?? pageRes.data?.WorkflowHeader ?? null);
        const irs = readPageIsReviewStage(pageRes.data);
        if (irs !== null) setPageIsReviewStage(irs);
        const cscUp = pickCurrentStageCodeUpdate(pageRes.data);
        if (cscUp !== undefined) setCrfCurrentStageCode(cscUp);
        setPermissionsRefreshKey((k) => k + 1);
      })
      .catch(() => { });
  }, [nCRFHdrNo, buildCrfPageUrl, applyCrfPageDcfFlags, applyCrfPageSaveType, applyCrfPageAssociatedInactiveChildren, applyCrfPageUploadLimit]);

  useEffect(() => {
    // Define toggleSubGroup function early so it's available when HTML is injected.
    // Use the clicked header's next sibling as the panel to toggle so each group only
    // toggles its own section (avoids duplicate IDs when same subGroup code exists in multiple groups).
    window.toggleSubGroup = function (e, subGroupCode) {
      const clickedElement = e && (e.target && e.target.closest ? e.target.closest('.subGroupDiv') : e) || e;

      if (!clickedElement) {
        console.warn('toggleSubGroup: clicked element not found');
        return;
      }

      // Panel to collapse is always the next sibling of the header (avoids wrong panel when IDs duplicate)
      let subGroupElement = clickedElement.nextElementSibling;
      if (!subGroupElement || !subGroupElement.classList.contains('templateSubGroup')) {
        subGroupElement = document.getElementById(subGroupCode);
      }
      if (!subGroupElement) {
        console.warn(`toggleSubGroup: SubGroup element not found: ${subGroupCode}`);
        return;
      }

      // Check current state - if it has 'show' class or is visible (display not 'none'), collapse it
      const isCurrentlyExpanded = subGroupElement.classList.contains('show') ||
        subGroupElement.style.display !== 'none';

      if (isCurrentlyExpanded) {
        // Collapse (do not use class name "collapse" — Tailwind defines .collapse and breaks injected CRF HTML)
        subGroupElement.classList.add(CRF_TEMPLATE_SUBGROUP_COLLAPSED_CLASS);
        subGroupElement.classList.remove('show');
        subGroupElement.classList.remove('collapse');
        subGroupElement.style.display = 'none';
      } else {
        // Expand
        subGroupElement.classList.remove('collapse', CRF_TEMPLATE_SUBGROUP_COLLAPSED_CLASS);
        subGroupElement.classList.add('show');
        subGroupElement.style.display = 'block';
      }

      clickedElement.setAttribute('aria-expanded', String(!isCurrentlyExpanded));

      // Update toggle icon/arrow if it exists
      const toggleIcon = clickedElement.querySelector('.collapseToggle, .fa, [class*="chevron"], i');
      if (toggleIcon) {
        if (isCurrentlyExpanded) {
          toggleIcon.classList.remove('fa-chevron-up');
          toggleIcon.classList.add('fa-chevron-down');
        } else {
          toggleIcon.classList.remove('fa-chevron-down');
          toggleIcon.classList.add('fa-chevron-up');
        }
      }
    };

    // Backend-generated CRF HTML may call CalculateForTextbox on input blur; define a no-op if missing
    if (typeof window.CalculateForTextbox !== 'function') {
      window.CalculateForTextbox = function () {
        // No-op: prevents ReferenceError when injected HTML has onblur="CalculateForTextbox(...)"
      };
    }

    // Backend-generated CRF HTML may reference ddlAlerton (e.g. onchange); define if missing
    if (window.ddlAlerton === undefined) {
      window.ddlAlerton = function () {
        // No-op: prevents ReferenceError when injected HTML has onchange="ddlAlerton(...)"
      };
    }

    // Ramp: CRF Time control onblur calls AutoTimeConvert(this, this.value). Must be defined for Time/AsyncTime fields.
    if (typeof window.msgalert !== 'function') {
      window.msgalert = function (msg) {
        if (msg) window.alert(msg);
      };
    }
    if (typeof window.TimeConvert !== 'function') {
      window.TimeConvert = function (paramTime, txtElement) {
        if (!paramTime || typeof paramTime !== 'string') return true;
        const s = paramTime.trim();
        if (s.length === 0 || s.toUpperCase() === 'UK:UK') return true;
        const match = s.match(/^(\d{1,2}):?(\d{2})?\s*(AM|PM)?$/i);
        if (!match) return false;
        let h = parseInt(match[1], 10);
        const m = match[2] ? parseInt(match[2], 10) : 0;
        if (match[3]) {
          if (/^PM$/i.test(match[3]) && h < 12) h += 12;
          if (/^AM$/i.test(match[3]) && h === 12) h = 0;
        }
        if (h < 0 || h > 23 || m < 0 || m > 59) return false;
        if (txtElement && txtElement.value !== undefined) {
          txtElement.value = String(h).padStart(2, '0') + ':' + String(m).padStart(2, '0');
        }
        return true;
      };
    }
    if (typeof window.AutoTimeConvert !== 'function') {
      window.AutoTimeConvert = function (txtTime, paramTime) {
        if (paramTime && typeof paramTime === 'string' && paramTime.trim().length > 0) {
          if (paramTime.toUpperCase() === 'UK:UK') return;
          if (!/^[0-9:]*$/.test(paramTime)) {
            window.msgalert('Please enter time in correct format as HH:MM');
            if (txtTime && txtTime.value !== undefined) txtTime.value = '';
            return;
          }
        }
        const result = window.TimeConvert(paramTime, txtTime);
        if (result === false && txtTime && txtTime.value !== undefined) txtTime.value = '';
      };
    }

    // Other Ramp CRF globals referenced by generated HTML (no-ops to avoid ReferenceError)
    if (typeof window.ValidateTextbox !== 'function') {
      window.ValidateTextbox = function () { };
    }
    if (typeof window.Calculate !== 'function') {
      window.Calculate = function () { };
    }
    if (typeof window.LengthValidation !== 'function') {
      window.LengthValidation = function () { return true; };
    }
    if (typeof window.RemoveSelection !== 'function') {
      window.RemoveSelection = function (rblId) {
        const el = document.getElementById(rblId);
        if (el && el.type === 'radio') el.checked = false;
      };
    }
    if (typeof window.CheckStandardDate !== 'function') {
      window.CheckStandardDate = function (selectEl, idDate, idMonth, idYear) {
        if (!selectEl || !idDate || !idMonth || !idYear) return;
        const baseId = selectEl.id.replace(/_Date$|_Month$|_Year$/, '');
        const textInput = document.getElementById(baseId);
        const dateSel = document.getElementById(idDate);
        const monthSel = document.getElementById(idMonth);
        const yearSel = document.getElementById(idYear);
        if (!textInput || !dateSel || !monthSel || !yearSel) return;
        const dd = (dateSel.value || '').trim();
        const monthOpt = monthSel.options[monthSel.selectedIndex];
        const monthText = monthOpt ? monthOpt.text.trim() : '';
        const yyyy = (yearSel.value || '').trim();
        textInput.value = dd + monthText + yyyy;
      };
    }
    if (typeof window.CheckStandardDateTime !== 'function') {
      window.CheckStandardDateTime = function (selectEl, idDate, idMonth, idYear, idHour, idMinute) {
        if (!selectEl || !idDate || !idMonth || !idYear || !idHour || !idMinute) return;
        const baseId = selectEl.id.replace(/_Date$|_Month$|_Year$|_Hour$|_Minute$/, '');
        const hiddenInput = document.getElementById(baseId);
        const dateSel = document.getElementById(idDate);
        const monthSel = document.getElementById(idMonth);
        const yearSel = document.getElementById(idYear);
        const hourSel = document.getElementById(idHour);
        const minuteSel = document.getElementById(idMinute);
        if (!hiddenInput || !dateSel || !monthSel || !yearSel || !hourSel || !minuteSel) return;
        const dd = (dateSel.value || '').trim();
        const monthOpt = monthSel.options[monthSel.selectedIndex];
        const monthText = monthOpt ? monthOpt.text.trim() : '';
        const yyyy = (yearSel.value || '').trim();
        const hh = (hourSel.value || '').trim();
        const mm = (minuteSel.value || '').trim();
        hiddenInput.value = dd + monthText + yyyy + hh + mm;
      };
    }

    if (effectiveTemplateId) {
      loadCRFTemplate();
    }

    // Cleanup: remove CRF globals so they don't leak when navigating away
    return () => {
      const crfGlobals = ['toggleSubGroup', 'CalculateForTextbox', 'ddlAlerton', 'msgalert', 'TimeConvert', 'AutoTimeConvert', 'ValidateTextbox', 'Calculate', 'LengthValidation', 'RemoveSelection', 'CheckStandardDate', 'CheckStandardDateTime'];
      crfGlobals.forEach((k) => { if (window[k] !== undefined) delete window[k]; });
    };
  }, [effectiveTemplateId, visitId]);

  // When opening from First Review (or similar), parent may not have templateId yet; resolve it from visit so loadCRFTemplate runs
  useEffect(() => {
    if (templateId || !visitId) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await visitService.getAssignedCRFTemplate(visitId);
        if (cancelled) return;
        const id = getAnyCase(data, 'templateId', 'TemplateId', 'templateID', 'templateNo', 'TemplateNo', 'vTemplateNo', 'VTemplateNo');
        if (id != null) setResolvedTemplateId(String(id));
      } catch (err) {
        if (!cancelled) console.error('[CRFForm] Resolve template for visit failed', err);
      }
    })();
    return () => { cancelled = true; };
  }, [templateId, visitId]);

  // When no CRF header yet (404 / Start Report), CRA still needs Draft Save and Submit so set default actions
  useEffect(() => {
    if (visitId && effectiveTemplateId && !nCRFHdrNo) {
      setAvailableActions(['DraftSave', 'Submit']);
      setCrfStage('In Progress');
      setPageIsReviewStage(null);
    }
  }, [visitId, effectiveTemplateId, nCRFHdrNo]);

  useEffect(() => {
    if (!nCRFHdrNo) {
      setPageIsReviewStage(null);
      setCrfCurrentStageCode(null);
      setCrfCSaveType('D');
      setAssociatedInactiveChildFieldCodes([]);
    }
  }, [nCRFHdrNo]);

  // Fetch CRF stage and available actions when we have a header (for Approve/Reject buttons)
  useEffect(() => {
    if (!nCRFHdrNo) return;
    let cancelled = false;
    const fetchActions = async () => {
      try {
        const res = await api.get(`/crf/page/${nCRFHdrNo}/actions`);
        if (cancelled) return;
        const stage = res.data?.stage ?? res.data?.Stage ?? null;
        if (stage) setCrfStage(stage);
        applyCrfPageDcfFlags(res.data);
        applyCrfPageSaveType(res.data);
        const irs = readPageIsReviewStage(res.data);
        if (irs !== null) setPageIsReviewStage(irs);
        const cscAct = pickCurrentStageCodeUpdate(res.data);
        if (cscAct !== undefined) setCrfCurrentStageCode(cscAct);
        const actions = res.data?.actions ?? res.data?.Actions;
        setAvailableActions(Array.isArray(actions) ? actions : []);
      } catch (err) {
        if (!cancelled) {
          setAvailableActions([]);
        }
      }
    };
    fetchActions();
    return () => { cancelled = true; };
  }, [nCRFHdrNo, applyCrfPageDcfFlags, applyCrfPageSaveType]);

  const populateFormFromSavedData = React.useCallback((content) => {
    const stsHeaderStageLabel = resolveStsApiHeaderStageLabel(visitUiStageLabel, workflowHeader, crfStage, {
      embedInModal,
      reviewMode: props.reviewMode === true,
      readOnly: props.readOnly === true,
    });
    const result = { populatedCount: 0, totalWithValue: 0, success: true };
    if (!savedFields?.length) return result;
    if (!content) return result;

    const popCommentsTaSel = CRF_COMMENTS_TEXTAREA_SEL;
    const isCommentColEl = isCrfCommentRegionElement;
    const shouldApplySavedTextToControl = (savedValueRaw, el) => {
      const savedTrim = String(savedValueRaw ?? '').trim();
      if (savedTrim) return true;
      if (!el) return true;
      const domTrim = String(el.value ?? '').trim();
      // Preserve user-entered DOM values across modal open/close refreshes: do not overwrite non-empty with empty.
      return !domTrim;
    };

    // Helper: set one select by matching option text or value (for Day/Month/Year/Hour/Minute)
    const setSelectByTextOrValue = (sel, val, matchByText = true) => {
      if (!sel || !val) return;
      const v = String(val).trim();
      for (let i = 0; i < sel.options.length; i++) {
        const opt = sel.options[i];
        if ((matchByText && (opt.text || '').trim().toUpperCase() === v.toUpperCase()) || opt.value === v) {
          sel.value = opt.value;
          return;
        }
      }
      if (v) sel.value = v; // fallback by value only
    };

    savedFields.forEach((f) => {
      const vFieldId = (f.vFieldId || f.VFieldId || '').trim();
      if (!vFieldId) return;
      const value = f.vFieldValue ?? f.VFieldValue ?? '';
      const comment = f.vComment ?? f.VComment ?? '';
      if (value || comment) result.totalWithValue++;

      const valStr = String(value || '').trim();
      // Match StandardDate (DD-MMM-YYYY, month letters only) and StandardDateTime — do not treat YYYY-MM-DD (HTML5 date) as StandardDate
      const isStandardDateTime = /^([^-]+)-([A-Za-z]{3})-([^\s-]+)\s+([^:]+):([^:]+)$/.test(valStr);
      const isStandardDate = /^([^-]+)-([A-Za-z]{3})-([^\s-]+)$/.test(valStr) && !isStandardDateTime;

      // StandardDate / StandardDateTime: hidden composite + part dropdowns (never bind generic select[id*=] — empty draft would set DD select to '' and show blank)
      const hiddenDateTimeInput = content.querySelector(`input.standarddatetime-text[id*="${vFieldId}"]`);
      const hiddenDateInput = content.querySelector(`input.standarddate-text[id*="${vFieldId}"]`);
      const hiddenStdInput = hiddenDateTimeInput || hiddenDateInput;
      if (hiddenStdInput) {
        const isDateTimeField = !!hiddenDateTimeInput;
        if (valStr && (isStandardDate || isStandardDateTime)) {
          hiddenStdInput.value = valStr;
          const baseId = hiddenStdInput.id;
          if (baseId) {
            const dateSel = content.querySelector(`#${CSS.escape(baseId + '_Date')}`);
            const monthSel = content.querySelector(`#${CSS.escape(baseId + '_Month')}`);
            const yearSel = content.querySelector(`#${CSS.escape(baseId + '_Year')}`);
            const parts = valStr.match(isStandardDateTime ? /^([^-]+)-([^-]+)-([^\s-]+)\s+([^:]+):([^:]+)$/ : /^([^-]+)-([^-]+)-([^\s-]+)$/);
            const dd = parts ? parts[1].trim() : '';
            const mmm = parts ? parts[2].trim() : '';
            const yyyy = parts ? parts[3].trim() : '';
            if (dateSel && dd) setSelectByTextOrValue(dateSel, dd);
            if (monthSel && mmm) setSelectByTextOrValue(monthSel, mmm, true);
            if (yearSel && yyyy) {
              // Year option for UKUK/UNK has value "0000" in backend; match by text UKUK or UNK, then set by option value
              const yyyyUpper = yyyy.toUpperCase();
              const opt = Array.from(yearSel.options).find(o => {
                const t = (o.text || '').trim().toUpperCase();
                const v = (o.value || '').trim();
                return t === yyyyUpper || v === yyyy || (yyyyUpper === 'UKUK' && (t === 'UNK' || v === '0000')) || (yyyyUpper === 'UNK' && (t === 'UKUK' || v === '0000'));
              });
              if (opt) yearSel.value = opt.value; else yearSel.value = yyyy;
            }
            if (isDateTimeField && parts && parts.length >= 5) {
              const hh = parts[4].trim();
              const mm = parts[5].trim();
              const hourSel = content.querySelector(`#${CSS.escape(baseId + '_Hour')}`);
              const minuteSel = content.querySelector(`#${CSS.escape(baseId + '_Minute')}`);
              if (hourSel && hh) setSelectByTextOrValue(hourSel, hh);
              if (minuteSel && mm) setSelectByTextOrValue(minuteSel, mm);
            }
          }
        } else if (!valStr) {
          // If user already selected parts, keep their in-progress DOM state while Visit Report is open.
          const domExisting = String(hiddenStdInput.value ?? '').trim();
          if (domExisting) {
            // no-op
          } else {
            hiddenStdInput.value = '';
          const baseId = hiddenStdInput.id;
          if (baseId) {
            const dateSel = content.querySelector(`#${CSS.escape(baseId + '_Date')}`);
            const monthSel = content.querySelector(`#${CSS.escape(baseId + '_Month')}`);
            const yearSel = content.querySelector(`#${CSS.escape(baseId + '_Year')}`);
            if (dateSel) setSelectByTextOrValue(dateSel, 'DD');
            if (monthSel) setSelectByTextOrValue(monthSel, 'MMM');
            if (yearSel) setSelectByTextOrValue(yearSel, 'YYYY');
            if (isDateTimeField) {
              const hourSel = content.querySelector(`#${CSS.escape(baseId + '_Hour')}`);
              const minuteSel = content.querySelector(`#${CSS.escape(baseId + '_Minute')}`);
              if (hourSel) setSelectByTextOrValue(hourSel, 'HH');
              if (minuteSel) setSelectByTextOrValue(minuteSel, 'MM');
            }
          }
          }
        } else {
          hiddenStdInput.value = valStr;
        }
        result.populatedCount++;
        const row = hiddenStdInput?.closest?.('.crf-report-row') || findCrfReportRowForFieldId(content, vFieldId);
        const commentsEl = (row ? row.querySelector(popCommentsTaSel) : null) ||
          content.querySelector(`[id="reportfield_${vFieldId}_comments"]`) ||
          content.querySelector(`[id="textbox_${vFieldId}_comments"]`) ||
          content.querySelector(`[id="textarea_${vFieldId}_comments"]`) ||
          content.querySelector(`[name="reportfield_${vFieldId}_comments"]`) ||
          content.querySelector(`[name="textarea_${vFieldId}_comments"]`) ||
          content.querySelector(`[id$="_comments"][id*="${vFieldId}"]`);
        if (commentsEl) {
          if (shouldApplySavedTextToControl(comment, commentsEl)) {
            commentsEl.value = comment;
          }
          if (comment) result.populatedCount++;
        }
        return; // skip generic input handling for this field
      }

      const input = content.querySelector(`.crf-answer-cell [data-field-id="${vFieldId}"]`) ||
        content.querySelector(`.crf-answer-col [data-field-id="${vFieldId}"]`) ||
        content.querySelector(`[data-field-id="${vFieldId}"]`) ||
        content.querySelector(`[id="reportfield_${vFieldId}_answer"]`) ||
        content.querySelector(`[id="textbox_${vFieldId}"]`) ||
        content.querySelector(`[id="textarea_${vFieldId}"]`) ||
        content.querySelector(`[id="combobox_${vFieldId}"]`) ||
        content.querySelector(`[id="datetime_${vFieldId}"]`) ||
        content.querySelector(`[id="time_${vFieldId}"]`) ||
        content.querySelector(`[id="file_${vFieldId}"]`) ||
        content.querySelector(`[name="reportfield_${vFieldId}_answer"]`) ||
        content.querySelector(`[name="textbox_${vFieldId}"]`) ||
        content.querySelector(`[name="textarea_${vFieldId}"]`) ||
        content.querySelector(`input[name="radio_${vFieldId}"], input[name^="radio_${vFieldId}_"]`) ||
        content.querySelector(`input[id^="radio_${vFieldId}_"], input[id="radio_${vFieldId}"]`) ||
        content.querySelector(`input[name="checkbox_${vFieldId}"], input[name^="checkbox_${vFieldId}_"]`) ||
        content.querySelector(`input[id="checkbox_${vFieldId}"]`) ||
        content.querySelector(`.crf-answer-cell input[name="radio_${vFieldId}"], .crf-answer-col input[name="radio_${vFieldId}"], .crf-answer-cell input[name^="radio_${vFieldId}_"], .crf-answer-col input[name^="radio_${vFieldId}_"]`) ||
        content.querySelector(`.crf-answer-cell input[name="checkbox_${vFieldId}"], .crf-answer-col input[name="checkbox_${vFieldId}"], .crf-answer-cell input[name^="checkbox_${vFieldId}_"], .crf-answer-col input[name^="checkbox_${vFieldId}_"]`) ||
        content.querySelector(`.crf-answer-cell input[id*="${vFieldId}"], .crf-answer-cell select[id*="${vFieldId}"], .crf-answer-cell textarea[id*="${vFieldId}"]`) ||
        content.querySelector(`.crf-answer-col input[id*="${vFieldId}"], .crf-answer-col select[id*="${vFieldId}"], .crf-answer-col textarea[id*="${vFieldId}"]`) ||
        content.querySelector(`[id$="_answer"][id*="${vFieldId}"]`) ||
        content.querySelector(`.crf-answer-cell input[type="file"][id*="${vFieldId}"], .crf-answer-col input[type="file"][id*="${vFieldId}"]`) ||
        content.querySelector(`.crf-answer-cell input[type="date"][id*="${vFieldId}"], .crf-answer-col input[type="date"][id*="${vFieldId}"]`) ||
        content.querySelector(`.crf-answer-cell input[type="datetime-local"][id*="${vFieldId}"], .crf-answer-col input[type="datetime-local"][id*="${vFieldId}"]`) ||
        content.querySelector(`input[id*="${vFieldId}"][id$="_answer"]:not([id*="_comments"]), select[id*="${vFieldId}"], textarea[id*="${vFieldId}"]:not([id*="_comments"])`) ||
        content.querySelector(`input[name*="${vFieldId}"]:not([name*="_comments"]), textarea[name*="${vFieldId}"]:not([name*="_comments"])`) ||
        content.querySelector(`input[type="date"][id*="${vFieldId}"]`) ||
        content.querySelector(`input[type="datetime-local"][id*="${vFieldId}"]`);
      // Use column/DOM like collectFormData: answer widgets in Answer column may legitimately have ids ending in _comments.
      if (input && !isCommentColEl(input)) {
        if (input.type === 'checkbox') {
          // RAMP-style: saved value can be comma-separated list of checked option values
          const savedStr = String(value || '').trim();
          const savedValues = savedStr ? savedStr.split(',').map((v) => v.trim()).filter(Boolean) : [];
          const checkboxes = content.querySelectorAll(`input[type="checkbox"][name="${input.name}"]`);
          const domAnyChecked = Array.from(checkboxes || []).some((cb) => cb.checked);
          if (!savedStr && domAnyChecked) {
            // Preserve user-entered checks when server has no saved value yet.
            result.populatedCount++;
          } else
          if (checkboxes.length > 1) {
            checkboxes.forEach((cb) => {
              const optionValue = (cb.value || '').trim();
              cb.checked = optionValue ? savedValues.includes(optionValue) : /^(yes|true|1|y)$/i.test(savedStr);
            });
          } else {
            input.checked = savedValues.length > 0
              ? savedValues.includes((input.value || '').trim()) || /^(yes|true|1|y)$/i.test(savedStr)
              : false;
          }
        } else if (input.type === 'radio') {
          const radios = content.querySelectorAll(`input[name="${input.name}"]`);
          const savedTrim = String(value ?? '').trim();
          const domAnyChecked = Array.from(radios || []).some((r) => r.checked);
          if (!savedTrim && domAnyChecked) {
            // Preserve user selection when server has no saved value yet.
          } else {
            radios.forEach((r) => { r.checked = r.value === value; });
          }
          result.populatedCount++;
        } else if (input.type === 'file') {
          // Browsers do not allow setting input.value on file inputs; show saved path as clickable link to open PDF
          if (value && String(value).trim()) {
            const pathValue = String(value).trim();
            const filename = (pathValue.split(/[/\\]/).pop() || pathValue).trim();
            input.setAttribute('data-saved-filename', filename);
            input.setAttribute('data-uploaded-path', pathValue);
            const answerCell = input.closest(CRF_ANSWER_REGION_SEL);
            const fileNamesEl = answerCell?.querySelector('.file-names-list') ||
              (input.id && content.querySelector(`[id="${input.id}_fileNames"]`)) ||
              (input.id && content.querySelector(`#${CSS.escape(input.id)}_fileNames`));
            const isServerPath = /^[A-Za-z0-9_-]+(\/[^\\]*)?$/i.test(pathValue) && (pathValue.startsWith('CRFUploads') || pathValue.includes('/'));
            const base = (typeof window !== 'undefined' && (sessionStorage.getItem('ctms_api_base_url') || '')) || '';
            const baseNorm = base.replace(/\/$/, '');
            const crfFilePath = baseNorm.toLowerCase().endsWith('/api') ? `${baseNorm}/crf/file` : `${baseNorm || ''}/api/crf/file`;
            const fileUrl = isServerPath ? `${crfFilePath}?path=${encodeURIComponent(pathValue)}` : null;
            const displayText = fileUrl ? '' : `Uploaded: ${filename}`;
            if (fileNamesEl) {
              fileNamesEl.classList.add('crf-saved-filename');
              if (fileUrl) {
                const link = document.createElement('a');
                link.href = fileUrl;
                link.target = '_blank';
                link.rel = 'noopener noreferrer';
                link.textContent = displayText + (filename ? ` (${filename})` : '');
                link.style.marginTop = '4px';
                link.style.display = 'inline-block';
                fileNamesEl.innerHTML = '';
                fileNamesEl.appendChild(link);
              } else {
                fileNamesEl.textContent = `Uploaded: ${filename}`;
              }
            } else {
              let span = answerCell?.querySelector('.crf-saved-filename');
              if (!span && answerCell) {
                span = document.createElement('span');
                span.className = 'crf-saved-filename';
                span.style.display = 'block';
                span.style.marginTop = '4px';
                input.parentNode?.appendChild(span);
              }
              if (span) {
                if (fileUrl) {
                  const link = document.createElement('a');
                  link.href = fileUrl;
                  link.target = '_blank';
                  link.rel = 'noopener noreferrer';
                  link.textContent = displayText + (filename ? ` (${filename})` : '');
                  span.innerHTML = '';
                  span.appendChild(link);
                } else {
                  span.textContent = `Uploaded: ${filename}`;
                }
              }
            }
            result.populatedCount++;
          }
        } else if (
          input.tagName === 'SELECT' &&
          (input.hasAttribute('data-standarddate-part') || input.hasAttribute('data-standarddatetime-part'))
        ) {
          // Composite date fields use hidden input + part selects; do not assign '' to DD/MMM (invalid option → blank UI)
          result.populatedCount++;
        } else if (input.tagName === 'SELECT') {
          if (shouldApplySavedTextToControl(value, input)) {
            applyComboboxOrSelectSavedValue(input, value);
          }
          result.populatedCount++;
        } else {
          // STS AutoCapture: preserve API-populated DOM when saved vFieldValue is still empty
          // (modal open/close re-runs processContent; stsAutoCaptureExecutedRef blocks re-fetch).
          const isStsAutoCapture = input.getAttribute?.('data-sts-autocapture') === 'true';
          const savedTrim = String(value ?? '').trim();
          const domTrim = String(input.value ?? '').trim();
          if (isStsAutoCapture) {
            if (savedTrim) {
              if (shouldStsShowRowSelectionMode()) {
                const stsNorm = normalizeStsAutoCaptureDataTable(savedTrim);
                if (stsNorm) {
                  input.value = serializeStsAutoCapturePersistedValue(stsNorm) || savedTrim;
                }
              } else {
                applyStsAutoCaptureDbValue(input, savedTrim, shouldStsEnableEditableCells());
              }
              input.setAttribute(STS_AUTOCAPTURE_SOURCE_ATTR, STS_AUTOCAPTURE_SOURCE_DB);
            } else if (!domTrim) {
              input.value = '';
            }
          } else if (!(isStsAutoCapture && !savedTrim && domTrim) && (savedTrim || !domTrim)) {
            input.value = value;
          }
          result.populatedCount++;
        }
        const row = input.closest('.crf-report-row') || input.closest(STS_AUTOCAPTURE_BLOCK_SEL);
        const commentsEl = resolveStsAutoCaptureCommentsEl(content, row, vFieldId, popCommentsTaSel);
        if (commentsEl) {
          if (shouldApplySavedTextToControl(comment, commentsEl)) {
            commentsEl.value = comment;
          }
          if (comment) result.populatedCount++;
        }
      } else {
        const row = findCrfReportRowForFieldId(content, vFieldId);
        const commentsEl = resolveStsAutoCaptureCommentsEl(content, row, vFieldId, popCommentsTaSel);
        if (commentsEl) {
          if (shouldApplySavedTextToControl(comment, commentsEl)) {
            commentsEl.value = comment;
          }
          if (comment) result.populatedCount++;
        }
      }
    });
    if (result.totalWithValue > 0 && result.populatedCount === 0) {
      console.warn('[CRFForm] Saved data could not be populated - no matching inputs found for field IDs:', savedFields.map((f) => f.vFieldId || f.VFieldId));
      result.success = false;
    } else if (result.totalWithValue > 0 && result.populatedCount < result.totalWithValue) {
      console.warn('[CRFForm] Partially populated:', result.populatedCount, 'of', result.totalWithValue, 'saved fields');
    }
    return result;
  }, [savedFields, visitUiStageLabel, workflowHeader, crfStage, embedInModal, props.reviewMode, props.readOnly]);

  const isReviewStage =
    pageIsReviewStage === true ||
    (pageIsReviewStage == null &&
      (crfStage === 'First Review' || crfStage === 'Second Review'));
  /** PM on a review lane (tab or header): own Open/Resolved DCFs must be Closed before Submit. */
  const pmAtReviewLaneWithOwnDcfToClose = React.useMemo(() => {
    if (!isPmReviewerProfile(currentUserProfile, currentUserProfileAliases)) return false;
    if (!isReviewStage && !(embedInModal && visitUiStageLabel.length > 0)) return false;
    return submitBlockedByOwnOpenDcf;
  }, [
    currentUserProfile,
    currentUserProfileAliases,
    isReviewStage,
    embedInModal,
    visitUiStageLabel,
    submitBlockedByOwnOpenDcf,
  ]);
  // Reviewer profiles (from server CanRaiseDCF): show Reviewer DCF view; only they see Raise DCF comment box and button. CRA does not.
  const showReviewerDcfView = isReviewStage || canRaiseDCFFromServer;

  const setFieldsEnabled = React.useCallback((content, enabled) => {
    if (!content) return;
    const inputs = content.querySelectorAll('input:not([type="hidden"]), select, textarea');
    inputs.forEach((el) => {
      if (el.closest?.('.crf-row-actions, [data-crf-action]')) return;
      // STS Auto Capture fields are always non-editable (value is system-populated).
      if (el.getAttribute?.('data-sts-autocapture') === 'true') {
        el.setAttribute('disabled', 'disabled');
        if ((el.tagName || '').toUpperCase() !== 'SELECT') {
          el.setAttribute('readonly', 'readonly');
        } else {
          el.removeAttribute('readonly');
        }
        return;
      }
      if (enabled) {
        el.removeAttribute('disabled');
        el.removeAttribute('readonly');
      } else {
        el.setAttribute('disabled', 'disabled');
        // HTML does not define readonly on <select>; pairing it with disabled can yield a blank closed combobox in some UAs.
        if ((el.tagName || '').toUpperCase() !== 'SELECT') {
          el.setAttribute('readonly', 'readonly');
        } else {
          el.removeAttribute('readonly');
        }
      }
    });
  }, []);

  const enableFieldsByCodes = React.useCallback((content, fieldCodes) => {
    if (!content || !fieldCodes || fieldCodes.size === 0) return;
    fieldCodes.forEach((vFieldId) => {
      const code = String(vFieldId || '').trim();
      if (!code) return;
      const inputs = content.querySelectorAll(
        `[data-field-id="${code}"], [id*="${code}"], [name*="${code}"]`
      );
      inputs.forEach((el) => {
        if (el.closest?.('.crf-row-actions, [data-crf-action]')) return;
        if (el.tagName !== 'INPUT' && el.tagName !== 'SELECT' && el.tagName !== 'TEXTAREA') return;
        if (el.getAttribute?.('data-sts-autocapture') === 'true') return;
        el.removeAttribute('disabled');
        el.removeAttribute('readonly');
      });
    });
  }, []);

  // Track which auto-capture elements have been executed for this component instance.
  const stsAutoCaptureExecutedRef = useRef(new Set());  // Clear execution cache whenever the report HTML is (re)bound.
  const stsAutoCapturePendingRef = useRef(new Map());
  const savedFieldsLatestRef = useRef(savedFields);
  useEffect(() => {
    savedFieldsLatestRef.current = savedFields;
    setStsRowSelectionContext({
      readOnly: props.readOnly,
      isViewOnly,
      crfStage,
    });
    if (!Array.isArray(savedFields) || savedFields.length === 0) return;
    const content = crfContainerRef.current?.querySelector('.crf-generated-content');
    if (!content) return;
    const stsHeaderStageLabel = resolveStsApiHeaderStageLabel(visitUiStageLabel, workflowHeader, crfStage, {
      embedInModal,
      reviewMode: props.reviewMode === true,
      readOnly: props.readOnly === true,
    });
    const stsCellsCanEdit = shouldStsEnableEditableCells();
    content.querySelectorAll(STS_AUTOCAPTURE_SEL).forEach((el) => {
      const db = getStsAutoCaptureSavedFieldValue(el, savedFields);
      if (!db) return;
      const detailNoRaw = (el.getAttribute('data-sts-api-detail-no') || '').trim();
      if (!detailNoRaw) {
        applyStsAutoCaptureDbValue(el, db, stsCellsCanEdit);
        return;
      }
      const key = stsAutoCaptureExecutionKey(detailNoRaw, el, stsHeaderStageLabel);
      const savedNorm = normalizeStsAutoCaptureDataTable(db);
      const resolvedSavedNorm = resolveStsAutoCaptureSavedNorm(el, savedFields) || savedNorm;
      const stsCellsCanEditForEl = stsCellsCanEdit || isStsOpenDcfCorrectionAllowed(el, savedFields, stsHeaderStageLabel);
      if (stsSavedNormNeedsDraftApiSource(resolvedSavedNorm, el, savedFields, stsHeaderStageLabel)) {
        if (stsAutoCaptureExecutedRef.current.has(key)) return;
        if (el.getAttribute(STS_AUTOCAPTURE_ERROR_ATTR) === 'true') return;
        if (stsAutoCapturePendingRef.current.has(key)) return;
        stsAutoCapturePendingRef.current.set(key, true);
        fetchStsAutoCaptureForElement(
          el,
          detailNoRaw,
          visitId,
          stsCellsCanEditForEl,
          resolvedSavedNorm,
          (err) => {
            stsAutoCapturePendingRef.current.delete(key);
            stsAutoCaptureExecutedRef.current.delete(key);
            const sn = resolveStsAutoCaptureSavedNorm(el, savedFieldsLatestRef.current)
              || normalizeStsAutoCaptureDataTable(
                getStsAutoCaptureSavedFieldValue(el, savedFieldsLatestRef.current)
              );
            const errHeaderStageLabel = stsHeaderStageLabel;
            if (stsSavedNormNeedsDraftApiSource(sn, el, savedFieldsLatestRef.current, errHeaderStageLabel)) {
              applyStsAutoCaptureDbValue(el, db, stsCellsCanEditForEl);
              stsAutoCaptureExecutedRef.current.add(key);
            }
            el.removeAttribute(STS_AUTOCAPTURE_LOADING_ATTR);
            if (!String(el.value ?? '').trim()) {
              showStsAutoCaptureMessage(el, resolveStsAutoCaptureApiErrorMessage(err));
            }
          },
          undefined,
          savedFields,
          stsHeaderStageLabel,
          () => {
            stsAutoCapturePendingRef.current.delete(key);
            stsAutoCaptureExecutedRef.current.add(key);
          },
          () => {
            stsAutoCapturePendingRef.current.delete(key);
            stsAutoCaptureExecutedRef.current.delete(key);
          }
        );
        return;
      }
      if (
        isStsOpenDcfCorrectionAllowed(el, savedFields, stsHeaderStageLabel)
        && stsDisplayHasRowSelectionTable(el)
        && stsDisplayHasEnableFieldData(el, resolvedSavedNorm)
      ) {
        const block =
          el.closest?.(STS_AUTOCAPTURE_BLOCK_SEL)
          || resolveStsAutoCaptureDisplayEl(el)?.closest?.(STS_AUTOCAPTURE_BLOCK_SEL)
          || null;
        applyStsAutoCaptureTableCellEditability(block || content, stsCellsCanEditForEl, savedFields);
        stsAutoCaptureExecutedRef.current.add(key);
        return;
      }
      applyStsAutoCaptureDbValue(el, db, stsCellsCanEditForEl);
      stsAutoCaptureExecutedRef.current.add(key);
    });
  }, [savedFields, props.readOnly, props.reviewMode, isViewOnly, crfStage, visitId, visitUiStageLabel, workflowHeader, embedInModal]);

  useEffect(() => {
    if (!Array.isArray(savedFields) || savedFields.length === 0 || !htmlContent) return undefined;
    let cancelled = false;
    const rafId = requestAnimationFrame(() => {
      if (cancelled) return;
      setStsRowSelectionContext({
        readOnly: props.readOnly,
        isViewOnly,
        crfStage,
      });
      // Review/reopen flows can miss the first DOM render pass; re-apply saved STS table JSON without calling the STS API.
      if (!props.reviewMode && shouldStsShowRowSelectionMode()) return;
      const content = crfContainerRef.current?.querySelector('.crf-generated-content');
      if (!content) return;
      content.querySelectorAll(STS_AUTOCAPTURE_SEL).forEach((el) => {
        const savedTrim = getStsAutoCaptureSavedFieldValue(el, savedFields);
        if (!savedTrim) return;
        applyStsAutoCaptureDbValue(el, savedTrim, !props.reviewMode && shouldStsEnableEditableCells());
      });
    });
    return () => {
      cancelled = true;
      cancelAnimationFrame(rafId);
    };
  }, [htmlContent, savedFields, props.readOnly, props.reviewMode, isViewOnly, crfStage]);

  // This ensures Draft reopen refreshes the value (e.g. 7 -> 8),
  // while the stage/action gating still prevents calls after Submit.
  useEffect(() => {
    stsAutoCaptureExecutedRef.current = new Set();
    stsAutoCapturePendingRef.current.clear();
  }, [htmlContent, nCRFHdrNo]);
  useEffect(() => () => {
    stsAutoCapturePendingRef.current.clear();
  }, []);

  const handleClearForm = () => {
    const content = crfContainerRef.current;
    if (!content) return;
    const inputs = content.querySelectorAll('input:not([type="hidden"]), select, textarea');
    inputs.forEach((el) => {
      if (!isCrfFormControlClearable(el)) return;
      if (el.type === 'file') {
        clearCrfFileInputAndDisplay(el);
        return;
      }
      if (el.type === 'checkbox' || el.type === 'radio') {
        el.checked = false;
      } else if (el.tagName === 'SELECT') {
        // Reset dropdown to first option (e.g. "Select") instead of blank
        if (el.options.length > 0) {
          el.selectedIndex = 0;
        } else {
          el.value = '';
        }
      } else {
        el.value = '';
      }
    });
  };

  // Build HTML for CRF Report Preview: clone current report content; optionally blank answers and comments (Without).
  // Renders radio/checkbox/select as proper read-only controls (not plain text).
  const getPreviewHtml = (blankAnswersAndComments) => {
    const content = crfContainerRef.current?.querySelector('.crf-generated-content');
    if (!content) return '';
    const clone = content.cloneNode(true);
    // Preview must stay fully expanded: keep all sub groups open and remove collapse interaction.
    expandVisitReportTemplateSubGroups(clone);
    clone.querySelectorAll('.subGroupDiv').forEach((header) => {
      header.removeAttribute('onclick');
      header.style.cursor = 'default';
    });
    // Visit Report read-only uses DOM static nodes + display:none on inputs; strip before preview so HTML is not doubled.
    clone.querySelectorAll('[data-crf-transaction-view-static]').forEach((el) => el.remove());
    clone.querySelectorAll('[data-crf-transaction-view-hide-ro-group="1"]').forEach((g) => {
      g.removeAttribute('data-crf-transaction-view-hide-ro-group');
      g.style.removeProperty('display');
    });
    clone.querySelectorAll('[data-crf-transaction-view-lock="1"]').forEach((c) => {
      delete c.dataset.crfTransactionViewLock;
      c.style.removeProperty('display');
      c.disabled = false;
      c.readOnly = false;
      c.removeAttribute('readonly');
    });
    // Remove action buttons (DCF, Audit, Action Items) so preview is clean
    clone.querySelectorAll('.col-sideBtns, .crf-row-actions, [data-crf-action]').forEach((el) => el.remove());
    // Remove visit-report fill-hint icon/button from question labels in both preview and PDF.
    clone.querySelectorAll('.crf-visit-report-fill-hint-btn, .crf-visit-report-fill-hint-svg').forEach((el) => el.remove());
    // Remove Formula field Calc buttons in preview mode (read-only preview, no calculation)
    clone.querySelectorAll('button.btnCalc').forEach((el) => el.remove());
    // Remove CRA placeholder "—" in comment cells so preview shows actual comment (or empty), not "-" in every field
    clone.querySelectorAll('.crf-comments-placeholder').forEach((el) => el.remove());
    if (blankAnswersAndComments) {
      clone.querySelectorAll('input, select, textarea').forEach((el) => {
        if (el.type === 'checkbox' || el.type === 'radio') {
          el.checked = false;
          el.removeAttribute('checked');
        } else if (el.tagName === 'SELECT') {
          el.removeAttribute('value');
          let emptyOpt = Array.from(el.options).find((o) => (o.value || '').trim() === '');
          if (!emptyOpt) {
            const blank = document.createElement('option');
            blank.value = '';
            blank.textContent = ' ';
            el.insertBefore(blank, el.firstChild);
            emptyOpt = blank;
          }
          Array.from(el.options).forEach((o) => { o.selected = false; o.removeAttribute('selected'); });
          emptyOpt.selected = true;
          emptyOpt.setAttribute('selected', 'selected');
        } else {
          el.value = '';
          el.removeAttribute('value');
        }
      });
      clone.querySelectorAll('textarea').forEach((ta) => {
        ta.textContent = '';
        ta.value = '';
      });
      // Remove uploaded file URLs/links in blank preview so "Open file" is not visible
      clone.querySelectorAll('.file-names-list, .crf-saved-filename').forEach((el) => {
        el.remove();
      });
      clone.querySelectorAll('input[type="file"]').forEach((input) => {
        input.removeAttribute('data-saved-filename');
        input.removeAttribute('data-uploaded-path');
      });
    }
    const replaceWithDisplayDiv = (el, text, className = 'crf-preview-field-value') => {
      const div = document.createElement('div');
      div.className = className;
      div.textContent = text || '\u00A0';
      el.parentNode.replaceChild(div, el);
    };
    // Format StandardDate composite value (DDMMMYYYY e.g. 07FEB2016) to DD-MMM-YYYY (e.g. 07-Feb-2016) for preview
    const formatStandardDateForPreview = (raw) => {
      if (blankAnswersAndComments || !raw) return '';
      const s = (raw || '').trim();
      const m = s.match(/^(\d{2})([A-Za-z]{3})(\d{4})?$/);
      if (!m) return s;
      const [, dd, mmm, yyyy] = m;
      const monthTitle = mmm ? mmm.charAt(0).toUpperCase() + mmm.slice(1).toLowerCase() : mmm;
      return yyyy ? `${dd}-${monthTitle}-${yyyy}` : `${dd}${monthTitle}`;
    };
    // Format StandardDateTime composite (DDMMMYYYYHHmm) to "DD-MMM-YYYY HH:MM" for preview
    const formatStandardDateTimeForPreview = (raw) => {
      if (blankAnswersAndComments || !raw) return '';
      const s = (raw || '').trim();
      const m = s.match(/^(\d{2})([A-Za-z]{3})(\d{4})(\d{2})(\d{2})$/);
      if (!m) return s;
      const [, dd, mmm, yyyy, hh, mm] = m;
      const monthTitle = mmm ? mmm.charAt(0).toUpperCase() + mmm.slice(1).toLowerCase() : mmm;
      return `${dd}-${monthTitle}-${yyyy} ${hh}:${mm}`;
    };
    // Show dates in DD-MMM-YYYY in preview for YYYY-MM-DD inputs (e.g. 24-Feb-2026)
    const previewDateText = (raw) => {
      if (blankAnswersAndComments || !raw) return '';
      const s = (raw || '').trim();
      if (!s) return '';
      // YYYY-MM-DD or YYYY-MM-DDTHH:mm:ss
      if (/^\d{4}-\d{2}-\d{2}(T|\s|$)/.test(s)) {
        const formatted = formatDateDDMMMYYYY(s);
        return formatted !== '—' ? formatted : s;
      }
      return s;
    };
    // 0) StandardDate: replace entire .standarddate-row with one read-only div (so we don't replace 1 input + 3 selects separately)
    clone.querySelectorAll('.standarddate-row').forEach((row) => {
      const textInput = row.querySelector('input.standarddate-text');
      let displayText = '';
      if (textInput && !blankAnswersAndComments) {
        const id = textInput.id;
        const originalInput = id ? content.querySelector(`#${CSS.escape(id)}`) : null;
        const value = (originalInput?.value || textInput.value || '').trim();
        displayText = formatStandardDateForPreview(value) || value;
      }
      const wrap = document.createElement('div');
      wrap.className = 'crf-preview-field-value crf-preview-select-value';
      wrap.textContent = displayText || '\u00A0';
      row.parentNode.replaceChild(wrap, row);
    });
    // 0a) StandardDateTime: replace entire .standarddatetime-row with one read-only div
    clone.querySelectorAll('.standarddatetime-row').forEach((row) => {
      const hiddenInput = row.querySelector('input.standarddatetime-text');
      let displayText = '';
      if (hiddenInput && !blankAnswersAndComments) {
        const id = hiddenInput.id;
        const originalInput = id ? content.querySelector(`#${CSS.escape(id)}`) : null;
        const value = (originalInput?.value || hiddenInput.value || '').trim();
        displayText = formatStandardDateTimeForPreview(value) || value;
      }
      const wrap = document.createElement('div');
      wrap.className = 'crf-preview-field-value crf-preview-select-value';
      wrap.textContent = displayText || '\u00A0';
      row.parentNode.replaceChild(wrap, row);
    });
    // 0a2) HTML5 date/datetime (Attribute Type DateTime): replace input[type="date"] and input[type="datetime-local"] with read-only div for PM Review
    clone.querySelectorAll('input[type="date"], input[type="datetime-local"]').forEach((input) => {
      const id = input.id;
      const originalInput = id ? content.querySelector(`#${CSS.escape(id)}`) : null;
      const value = (originalInput?.value || input.value || '').trim();
      const displayText = blankAnswersAndComments ? '' : (value ? previewDateText(value) || value : '\u00A0');
      const wrap = document.createElement('div');
      wrap.className = 'crf-preview-field-value crf-preview-select-value';
      wrap.textContent = displayText || '\u00A0';
      input.parentNode?.replaceChild(wrap, input);
    });
    // 0b) Fallback: old StandardDate HTML (no .standarddate-row, just three selects _Date, _Month, _Year) – replace their parent row with one div
    clone.querySelectorAll('select[id*="standarddate_"][id$="_Date"]').forEach((sel) => {
      if (sel.closest('.standarddate-row')) return;
      const row = sel.closest('.row');
      if (!row) return;
      const dateSel = row.querySelector('select[id$="_Date"]');
      const monthSel = row.querySelector('select[id$="_Month"]');
      const yearSel = row.querySelector('select[id$="_Year"]');
      if (!dateSel || !monthSel || !yearSel) return;
      let displayText = '';
      if (!blankAnswersAndComments) {
        const baseId = sel.id.replace(/_Date$/, '');
        const origDate = content.querySelector(`#${CSS.escape(baseId + '_Date')}`);
        const origMonth = content.querySelector(`#${CSS.escape(baseId + '_Month')}`);
        const origYear = content.querySelector(`#${CSS.escape(baseId + '_Year')}`);
        const dd = (origDate?.value || dateSel.value || '').trim();
        const monthOpt = origMonth ? origMonth.options[origMonth.selectedIndex] : monthSel.options[monthSel.selectedIndex];
        const mmm = (monthOpt?.text || '').trim();
        const yyyy = (origYear?.value || yearSel.value || '').trim();
        if (dd || mmm || yyyy) displayText = formatStandardDateForPreview(dd + mmm + yyyy) || `${dd} ${mmm} ${yyyy}`.trim();
      }
      const wrap = document.createElement('div');
      wrap.className = 'crf-preview-field-value crf-preview-select-value';
      wrap.textContent = displayText || '\u00A0';
      row.parentNode.replaceChild(wrap, row);
    });
    const previewOptionTextFromInput = (inputEl, type) => {
      if (!inputEl) return '';
      const inputType = (type || '').toLowerCase();
      const nextSpanText = (inputEl.nextElementSibling?.textContent || '').replace(/\s+/g, ' ').trim();
      if (nextSpanText) return nextSpanText;
      const label = inputEl.closest('label');
      if (label) {
        // Keep only the label text, excluding nested controls.
        const cloned = label.cloneNode(true);
        cloned.querySelectorAll(`input[type="${inputType}"]`).forEach((n) => n.remove());
        const t = (cloned.textContent || '').replace(/\s+/g, ' ').trim();
        if (t) return t;
      }
      return (inputEl.value || '').trim();
    };

    // 1–2) Radio / checkbox groups: show only selected answer text (no full option list) for Visit Report preview.
    clone.querySelectorAll('.form-check-group').forEach((group) => {
      const radios = group.querySelectorAll('input[type="radio"]');
      const checkboxes = group.querySelectorAll('input[type="checkbox"]');
      const wrap = document.createElement('div');
      wrap.className = 'crf-preview-field-value crf-preview-select-value';
      if (radios.length > 0) {
        if (blankAnswersAndComments) {
          wrap.textContent = '\u00A0';
        } else {
          const sel = Array.from(radios).find((r) => r.checked);
          const t = previewOptionTextFromInput(sel, 'radio');
          wrap.textContent = t || '\u00A0';
        }
        group.parentNode.replaceChild(wrap, group);
        return;
      }
      if (checkboxes.length > 0) {
        if (blankAnswersAndComments) {
          wrap.textContent = '\u00A0';
        } else {
          const parts = Array.from(checkboxes)
            .filter((cb) => cb.checked)
            .map((cb) => previewOptionTextFromInput(cb, 'checkbox'))
            .filter(Boolean);
          wrap.textContent = parts.length ? parts.join(', ') : '\u00A0';
        }
        group.parentNode.replaceChild(wrap, group);
      }
    });
    // Fallback for radio/checkbox templates that do not render a .form-check-group wrapper
    // (e.g., Associated/legacy HTML). Convert per input-name group into one preview value.
    const replaceLooseChoiceGroups = (selector, type) => {
      const handledNames = new Set();
      clone.querySelectorAll(selector).forEach((inputEl) => {
        const name = (inputEl.getAttribute('name') || '').trim();
        if (!name || handledNames.has(name)) return;
        handledNames.add(name);
        const cloneInputs = Array.from(clone.querySelectorAll(`${selector}[name="${CSS.escape(name)}"]`));
        if (cloneInputs.length === 0) return;
        const sourceInputs = Array.from(content.querySelectorAll(`${selector}[name="${CSS.escape(name)}"]`));
        const selectedSource = type === 'radio'
          ? sourceInputs.find((i) => i.checked)
          : null;
        const selectedText = blankAnswersAndComments
          ? ''
          : (type === 'radio'
            ? previewOptionTextFromInput(selectedSource, 'radio')
            : sourceInputs
              .filter((i) => i.checked)
              .map((i) => previewOptionTextFromInput(i, 'checkbox'))
              .filter(Boolean)
              .join(', '));
        const wrap = document.createElement('div');
        wrap.className = 'crf-preview-field-value crf-preview-select-value';
        wrap.textContent = selectedText || '\u00A0';
        const container = cloneInputs[0].closest('.crf-answer-cell, .crf-answer-col, .crf-answer') || cloneInputs[0].parentNode;
        if (container) {
          cloneInputs.forEach((n) => n.remove());
          container.querySelectorAll('label').forEach((l) => {
            if (!l.querySelector('input')) l.remove();
          });
          container.innerHTML = '';
          container.appendChild(wrap);
        }
      });
    };
    replaceLooseChoiceGroups('input[type="radio"]', 'radio');
    replaceLooseChoiceGroups('input[type="checkbox"]', 'checkbox');
    // 3) Select/Combo: replace with a div styled like a dropdown showing selected value.
    // Read selected value from the original content so we show the actual selection (clone can lose selectedIndex).
    clone.querySelectorAll('select').forEach((el) => {
      let text = '';
      if (!blankAnswersAndComments) {
        const selectId = el.id || el.name;
        const originalSelect = selectId ? content.querySelector(`select[id="${CSS.escape(selectId)}"], select[name="${CSS.escape(selectId)}"]`) : null;
        const source = originalSelect || el;
        const selectedOpt = Array.from(source.options).find((o) => o.selected) || source.options[source.selectedIndex];
        if (selectedOpt) {
          const raw = (selectedOpt.textContent || selectedOpt.text || selectedOpt.value || '').trim();
          // Don't show placeholder-only as value (e.g. "Select", empty)
          if (raw && raw.toLowerCase() !== 'select') text = raw;
          else if ((selectedOpt.value || '').trim()) text = (selectedOpt.value || '').trim();
        }
      }
      replaceWithDisplayDiv(el, text || '\u00A0', 'crf-preview-field-value crf-preview-select-value');
    });
    processStsAutocapturePreviewBlocks(clone, blankAnswersAndComments);
    clone.querySelectorAll('textarea').forEach((el) => {
      if (el.closest(STS_AUTOCAPTURE_BLOCK_SEL)) return;
      const text = blankAnswersAndComments ? '' : (el.value || '').trim();
      replaceWithDisplayDiv(el, text);
    });
    clone.querySelectorAll('input').forEach((el) => {
      if (el.type === 'hidden') return;
      if (el.type === 'radio' || el.type === 'checkbox') return; // already replaced as groups
      let text = '';
      if (el.type === 'file') {
        if (!blankAnswersAndComments) {
          if (el.files && el.files[0]) {
            text = el.files[0].name;
          } else {
            const savedName = el.getAttribute('data-saved-filename');
            if (savedName) {
              text = savedName;
            } else {
              const cell = el.closest(CRF_ANSWER_REGION_SEL);
              const savedEl = cell?.querySelector('.crf-saved-filename, .file-names-list');
              const savedText = (savedEl?.textContent || '').trim().replace(/^Uploaded:\s*/i, '').trim();
              text = savedText || 'Choose File';
            }
          }
          if (!text) text = 'Choose File';
        } else {
          // Blank preview: show "Choose File" so user gets the idea, but control is replaced by read-only div
          text = 'Choose File';
        }
      } else if (!blankAnswersAndComments) {
        const raw = (el.value || '').trim();
        text = previewDateText(raw) || raw;
      }
      replaceWithDisplayDiv(el, text);
    });
    return clone.innerHTML;
  };

  const handlePreviewWith = () => {
    const withAttachments = previewAttachmentsChoice === 'with';
    const html = getPreviewHtml(false);
    setPreviewHtml(html);
    setExportedOnDisplay(formatDateTimeDDMMMYYYY(new Date()));
    setPreviewOmitWorkflowSignerFields(false);
    setIncludeOpenIssuesInPreview(true);
    setIncludeAttachmentsInPreview(withAttachments);
    setShowPreviewChoiceModal(false);
    setShowPreviewModal(true);
  };

  const handlePreviewWithout = () => {
    const html = getPreviewHtml(true);
    setPreviewHtml(html);
    setExportedOnDisplay(formatDateTimeDDMMMYYYY(new Date()));
    setPreviewOmitWorkflowSignerFields(true);
    setIncludeOpenIssuesInPreview(false);
    setIncludeAttachmentsInPreview(false);
    setOpenIssuesForReport([]);
    setShowPreviewChoiceModal(false);
    setShowPreviewModal(true);
  };

  const closePreviewModal = () => {
    // Prevent closing while PDF generation is in progress.
    if (pdfDownloading) return;
    if (previewLayoutRestoreRef.current) {
      restoreVisitReportPreviewLayout(previewLayoutRestoreRef.current);
      previewLayoutRestoreRef.current = null;
    }
    previewAttachmentObjectUrlsRef.current.forEach((u) => {
      try {
        URL.revokeObjectURL(u);
      } catch (_) {
        /* ignore */
      }
    });
    previewAttachmentObjectUrlsRef.current = [];
    setShowPreviewModal(false);
    setPreviewHtml('');
    setPreviewOmitWorkflowSignerFields(false);
    setPdfDownloading(false);
    resetPdfProgress();
    setOpenIssuesForReport([]);
    setIncludeAttachmentsInPreview(false);
    setPreviewAttachmentAppendixItems([]);
    setPreviewAttachmentsFetchError('');
  };

  const updatePdfProgress = (percent, label) => {
    setPdfProgressPercent(Math.max(0, Math.min(100, Number(percent) || 0)));
    setPdfProgressLabel(label || '');
  };

  const resetPdfProgress = () => {
    updatePdfProgress(0, '');
  };

  const handleDownloadPreviewPdf = () => {
    const element = previewContentRef.current;
    if (!element) return;
    setPdfDownloading(true);
    updatePdfProgress(5, 'Preparing report...');

    const filename = 'Visit-Report-Preview.pdf';

    (async () => {
      const stsWrapRestore = [];
      let pdfStsTableRestore = [];
      try {
        updatePdfProgress(10, 'Preparing report layout...');
        element.classList.add('crf-report-pdf-capture');
        element.querySelectorAll('.sts-autocapture-table-wrap').forEach((wrap) => {
          stsWrapRestore.push({
            el: wrap,
            maxHeight: wrap.style.maxHeight,
            overflow: wrap.style.overflow,
            overflowX: wrap.style.overflowX,
            overflowY: wrap.style.overflowY,
          });
          wrap.style.maxHeight = 'none';
          wrap.style.overflow = 'visible';
          wrap.style.overflowX = 'visible';
          wrap.style.overflowY = 'visible';
        });
        pdfStsTableRestore = applyPdfStsTableColumnFit(element);
        await new Promise((r) => {
          requestAnimationFrame(() => {
            requestAnimationFrame(r);
          });
        });

        try {
          updatePdfProgress(35, 'Rendering PDF...');
          let serverPdfBlob = await renderVisitReportPdfOnServer(element);
          let serverPdfBlobsToAppend = [];
          if (includeAttachmentsInPreview) {
            updatePdfProgress(70, 'Collecting attachments...');
            const contentRoot = crfContainerRef.current?.querySelector('.crf-generated-content');
            const desc = contentRoot ? collectCrfUploadedAttachmentDescriptors(contentRoot) : [];
            for (let b = 0; b < desc.length; b += 1) {
              try {
                const blob = await fetchCrfFileBlob(desc[b].path);
                if (classifyAttachmentForPreview(blob, desc[b].filename) === 'pdf') {
                  serverPdfBlobsToAppend.push(blob);
                }
              } catch (_) {
                /* skip unreadable attachment */
              }
            }
          }
          if (serverPdfBlobsToAppend.length > 0) {
            updatePdfProgress(85, 'Merging attachments...');
            const mergedBytes = await appendPdfBlobsToPdfBytes(await serverPdfBlob.arrayBuffer(), serverPdfBlobsToAppend);
            serverPdfBlob = new Blob([mergedBytes], { type: 'application/pdf' });
          }
          updatePdfProgress(95, 'Finalizing download...');
          updatePdfProgress(100, 'Download ready.');
          await new Promise((r) => requestAnimationFrame(r));
          downloadBlob(serverPdfBlob, filename);
          return;
        } catch (serverPdfErr) {
          console.warn('[VisitReport PDF] Puppeteer rendering failed.', serverPdfErr);
          throw serverPdfErr;
        }
      } catch (err) {
        console.error('[VisitReport PDF]', err);
        const message = await getVisitReportPdfErrorMessage(err);
        setToast({ type: 'error', message });
      } finally {
        restorePdfStsTableColumnFit(pdfStsTableRestore);
        stsWrapRestore.forEach(({ el, maxHeight, overflow, overflowX, overflowY }) => {
          if (!el) return;
          el.style.maxHeight = maxHeight;
          el.style.overflow = overflow;
          el.style.overflowX = overflowX;
          el.style.overflowY = overflowY;
        });
        element.classList.remove('crf-report-pdf-capture');
        setPdfDownloading(false);
        resetPdfProgress();
      }
    })();
  };

  useEffect(() => {
    if (!showPreviewModal || !previewHtml) {
      return undefined;
    }
    let cancelled = false;
    const applyLayout = () => {
      if (cancelled) return;
      const el = previewContentRef.current;
      if (!el) return;
      if (previewLayoutRestoreRef.current) {
        restoreVisitReportPreviewLayout(previewLayoutRestoreRef.current);
      }
      previewLayoutRestoreRef.current = prepareVisitReportPreviewLayout(el);
    };
    const rafId = requestAnimationFrame(() => {
      requestAnimationFrame(applyLayout);
    });
    return () => {
      cancelled = true;
      cancelAnimationFrame(rafId);
    };
  }, [showPreviewModal, previewHtml]);

  useEffect(() => {
    if (showPreviewModal) return undefined;
    if (previewLayoutRestoreRef.current) {
      restoreVisitReportPreviewLayout(previewLayoutRestoreRef.current);
      previewLayoutRestoreRef.current = null;
    }
    return undefined;
  }, [showPreviewModal]);

  useEffect(() => {
    const sidFromProp = defaultSiteIdForActionItem != null && String(defaultSiteIdForActionItem).trim() !== ''
      ? Number(defaultSiteIdForActionItem)
      : null;
    if (sidFromProp != null && Number.isFinite(sidFromProp) && sidFromProp > 0) {
      setResolvedSiteIdForAppendix((prev) => (prev != null && prev > 0 ? prev : sidFromProp));
    }
  }, [defaultSiteIdForActionItem]);

  useEffect(() => {
    if (!showPreviewModal || !includeOpenIssuesInPreview || !visitId) {
      if (!includeOpenIssuesInPreview) {
        setOpenIssuesForReport([]);
      }
      setOpenIssuesLoading(false);
      return;
    }

    let siteId = resolvedSiteIdForAppendix != null && resolvedSiteIdForAppendix > 0 ? resolvedSiteIdForAppendix : null;
    if (siteId == null && defaultSiteIdForActionItem != null && String(defaultSiteIdForActionItem).trim() !== '') {
      const n = Number(defaultSiteIdForActionItem);
      if (Number.isFinite(n) && n > 0) siteId = n;
    }
    const projectCode = (formData.protocolNo || '').trim();
    if (!siteId || siteId <= 0 || !projectCode) {
      setOpenIssuesForReport([]);
      setOpenIssuesLoading(false);
      return;
    }

    let cancelled = false;
    setOpenIssuesLoading(true);
    const load = async () => {
      try {
        const visitIdNum = parseInt(visitId, 10);
        const params = { siteId, projectCode };
        if (Number.isFinite(visitIdNum) && visitIdNum > 0) {
          params.scheduledVisitId = visitIdNum;
        }
        const res = await api.get('/crf/visit-report-site-appendix', {
          params,
        });
        if (cancelled) return;
        const data = res.data ?? {};
        setOpenIssuesForReport(Array.isArray(data.openIssues) ? data.openIssues : []);
      } catch (_) {
        if (!cancelled) {
          setOpenIssuesForReport([]);
        }
      } finally {
        if (!cancelled) setOpenIssuesLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [showPreviewModal, includeOpenIssuesInPreview, visitId, resolvedSiteIdForAppendix, defaultSiteIdForActionItem, formData.protocolNo]);

  useEffect(() => {
    const revokeAll = () => {
      previewAttachmentObjectUrlsRef.current.forEach((u) => {
        try {
          URL.revokeObjectURL(u);
        } catch (_) {
          /* ignore */
        }
      });
      previewAttachmentObjectUrlsRef.current = [];
    };

    if (!showPreviewModal || !includeAttachmentsInPreview) {
      revokeAll();
      setPreviewAttachmentAppendixItems([]);
      setPreviewAttachmentsLoading(false);
      setPreviewAttachmentsFetchError('');
      return undefined;
    }

    const content = crfContainerRef.current?.querySelector('.crf-generated-content');
    if (!content) {
      setPreviewAttachmentAppendixItems([]);
      setPreviewAttachmentsLoading(false);
      return undefined;
    }

    let cancelled = false;
    setPreviewAttachmentsLoading(true);
    setPreviewAttachmentsFetchError('');

    (async () => {
      try {
        revokeAll();
        const desc = collectCrfUploadedAttachmentDescriptors(content);
        if (!desc.length) {
          if (!cancelled) {
            setPreviewAttachmentAppendixItems([]);
            setPreviewAttachmentsLoading(false);
          }
          return;
        }
        const items = [];
        const urls = [];
        for (let di = 0; di < desc.length; di += 1) {
          const d = desc[di];
          const blob = await fetchCrfFileBlob(d.path);
          if (cancelled) return;
          const kind = classifyAttachmentForPreview(blob, d.filename);
          let objectUrl = null;
          if (kind === 'image' || kind === 'pdf') {
            objectUrl = URL.createObjectURL(blob);
            urls.push(objectUrl);
          }
          items.push({
            path: d.path,
            filename: d.filename,
            kind,
            objectUrl,
          });
        }
        if (!cancelled) {
          previewAttachmentObjectUrlsRef.current = urls;
          setPreviewAttachmentAppendixItems(items);
        }
      } catch (e) {
        if (!cancelled) {
          setPreviewAttachmentsFetchError(e.message || 'Failed to load attachments');
          setPreviewAttachmentAppendixItems([]);
        }
      } finally {
        if (!cancelled) setPreviewAttachmentsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      revokeAll();
    };
  }, [showPreviewModal, includeAttachmentsInPreview]);

  useLayoutEffect(() => {
    if (!htmlContent) return;

    // Use visitUiStageLabel (the stage tab user opened from) for DCF resolution gate;
    // falls back to actual header stage when not embedded from a specific review lane.
    const headerStageForOpenDcfGate = String(
      visitUiStageLabel ||
      getAnyCase(workflowHeader, 'vStage', 'VStage', 'stage', 'Stage') || crfStage || ''
    ).trim();
    const apiHeaderStageInProgress = pageHeaderStageInProgress === true;

    // Completed read-only visit report (VStage Completed): suppress yellow DCF row highlight.
    const suppressDcfVisitRowYellow =
      !!props.readOnly && String(crfStage != null ? crfStage : '').trim().toLowerCase() === 'completed';

    let rafId;
    const wireBackendDcfAuditButtons = (c) => {
      if (!c) return;
      // Formula Calc buttons: replace inline onclick with data attributes so we can run calculation in React with correct content scope
      const calcBtns = c.querySelectorAll('button.btnCalc');
      calcBtns.forEach((btn) => {
        const onclick = (btn.getAttribute('onclick') || '').trim();
        const match = onclick.match(/Calculate\s*\(\s*true\s*,\s*'([^']*)'\s*,\s*'([^']*)'\s*,\s*'([^']*)'\s*\)\s*;?/);
        if (match) {
          const [, formula, formulaId, decimalNo] = match;
          btn.setAttribute('data-formula', formula || '');
          btn.setAttribute('data-formula-id', formulaId || '');
          btn.setAttribute('data-decimal-no', decimalNo != null ? String(decimalNo) : '0');
          btn.removeAttribute('onclick');
        }
        // Disable Calc button outside In Progress stage (e.g. First/Second Review and later stages)
        if (props.readOnly || props.reviewMode || isViewOnly) {
          btn.disabled = true;
          btn.setAttribute('aria-disabled', 'true');
        } else {
          btn.disabled = false;
          btn.removeAttribute('aria-disabled');
        }
      });

      // console.log('[CRFForm] Wiring DCF buttons. SavedFields:', savedFields.length); // Debug
      const dcfEls = c.querySelectorAll('[id*="DcfBtn"], [id*="dcfBtn"], [class*="DcfBtn"], [class*="dcfBtn"], [class*="fieldDcfBtn"], [data-crf-action="dcf"]');
      dcfEls.forEach((el) => {
        if (!el.hasAttribute('data-crf-action')) el.setAttribute('data-crf-action', 'dcf');
        setReviewerCommentTooltip(el);

        // DCF field highlighting: yellow background when DCF is raised (Open), and keep yellow when Resolved or Closed so users see DCF-generated fields
        const stsBlock = el.closest?.(STS_AUTOCAPTURE_BLOCK_SEL);
        const row = stsBlock || el.closest('.crf-report-row');
        if (row) {
          let fieldId = '';
          if (stsBlock) {
            fieldId = (stsBlock.getAttribute('data-field-id') || stsBlock.getAttribute('data-medex-code') || '').trim();
          } else {
            const input = getPrimaryCrfAnswerControlForRow(row);
            if (input) {
              fieldId = input.getAttribute('data-field-id') || '';
              if (!fieldId) {
                const rawId = input.id || input.name || '';
                fieldId = extractFieldCode(rawId);
              }
            }
          }
          const rowDtlNo = row.getAttribute('data-n-crf-dtl-no');
          const nCRFDtlNoForRow = rowDtlNo != null && rowDtlNo !== '' ? Number(rowDtlNo) : null;

          // Lookup field: prefer nCRFDtlNo (reliable after refresh), then fieldId
          let field = null;
          if (nCRFDtlNoForRow != null && !isNaN(nCRFDtlNoForRow)) {
            field = (savedFields || []).find(f => Number(f.nCRFDtlNo ?? f.NCRFDtlNo ?? f.ncrfDtlNo ?? 0) === nCRFDtlNoForRow);
          }
          if (!field && fieldId) {
            field = (savedFields || []).find(f => {
              const fId = (f.vFieldId || f.VFieldId || '').trim();
              return fId === fieldId || fId.toLowerCase() === fieldId.toLowerCase();
            });
          }

          if (field) {
            const statusRaw = field.vDCFStatus ?? field.VDCFStatus ?? null;
            const status = (statusRaw || '').trim().toLowerCase();
            // Yellow for any DCF-marked row so reviewer-raised DCFs remain visible across lanes (suppressed on Completed read-only report).
            const hasDcfYellow =
              !suppressDcfVisitRowYellow &&
              (status === 'open' ||
                status === 'resolved' ||
                status === 'closed');

            const resetStyles = (element) => {
              element.classList.remove('text-danger', 'text-warning', 'text-success', 'text-secondary');
              element.style.removeProperty('color');
              element.style.removeProperty('border-color');
              element.style.removeProperty('background-color');
            };

            resetStyles(el);
            const icon = el.querySelector('i');
            if (icon) resetStyles(icon);

            // Yellow background for answer input and comments cell when field has DCF (raised, resolved, or closed)
            const inputEl = stsBlock
              ? row.querySelector('.sts-autocapture-cell-input')
              : row.querySelector('.crf-answer-cell input, .crf-answer-cell select, .crf-answer-cell textarea, .crf-answer-col input, .crf-answer-col select, .crf-answer-col textarea');
            const answerCell = stsBlock
              ? row.querySelector('.crf-sts-answer-row, .crf-sts-autocapture-table-strip')
              : row.querySelector('.crf-answer-cell, .crf-answer-col');
            const commentsCell = stsBlock
              ? row.querySelector('.crf-sts-autocapture-comments')
              : row.querySelector(CRF_COMMENT_REGION_SEL);
            const dcfYellowBg = '#fff9c4';
            const dcfYellowBorder = '#f0d000';

            if (hasDcfYellow) {
              if (inputEl) {
                inputEl.style.backgroundColor = dcfYellowBg;
                inputEl.style.borderColor = dcfYellowBorder;
                inputEl.style.borderWidth = '1px';
              }
              if (answerCell) {
                answerCell.style.backgroundColor = dcfYellowBg;
              }
              if (commentsCell) {
                commentsCell.classList.add('crf-comments-dcf-highlight');
              }
              if (stsBlock) {
                row.querySelectorAll('.sts-autocapture-cell-input').forEach((inp) => {
                  inp.style.backgroundColor = dcfYellowBg;
                  inp.style.borderColor = dcfYellowBorder;
                  inp.style.borderWidth = '1px';
                });
              }
            } else {
              if (inputEl) {
                inputEl.style.removeProperty('background-color');
                inputEl.style.removeProperty('border-color');
                inputEl.style.removeProperty('border-width');
              }
              if (answerCell) answerCell.style.removeProperty('background-color');
              if (commentsCell) {
                commentsCell.classList.remove('crf-comments-dcf-highlight');
                commentsCell.style.backgroundColor = 'white';
              }
              if (stsBlock) {
                row.querySelectorAll('.sts-autocapture-cell-input').forEach((inp) => {
                  inp.style.removeProperty('background-color');
                  inp.style.removeProperty('border-color');
                  inp.style.removeProperty('border-width');
                });
              }
            }

            if (status === 'open') {
              el.classList.add('text-danger');
              if (icon) icon.classList.add('text-danger');
              el.style.setProperty('color', '#dc3545', 'important');
              if (icon) icon.style.setProperty('color', '#dc3545', 'important');
            } else if (status === 'resolved') {
              el.classList.add('text-warning');
              if (icon) icon.classList.add('text-warning');
              el.style.setProperty('color', '#ffc107', 'important');
              if (icon) icon.style.setProperty('color', '#ffc107', 'important');
            } else if (status === 'closed') {
              el.classList.add('text-success');
              if (icon) icon.classList.add('text-success');
              el.style.setProperty('color', '#198754', 'important');
              if (icon) icon.style.setProperty('color', '#198754', 'important');
            }
          }
        }
      });

      // Apply yellow background to any row whose field has Open/Resolved/Closed DCF when not suppressed (not dependent on DCF button selector)
      const dcfYellowBg = '#fff9c4';
      const dcfYellowBorder = '#f0d000';
      const allRows = c.querySelectorAll('.crf-report-row, .crf-sts-autocapture-block');
      allRows.forEach((row) => {
        const isStsBlock = row.matches?.(STS_AUTOCAPTURE_BLOCK_SEL);
        const rowDtlNo = row.getAttribute('data-n-crf-dtl-no');
        const nCRFDtlNoForRow = rowDtlNo != null && rowDtlNo !== '' ? Number(rowDtlNo) : null;
        let fieldId = '';
        if (isStsBlock) {
          fieldId = (row.getAttribute('data-field-id') || row.getAttribute('data-medex-code') || '').trim();
        } else {
          const input = getPrimaryCrfAnswerControlForRow(row);
          if (input) {
            fieldId = (input.getAttribute('data-field-id') || '').trim();
            if (!fieldId && (input.id || input.name)) fieldId = extractFieldCode(input.id || input.name);
          }
        }
        let field = null;
        if (nCRFDtlNoForRow != null && !isNaN(nCRFDtlNoForRow)) {
          field = (savedFields || []).find(f => Number(f.nCRFDtlNo ?? f.NCRFDtlNo ?? f.ncrfDtlNo ?? 0) === nCRFDtlNoForRow);
        }
        if (!field && fieldId) {
          field = (savedFields || []).find(f => {
            const fId = (f.vFieldId || f.VFieldId || '').trim();
            return fId === fieldId || fId.toLowerCase() === fieldId.toLowerCase();
          });
        }
        if (field) {
          const statusRaw = field.vDCFStatus ?? field.VDCFStatus ?? null;
          const status = (statusRaw || '').trim().toLowerCase();
          const hasDcfYellow =
            !suppressDcfVisitRowYellow &&
            (status === 'open' ||
              status === 'resolved' ||
              status === 'closed');
          const inputEl = isStsBlock
            ? row.querySelector('.sts-autocapture-cell-input')
            : row.querySelector('.crf-answer-cell input, .crf-answer-cell select, .crf-answer-cell textarea, .crf-answer-col input, .crf-answer-col select, .crf-answer-col textarea');
          const answerCell = isStsBlock
            ? row.querySelector('.crf-sts-answer-row, .crf-sts-autocapture-table-strip')
            : row.querySelector('.crf-answer-cell, .crf-answer-col');
          const commentsCell = isStsBlock
            ? row.querySelector('.crf-sts-autocapture-comments')
            : row.querySelector(CRF_COMMENT_REGION_SEL);
          if (hasDcfYellow) {
            if (inputEl) {
              inputEl.style.setProperty('background-color', dcfYellowBg, 'important');
              inputEl.style.setProperty('border-color', dcfYellowBorder, 'important');
              inputEl.style.setProperty('border-width', '1px', 'important');
            }
            if (answerCell) answerCell.style.setProperty('background-color', dcfYellowBg, 'important');
            if (commentsCell) {
              commentsCell.classList.add('crf-comments-dcf-highlight');
            }
            if (isStsBlock) {
              row.querySelectorAll('.sts-autocapture-cell-input').forEach((inp) => {
                inp.style.setProperty('background-color', dcfYellowBg, 'important');
                inp.style.setProperty('border-color', dcfYellowBorder, 'important');
                inp.style.setProperty('border-width', '1px', 'important');
              });
            }
          } else {
            if (inputEl) {
              inputEl.style.removeProperty('background-color');
              inputEl.style.removeProperty('border-color');
              inputEl.style.removeProperty('border-width');
            }
            if (answerCell) answerCell.style.removeProperty('background-color');
            if (commentsCell) {
              commentsCell.classList.remove('crf-comments-dcf-highlight');
              commentsCell.style.backgroundColor = 'white';
            }
            if (isStsBlock) {
              row.querySelectorAll('.sts-autocapture-cell-input').forEach((inp) => {
                inp.style.removeProperty('background-color');
                inp.style.removeProperty('border-color');
                inp.style.removeProperty('border-width');
              });
            }
          }
        }
      });

      const auditEls = c.querySelectorAll('[id*="auditBtn"], [id*="AuditBtn"], [class*="auditBtn"], [class*="fieldauditBtn"]');
      auditEls.forEach((el) => { el.setAttribute('data-crf-action', 'audit'); el.setAttribute('title', 'View Audit'); });

      // Keep row-action icons visible so DCF and Issues are always available from Visit Report.
      const hideRowTxn = false;
      c.querySelectorAll('.crf-row-actions').forEach((wrap) => {
        if (hideRowTxn) {
          wrap.setAttribute('data-crf-visit-ro-hidden', '1');
          wrap.style.display = 'none';
        } else if (wrap.getAttribute('data-crf-visit-ro-hidden') === '1') {
          wrap.removeAttribute('data-crf-visit-ro-hidden');
          wrap.style.removeProperty('display');
        }
      });
    };

    // CRA with open DCF: all comments visible (no hyphen placeholder); lock comments except on rows with Open DCF (IsEditable).
    const hideCommentsForNonDcfFieldsWhenCra = (content) => {
      if (!content) return;
      const rows = content.querySelectorAll('.crf-report-row');
      const releasedOpenDcfInSavedFields =
        Array.isArray(savedFields) &&
        savedFields.some((f) => {
          if (!isFieldDcfOpen(f)) return false;
          const sub = f.iSubmitDCFToCRA ?? f.ISubmitDCFToCRA ?? f.isubmitDCFToCRA;
          return sub === 1 || sub === '1' || sub === true;
        });
      const craReleasedForUi = hasOpenDcfReleasedToCra || releasedOpenDcfInSavedFields;
      const craWithOpenDcf = isCraUser && craReleasedForUi && savedFields?.length > 0;

      const ensureCommentsVisibleAndReadOnly = (cell, row) => {
        if (!cell) return;
        cell.classList.remove('crf-comments-hidden-for-cra');
        cell.querySelector('.crf-comments-placeholder')?.remove();
        const ta = cell.querySelector('textarea, input[type="text"]');
        if (ta) {
          ta.style.removeProperty('display');
          const field = row ? findSavedFieldForCrfReportRow(row, savedFields) : null;
          const isEditableOpenDcf =
            field &&
            (field.isEditable === true || field.IsEditable === true) &&
            hasOpenDCF &&
            (!isReviewStage || (isCraUser && craReleasedForUi)) &&
            openDcfResolutionAllowedAtHeaderStage(
              field,
              headerStageForOpenDcfGate,
              crfCurrentStageCode,
              apiHeaderStageInProgress
            );
          if (craWithOpenDcf && !isEditableOpenDcf) {
            ta.setAttribute('readonly', 'readonly');
            ta.setAttribute('disabled', 'disabled');
          } else {
            ta.removeAttribute('readonly');
            ta.removeAttribute('disabled');
          }
        }
      };

      if (!craWithOpenDcf) {
        rows.forEach((row) => ensureCommentsVisibleAndReadOnly(row.querySelector(CRF_COMMENT_REGION_SEL), row));
        return;
      }

      // CRA + open DCF: show all comments (no "—"); readonly except where server marked the row IsEditable (open DCF correction).
      rows.forEach((row) => {
        if (row.classList.contains('crf-report-row-label-only')) {
          const commentsCell = row.querySelector(CRF_COMMENT_REGION_SEL);
          if (commentsCell) commentsCell.classList.add('crf-comments-hidden-for-cra');
          return;
        }
        ensureCommentsVisibleAndReadOnly(row.querySelector(CRF_COMMENT_REGION_SEL), row);
      });
    };

    const processContent = (content) => {
      setStsRowSelectionContext({
        readOnly: props.readOnly,
        isViewOnly,
        crfStage,
      });
      removeUnwantedElements(content);
      // Cached/old CRF HTML may still use <label for="..."> on file fields; that opens the picker when clicking the question.
      content.querySelectorAll('label[for]').forEach((lbl) => {
        const fid = lbl.getAttribute('for');
        if (!fid) return;
        let inp;
        try {
          inp = content.querySelector(`#${CSS.escape(fid)}`);
        } catch {
          return;
        }
        if (!inp || inp.type !== 'file') return;
        lbl.removeAttribute('for');
        const lid = lbl.id || `${fid}_label`;
        if (!lbl.id) lbl.id = lid;
        if (!inp.getAttribute('aria-labelledby')) inp.setAttribute('aria-labelledby', lid);
      });
      updateSubGroupHeaders(content);
      const visitReportViewOnly = !!(props.readOnly || isViewOnly);
      transformToReportFormat(content, crfActionHandlersRef, fieldIdToDtlNo, savedFields, canRaiseActionItemFromServer, visitReportViewOnly);
      expandVisitReportTemplateSubGroups(content);
      applyActionItemRowIconHighlights(content, savedFields);
      wireBackendDcfAuditButtons(content);
      const result = populateFormFromSavedData(content);
      applyVisitReportFillHintIcons(content, savedFields);

      // Associated visibility depends on driver radios populated above; run once synchronously so branch rows hide
      // before async stage fetch (matches API ResolveAssociatedActiveBranchKey once saved driver value is bound).
      if (!isPreviewInNewTab) {
        const reviewTierSubmitLockedEarly =
          isReviewStage && String(crfCSaveType || '').trim().toUpperCase() === 'E';
        applyAssociatedFieldVisibility(content, [], {
          savedFields,
          crfStage,
          crfCurrentStageCode,
          isReviewStage,
          readOnly: props.readOnly,
          isViewOnly,
          allowReviewAssociatedEdit: isReviewStage && !reviewTierSubmitLockedEarly,
          enableFieldsByCodes,
          serverInactiveChildFieldCodes: associatedInactiveChildFieldCodesRef.current,
          hasOpenDCF,
          headerStageForOpenDcf: headerStageForOpenDcfGate,
          pageHeaderStageInProgress,
        });
      }

      // When user opens blank preview (template in new tab, no report): clear file URLs and make file inputs visible but not clickable
      if (isPreviewInNewTab) {
        content.querySelectorAll('.file-names-list, .crf-saved-filename').forEach((el) => {
          el.innerHTML = '';
          el.textContent = '';
        });
        content.querySelectorAll('input[type="file"]').forEach((input) => {
          input.removeAttribute('data-saved-filename');
          input.removeAttribute('data-uploaded-path');
          input.disabled = true;
          input.classList.add('crf-preview-file-disabled');
          input.setAttribute('aria-label', 'Choose File (preview only, not clickable)');
        });
      }

      // Sync StandardDate text input value to DD/MMM/YYYY dropdowns (for display and when loading saved data)
      content.querySelectorAll('input.standarddate-text').forEach((input) => {
        const baseId = input.id;
        if (!baseId) return;
        const dateSel = content.querySelector(`#${CSS.escape(baseId + '_Date')}`);
        const monthSel = content.querySelector(`#${CSS.escape(baseId + '_Month')}`);
        const yearSel = content.querySelector(`#${CSS.escape(baseId + '_Year')}`);
        if (!dateSel || !monthSel || !yearSel) return;
        const v = (input.value || '').trim();
        let dd = '', mmm = '', yyyy = '';
        const savedFormat = v.match(/^(.{2})-([A-Za-z]{3})-(\d{4})$/);
        if (savedFormat) {
          [, dd, mmm, yyyy] = savedFormat;
        } else {
          const withUKUK = v.match(/^([^-]+)-([^-]+)-([^\s-]+)$/);
          if (withUKUK) [, dd, mmm, yyyy] = withUKUK;
          else {
            const rawMatch = v.match(/^(\d{2})([A-Za-z]{3})(\d{4})?$/);
            if (rawMatch) [, dd, mmm, yyyy] = rawMatch;
          }
        }
        if (dd || mmm || yyyy) {
          if (dd) {
            const ddNorm = (dd || '').trim().toUpperCase();
            for (let i = 0; i < dateSel.options.length; i++) {
              const optText = (dateSel.options[i].text || '').trim().toUpperCase();
              if (optText === ddNorm || dateSel.options[i].value === dd) {
                dateSel.value = dateSel.options[i].value;
                break;
              }
            }
          }
          if (mmm) {
            for (let i = 0; i < monthSel.options.length; i++) {
              if ((monthSel.options[i].text || '').trim().toUpperCase() === (mmm || '').toUpperCase()) {
                monthSel.value = monthSel.options[i].value;
                break;
              }
            }
          }
          if (yyyy) {
            const yyyyUpper = (yyyy || '').trim().toUpperCase();
            const opt = Array.from(yearSel.options).find(o => {
              const t = (o.text || '').trim().toUpperCase();
              const val = (o.value || '').trim();
              return t === yyyyUpper || val === yyyy || (yyyyUpper === 'UKUK' && (t === 'UNK' || val === '0000')) || (yyyyUpper === 'UNK' && (t === 'UKUK' || val === '0000'));
            });
            if (opt) yearSel.value = opt.value; else yearSel.value = yyyy;
          }
        }
      });

      // Sync StandardDateTime hidden input value to DD/MMM/YYYY/HH/MM dropdowns (when loading saved data)
      content.querySelectorAll('input.standarddatetime-text').forEach((input) => {
        const baseId = input.id;
        if (!baseId) return;
        const dateSel = content.querySelector(`#${CSS.escape(baseId + '_Date')}`);
        const monthSel = content.querySelector(`#${CSS.escape(baseId + '_Month')}`);
        const yearSel = content.querySelector(`#${CSS.escape(baseId + '_Year')}`);
        const hourSel = content.querySelector(`#${CSS.escape(baseId + '_Hour')}`);
        const minuteSel = content.querySelector(`#${CSS.escape(baseId + '_Minute')}`);
        if (!dateSel || !monthSel || !yearSel || !hourSel || !minuteSel) return;
        const v = (input.value || '').trim();
        let dd = '', mmm = '', yyyy = '', hh = '', mm = '';
        const savedFormat = v.match(/^(.{2})-([A-Za-z]{3})-(\d{4})\s+(\d{2}):(\d{2})$/);
        if (savedFormat) {
          [, dd, mmm, yyyy, hh, mm] = savedFormat;
        } else {
          const withUKUK = v.match(/^([^-]+)-([^-]+)-([^\s-]+)\s+([^:]+):([^:]+)$/);
          if (withUKUK) [, dd, mmm, yyyy, hh, mm] = withUKUK;
          else {
            const rawMatch = v.match(/^(\d{2})([A-Za-z]{3})(\d{4})(\d{2})(\d{2})$/);
            if (rawMatch) [, dd, mmm, yyyy, hh, mm] = rawMatch;
          }
        }
        if (dd || mmm || yyyy || hh || mm) {
          if (dd) {
            const ddNorm = (dd || '').trim().toUpperCase();
            for (let i = 0; i < dateSel.options.length; i++) {
              const optText = (dateSel.options[i].text || '').trim().toUpperCase();
              if (optText === ddNorm || dateSel.options[i].value === dd) {
                dateSel.value = dateSel.options[i].value;
                break;
              }
            }
          }
          if (mmm) {
            for (let i = 0; i < monthSel.options.length; i++) {
              if ((monthSel.options[i].text || '').trim().toUpperCase() === (mmm || '').toUpperCase()) {
                monthSel.value = monthSel.options[i].value;
                break;
              }
            }
          }
          if (yyyy) {
            const yyyyUpper = (yyyy || '').trim().toUpperCase();
            const opt = Array.from(yearSel.options).find(o => {
              const t = (o.text || '').trim().toUpperCase();
              const val = (o.value || '').trim();
              return t === yyyyUpper || val === yyyy || (yyyyUpper === 'UKUK' && (t === 'UNK' || val === '0000')) || (yyyyUpper === 'UNK' && (t === 'UKUK' || val === '0000'));
            });
            if (opt) yearSel.value = opt.value; else yearSel.value = yyyy;
          }
          if (hh) hourSel.value = hh;
          if (mm) minuteSel.value = mm;
        }
      });

      // CRA filling DCF: show remarks only on DCF-generated fields; hide for rest
      hideCommentsForNonDcfFieldsWhenCra(content);

      // Permission Logic:
      // 0. Strict Read-Only Mode (e.g. Dual Visibility for Reviewer, or Viewer profile / IsViewOnly from server)
      if (props.readOnly || isViewOnly) {
        if (!isPreviewInNewTab) {
          const pcRo = auditProjectCode || (formData.protocolNo || '').trim() || undefined;
          const runRo = (list) =>
            applyAssociatedFieldVisibility(content, Array.isArray(list) ? list : [], {
              savedFields,
              crfStage,
              crfCurrentStageCode,
              isReviewStage,
              readOnly: true,
              isViewOnly,
              allowReviewAssociatedEdit: false,
              enableFieldsByCodes,
              serverInactiveChildFieldCodes: associatedInactiveChildFieldCodesRef.current,
              hasOpenDCF,
              headerStageForOpenDcf: headerStageForOpenDcfGate,
              pageHeaderStageInProgress,
            });
          void stageService.getStagesForCrFAssociation(pcRo).then(runRo).catch(() => runRo([]));
        }
        setFieldsEnabled(content, false);
        applyCraResolvedDcfCommentHidingToContent(content, savedFields, isCraUser, props.readOnly, isViewOnly, isReviewStage);
        // In review stage, do NOT replace controls with static text - just disable them.
        // This preserves the visual layout when reviewer's readOnly state changes (e.g., after Submit All to CRA).
        // Only apply static view transformation for truly view-only scenarios (isViewOnly from server) or preview mode.
        if (isViewOnly || isPreviewInNewTab) {
          applyCrfReportAllRowsVisitReadOnlyStaticView(content);
        }
        return result; // Skip all other logic
      }

      // 1. If in Review Stage OR if there are Open DCFs (In Progress Correction), default to DISABLED (locked).
      // 2. If not in Review Stage and no Open DCFs, default to ENABLED.
      // 3. Then, specific fields can override this if IsEditable is strictly set (e.g. Open DCF).

      const reviewTierSubmitLocked =
        isReviewStage && String(crfCSaveType || '').trim().toUpperCase() === 'E';

      const releasedOpenDcfInSavedFieldsForAnswers =
        Array.isArray(savedFields) &&
        savedFields.some((f) => {
          if (!isFieldDcfOpen(f)) return false;
          const sub = f.iSubmitDCFToCRA ?? f.ISubmitDCFToCRA ?? f.isubmitDCFToCRA;
          return sub === 1 || sub === '1' || sub === true;
        });
      const craReleasedForAnswerEnable = hasOpenDcfReleasedToCra || releasedOpenDcfInSavedFieldsForAnswers;

      if (isReviewStage || hasOpenDCF) {
        // Default: Disable all
        setFieldsEnabled(content, false);

        const allowReviewAssociatedEdit = isReviewStage && !reviewTierSubmitLocked;
        if (isReviewStage && allowReviewAssociatedEdit) {
          const editableAssociatedChildCodes = getEditableAssociatedChildCodes(
            content,
            savedFields,
            crfCurrentStageCode,
            crfStage,
            []
          );
          mergeOpenDcfEditableAssociatedBranchChildCodes(
            content,
            savedFields,
            hasOpenDCF,
            editableAssociatedChildCodes,
            headerStageForOpenDcfGate,
            crfCurrentStageCode,
            apiHeaderStageInProgress
          );
          enableFieldsByCodes(content, editableAssociatedChildCodes);
        }

        // Master gate: if API marks any Open DCF row as IsEditable, allow this loop even in review stage.
        // This keeps server-side IsEditable authoritative for branch/general routing.
        const hasServerEditableOpenDcf =
          Array.isArray(savedFields) &&
          savedFields.some((f) => (f.isEditable === true || f.IsEditable === true) && isFieldDcfOpen(f));
        // Enable by IsEditable for In Progress correction (!isReviewStage), CRA after Submit All to CRA (released),
        // explicit API in-progress signal, or server-authorized Open DCF at review stage.
        if (
          !reviewTierSubmitLocked &&
          hasOpenDCF &&
          (!isReviewStage ||
            (isCraUser && craReleasedForAnswerEnable) ||
            apiHeaderStageInProgress ||
            hasServerEditableOpenDcf) &&
          savedFields &&
          savedFields.length > 0
        ) {
          savedFields.forEach(f => {
            // Check both lowercase and uppercase variations
            const isEditable = f.isEditable === true || f.IsEditable === true;
            if (
              isEditable &&
              (!isFieldDcfOpen(f) ||
                openDcfResolutionAllowedAtHeaderStage(
                  f,
                  headerStageForOpenDcfGate,
                  crfCurrentStageCode,
                  apiHeaderStageInProgress
                ))
            ) {
              const vFieldId = (f.vFieldId || f.VFieldId || '').trim();
              if (!vFieldId) return;

              // Find inputs for this field and enable them
              const inputs = content.querySelectorAll(`
                        [data-field-id="${vFieldId}"], 
                        [id*="${vFieldId}"], 
                        [name*="${vFieldId}"]
                     `);

              inputs.forEach(el => {
                // Never enable STS auto capture fields.
                if (el.getAttribute?.('data-sts-autocapture') === 'true') return;
                // Double check it matches the field ID logic used in populateFormFromSavedData
                if (el.tagName === 'INPUT' || el.tagName === 'SELECT' || el.tagName === 'TEXTAREA') {
                  el.removeAttribute('disabled');
                  el.removeAttribute('readonly');
                }
              });
            }
          });
        }
      } else {
        setFieldsEnabled(content, true);
      }

      // Enforce STS Auto Capture fields disabled in all modes (even if other logic enabled them).
      content.querySelectorAll?.('[data-sts-autocapture="true"]').forEach((el) => {
        try {
          el.setAttribute('disabled', 'disabled');
          if ((el.tagName || '').toUpperCase() !== 'SELECT') {
            el.setAttribute('readonly', 'readonly');
          } else {
            el.removeAttribute('readonly');
          }
        } catch (_) { /* ignore */ }
      });

      const stsApiHeaderStageLabel = resolveStsApiHeaderStageLabel(visitUiStageLabel, workflowHeader, crfStage, {
        embedInModal,
        reviewMode: props.reviewMode === true,
        readOnly: props.readOnly === true,
      });

      applyStsAutoCaptureTableCellEditability(content, shouldStsEnableEditableCells(), savedFields);
      // Open DCF correction: allow editing STS enablefields only for the STS block(s) that have Open DCF.
      // This is intentionally scoped to the STS block to avoid enabling STS cells globally in review stages.
      if (!shouldStsEnableEditableCells()) {
        content.querySelectorAll(STS_AUTOCAPTURE_SEL).forEach((el) => {
          try {
            if (!isStsOpenDcfCorrectionAllowed(el, savedFields, stsApiHeaderStageLabel)) return;
            const block =
              el.closest?.(STS_AUTOCAPTURE_BLOCK_SEL)
              || resolveStsAutoCaptureDisplayEl(el)?.closest?.(STS_AUTOCAPTURE_BLOCK_SEL)
              || null;
            applyStsAutoCaptureTableCellEditability(block || el.closest?.('.crf-generated-content') || content, true, savedFields);
          } catch (_) { /* ignore */ }
        });
      }

      applyCraResolvedDcfCommentHidingToContent(content, savedFields, isCraUser, props.readOnly, isViewOnly, isReviewStage);

      // Row modal lock runs in useLayoutEffect; processContent runs in requestAnimationFrame and setFieldsEnabled
      // clears disabled/readonly on all report inputs — re-apply the row lock so that row stays non-editable while the modal is open.
      const rowModalLockDtl = getVisitReportCrfRowModalLockDetailNo({
        dcfModalOpen,
        dcfNcrfDtlNo: dcfSelected.nCRFDtlNo,
        auditModalOpen,
        auditNcrfDtlNo: auditSelected.nCRFDtlNo,
        actionItemsModalOpen,
        actionItemsNcrfDtlNo: actionItemsSelected.nCRFDtlNo,
        isCreateActionItemModalOpen,
        createActionItemNcrfDtlNo: createActionItemNCRFDtlNo,
      });
      if (rowModalLockDtl != null && !props.readOnly && !isViewOnly) {
        applyCrfReportRowNonTransactionalViewLock(content, rowModalLockDtl);
      }

      if (!isPreviewInNewTab) {
        const pcAssoc = auditProjectCode || (formData.protocolNo || '').trim() || undefined;
        const assocOpts = {
          savedFields,
          crfStage,
          crfCurrentStageCode,
          isReviewStage,
          readOnly: props.readOnly,
          isViewOnly,
          allowReviewAssociatedEdit: isReviewStage && !reviewTierSubmitLocked,
          enableFieldsByCodes,
          headerStageForOpenDcf: headerStageForOpenDcfGate,
          pageHeaderStageInProgress,
        };
        const runAssoc = (list) =>
          applyAssociatedFieldVisibility(content, Array.isArray(list) ? list : [], {
            ...assocOpts,
            serverInactiveChildFieldCodes: associatedInactiveChildFieldCodesRef.current,
            hasOpenDCF,
          });
        void stageService.getStagesForCrFAssociation(pcAssoc).then(runAssoc).catch(() => runAssoc([]));
      }

      if (!isPreviewInNewTab && !props.readOnly && !isViewOnly) {
        applyInProgressAssociatedStageBranchLocks(
          content,
          crfAssociationStageListRef.current,
          savedFields,
          crfStage,
          crfCurrentStageCode,
          isReviewStage,
          props.readOnly,
          isViewOnly
        );
        applyOpenDcfRoutedWrongStageLocks(
          content,
          savedFields,
          headerStageForOpenDcfGate,
          crfCurrentStageCode,
          apiHeaderStageInProgress
        );
        applyOpenDcfEditableRowUnlocks(
          content,
          savedFields,
          headerStageForOpenDcfGate,
          crfCurrentStageCode,
          apiHeaderStageInProgress
        );
      }

      // STS AutoCapture runtime execution: only on live report (not in preview-only HTML snapshot).
      // Populate after permissions so the field stays disabled but gets a value.
      // Requirement:
      // - Before Draft Save (no persisted vFieldValue): call external API once.
      // - After Draft Save / Open DCF correction (In Progress or CRA): re-fetch API for full table;
      //   CRFDtl stores columns + selected rows + enablefields (not sourceRows).
      //   Row matching on reopen compares non-enableFields columns only.
      // - After Submit: never call API again; keep the stored CRF value as-is.
      const autoCaptureEls = content.querySelectorAll(STS_AUTOCAPTURE_SEL);
      if (!autoCaptureEls || autoCaptureEls.length === 0) {
        return result;
      }

      const hasOpenDcfStsAutoCapture =
        [...autoCaptureEls].some((el) => isStsOpenDcfCorrectionAllowed(el, savedFields, stsApiHeaderStageLabel));

      const stsRuntimeBaseAllowed =
        !props.readOnly &&
        !isViewOnly &&
        (!props.reviewMode || hasOpenDcfStsAutoCapture);

      if (!stsRuntimeBaseAllowed) {
        return result;
      }

      const stsRuntimeDraftSaveAllowed =
        crfStage === 'In Progress' &&
        Array.isArray(availableActions) &&
        availableActions.includes('DraftSave');

      const hasOpenDcfStsAutoCaptureForGate =
        !stsRuntimeDraftSaveAllowed && hasOpenDcfStsAutoCapture;

      if (!stsRuntimeDraftSaveAllowed && !hasOpenDcfStsAutoCaptureForGate) {
        return result;
      }
      const stsCellsCanEdit = shouldStsEnableEditableCells();
      const hdrNoNum =
        nCRFHdrNo != null && nCRFHdrNo !== '' && !Number.isNaN(Number(nCRFHdrNo)) ? Number(nCRFHdrNo) : 0;
      autoCaptureEls.forEach((el) => {
        const allowThisSts =
          stsRuntimeDraftSaveAllowed ||
          isStsOpenDcfCorrectionAllowed(el, savedFields, stsApiHeaderStageLabel);
        if (!allowThisSts) return;

        const savedTrim = getStsAutoCaptureSavedFieldValue(el, savedFields);
        const detailNoRaw = (el.getAttribute('data-sts-api-detail-no') || '').trim();
        if (!detailNoRaw) {
          if (savedTrim) {
            applyStsAutoCaptureDbValue(el, savedTrim, stsCellsCanEdit);
          } else {
            showStsAutoCaptureMessage(el, 'AutoCapture configuration is missing.');
          }
          return;
        }
        const key = stsAutoCaptureExecutionKey(detailNoRaw, el, stsApiHeaderStageLabel);

        el.setAttribute('disabled', 'disabled');
        if ((el.tagName || '').toUpperCase() !== 'SELECT') {
          el.setAttribute('readonly', 'readonly');
        } else {
          el.removeAttribute('readonly');
        }

        const applyDbFromSavedFields = () => {
          const db = getStsAutoCaptureSavedFieldValue(el, savedFieldsLatestRef.current);
          if (!db) return false;
          const sn = resolveStsAutoCaptureSavedNorm(el, savedFieldsLatestRef.current)
            || normalizeStsAutoCaptureDataTable(db);
          if (stsSavedNormNeedsDraftApiSource(sn, el, savedFieldsLatestRef.current, stsApiHeaderStageLabel)) return false;
          if (
            isStsOpenDcfCorrectionAllowed(el, savedFieldsLatestRef.current, stsApiHeaderStageLabel)
            && stsDisplayHasRowSelectionTable(el)
            && stsDisplayHasEnableFieldData(el, sn)
          ) {
            return true;
          }
          applyStsAutoCaptureDbValue(el, db, stsCellsCanEditForEl);
          return true;
        };

        const savedNormForDraft = savedTrim ? normalizeStsAutoCaptureDataTable(savedTrim) : null;
        const resolvedSavedNorm = resolveStsAutoCaptureSavedNorm(el, savedFields) || savedNormForDraft;
        const stsCellsCanEditForEl = stsCellsCanEdit || isStsOpenDcfCorrectionAllowed(el, savedFields, stsApiHeaderStageLabel);
        if (stsSavedNormNeedsDraftApiSource(resolvedSavedNorm, el, savedFields, stsApiHeaderStageLabel)) {
          if (stsAutoCaptureExecutedRef.current.has(key)) {
            applyDbFromSavedFields();
            return;
          }
          if (el.getAttribute(STS_AUTOCAPTURE_ERROR_ATTR) === 'true') return;
          if (stsAutoCapturePendingRef.current.has(key)) return;
          stsAutoCapturePendingRef.current.set(key, true);
          fetchStsAutoCaptureForElement(
            el,
            detailNoRaw,
            visitId,
            stsCellsCanEditForEl,
            resolvedSavedNorm,
            (err) => {
              stsAutoCapturePendingRef.current.delete(key);
              stsAutoCaptureExecutedRef.current.delete(key);
              if (applyDbFromSavedFields()) {
                stsAutoCaptureExecutedRef.current.add(key);
                return;
              }
              const msg = err?.response?.data?.error || err?.message || stsAutoCaptureMessages.loadFailed;
              el.removeAttribute(STS_AUTOCAPTURE_LOADING_ATTR);
              const domTrimAfter = String(el.value ?? '').trim();
              if (!domTrimAfter) {
                el.value = '';
                showStsAutoCaptureMessage(el, resolveStsAutoCaptureApiErrorMessage(err));
                try {
                  setToast({ type: 'error', message: `AutoCapture failed: ${msg}` });
                } catch (_) { /* ignore */ }
              } else {
                el.removeAttribute(STS_AUTOCAPTURE_ERROR_ATTR);
                el.removeAttribute('placeholder');
              }
            },
            undefined,
            savedFields,
            stsApiHeaderStageLabel,
            () => {
              stsAutoCapturePendingRef.current.delete(key);
              stsAutoCaptureExecutedRef.current.add(key);
            },
            () => {
              stsAutoCapturePendingRef.current.delete(key);
              stsAutoCaptureExecutedRef.current.delete(key);
            }
          );
          return;
        }

        if (
          isStsOpenDcfCorrectionAllowed(el, savedFields, stsApiHeaderStageLabel)
          && stsDisplayHasRowSelectionTable(el)
          && resolvedSavedNorm
          && stsDisplayHasEnableFieldData(el, resolvedSavedNorm)
        ) {
          const block =
            el.closest?.(STS_AUTOCAPTURE_BLOCK_SEL)
            || resolveStsAutoCaptureDisplayEl(el)?.closest?.(STS_AUTOCAPTURE_BLOCK_SEL)
            || null;
          applyStsAutoCaptureTableCellEditability(block || content, stsCellsCanEditForEl, savedFields);
          stsAutoCaptureExecutedRef.current.add(key);
          return;
        }

        if (savedTrim) {
          applyStsAutoCaptureDbValue(el, savedTrim, stsCellsCanEdit);
          stsAutoCaptureExecutedRef.current.add(key);
          return;
        }

        const row = el.closest?.('.crf-report-row');
        const rowDtl = row?.getAttribute?.('data-n-crf-dtl-no');
        const domTrim = String(el.value ?? '').trim();
        if (
          hdrNoNum > 0 &&
          rowDtl != null &&
          rowDtl !== '' &&
          domTrim &&
          el.getAttribute(STS_AUTOCAPTURE_ERROR_ATTR) !== 'true'
        ) {
          el.setAttribute(STS_AUTOCAPTURE_SOURCE_ATTR, STS_AUTOCAPTURE_SOURCE_DB);
          stsAutoCaptureExecutedRef.current.add(key);
          return;
        }

        if (el.getAttribute(STS_AUTOCAPTURE_SOURCE_ATTR) === STS_AUTOCAPTURE_SOURCE_DB) {
          stsAutoCaptureExecutedRef.current.add(key);
          return;
        }

        if (stsAutoCaptureExecutedRef.current.has(key)) {
          applyDbFromSavedFields();
          return;
        }

        if (el.getAttribute(STS_AUTOCAPTURE_ERROR_ATTR) === 'true') return;
        if (stsAutoCapturePendingRef.current.has(key)) return;
        stsAutoCapturePendingRef.current.set(key, true);

        fetchStsAutoCaptureForElement(
          el,
          detailNoRaw,
          visitId,
          stsCellsCanEdit,
          null,
          (err) => {
            stsAutoCapturePendingRef.current.delete(key);
            stsAutoCaptureExecutedRef.current.delete(key);
            if (applyDbFromSavedFields()) {
              stsAutoCaptureExecutedRef.current.add(key);
              return;
            }
            if (el.getAttribute(STS_AUTOCAPTURE_SOURCE_ATTR) === STS_AUTOCAPTURE_SOURCE_DB) return;
            const msg = err?.response?.data?.error || err?.message || stsAutoCaptureMessages.loadFailed;
            el.removeAttribute(STS_AUTOCAPTURE_LOADING_ATTR);
            const domTrimAfter = String(el.value ?? '').trim();
            if (!domTrimAfter) {
              el.value = '';
              showStsAutoCaptureMessage(el, resolveStsAutoCaptureApiErrorMessage(err));
              try {
                setToast({ type: 'error', message: `AutoCapture failed: ${msg}` });
              } catch (_) { /* ignore */ }
            } else {
              el.removeAttribute(STS_AUTOCAPTURE_ERROR_ATTR);
              el.removeAttribute('placeholder');
            }
          },
          () => {
            if (applyDbFromSavedFields()) return true;
            if (el.getAttribute(STS_AUTOCAPTURE_SOURCE_ATTR) === STS_AUTOCAPTURE_SOURCE_DB) return true;
            return false;
          },
          null,
          null,
          () => {
            stsAutoCapturePendingRef.current.delete(key);
            stsAutoCaptureExecutedRef.current.add(key);
          },
          () => {
            stsAutoCapturePendingRef.current.delete(key);
            stsAutoCaptureExecutedRef.current.delete(key);
          }
        );
      });

      return result;
    };

    const isContentReady = (el) =>
      el && (el.children.length > 0 || el.querySelector('.templateSubGroup, .crf-report-row, input, select, textarea'));

    const runWhenContentReady = (getContent, onReady) => {
      const check = () => {
        const el = typeof getContent === 'function' ? getContent() : document.querySelector(getContent);
        if (isContentReady(el)) {
          const result = onReady(el);
          if (result?.totalWithValue > 0 && result?.populatedCount === 0) {
            console.warn('[CRFForm] Saved data could not be populated. Field IDs:', savedFields?.map((f) => f.vFieldId || f.VFieldId));
          } else if (result?.totalWithValue > 0 && result?.populatedCount < result?.totalWithValue) {
            console.warn('[CRFForm] Partially populated:', result.populatedCount, 'of', result.totalWithValue);
          }
          // When opened for Review (First/Second) but page has no detail rows, one-time draft save to create rows so Raise DCF works
          if (!hydratedForReviewRef.current && (!savedFields || savedFields.length === 0) && nCRFHdrNo && visitId && isReviewStage && !props.readOnly && !isViewOnly) {
            hydratedForReviewRef.current = true;
            const hydrateDetailRows = async () => {
              const fields = collectFormData(el);
              if (fields.length === 0) return;
              try {
                await api.post('/crf/page/save', {
                  nCRFHdrNo,
                  vTemplateNo: effectiveTemplateId,
                  vVisitType: visitType,
                  iVisitNodeId: parseInt(visitId, 10),
                  isSubmit: false,
                  fields,
                  viewingStage: visitUiStageLabel || null,
                });
                const pageRes = await api.get(buildCrfPageUrl(nCRFHdrNo));
                const nextFields = pageRes.data?.fields ?? pageRes.data?.Fields ?? [];
                setSavedFields(Array.isArray(nextFields) ? nextFields : []);
                applyCrfPageDcfFlags(pageRes.data);
                applyCrfPageSaveType(pageRes.data);
                applyCrfPageAssociatedInactiveChildren(pageRes.data);
                applyCrfPageUploadLimit(pageRes.data);
                setWorkflowHeader(pageRes.data?.workflowHeader ?? pageRes.data?.WorkflowHeader ?? null);
                const irs = readPageIsReviewStage(pageRes.data);
                if (irs !== null) setPageIsReviewStage(irs);
                const cscHy = pickCurrentStageCodeUpdate(pageRes.data);
                if (cscHy !== undefined) setCrfCurrentStageCode(cscHy);
              } catch (err) {
                console.error('[CRFForm] Auto-hydrate detail rows for review failed', err);
                hydratedForReviewRef.current = false;
              }
            };
            hydrateDetailRows();
          }
          return;
        }
        rafId = requestAnimationFrame(check);
      };
      rafId = requestAnimationFrame(check);
    };

    runWhenContentReady(() => crfContainerRef.current?.querySelector('.crf-generated-content'), (content) => processContent(content));
    return () => {
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [
    htmlContent,
    populateFormFromSavedData,
    savedFields,
    isReviewStage,
    setFieldsEnabled,
    enableFieldsByCodes,
    fieldIdToDtlNo,
    hasOpenDCF,
    hasOpenDcfReleasedToCra,
    isCraUser,
    props.readOnly,
    props.reviewMode,
    embedInModal,
    isViewOnly,
    permissionsRefreshKey,
    isPreviewInNewTab,
    canRaiseActionItemFromServer,
    dcfModalOpen,
    dcfSelected.nCRFDtlNo,
    auditModalOpen,
    auditSelected.nCRFDtlNo,
    actionItemsModalOpen,
    actionItemsSelected.nCRFDtlNo,
    isCreateActionItemModalOpen,
    createActionItemNCRFDtlNo,
    availableActions,
    reviewAssociatedRequiresSaveFlow,
    crfCSaveType,
    crfCurrentStageCode,
    crfStage,
    applyCrfPageDcfFlags,
    applyCrfPageAssociatedInactiveChildren,
    applyCrfPageUploadLimit,
    associatedInactiveChildFieldCodes,
    nCRFHdrNo,
    visitId,
    effectiveTemplateId,
    visitType,
    workflowHeader,
    pageHeaderStageInProgress,
    buildCrfPageUrl,
    visitUiStageLabel,
  ]);

  // When stage changes to/from review or after DCF refetch, re-apply enable/disable to this instance's content only
  useLayoutEffect(() => {
    const content = crfContainerRef.current?.querySelector('.crf-generated-content');
    if (!content) return;

    if (props.readOnly || isViewOnly) {
      setFieldsEnabled(content, false);
      applyCraResolvedDcfCommentHidingToContent(content, savedFields, isCraUser, props.readOnly, isViewOnly, isReviewStage);
      // In review stage, do NOT replace controls with static text - just disable them.
      // This preserves the visual layout when reviewer's readOnly state changes (e.g., after Submit All to CRA).
      // Only apply static view transformation for truly view-only scenarios (isViewOnly from server).
      if (isViewOnly) {
        applyCrfReportAllRowsVisitReadOnlyStaticView(content);
      }
      if (Array.isArray(savedFields) && savedFields.length > 0) {
        applyVisitReportFillHintIcons(content, savedFields);
      }
      return;
    }

    // If we have Open DCF, we rely on the main effect (processContent) to handle granular permissions.
    // We only blindly enable here if we are DEFINITELY not in a restricted mode.
    if (!isReviewStage && !hasOpenDCF) {
      setFieldsEnabled(content, true);
      applyInProgressAssociatedStageBranchLocks(
        content,
        crfAssociationStageListRef.current,
        savedFields,
        crfStage,
        crfCurrentStageCode,
        isReviewStage,
        props.readOnly,
        isViewOnly
      );
    }
    applyCraResolvedDcfCommentHidingToContent(content, savedFields, isCraUser, props.readOnly, isViewOnly, isReviewStage);
    if (Array.isArray(savedFields) && savedFields.length > 0) {
      applyVisitReportFillHintIcons(content, savedFields);
    }
  }, [
    isReviewStage,
    hasOpenDCF,
    hasOpenDcfReleasedToCra,
    setFieldsEnabled,
    props.readOnly,
    isViewOnly,
    permissionsRefreshKey,
    savedFields,
    isCraUser,
    crfStage,
    crfCurrentStageCode,
  ]);

  // Associated CRF: driver row always visible; branch rows by driver branch + optional stage gate
  useEffect(() => {
    if (isPreviewInNewTab) return;
    const container = crfContainerRef.current;
    if (!container) return;
    const content = container.querySelector('.crf-generated-content');
    if (!content) return;

    let cancelled = false;
    const pc = auditProjectCode || (formData.protocolNo || '').trim() || undefined;
    const stageListRef = { current: [] };

    const applyAll = () => {
      const reviewTierSubmitLockedAssoc =
        isReviewStage && String(crfCSaveType || '').trim().toUpperCase() === 'E';
      applyAssociatedFieldVisibility(content, stageListRef.current, {
        savedFields,
        crfStage,
        crfCurrentStageCode,
        isReviewStage,
        readOnly: props.readOnly,
        isViewOnly,
        allowReviewAssociatedEdit: isReviewStage && !reviewTierSubmitLockedAssoc,
        enableFieldsByCodes,
        serverInactiveChildFieldCodes: associatedInactiveChildFieldCodesRef.current,
        hasOpenDCF,
        headerStageForOpenDcf: String(
          getAnyCase(workflowHeader, 'vStage', 'VStage', 'stage', 'Stage') || crfStage || ''
        ).trim(),
        pageHeaderStageInProgress,
      });
    };

    const loadStages = async () => {
      try {
        stageListRef.current = await stageService.getStagesForCrFAssociation(pc);
      } catch {
        stageListRef.current = [];
      }
      crfAssociationStageListRef.current = Array.isArray(stageListRef.current) ? [...stageListRef.current] : [];
      const pmBranchCodes = getAssociatedBranchChildCodesForStageGate(
        content,
        stageListRef.current,
        isPmFirstReviewStageGate
      );
      crfPmFirstReviewBranchChildCodesRef.current = Array.from(pmBranchCodes);
      if (!cancelled) applyAll();
    };

    void loadStages();

    const onDom = () => {
      applyAll();
    };
    content.addEventListener('change', onDom);
    content.addEventListener('input', onDom);
    content.addEventListener('click', onDom);
    return () => {
      cancelled = true;
      content.removeEventListener('change', onDom);
      content.removeEventListener('input', onDom);
      content.removeEventListener('click', onDom);
    };
  }, [
    htmlContent,
    savedFields,
    crfStage,
    crfCurrentStageCode,
    isReviewStage,
    crfCSaveType,
    availableActions,
    reviewAssociatedRequiresSaveFlow,
    props.readOnly,
    isViewOnly,
    enableFieldsByCodes,
    auditProjectCode,
    formData.protocolNo,
    isPreviewInNewTab,
    associatedInactiveChildFieldCodes,
    hasOpenDCF,
    workflowHeader,
    pageHeaderStageInProgress,
  ]);

  // File input change: upload file to server, store path in data-uploaded-path, show filename
  useEffect(() => {
    const container = crfContainerRef.current;
    if (!container) return;
    const content = container.querySelector('.crf-generated-content');
    if (!content) return;

    const clearUploadedFileState = (input) => {
      if (!input) return;
      input.removeAttribute('data-uploaded-path');
      input.removeAttribute('data-saved-filename');
      const answerCell = input.closest(CRF_ANSWER_REGION_SEL);
      const fileNamesEl = answerCell?.querySelector('.file-names-list') || (input.id && content.querySelector(`[id="${input.id}_fileNames"]`));
      const span = answerCell?.querySelector('.crf-saved-filename');
      const target = fileNamesEl || span;
      if (target) target.textContent = '';
    };

    const handleFileChange = async (e) => {
      const input = e.target;
      if (input.type !== 'file' || !input.files?.length) return;
      const file = input.files[0];
      // New selection invalidates any previously uploaded server path for this control.
      clearUploadedFileState(input);
      if (file.size > crfMaxUploadFileSizeBytes) {
        const sizeMb = (file.size / (1024 * 1024)).toFixed(1);
        const maxMb = (crfMaxUploadFileSizeBytes / (1024 * 1024)).toFixed(0);
        setToast({
          type: 'error',
          message: `The maximum upload size is 27 MB.`,
        });
        input.value = '';
        return;
      }
      const fieldId = (input.getAttribute('data-field-id') || (input.id || input.name || '').replace(/^file_/i, '').replace(/_\d+$/, '').trim()) || '';
      const formData = new FormData();
      formData.append('file', file);
      if (nCRFHdrNo) formData.append('nCRFHdrNo', String(nCRFHdrNo));
      if (fieldId) formData.append('vFieldId', fieldId);
      try {
        const res = await api.post('/crf/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
        const path = res.data?.path;
        const filename = res.data?.filename || file.name;
        if (path) {
          input.setAttribute('data-uploaded-path', path);
          input.setAttribute('data-saved-filename', filename);
          const answerCell = input.closest(CRF_ANSWER_REGION_SEL);
          const fileNamesEl = answerCell?.querySelector('.file-names-list') || (input.id && content.querySelector(`[id="${input.id}_fileNames"]`));
          const span = answerCell?.querySelector('.crf-saved-filename');
          const target = fileNamesEl || span;
          if (target) {
            target.textContent = `${filename}`;
            target.classList.add('crf-saved-filename');
          } else if (answerCell) {
            const s = document.createElement('span');
            s.className = 'crf-saved-filename';
            s.style.display = 'block';
            s.style.marginTop = '4px';
            s.textContent = `Uploaded: ${filename} (will save on Draft Save)`;
            input.parentNode?.appendChild(s);
          }
        }
      } catch (err) {
        const st = err.response?.status;
        const maxMb = (crfMaxUploadFileSizeBytes / (1024 * 1024)).toFixed(0);
        const friendly413 =
          `The maximum upload size is 27 MB.`;
        clearUploadedFileState(input);
        input.value = '';
        setToast({
          type: 'error',
          message:
            st === 413
              ? friendly413
              : err.response?.data?.error || err.message || 'File upload failed.',
        });
      }
    };

    content.addEventListener('change', handleFileChange);
    return () => content.removeEventListener('change', handleFileChange);
  }, [htmlContent, nCRFHdrNo, crfMaxUploadFileSizeBytes]);

  // Document-level handler for DCF/Audit/ActionItems: [data-crf-action] (our transform) OR backend buttons (fieldDcfBtn, fieldauditBtn)
  useEffect(() => {
    const runFormulaCalculation = (content, formula, formulaId, decimalNo) => {
      if (!content || !formula || !formulaId) return;
      const dec = parseInt(decimalNo, 10);
      const decimalNoSafe = Number.isNaN(dec) ? 0 : Math.max(0, dec);
      const resultEl = content.querySelector(`input[id$="${formulaId}"], select[id$="${formulaId}"], textarea[id$="${formulaId}"]`);
      const sanitized = substituteFieldCodesInFormula(content, formula);
      const ev = evaluateSanitizedFormula(sanitized);
      if (!ev.ok) {
        if (resultEl) resultEl.value = '';
        setToast({ type: 'error', message: ev.message || 'Could not calculate formula.' });
        return;
      }
      if (resultEl) {
        resultEl.value = formatFormulaCalcResult(ev.value, decimalNoSafe);
      }
    };

    const handleCrfActionClick = (e) => {
      const target = e.target;
      const calcBtn = target?.closest?.('.btnCalc');
      if (calcBtn) {
        if (calcBtn.disabled || calcBtn.getAttribute('aria-disabled') === 'true') {
          e.preventDefault();
          e.stopPropagation();
          return;
        }
        const content = calcBtn.closest('.crf-generated-content');
        if (content && crfContainerRef.current?.contains(content)) {
          let formula = calcBtn.getAttribute('data-formula') || '';
          let formulaId = calcBtn.getAttribute('data-formula-id') || '';
          let decimalNo = calcBtn.getAttribute('data-decimal-no') || '0';
          if (!formula || !formulaId) {
            const onclick = (calcBtn.getAttribute('onclick') || '').trim();
            const match = onclick.match(/Calculate\s*\(\s*true\s*,\s*'([^']*)'\s*,\s*'([^']*)'\s*,\s*'([^']*)'\s*\)\s*;?/);
            if (match) {
              formula = match[1] || '';
              formulaId = match[2] || '';
              decimalNo = match[3] != null ? String(match[3]) : '0';
              calcBtn.setAttribute('data-formula', formula);
              calcBtn.setAttribute('data-formula-id', formulaId);
              calcBtn.setAttribute('data-decimal-no', decimalNo);
              calcBtn.removeAttribute('onclick');
            }
          }
          runFormulaCalculation(content, formula, formulaId, decimalNo);
          e.preventDefault();
          e.stopPropagation();
        }
        return;
      }

      if (target?.tagName === 'INPUT' && target.type === 'file') return;
      if (target?.tagName === 'LABEL' && target.htmlFor) {
        const forEl = document.getElementById(target.htmlFor);
        if (forEl?.tagName === 'INPUT' && forEl.type === 'file') return;
      }
      if (target?.closest?.('label[for]')) {
        const forId = target.closest('label').getAttribute('for');
        if (forId && document.getElementById(forId)?.type === 'file') return;
      }
      if (target?.closest?.('input[type="file"]')) return;
      let actionEl = e.target.closest?.('[data-crf-action="dcf"], [data-crf-action="audit"], [data-crf-action="actionitems"], [data-crf-action="createactionitem"]');
      let action = actionEl?.getAttribute('data-crf-action');

      if (!actionEl || !action) {
        const clicked = e.target.closest?.('[id],[class]') || e.target;
        const id = (clicked?.getAttribute?.('id') || '').toLowerCase();
        const cls = (clicked?.getAttribute?.('class') || '').toLowerCase();
        const combined = `${id} ${cls}`;
        if (combined.includes('dcfbtn') || combined.includes('fielddcfbtn')) {
          actionEl = clicked.closest?.('[id*="DcfBtn"], [id*="dcfBtn"], [class*="DcfBtn"], [class*="dcfBtn"]') || clicked;
          action = 'dcf';
        } else if (combined.includes('auditbtn') || combined.includes('fieldauditbtn')) {
          actionEl = clicked.closest?.('[id*="auditBtn"], [id*="AuditBtn"], [class*="auditBtn"], [class*="AuditBtn"]') || clicked;
          action = 'audit';
        } else if (combined.includes('actionitem') || combined.includes('action-item')) {
          actionEl = clicked.closest?.('[id*="ActionItem"], [class*="ActionItem"], [data-crf-action="actionitems"]') || clicked;
          action = combined.includes('create') ? 'createactionitem' : 'actionitems';
        }
      } else {
        action = actionEl.getAttribute('data-crf-action');
      }

      if (!actionEl || !action) return;
      const content = actionEl.closest?.('.crf-generated-content');
      if (!content) return;
      if (crfContainerRef.current != null && !crfContainerRef.current.contains(content)) return;
      if ((props.readOnly || isViewOnly) && ['createactionitem'].includes(action)) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      e.preventDefault();
      e.stopPropagation();

      const row =
        actionEl.closest?.('.crf-sts-autocapture-block')
        || actionEl.closest?.('.crf-report-row')
        || actionEl.closest?.('.row')
        || actionEl.closest?.('tr')
        || actionEl.closest?.('.form-group')?.closest?.('.row')
        || actionEl.closest?.('[class*="report"]');
      if (!row) return;

      // Prefer nCRFDtlNo from row (set by transform) so DCF works even when fieldId doesn't match savedFields keys
      let nCRFDtlNo = row.getAttribute?.('data-n-crf-dtl-no');
      if (nCRFDtlNo != null && nCRFDtlNo !== '') nCRFDtlNo = Number(nCRFDtlNo);
      else nCRFDtlNo = null;

      // For Create Action Item: use MedExMst.vMedExCode stored on the row (data-medex-code); fallback to data-field-id / input id.
      let fieldCode = (row.getAttribute?.('data-medex-code') || '').trim();
      if (!fieldCode) {
        const fieldIdEl =
          row.querySelector('.crf-answer-cell[data-field-id], .crf-answer-cell [data-field-id], .crf-answer-col[data-field-id], .crf-answer-col [data-field-id]') ||
          row.querySelector('[data-field-id]');
        fieldCode = (fieldIdEl?.getAttribute?.('data-field-id') || '').trim();
      }
      let rawFieldId = fieldCode;
      if (!fieldCode) {
        const inputInRow =
          row.querySelector(
            '.crf-answer-cell input:not([type="hidden"]), .crf-answer-col input:not([type="hidden"]), .crf-answer-cell select, .crf-answer-col select, .crf-answer-cell textarea, .crf-answer-col textarea'
          ) || row.querySelector('input:not([type="hidden"]), select, textarea');
        rawFieldId = (inputInRow?.id || inputInRow?.name || '').trim();
        if (rawFieldId.length <= 5) fieldCode = rawFieldId;
        else fieldCode = rawFieldId.replace(/_answer$/i, '').split('_').pop()?.substring(0, 5) || rawFieldId.substring(0, 5);
      }
      if (nCRFDtlNo == null) {
        const trimmed = fieldCode?.replace(/_answer$/i, '').trim();
        nCRFDtlNo = fieldIdToDtlNo[fieldCode] ?? fieldIdToDtlNo[trimmed]
          ?? fieldIdToDtlNo[rawFieldId] ?? fieldIdToDtlNo[rawFieldId?.replace(/_answer$/i, '').trim()]
          ?? (fieldCode && /^\d{1,5}$/.test(fieldCode) ? fieldIdToDtlNo[fieldCode.padStart(5, '0')] : undefined);
      }

      let questionEl = row.querySelector('.crf-question-cell label, .crf-question-cell .form-label, .crf-question-cell');
      if (!questionEl) questionEl = row.querySelector('label, .form-label, [class*="question"]');
      const questionText = getCrfQuestionTextWithoutHintIcon(questionEl);

      if (action === 'dcf') {
        const associationContent = resolveCrfAssociationLookupContent(
          content,
          htmlContent,
          associationLookupParsedContentRef
        );
        const stageListForDcf = crfAssociationStageListRef.current;
        let cachedPmBranchCodes = crfPmFirstReviewBranchChildCodesRef.current;
        if (associationContent) {
          cachedPmBranchCodes = Array.from(
            getAssociatedBranchChildCodesForStageGate(
              associationContent,
              stageListForDcf,
              isPmFirstReviewStageGate
            )
          );
          crfPmFirstReviewBranchChildCodesRef.current = cachedPmBranchCodes;
        }
        if (!fieldHasActiveDcfThread(savedFields, fieldCode, nCRFDtlNo)) {
          const dcfRaiseBlock = getDcfRaiseBlockMessageForField(
            associationContent,
            fieldCode,
            savedFields,
            stageListForDcf,
            crfStage,
            crfCurrentStageCode,
            currentUserProfile,
            visitUiStageLabel,
            isReviewStage,
            !isCraUser && (canApproveReject || canRaiseDCFFromServer),
            currentUserProfileAliases,
            cachedPmBranchCodes,
            hasUnreleasedOpenDcf,
            row
          );
          if (dcfRaiseBlock) {
            setToast({ type: 'error', message: dcfRaiseBlock });
            return;
          }
        }
        setDcfSelected({ nCRFDtlNo: nCRFDtlNo ?? null, fieldId: fieldCode, questionText });
        setDcfModalOpen(true);
      } else if (action === 'audit') {
        setAuditSelected({
          fieldId: fieldCode,
          questionText,
          nCRFDtlNo: nCRFDtlNo ?? null,
          nCRFActionItemNo: null,
          actionItemAuditFieldName: null,
        });
        setAuditModalOpen(true);
      } else if (action === 'actionitems') {
        setActionItemsSelected({ nCRFDtlNo: nCRFDtlNo ?? null, fieldId: fieldCode, questionText });
        setActionItemsModalOpen(true);
      } else if (action === 'createactionitem') {
        if (!canRaiseActionItemFromServer) {
          setToast({ type: 'error', message: actionItemMessages.cannotCreateIssueNoPermission });
          return;
        }
        // When report is in Start Report mode (no Draft Save yet), there is no CRF header — block and ask for Draft Save first.
        if (!nCRFHdrNo) {
          setToast({ type: 'error', message: actionItemMessages.pleaseDraftSaveFirst });
          return;
        }
        // When from a CRF data row (has data-n-crf-dtl-no): pass nCRFDtlNo and fieldCode so modal shows only that section attribute (selected and disabled). From footer, pass null for full list.
        const isFieldLevelRow = row?.getAttribute?.('data-n-crf-dtl-no') != null && row.getAttribute('data-n-crf-dtl-no') !== '';
        const fc = (fieldCode && String(fieldCode).trim() !== '') ? String(fieldCode).trim() : null;
        const payload = { fieldCode: fc, fieldId: fc, nCRFDtlNo: nCRFDtlNo ?? null };
        createActionItemPayloadRef.current = payload;
        setCreateActionItemNCRFDtlNo(isFieldLevelRow ? (nCRFDtlNo ?? null) : null);
        setCreateActionItemFieldCode(isFieldLevelRow && fieldCode ? fieldCode : null);
        setIsCreateActionItemModalOpen(true);
      }
    };
    document.addEventListener('click', handleCrfActionClick, true);
    return () => document.removeEventListener('click', handleCrfActionClick, true);
  }, [
    fieldIdToDtlNo,
    nCRFHdrNo,
    setToast,
    canRaiseActionItemFromServer,
    props.readOnly,
    isViewOnly,
    savedFields,
    crfStage,
    crfCurrentStageCode,
    currentUserProfile,
    currentUserProfileAliases,
    visitUiStageLabel,
    isReviewStage,
    canApproveReject,
    canRaiseDCFFromServer,
    isCraUser,
    htmlContent,
    hasUnreleasedOpenDcf,
  ]);

  // DCF / View Audit / Issues: for the affected report row only, replace Answer & Comments inputs with static text while the modal is open.
  useLayoutEffect(() => {
    if (props.readOnly || isViewOnly) return undefined;
    const container = crfContainerRef.current;
    if (!container) return undefined;
    const content = container.querySelector('.crf-generated-content');
    if (!content) return undefined;

    const lockDtl = getVisitReportCrfRowModalLockDetailNo({
      dcfModalOpen,
      dcfNcrfDtlNo: dcfSelected.nCRFDtlNo,
      auditModalOpen,
      auditNcrfDtlNo: auditSelected.nCRFDtlNo,
      actionItemsModalOpen,
      actionItemsNcrfDtlNo: actionItemsSelected.nCRFDtlNo,
      isCreateActionItemModalOpen,
      createActionItemNcrfDtlNo: createActionItemNCRFDtlNo,
    });

    // When NO modal is open, sweep ALL rows to clear any leftover lock overlays.
    // This guards against race conditions where individual row cleanup might miss some elements.
    if (lockDtl == null) {
      const allRows = content.querySelectorAll('.crf-report-row[data-n-crf-dtl-no]');
      allRows.forEach((row) => sweepVisitReportRowModalLockOverlay(row));
      return undefined;
    }

    const cleanup = applyCrfReportRowNonTransactionalViewLock(content, lockDtl);
    return () => {
      if (typeof cleanup === 'function') cleanup();
      // Row cleanup restores controls with disabled=false; re-run Visit Report permission pass (processContent via rAF).
      setPermissionsRefreshKey((k) => k + 1);
    };
  }, [
    dcfModalOpen,
    dcfSelected.nCRFDtlNo,
    auditModalOpen,
    auditSelected.nCRFDtlNo,
    actionItemsModalOpen,
    actionItemsSelected.nCRFDtlNo,
    isCreateActionItemModalOpen,
    createActionItemNCRFDtlNo,
    htmlContent,
    props.readOnly,
    isViewOnly,
  ]);

  // When leaving Visit Report read-only, restore Answer/Comments controls (static overlay is not in React tree).
  useEffect(() => {
    const cur = !!(props.readOnly || isViewOnly);
    if (prevCrfVisitReportRoRef.current === true && cur === false) {
      const content = crfContainerRef.current?.querySelector('.crf-generated-content');
      clearCrfReportVisitReadOnlyStaticView(content);
    }
    prevCrfVisitReportRoRef.current = cur;
  }, [props.readOnly, isViewOnly, htmlContent]);

  // When opening DCF modal in review stage, refetch CRF page so savedFields has latest vDCFStatus and vDCFRaisedByProfileCode
  useEffect(() => {
    if (!dcfModalOpen || !isReviewStage || !nCRFHdrNo) return;
    let cancelled = false;
    api.get(buildCrfPageUrl(nCRFHdrNo))
      .then(res => {
        if (cancelled) return;
        const nextFields = res.data?.fields ?? res.data?.Fields ?? [];
        setSavedFields(prev => (Array.isArray(nextFields) && nextFields.length > 0 ? nextFields : prev));
        applyCrfPageDcfFlags(res.data);
        applyCrfPageSaveType(res.data);
        applyCrfPageAssociatedInactiveChildren(res.data);
        applyCrfPageUploadLimit(res.data);
        const irs = readPageIsReviewStage(res.data);
        if (irs !== null) setPageIsReviewStage(irs);
        const cscDcf = pickCurrentStageCodeUpdate(res.data);
        if (cscDcf !== undefined) setCrfCurrentStageCode(cscDcf);
      })
      .catch(() => { if (!cancelled) { /* keep existing savedFields */ } });
    return () => { cancelled = true; };
  }, [dcfModalOpen, isReviewStage, nCRFHdrNo, buildCrfPageUrl, applyCrfPageDcfFlags, applyCrfPageSaveType, applyCrfPageAssociatedInactiveChildren, applyCrfPageUploadLimit]);

  // Fetch DCF History when modal opens and nCRFDtlNo is set
  useEffect(() => {
    if (dcfModalOpen && dcfSelected.nCRFDtlNo) {
      setLoadingHistory(true);
      setDcfHistory([]);
      dcfHistoryApi(dcfSelected.nCRFDtlNo)
        .then(res => {
          setDcfHistory(res.data || []);
        })
        .catch(err => {
          console.error("Failed to fetch DCF history", err);
        })
        .finally(() => {
          setLoadingHistory(false);
        });
    } else {
      setDcfHistory([]);
    }
  }, [dcfModalOpen, dcfSelected.nCRFDtlNo]);

  // Fetch field-level audit trail when View Audit modal opens (CRFDtl row, or CRFActionItem for Issue Review)
  useEffect(() => {
    if (!auditModalOpen) {
      setAuditTrailList([]);
      setAuditTrailError(null);
      return;
    }
    const actionItemNo = auditSelected.nCRFActionItemNo != null && Number(auditSelected.nCRFActionItemNo) > 0
      ? Number(auditSelected.nCRFActionItemNo)
      : null;

    if (actionItemNo != null) {
      let cancelled = false;
      setAuditTrailLoading(true);
      setAuditTrailError(null);
      auditService
        .getAuditHdrTrail('CRFActionItem', String(actionItemNo), 100, {
          projectId: auditProjectId,
          projectCode:
            auditProjectId != null && auditProjectId !== '' ? undefined : auditProjectCode,
        })
        .then((data) => {
          if (!cancelled) setAuditTrailList(Array.isArray(data) ? data : []);
        })
        .catch((err) => {
          if (!cancelled) setAuditTrailError(err?.response?.data?.error || err?.message || 'Failed to load audit trail');
        })
        .finally(() => {
          if (!cancelled) setAuditTrailLoading(false);
        });
      return () => { cancelled = true; };
    }

    const fid = auditSelected.fieldId && auditSelected.fieldId.trim();
    const recordId = auditSelected.nCRFDtlNo
      ?? (fid && fieldIdToDtlNo?.[fid])
      ?? (fid && fieldIdToDtlNo?.[fid.replace(/_answer$/i, '').trim()])
      ?? (fid && /^\d{1,5}$/.test(fid) && fieldIdToDtlNo?.[fid.padStart(5, '0')]);
    if (recordId == null) {
      setAuditTrailList([]);
      setAuditTrailError(null);
      return;
    }
    let cancelled = false;
    setAuditTrailLoading(true);
    setAuditTrailError(null);
    auditService
      .getAuditHdrTrail('CRFDtl', String(recordId), 100, {
        projectId: auditProjectId,
        projectCode:
          auditProjectId != null && auditProjectId !== '' ? undefined : auditProjectCode,
      })
      .then((data) => {
        if (!cancelled) setAuditTrailList(Array.isArray(data) ? data : []);
      })
      .catch((err) => {
        if (!cancelled) setAuditTrailError(err?.response?.data?.error || err?.message || 'Failed to load audit trail');
      })
      .finally(() => {
        if (!cancelled) setAuditTrailLoading(false);
      });
    return () => { cancelled = true; };
  }, [auditModalOpen, auditSelected.nCRFActionItemNo, auditSelected.nCRFDtlNo, auditSelected.fieldId, fieldIdToDtlNo, auditProjectCode, auditProjectId]);

  // Pre-fill vDCFResponse when CRA or approve-sendback user opens DCF modal (resolve / response flows).
  // Open rows after close+re-raise still carry prior vDCFResponse on CRFDtl; do not show that in the remarks box.
  useEffect(() => {
    if (!dcfModalOpen || !dcfSelected.nCRFDtlNo || !savedFields?.length) return;
    if (!isCraUser && !canApproveReject) return;
    const no = Number(dcfSelected.nCRFDtlNo);
    const f = savedFields.find(x => Number(x.nCRFDtlNo ?? x.NCRFDtlNo ?? x.ncrfDtlNo ?? 0) === no);
    const status = (f?.vDCFStatus ?? f?.VDCFStatus ?? '').toString().trim().toLowerCase();
    if (status === 'open') {
      setDcfResponse('');
      return;
    }
    const existing = f?.vDCFResponse ?? f?.VDCFResponse ?? '';
    setDcfResponse(existing || '');
  }, [dcfModalOpen, isCraUser, canApproveReject, dcfSelected.nCRFDtlNo, savedFields]);

  // Fetch action items for the selected CRF field when Action Items modal opens (by nCRFDtlNo)
  useEffect(() => {
    if (!actionItemsModalOpen || !actionItemsSelected.nCRFDtlNo) {
      setActionItemsList([]);
      return;
    }
    setActionItemsLoading(true);
    const nCRFDtlNo = Number(actionItemsSelected.nCRFDtlNo);
    const params = { nCRFDtlNo };
    if (nCRFHdrNo) {
      params.nCRFHdrNo = nCRFHdrNo;
      params.includeHistory = true;
    }
    api.get('/action-items/by-crf-field', { params })
      .then(res => {
        const list = Array.isArray(res.data) ? res.data : [];
        setActionItemsList(nCRFHdrNo ? list : list.filter((x) => (x?.status || '').toString().trim().toLowerCase() !== 'closed'));
        setSelectedActionItemIndex(0);
      })
      .catch(() => {
        setActionItemsList([]);
      })
      .finally(() => {
        setActionItemsLoading(false);
      });
  }, [actionItemsModalOpen, actionItemsSelected.nCRFDtlNo, nCRFHdrNo]);

  // Sync Action Item Review form when selected item or list changes (Field Level: Action Description readonly, Status default Closed and disabled)
  useEffect(() => {
    if (!actionItemsList.length) {
      setActionDescriptionEdit('');
      setActionReplyRemark('');
      setActionReplyHistoryEntries([]);
      setActionReplyDraft('');
      setStatusEdit('Closed');
      setActionItemReopenIntent('Closed');
      setActionItemClosingDate('');
      actionItemReplyRemarkOriginalRef.current = '';
      return;
    }
    const idx = Math.min(selectedActionItemIndex, actionItemsList.length - 1);
    const item = actionItemsList[idx];
    setActionDescriptionEdit(item.findingDescription ?? '');
    const reply = item.replyRemark ?? '';
    actionItemReplyRemarkOriginalRef.current = reply;
    setActionReplyRemark(reply);
    setActionReplyDraft('');
    setStatusEdit((item.status || '').toString() || 'Open');
    setActionItemReopenIntent('Closed');
    const stLower = (item.status || '').toString().toLowerCase();
    const sessionMid = Number(sessionRecordedByMappingId ?? 0) || 0;
    const currentProfile = (user?.profileCode || user?.vUserTypeCode || user?.role || '').toString().toUpperCase().trim();
    const raisedByProfile = (item.createdByProfileCode || item.CreatedByProfileCode || '').toString().toUpperCase().trim();
    const canCreatorClose =
      stLower === 'resolved'
      && sessionMid > 0
      && Number(item.createdBy || 0) === sessionMid
      && (!raisedByProfile || (Boolean(currentProfile) && currentProfile === raisedByProfile));
    if (canCreatorClose) {
      const b = getActionItemClosingDateBounds(
        getAnyCase(item, 'dateOfIssue', 'DateOfIssue', 'dDateOfIssue', 'DDateOfIssue')
      );
      setActionItemClosingDate(clampIsoDateToMinMax(todayIsoDateLocal(), b.minIso, b.maxIso));
    } else {
      setActionItemClosingDate('');
    }
  }, [actionItemsList, selectedActionItemIndex, user, sessionRecordedByMappingId]);

  // Description of Action: by-crf-field list often omits stamped "Name (login)(profile) date time" lines; load audit
  // trail like VisitManagement so ActionItemReplyThread shows the same meta + body as Reply from visit list.
  useEffect(() => {
    if (!actionItemsModalOpen || !actionItemsList.length) {
      setActionReplyHistoryEntries([]);
      return;
    }
    const idx = Math.min(selectedActionItemIndex, actionItemsList.length - 1);
    const item = actionItemsList[idx];
    const aid = item?.actionItemID ?? item?.nCRFActionItemNo;
    if (!aid) {
      setActionReplyHistoryEntries([]);
      return;
    }

    let cancelled = false;
    const pid = projectInfo?.projectNo ?? projectInfo?.ProjectNo ?? projectInfo?.projectId ?? projectInfo?.ProjectId;
    const pcode = (typeof currentProject === 'string' && currentProject && currentProject !== 'all')
      ? currentProject
      : (projectInfo?.projectCode ?? projectInfo?.ProjectCode);

    auditService
      .getAuditHdrTrail('CRFActionItem', String(aid), 200, {
        projectId: pid != null && pid !== '' && Number(pid) !== 0 ? pid : undefined,
        projectCode: pid != null && pid !== '' && Number(pid) !== 0 ? undefined : pcode,
      })
      .then((data) => {
        if (cancelled) return;
        const trail = Array.isArray(data) ? data : [];
        const entries = trail.flatMap((hdr, hi) => {
          const details = hdr.details || hdr.Details || [];
          const recordedOn = hdr.dRecordedOnUTC || hdr.dRecordedOnUtc;
          const offset = hdr.vRecordedAtOffSet || hdr.vRecordedAtOffset || '';
          const valueRows = details.filter((d) => (d.vFieldName || d.VFieldName || '').toLowerCase() === 'vreplyremark');
          return valueRows.map((dtl, di) => {
            const sign = (dtl.vRecordedSign ?? dtl.VRecordedSign ?? hdr.performedBy ?? hdr.vRecordedSign ?? '—').toString().trim() || '—';
            const rawNew = dtl.vNewValue ?? dtl.VNewValue;
            const body = formatReplyRemarkPlainForAuditDisplay(rawNew).toString().trim();
            const at = formatPerformedOnUtcToLocal(recordedOn, offset);
            return {
              key: `${hdr.nAuditHdrNo ?? hi}-${di}`,
              metaText: `${sign} ${at && at !== '—' ? at : ''}`.trim(),
              body,
              isMine: false,
              recordedOn: recordedOn ? new Date(recordedOn).getTime() : 0,
              auditDtlNo: Number(dtl.nAuditDtlNo ?? dtl.NAuditDtlNo ?? 0),
            };
          });
        })
          .filter((x) => x.body !== '' && x.body.toLowerCase() !== 'null')
          .sort((a, b) => {
            const t = (a.recordedOn || 0) - (b.recordedOn || 0);
            if (t !== 0) return t;
            return (a.auditDtlNo || 0) - (b.auditDtlNo || 0);
          });
        setActionReplyHistoryEntries(entries);
      })
      .catch(() => {
        if (!cancelled) setActionReplyHistoryEntries([]);
      });

    return () => { cancelled = true; };
  }, [actionItemsModalOpen, actionItemsList, selectedActionItemIndex, currentProject, projectInfo]);

  const loadCRFTemplate = async () => {
    const idToUse = effectiveTemplateId;
    if (!idToUse) return;
    let fields = [];
    try {
      setLoading(true);
      setError(null);

      // Get URL parameters for project, site, visit type
      const urlParams = new URLSearchParams(window.location.search);
      const projectCode = urlParams.get('project');
      const siteId = urlParams.get('siteId');
      const urlVisitType = urlParams.get('visitType') || visitType;

      // Fetch template and generate CRF HTML (use effectiveTemplateId so it runs when resolved from visit)
      const queryParams = new URLSearchParams({
        columnView: '1',
        showTemplateName: 'true'
      });
      if (projectCode) queryParams.append('projectCode', projectCode);
      if (siteId) queryParams.append('siteId', siteId);
      if (urlVisitType) queryParams.append('visitType', urlVisitType);

      const response = await api.get(`/crf/template/${idToUse}?${queryParams.toString()}`);
      const resData = response.data ?? {};
      const success = getAnyCase(resData, 'success', 'Success') === true;

      if (success) {
        const html = getAnyCase(resData, 'html', 'Html') ?? '';
        let nCRFHdrNoVal = null;

        if (visitId) {
          try {
            const visitRes = await api.get(`/visit/${visitId}`);
            const visit = visitRes.data ?? {};
            const siteCode = (getAnyCase(visit, 'siteCode', 'SiteCode', 'vSiteCode') || '').toString().trim();
            const siteName = (getAnyCase(visit, 'siteName', 'SiteName') || '').toString().trim();
            const siteDisplay = siteCode && siteName
              ? `${siteCode} - ${siteName}`
              : siteName || (siteCode ? `Site ${siteCode}` : '');
            const visitSiteId = getAnyCase(visit, 'siteID', 'SiteID', 'siteId');
            if (visitSiteId != null && String(visitSiteId).trim() !== '') {
              const nSid = Number(visitSiteId);
              if (Number.isFinite(nSid) && nSid > 0) setResolvedSiteIdForAppendix(nSid);
            }
            setFormData({
              protocolNo: getAnyCase(visit, 'projectCode', 'ProjectCode') || projectCode || '',
              siteNumber: siteDisplay,
              piName: getAnyCase(visit, 'piName', 'PiName', 'investigatorName', 'InvestigatorName') || '',
              actualStartDate: (() => {
                const d = getAnyCase(visit, 'actualStartDate', 'ActualStartDate');
                return d ? formatDateDDMMMYYYY(d) : '';
              })(),
              actualEndDate: (() => {
                const d = getAnyCase(visit, 'actualEndDate', 'ActualEndDate');
                return d ? formatDateDDMMMYYYY(d) : '';
              })(),
              visitMode: (getAnyCase(visit, 'visitMode', 'VisitMode') || '').toString().trim(),
              sponsor: (getAnyCase(visit, 'sponsorDescription', 'SponsorDescription') || '').toString().trim()
            });

            let pageData = null;
            try {
              const byVisitQs = new URLSearchParams({ visitType });
              if (embedInModal && visitUiStageLabel) byVisitQs.set('viewingStage', visitUiStageLabel);
              const pageRes = await api.get(`/crf/page/by-visit/${visitId}/template/${idToUse}?${byVisitQs.toString()}`);
              pageData = pageRes.data ?? {};
            } catch (pageErr) {
              if (pageErr.response?.status === 404) {
                // No CRF page yet (user has not saved draft); show empty form until first Draft Save
                pageData = {
                  fields: [],
                  requiredFieldCodes: [],
                  isReviewStage: false,
                  isHeaderStageInProgress: false,
                  associatedInactiveChildFieldCodes: [],
                };
              } else {
                // Do not rethrow: outer catch would wipe visit-derived Project/Site/PI. Keep header; show empty answers + error.
                console.error('CRF page by-visit failed (visit metadata kept):', pageErr);
                const pe = pageErr.response?.data ?? {};
                const msg = getAnyCase(pe, 'error', 'Error', 'message', 'Message', 'title', 'Title') || pageErr.message || 'Failed to load CRF page data';
                setError(String(msg));
                pageData = {
                  fields: [],
                  requiredFieldCodes: [],
                  isReviewStage: false,
                  isHeaderStageInProgress: false,
                  associatedInactiveChildFieldCodes: [],
                };
                setWorkflowHeader(null);
              }
            }
            if (pageData) {
              nCRFHdrNoVal = getAnyCase(pageData, 'nCRFHdrNo', 'NCRFHdrNo', 'ncrfHdrNo');
              const rawFields = getAnyCase(pageData, 'fields', 'Fields') ?? [];
              fields = Array.isArray(rawFields) ? rawFields : [];
              const pageStage = getAnyCase(pageData, 'vStage', 'VStage', 'stage', 'Stage');
              if (pageStage != null) setCrfStage(String(pageStage));
              const irs = readPageIsReviewStage(pageData);
              if (irs !== null) setPageIsReviewStage(irs);
              const cscPage = pickCurrentStageCodeUpdate(pageData);
              if (cscPage !== undefined) setCrfCurrentStageCode(cscPage);
              const rawRequired = getAnyCase(pageData, 'requiredFieldCodes', 'RequiredFieldCodes') ?? [];
              const rawWorkflow = getAnyCase(pageData, 'workflowHeader', 'WorkflowHeader') ?? null;
              setRequiredFieldCodes(Array.isArray(rawRequired) ? rawRequired : []);
              setRequiredFieldErrors([]);
              setWorkflowHeader(rawWorkflow);
              applyCrfPageDcfFlags(pageData);
              applyCrfPageSaveType(pageData);
              applyCrfPageAssociatedInactiveChildren(pageData);
              applyCrfPageUploadLimit(pageData);
            }
          } catch (err) {
            console.error('Error fetching visit/CRF page data:', err);
            setFormData({
              protocolNo: projectCode || '',
              siteNumber: '',
              piName: '',
              actualStartDate: '',
              actualEndDate: '',
              visitMode: '',
              sponsor: ''
            });
            setWorkflowHeader(null);
            setAssociatedInactiveChildFieldCodes([]);
          }
        } else {
          // Preview from CRF Template Management (no visit): keep Visit Date blank
          const isTemplatePreview = searchParams.get('preview') === '1';
          setFormData({
            protocolNo: projectCode || '',
            siteNumber: '',
            piName: '',
            actualStartDate: '',
            actualEndDate: '',
            visitMode: '',
            sponsor: ''
          });
          setRequiredFieldCodes([]);
          setRequiredFieldErrors([]);
          setWorkflowHeader(null);
          setAssociatedInactiveChildFieldCodes([]);
        }

        setHtmlContent(html);
        {
          const hdrNum = nCRFHdrNoVal != null && nCRFHdrNoVal !== '' ? Number(nCRFHdrNoVal) : NaN;
          if (Number.isFinite(hdrNum) && hdrNum > 0) setNCRFHdrNo(hdrNum);
          else setNCRFHdrNo(null);
        }
        setSavedFields(fields);
      } else {
        setError('Failed to generate CRF form');
      }
    } catch (err) {
      const resData = err.response?.data ?? {};
      const errorMessage = getAnyCase(resData, 'error', 'Error', 'message', 'Message') || err.message || 'Failed to load CRF template';
      setError(errorMessage);
      console.error('Error loading CRF template:', err);
    } finally {
      setLoading(false);
    }
  };

  const removeUnwantedElements = (content) => {
    if (!content) return;

    // 1. Remove template name header
    const templateHeaders = content.querySelectorAll('section h4');
    templateHeaders.forEach(header => {
      const text = header.textContent || '';
      if (text.includes('Template') || text.includes('Standard Template') || text.trim()) {
        const section = header.closest('section');
        if (section) {
          // Check if section only contains the header
          const sectionContent = section.innerHTML || '';
          if (sectionContent.includes('<h4') && !sectionContent.includes('templateSubGroup')) {
            section.remove();
          } else {
            header.remove();
          }
        } else {
          header.remove();
        }
      }
    });

    // 2. Remove version information section (Version No, Version Date, Version Status)
    const allSections = content.querySelectorAll('section');
    allSections.forEach(section => {
      const html = section.innerHTML || '';
      const text = section.textContent || '';

      // Check if this section contains version information
      if (html.includes('Version:') || html.includes('Version Date:') ||
        html.includes('Version Status:') || html.includes('VersionLabel') ||
        html.includes('btnRepeatGo') || html.includes('btnReview') ||
        html.includes('ddlRepeatation') ||
        (text.includes('Version:') && text.includes('Version Date:'))) {
        section.remove();
        return;
      }
    });

    // 3. Remove Go button (if still present)
    const goButtons = content.querySelectorAll('#btnRepeatGo, .btngo, button[id*="RepeatGo"], button.btn.btngo');
    goButtons.forEach(btn => {
      btn.remove();
      // Also remove parent container if it only contains the button
      const parent = btn.parentElement;
      if (parent && parent.children.length === 1) {
        parent.remove();
      }
    });

    // 4. Remove Review button (if still present)
    const reviewButtons = content.querySelectorAll('#btnReview, button[id*="Review"]');
    reviewButtons.forEach(btn => {
      btn.remove();
      // Also remove parent container if it only contains the button
      const parent = btn.parentElement;
      if (parent && parent.children.length === 1) {
        parent.remove();
      }
    });

    // 5. Remove OpenGroup buttons (tab buttons)
    const tabContainers = content.querySelectorAll('.tab');
    tabContainers.forEach(container => container.remove());

    const tabButtons = content.querySelectorAll('.tablinks, button[onclick*="openGroup"]');
    tabButtons.forEach(btn => btn.remove());

    // 6. Remove empty textarea/select elements (ddlRepeatation)
    const repeatSelects = content.querySelectorAll('#ddlRepeatation, select[id*="Repeatation"]');
    repeatSelects.forEach(select => {
      const parent = select.closest('section, div');
      if (parent) {
        // Check if parent only contains version-related elements
        const parentHtml = parent.innerHTML || '';
        if (parentHtml.includes('Version') || parentHtml.includes('btnRepeatGo')) {
          parent.remove();
        } else {
          select.remove();
        }
      } else {
        select.remove();
      }
    });

    // 7. Remove any remaining version labels
    const versionLabels = content.querySelectorAll('.VersionLabel, span.VersionLabel');
    versionLabels.forEach(label => {
      const parent = label.closest('p, label, div');
      if (parent) {
        const parentText = parent.textContent || '';
        if (parentText.includes('Version')) {
          parent.remove();
        } else {
          label.remove();
        }
      } else {
        label.remove();
      }
    });
  };

  const updateSubGroupHeaders = (content) => {
    if (!content) return;

    // Find all subgroup header divs
    const subGroupHeaders = content.querySelectorAll('.subGroupDiv');

    subGroupHeaders.forEach((headerDiv) => {
      // Get the onclick attribute to extract subgroup ID
      const onclickAttr = headerDiv.getAttribute('onclick');
      if (!onclickAttr) return;

      // Extract subgroup ID from onclick: toggleSubGroup(this, 'SUBGROUP_ID')
      const match = onclickAttr.match(/toggleSubGroup\(this,\s*['"]([^'"]+)['"]\)/);
      if (!match || !match[1]) return;

      const subGroupId = match[1];

      // Find the corresponding templateSubGroup element (use [id="..."] because #id is invalid when id starts with a digit)
      const subGroupElement = content.querySelector(`[id="${String(subGroupId).replace(/"/g, '\\"')}"].templateSubGroup`);
      if (!subGroupElement) return;

      // Get the description from aria-labelledby attribute
      // Format: "INVESTIGATOR'S_BROCHURE" -> "INVESTIGATOR'S BROCHURE"
      const ariaLabelledBy = subGroupElement.getAttribute('aria-labelledby');
      if (ariaLabelledBy) {
        const description = ariaLabelledBy.replace(/_/g, ' ');
        const label = headerDiv.querySelector('label.form-label');
        if (label) {
          // Only update if the label is empty or contains just the code
          // This ensures we use the correct subgroup description from the backend
          const currentText = label.textContent.trim();
          if (!currentText || currentText.length < 3 || currentText === subGroupId.split('_')[0]) {
            label.textContent = description.toUpperCase();
          }
        }
      }
    });
  };

  const transformToReportFormat = (content, actionHandlerRef, fieldIdToDtlNoMap, savedFieldsList, canRaiseActionItem, visitReportViewOnly) => {
    if (!content) return;
    const idToDtl = fieldIdToDtlNoMap || {};
    const sanitizeQuestionLabelNode = (node) => {
      if (!node || typeof node.cloneNode !== 'function') return null;
      const clone = node.cloneNode(true);
      if (clone.classList) {
        Array.from(clone.classList).forEach((cls) => {
          if (/^(col|offset)(-|$)/i.test(cls)) clone.classList.remove(cls);
        });
      }
      if (clone.style) {
        clone.style.removeProperty('width');
        clone.style.removeProperty('max-width');
        clone.style.removeProperty('min-width');
        clone.style.removeProperty('flex');
      }
      return clone;
    };

    // Remove unwanted buttons from CRF preview (Submit, Save, Approve, Reject)
    // These buttons are generated by the backend inside each section but should not appear in preview
    // NOTE: Footer buttons (Draft Save, Submit, Print, Close) are NOT in .crf-generated-content, so they remain
    const unwantedButtonSelectors = [
      '.template-Submit-btn',
      '.template-SaveContinue-btn',
      '.template-Approve-btn',
      '.template-Reject-btn',
      'button[class*="template-Submit"]',
      'button[class*="template-Save"]',
      'button[class*="template-Approve"]',
      'button[class*="template-Reject"]'
    ];

    unwantedButtonSelectors.forEach(selector => {
      const buttons = content.querySelectorAll(selector);
      buttons.forEach((btn) => {
        // Make sure we're not removing footer buttons (they're outside .crf-generated-content)
        const isInFooter = btn.closest('.crf-form-footer');
        if (!isInFooter) {
          // Remove the button and its container if it only contains unwanted buttons
          const container = btn.closest('.container.m-3.text-center, .text-center, .container');
          if (container) {
            const buttonsInContainer = container.querySelectorAll('button');
            const hasOnlyUnwantedButtons = Array.from(buttonsInContainer).every(b => {
              const classList = Array.from(b.classList);
              return classList.some(cls =>
                cls.includes('template-Submit') ||
                cls.includes('template-Save') ||
                cls.includes('template-Approve') ||
                cls.includes('template-Reject')
              );
            });
            if (hasOnlyUnwantedButtons && buttonsInContainer.length > 0) {
              container.remove();
            } else {
              btn.remove();
            }
          } else {
            btn.remove();
          }
        }
      });
    });

    // Process each template subgroup (and fallback for First Review / alternate HTML without .templateSubGroup)
    let subGroups = content.querySelectorAll('.templateSubGroup');
    if (subGroups.length === 0) {
      subGroups = content.querySelectorAll('section, [class*="templateSubGroup"], [class*="template-subgroup"], .templateSubGroup');
    }
    subGroups.forEach((subGroup) => {
      // Check if already transformed
      if (subGroup.classList.contains('crf-report-transformed')) return;

      // Get form sections: .form-section-box, or .row with .form-group, or .crf-report-row (ReportField)
      // Include .form-group that are not inside .form-section-box so Label-only rows (template type Label) are shown
      let formSections = subGroup.querySelectorAll('.form-section-box');
      const labelOnlyGroups = subGroup.querySelectorAll('.form-group:not(.form-section-box .form-group)');
      const labelOnlySections = Array.from(labelOnlyGroups).filter((g) => !g.closest('.form-section-box')).map((r) => ({
        querySelector: (sel) => (sel === '.row' ? r.parentElement : r.querySelector(sel)),
        closest: (s) => r.closest(s)
      }));
      if (formSections.length === 0 && labelOnlySections.length === 0) {
        const reportRows = subGroup.querySelectorAll('.crf-report-row');
        if (reportRows.length > 0) {
          formSections = Array.from(reportRows).map((r) => ({
            querySelector: (sel) => (sel === '.row' ? r : r.querySelector(sel)),
            closest: () => null
          }));
        } else {
          let rows = subGroup.querySelectorAll('.row');
          if (rows.length === 0) rows = subGroup.querySelectorAll('.form-group');
          if (rows.length === 0) return;
          formSections = Array.from(rows).map((r) => ({
            querySelector: (sel) => (sel === '.row' ? r : r.querySelector(sel)),
            closest: () => null
          }));
        }
      } else {
        // Merge and order: form-section-box and label-only .form-group in document order so Label rows appear
        const ordered = [];
        const allCandidates = subGroup.querySelectorAll('.form-section-box, .form-group');
        allCandidates.forEach((el) => {
          if (el.classList.contains('form-section-box')) {
            ordered.push(el);
          } else if (el.classList.contains('form-group') && !el.closest('.form-section-box')) {
            ordered.push({
              querySelector: (sel) => (sel === '.row' ? el.parentElement : el.querySelector(sel)),
              closest: (s) => el.closest(s)
            });
          }
        });
        if (ordered.length > 0) formSections = ordered;
        else if (labelOnlySections.length > 0) formSections = labelOnlySections;
      }

      const allFormSections = Array.from(formSections);
      const regularSections = allFormSections.filter((s) => !isStsAutocaptureSection(s));
      const stsSections = allFormSections.filter((s) => isStsAutocaptureSection(s));
      const hasRegularReportRows = regularSections.length > 0;

      // Standard Question | Answer | Comments table (omit header/table when subgroup is STS-only).
      let tableContainer = null;
      if (hasRegularReportRows) {
        tableContainer = document.createElement('div');
        tableContainer.className = 'crf-report-table';
        tableContainer.style.display = 'table';
        tableContainer.style.width = '100%';
        tableContainer.style.borderCollapse = 'collapse';

        const headerRow = document.createElement('div');
        headerRow.className = 'crf-table-header';
        headerRow.style.display = 'table-row';
        headerRow.style.backgroundColor = '#f0f0f0';
        headerRow.style.fontWeight = 'bold';

        const header1 = document.createElement('div');
        header1.className = 'crf-header-cell';
        header1.style.display = 'table-cell';
        header1.style.padding = '12px';
        header1.style.border = '1px solid #ddd';
        header1.textContent = 'Question';

        const header2 = document.createElement('div');
        header2.className = 'crf-header-cell';
        header2.style.display = 'table-cell';
        header2.style.padding = '12px';
        header2.style.border = '1px solid #ddd';
        header2.textContent = 'Answer';

        const header3 = document.createElement('div');
        header3.className = 'crf-header-cell';
        header3.style.display = 'table-cell';
        header3.style.padding = '12px';
        header3.style.border = '1px solid #ddd';
        header3.textContent = 'Comments';

        headerRow.appendChild(header1);
        headerRow.appendChild(header2);
        headerRow.appendChild(header3);
        tableContainer.appendChild(headerRow);
      }

      const stsControlTypes = ['radio', 'checkbox', 'textbox', 'textarea', 'combobox', 'label', 'formula', 'file', 'reportfield', 'datetime', 'time', 'standarddate', 'stsautocapture'];

      // Transform each non-STS form section (standard Question | Answer | Comments rows)
      regularSections.forEach((section) => {
        if (isStsAutocaptureSection(section)) return;
        const row = section.querySelector('.row') || section;
        /** Original generator puts data-crf-associated on .form-section-box; we only clone .form-group into the table — preserve markers on the report row. */
        let assocMarkerSource = null;
        try {
          if (section && section.nodeType === 1 && typeof section.querySelector === 'function') {
            assocMarkerSource = section.matches('[data-crf-associated="1"]')
              ? section
              : section.querySelector('[data-crf-associated="1"]');
          }
        } catch (_) {
          assocMarkerSource = null;
        }
        if (!assocMarkerSource && row && typeof row.closest === 'function') {
          assocMarkerSource = row.closest('[data-crf-associated="1"]');
        }
        const isReportRow = row.classList?.contains('crf-report-row');
        const label = row.querySelector('label.form-label, .crf-question-text');
        const controlGroup = row.querySelector('.form-group');
        const answerCol = row.querySelector('.crf-answer-col');
        // Associated template puts .crf-associated-marker (hidden) first; driver radios/select follow. Bind row to driver field for DCF/Audit/nCRFDtlNo.
        let input = controlGroup?.querySelector('input, select, textarea') || answerCol?.querySelector('input, select, textarea');
        if (assocMarkerSource) {
          const driverInWrap = controlGroup?.querySelector(
            '.crf-associated-driver-wrap input, .crf-associated-driver-wrap select, .crf-associated-driver-wrap textarea'
          );
          const comboSelect = controlGroup?.querySelector('select.form-select');
          const nonMarker = controlGroup?.querySelector(
            'input:not(.crf-associated-marker):not([type="hidden"]), select, textarea'
          ) || answerCol?.querySelector('input:not(.crf-associated-marker):not([type="hidden"]), select, textarea');
          input = driverInWrap || comboSelect || nonMarker || input;
        }
        const labelEl = label || row.querySelector('.crf-question-text');

        // Label-only row (template type Label): no input/select/textarea, only label text — still show as a table row
        if (!input) {
          if (!labelEl) return;
          const tableRow = document.createElement('div');
          tableRow.className = 'crf-report-row crf-report-row-label-only';
          tableRow.style.display = 'table-row';
          const labelId = labelEl.id || labelEl.getAttribute?.('for') || '';
          const labelFieldCode = (labelId && (labelId.startsWith('lbl_') || labelId.match(/^[a-z]+_(\d{4,5})$/i))) ? (labelId.split('_')[1] || labelId) : '';
          if (labelFieldCode) tableRow.setAttribute('data-medex-code', labelFieldCode);
          const questionCell = document.createElement('div');
          questionCell.className = 'crf-question-cell';
          questionCell.style.display = 'table-cell';
          questionCell.style.width = '55%';
          questionCell.style.padding = '12px';
          questionCell.style.verticalAlign = 'top';
          questionCell.style.border = '1px solid #ddd';
          questionCell.style.backgroundColor = '#fafafa';
          const labelClone = sanitizeQuestionLabelNode(labelEl);
          if (labelClone) questionCell.appendChild(labelClone);
          else questionCell.textContent = labelEl.textContent || '';
          const answerCell = document.createElement('div');
          answerCell.className = 'crf-answer-cell';
          answerCell.style.display = 'table-cell';
          answerCell.style.width = '25%';
          answerCell.style.padding = '12px';
          answerCell.style.border = '1px solid #ddd';
          const commentsCell = document.createElement('div');
          commentsCell.className = 'crf-comments-cell';
          commentsCell.style.display = 'table-cell';
          commentsCell.style.width = '20%';
          commentsCell.style.padding = '12px';
          commentsCell.style.border = '1px solid #ddd';
          tableRow.appendChild(questionCell);
          tableRow.appendChild(answerCell);
          tableRow.appendChild(commentsCell);
          tableContainer.appendChild(tableRow);
          return;
        }
        if (!labelEl) return;

        const rawFieldId = input.id || input.name || '';
        let fieldCode = rawFieldId.length <= 5 ? rawFieldId : (() => {
          const parts = rawFieldId.split('_');
          const five = parts.find((p) => p.length === 5 && /^[A-Za-z0-9]+$/.test(p));
          return five || parts.pop()?.substring(0, 5) || rawFieldId.substring(0, 5);
        })();
        // For radio/checkbox/file, avoid using control-type as code (e.g. "radio"); use name or id to get real field code
        const controlTypes = ['radio', 'checkbox', 'textbox', 'textarea', 'combobox', 'label', 'formula', 'file', 'reportfield', 'datetime', 'time', 'standarddate'];
        if (fieldCode && controlTypes.includes(fieldCode.toLowerCase()) && (input.type === 'radio' || input.type === 'checkbox' || input.type === 'file') && input.name) {
          const parts = input.name.trim().split('_');
          const five = parts.find((p) => p.length === 5 && /^[A-Za-z0-9]+$/.test(p));
          if (five) fieldCode = five;
        }
        // If fieldCode is still a control-type (e.g. id was "radio"), derive from name, id, or sibling inputs
        if (!fieldCode || controlTypes.includes((fieldCode || '').toLowerCase())) {
          if (input.name && input.name.includes('_')) {
            const parts = input.name.trim().split('_');
            const five = parts.find((p) => p.length === 5 && /^[A-Za-z0-9]+$/.test(p));
            if (five) fieldCode = five;
          }
          if ((!fieldCode || controlTypes.includes((fieldCode || '').toLowerCase())) && input.id && input.id.includes('_')) {
            const parts = input.id.split('_');
            const five = parts.find((p) => p.length === 5 && /^[A-Za-z0-9]+$/.test(p));
            if (five) fieldCode = five;
          }
          if (!fieldCode || controlTypes.includes((fieldCode || '').toLowerCase())) {
            const siblings = (controlGroup || answerCol || row).querySelectorAll('input, select, textarea');
            for (let i = 0; i < siblings.length; i++) {
              const s = siblings[i];
              const rid = (s.id || s.name || '').trim();
              if (rid && rid.includes('_')) {
                const parts = rid.split('_');
                const five = parts.find((p) => p.length === 5 && /^[A-Za-z0-9]+$/.test(p));
                if (five) { fieldCode = five; break; }
              }
            }
          }
        }
        // For radio/checkbox, prefer data-field-id from backend so data-n-crf-dtl-no and data-medex-code bind correctly
        if ((input.type === 'radio' || input.type === 'checkbox') && (input.getAttribute && input.getAttribute('data-field-id'))) {
          const dataFieldId = (input.getAttribute('data-field-id') || '').trim();
          if (dataFieldId) fieldCode = dataFieldId;
        }
        if ((input.type === 'radio' || input.type === 'checkbox') && (!fieldCode || controlTypes.includes((fieldCode || '').toLowerCase()))) {
          const siblingWithDataFieldId = (controlGroup || answerCol || row).querySelector && (controlGroup || answerCol || row).querySelector('input[data-field-id]');
          if (siblingWithDataFieldId) {
            const dataFieldId = (siblingWithDataFieldId.getAttribute('data-field-id') || '').trim();
            if (dataFieldId) fieldCode = dataFieldId;
          }
        }
        // True only for the Association *driver* row (not branch children under the same [data-crf-associated] subtree).
        let isAssociatedDriverControlRow = false;
        // Only bind the driver MedEx code to rows that actually render the driver control. Branch children
        // live under the same [data-crf-associated] ancestor in source HTML; overwriting fieldCode here
        // incorrectly attached branch rows to the driver's CRFDtl (DCF highlight / DCF modal target).
        if (assocMarkerSource) {
          let driverFromCfg = '';
          try {
            const rawCfg = assocMarkerSource.getAttribute('data-associated-config');
            const cfg = JSON.parse(decodeAssociatedConfigAttribute(rawCfg) || '{}');
            driverFromCfg = String(cfg.driverFieldCode || cfg.DriverFieldCode || '').trim();
          } catch (_) {
            /* keep fieldCode from control */
          }
          if (driverFromCfg) {
            const drvTrim = driverFromCfg.trim();
            const nDrv = normalizeCrfFieldCodeForMatch(drvTrim);
            const nField = normalizeCrfFieldCodeForMatch(fieldCode);
            const markerOrDriverWrap =
              input?.classList?.contains('crf-associated-marker')
              || (typeof input?.closest === 'function' && !!input.closest('.crf-associated-driver-wrap'));
            const idOrDataField = (input?.id || input?.name || '').trim();
            const extractedFromId = idOrDataField ? extractFieldCode(idOrDataField) : '';
            const dataFieldAttr = (input?.getAttribute?.('data-field-id') || '').trim();
            const nFromExtracted = extractedFromId ? normalizeCrfFieldCodeForMatch(extractedFromId) : '';
            const nFromDataAttr = dataFieldAttr ? normalizeCrfFieldCodeForMatch(dataFieldAttr) : '';
            const idMatchesDriver =
              nDrv.length > 0
              && ((nFromExtracted && nFromExtracted === nDrv) || (nFromDataAttr && nFromDataAttr === nDrv));
            if (nField === nDrv || markerOrDriverWrap || idMatchesDriver) {
              fieldCode = drvTrim;
              isAssociatedDriverControlRow = true;
            }
          }
        }
        // Use full rawFieldId (do not strip trailing _\d+) so SSV TextBox, SSV TextBox1, SSV TextBox2 each get unique comment fields
        // Resolve Textbox showing in Comment Section Instead of Textarea
        // START
        const commentsBase = rawFieldId.replace(/_answer$/i, '');
        const commentsId = commentsBase + '_comments';
        // Find any existing comments control (input or textarea)
        const existingComments =
          row.querySelector('.crf-comments-col textarea, [id$="_comments"], [name$="_comments"]') || null;
        const commentsField = document.createElement('textarea');
        commentsField.id = commentsId;
        commentsField.name = commentsId;
        commentsField.setAttribute('data-field-id', fieldCode);
        commentsField.className = 'form-control crf-comments-field';
        commentsField.placeholder = 'Enter the Comments';
        commentsField.rows = 2;
        commentsField.maxLength = 500;
        // If we had an existing comments control, copy its content/value into the textarea
        if (existingComments) {
          const value =
            (existingComments.value !== undefined ? existingComments.value : existingComments.textContent) || '';
          commentsField.value = value;
        }
        // END

        // Create table row: store nCRFDtlNo and MedExMst.vMedExCode on every field for Create Action Item (section-attributes-for-crf).
        const tableRow = document.createElement('div');
        tableRow.className = 'crf-report-row';
        tableRow.style.display = 'table-row';
        const list = savedFieldsList || [];
        const nCRFDtlNoFromMap =
          idToDtl[fieldCode] ??
          idToDtl[fieldCode.replace(/_answer$/i, '').trim()] ??
          (fieldCode && /^\d{1,5}$/.test(fieldCode) ? idToDtl[fieldCode.padStart(5, '0')] : undefined) ??
          null;
        const fcTrim = (fieldCode || '').trim();
        const fcNorm = fcTrim ? normalizeCrfFieldCodeForMatch(fcTrim) : '';
        const savedFieldByFieldId = fcTrim
          ? list.find((f) => {
              const fid = (f.vFieldId || f.VFieldId || '').trim();
              if (!fid) return false;
              if (fid.toLowerCase() === fcTrim.toLowerCase()) return true;
              return fcNorm ? normalizeCrfFieldCodeForMatch(fid) === fcNorm : false;
            })
          : null;
        const savedFieldByDtl =
          nCRFDtlNoFromMap != null
            ? list.find((f) => Number(f.nCRFDtlNo ?? f.NCRFDtlNo ?? f.ncrfDtlNo ?? 0) === nCRFDtlNoFromMap)
            : null;
        let savedField = savedFieldByFieldId || savedFieldByDtl;
        if (
          savedFieldByFieldId &&
          savedFieldByDtl &&
          Number(savedFieldByFieldId.nCRFDtlNo ?? savedFieldByFieldId.NCRFDtlNo ?? savedFieldByFieldId.ncrfDtlNo ?? 0) !==
            Number(savedFieldByDtl.nCRFDtlNo ?? savedFieldByDtl.NCRFDtlNo ?? savedFieldByDtl.ncrfDtlNo ?? 0)
        ) {
          savedField = savedFieldByFieldId;
        }
        const nFromSaved = savedField != null ? Number(savedField.nCRFDtlNo ?? savedField.NCRFDtlNo ?? savedField.ncrfDtlNo ?? 0) : NaN;
        const nCRFDtlNoForRow = !Number.isNaN(nFromSaved) && nFromSaved > 0 ? nFromSaved : nCRFDtlNoFromMap;
        if (nCRFDtlNoForRow != null) tableRow.setAttribute('data-n-crf-dtl-no', String(nCRFDtlNoForRow));
        const medExCode =
          savedField && (savedField.vFieldId || savedField.VFieldId)
            ? String(savedField.vFieldId || savedField.VFieldId).trim()
            : fieldCode
              ? String(fieldCode).trim()
              : '';
        if (medExCode) tableRow.setAttribute('data-medex-code', medExCode);
        if (assocMarkerSource) {
          tableRow.classList.add('crf-report-row-associated');
          tableRow.setAttribute('data-crf-associated', '1');
          const cfgAttr = assocMarkerSource.getAttribute('data-associated-config');
          if (cfgAttr != null) tableRow.setAttribute('data-associated-config', cfgAttr);
          const codeAttr = assocMarkerSource.getAttribute('data-associated-field-code');
          if (codeAttr != null) tableRow.setAttribute('data-associated-field-code', codeAttr);
        }

        // Question cell
        const questionCell = document.createElement('div');
        questionCell.className = 'crf-question-cell';
        questionCell.style.display = 'table-cell';
        questionCell.style.width = '55%';
        questionCell.style.padding = '12px';
        questionCell.style.verticalAlign = 'top';
        questionCell.style.border = '1px solid #ddd';
        questionCell.style.backgroundColor = '#fafafa';
        {
          const labelClone = sanitizeQuestionLabelNode(labelEl);
          if (labelClone) questionCell.appendChild(labelClone);
          else questionCell.textContent = labelEl.textContent || '';
        }
        const sideBtns = (controlGroup || answerCol || row).querySelector('.col-sideBtns');
        const actionsWrap = document.createElement('div');
        actionsWrap.className = 'col-sideBtns crf-row-actions';
        actionsWrap.style.display = 'inline-flex';
        actionsWrap.style.gap = '12px';
        actionsWrap.style.marginLeft = '0';
        actionsWrap.style.verticalAlign = 'middle';
        if (sideBtns) {
          const sideBtnsClone = sideBtns.cloneNode(true);
          sideBtnsClone.querySelectorAll('.fieldDcfBtn, [class*="fieldDcfBtn"], [class*="DcfBtn"]').forEach((el) => {
            el.setAttribute('data-crf-action', 'dcf');
            setReviewerCommentTooltip(el);
            el.setAttribute('role', 'button');
            el.style.pointerEvents = 'auto';
            el.style.cursor = 'pointer';
          });
          sideBtnsClone.querySelectorAll('.fieldauditBtn, [class*="fieldauditBtn"], [class*="auditBtn"]').forEach((el) => {
            if (el.getAttribute('data-crf-action') === 'dcf') return;
            el.setAttribute('data-crf-action', 'audit');
            el.setAttribute('title', 'View Audit');
            el.setAttribute('role', 'button');
            el.style.pointerEvents = 'auto';
            el.style.cursor = 'pointer';
          });
          const wrapperSelector = 'a, button, [role="button"], .icon-btn';
          const topLevelWrappers = Array.from(sideBtnsClone.querySelectorAll(wrapperSelector)).filter(
            (el) => el.closest(wrapperSelector) === el
          );
          const allClickables = sideBtnsClone.querySelectorAll('button, a, [role="button"], .icon-btn, i, span, svg');
          const btns = topLevelWrappers.length >= 2 ? topLevelWrappers : Array.from(allClickables).slice(0, 2);
          if (!sideBtnsClone.querySelector('[data-crf-action="dcf"]')) {
            btns[0]?.setAttribute('data-crf-action', 'dcf');
            setReviewerCommentTooltip(btns[0]);
            btns[0]?.setAttribute('role', 'button');
          }
          if (!sideBtnsClone.querySelector('[data-crf-action="audit"]') && btns[1]) {
            btns[1].setAttribute('data-crf-action', 'audit');
            btns[1].setAttribute('title', 'View Audit');
            btns[1].setAttribute('role', 'button');
          }
          actionsWrap.appendChild(sideBtnsClone);
        } else {
          const dcfBtn = document.createElement('button');
          dcfBtn.type = 'button';
          dcfBtn.className = 'btn btn-sm btn-outline-secondary crf-action-btn';
          dcfBtn.setAttribute('data-crf-action', 'dcf');
          setReviewerCommentTooltip(dcfBtn);
          dcfBtn.textContent = "Reviewer's Comment";
          const auditBtn = document.createElement('button');
          auditBtn.type = 'button';
          auditBtn.className = 'btn btn-sm btn-outline-secondary crf-action-btn';
          auditBtn.setAttribute('data-crf-action', 'audit');
          auditBtn.setAttribute('title', 'View Audit');
          auditBtn.textContent = 'Audit';
          const actionItemsBtn = document.createElement('span');
          actionItemsBtn.className = 'btn btn-sm btn-outline-primary crf-action-btn crf-btn-icon';
          actionItemsBtn.setAttribute('data-crf-action', 'actionitems');
          actionItemsBtn.setAttribute('title', 'Issue Review');
          actionItemsBtn.setAttribute('aria-label', 'Issue Review');
          actionItemsBtn.setAttribute('role', 'button');
          actionItemsBtn.setAttribute('tabindex', '0');
          actionItemsBtn.innerHTML = crfActionIconSvg('actionitem');
          actionsWrap.appendChild(dcfBtn);
          actionsWrap.appendChild(auditBtn);
          actionsWrap.appendChild(actionItemsBtn);
          if (canRaiseActionItem) {
            const createActionItemBtn = document.createElement('span');
            createActionItemBtn.className = 'btn btn-sm btn-outline-primary crf-action-btn crf-btn-icon';
            createActionItemBtn.setAttribute('data-crf-action', 'createactionitem');
            createActionItemBtn.setAttribute('title', 'Create Issue');
            createActionItemBtn.setAttribute('aria-label', 'Create Issue');
            createActionItemBtn.setAttribute('role', 'button');
            createActionItemBtn.setAttribute('tabindex', '0');
            createActionItemBtn.innerHTML = crfActionIconSvg('plus');
            actionsWrap.appendChild(createActionItemBtn);
          }
        }
        if (sideBtns) {
          const actionItemsBtn = document.createElement('span');
          actionItemsBtn.setAttribute('style', 'color: #208094;');
          actionItemsBtn.setAttribute('data-crf-action', 'actionitems');
          actionItemsBtn.setAttribute('title', 'Issue Review');
          actionItemsBtn.setAttribute('aria-label', 'Issue Review');
          actionItemsBtn.setAttribute('role', 'button');
          actionItemsBtn.setAttribute('tabindex', '0');
          actionItemsBtn.innerHTML = crfActionIconSvg('actionitem');
          actionsWrap.appendChild(actionItemsBtn);
          if (canRaiseActionItem) {
            const createActionItemBtn = document.createElement('span');
            createActionItemBtn.setAttribute('style', 'color: #208094;');
            createActionItemBtn.setAttribute('data-crf-action', 'createactionitem');
            createActionItemBtn.setAttribute('title', 'Create Issue');
            createActionItemBtn.setAttribute('aria-label', 'Create Issue');
            createActionItemBtn.setAttribute('role', 'button');
            createActionItemBtn.setAttribute('tabindex', '0');
            createActionItemBtn.innerHTML = crfActionIconSvg('plus');
            actionsWrap.appendChild(createActionItemBtn);
          }
        }
        questionCell.appendChild(actionsWrap);

        // Ensure DCF, View Audit, Action Items and Create Action Item are never disabled and stay clickable (incl. First/Second Review)
        const dcfBtnEl = actionsWrap.querySelector('[data-crf-action="dcf"]');
        const auditBtnEl = actionsWrap.querySelector('[data-crf-action="audit"]');
        const actionItemsBtnEl = actionsWrap.querySelector('[data-crf-action="actionitems"]');
        const createActionItemBtnEl = actionsWrap.querySelector('[data-crf-action="createactionitem"]');
        [dcfBtnEl, auditBtnEl, actionItemsBtnEl, createActionItemBtnEl].forEach((el) => {
          if (!el) return;
          el.removeAttribute('disabled');
          el.setAttribute('tabindex', '0');
          el.style.pointerEvents = 'auto';
          el.style.cursor = 'pointer';
        });
        actionsWrap.style.pointerEvents = 'auto';

        // Direct click handlers so DCF/Audit work when CRF is inside nested modal (First/Second Review)
        const questionTextForAction = getCrfQuestionTextWithoutHintIcon(labelEl);
        const wireDcfIconClick = (el) => {
          if (!el || !actionHandlerRef?.current) return;
          el.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            const clickRow = e.target.closest?.('.crf-report-row') || tableRow;
            const blockMsg = actionHandlerRef.current.getDcfRaiseBlockMessage?.(
              fieldCode,
              clickRow,
              nCRFDtlNoForRow,
              true
            );
            if (blockMsg) {
              actionHandlerRef.current.showDcfRaiseBlockToast?.(blockMsg);
              return;
            }
            actionHandlerRef.current.openDcf({
              nCRFDtlNo: nCRFDtlNoForRow,
              fieldId: fieldCode,
              questionText: questionTextForAction,
            });
          });
        };
        actionsWrap.querySelectorAll('[data-crf-action="dcf"]').forEach(wireDcfIconClick);
        if (auditBtnEl && actionHandlerRef?.current) {
          auditBtnEl.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            // Resolve nCRFDtlNo from row at click time so radio (and any) row works even if transform ran before savedFields had this field
            const row = e.target.closest?.('.crf-report-row');
            const nCRFDtlNoFromRow = row?.getAttribute?.('data-n-crf-dtl-no');
            const nCRFDtlNo = (nCRFDtlNoFromRow != null && nCRFDtlNoFromRow !== '') ? Number(nCRFDtlNoFromRow) : nCRFDtlNoForRow;
            actionHandlerRef.current.openAudit({ nCRFDtlNo: nCRFDtlNo ?? null, fieldId: fieldCode, questionText: questionTextForAction });
          });
        }
        if (actionItemsBtnEl && actionHandlerRef?.current) {
          actionItemsBtnEl.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            actionHandlerRef.current.openActionItems({ nCRFDtlNo: nCRFDtlNoForRow, fieldId: fieldCode, questionText: questionTextForAction });
          });
        }
        if (createActionItemBtnEl && actionHandlerRef?.current) {
          createActionItemBtnEl.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            const r = e.target.closest('.crf-report-row');
            const fromRow = r?.getAttribute?.('data-medex-code')
              || (r?.querySelector?.('.crf-answer-cell[data-field-id], .crf-answer-col[data-field-id]')?.getAttribute?.('data-field-id'))?.trim();
            const vMedExCode = (fromRow && fromRow !== '') ? fromRow : (medExCode || fieldCode || '');
            actionHandlerRef.current.openCreateActionItem({ fieldCode: vMedExCode || null, nCRFDtlNo: nCRFDtlNoForRow });
          });
        }

        // Answer cell (set data-field-id on cell too so DCF/Audit row lookup works)
        const answerCell = document.createElement('div');
        answerCell.className = 'crf-answer-cell';
        if (assocMarkerSource) answerCell.classList.add('crf-answer-cell-associated');
        answerCell.setAttribute('data-field-id', fieldCode);
        answerCell.style.display = 'table-cell';
        answerCell.style.width = '15%';
        answerCell.style.padding = '12px';
        answerCell.style.verticalAlign = 'top';
        answerCell.style.border = '1px solid #ddd';
        answerCell.style.backgroundColor = 'white';

        // Clone the entire container to preserve radio groups, checkboxes with labels, etc.
        const containerToClone = controlGroup || answerCol || input.closest('.form-group') || input.parentElement;
        if (containerToClone) {
          const containerClone = containerToClone.cloneNode(true);

          // Clean up the clone: remove side buttons, main label (question), and any other non-answer elements
          const sideBtnsInClone = containerClone.querySelectorAll('.col-sideBtns');
          sideBtnsInClone.forEach(el => el.remove());
          const labelsInClone = containerClone.querySelectorAll('label.form-label, .crf-question-text');
          labelsInClone.forEach(el => {
            // Only remove if it's the main question label (usually has specific classes or is the first label)
            // But for checkboxes/radios, the label NEXT to the input is the option label, keep it.
            // Heuristic: if it looks like the main question text, remove it.
            if (el.textContent === (labelEl?.textContent || '')) {
              el.remove();
            }
          });
          // Driver row only: branch attributes (e.g. ABC1) must keep their full Answer widgets (often textarea).
          // Running this strip on branch rows removed all textarea nodes (assocNoise), broke data-field-id / DCF highlight.
          if (isAssociatedDriverControlRow) {
            // Associated rows must only render the driver control in Answer column.
            // Strip hidden marker and any comment controls that can leak from source layout.
            const assocNoise = containerClone.querySelectorAll(
              '.crf-associated-marker, .crf-comments-col, .crf-comments-field, textarea, [id$="_comments"], [name$="_comments"]'
            );
            assocNoise.forEach((el) => el.remove());
            // Flatten nested bootstrap grid from Associated source HTML so Answer cell does not look like 3-4 columns.
            containerClone.querySelectorAll('.row').forEach((el) => {
              el.classList.remove('row');
              el.style.display = 'block';
            });
            containerClone.querySelectorAll('[class]').forEach((el) => {
              if (!el.classList) return;
              Array.from(el.classList).forEach((cls) => {
                if (/^(col|offset)(-|$)/i.test(cls)) el.classList.remove(cls);
              });
            });
            // Keep only the actual driver UI (radio/checkbox wrap, dropdown, or text input)
            // so the Answer cell remains a single cell (no nested pseudo-columns).
            const assocDriverOnly = containerClone.querySelector(
              '.crf-associated-driver-wrap, select, input[type="text"]'
            );
            if (assocDriverOnly) {
              const clean = document.createElement('div');
              clean.className = 'crf-associated-driver-only';
              clean.appendChild(assocDriverOnly.cloneNode(true));
              containerClone.innerHTML = '';
              containerClone.appendChild(clean);
            }
            const assocWrap = containerClone.querySelector('.crf-associated-driver-wrap');
            if (assocWrap) {
              assocWrap.classList.add('crf-associated-driver-inline');
              assocWrap.style.display = 'inline-flex';
              assocWrap.style.gap = '12px';
              assocWrap.style.flexWrap = 'wrap';
            }
          }

          // Ensure all inputs in the clone have data-field-id (exclude hidden to prevent population issues)
          const inputsInClone = containerClone.querySelectorAll('input:not([type="hidden"]), select, textarea');
          inputsInClone.forEach(inp => inp.setAttribute('data-field-id', fieldCode));

          // Append the cleaned container
          answerCell.appendChild(containerClone);
          pruneDuplicateVisitReportAnswerTextInputs(answerCell, fieldCode);
        } else {
          // Fallback: just clone the input if no container found
          const inputClone = input.cloneNode(true);
          inputClone.setAttribute('data-field-id', fieldCode);
          inputClone.style.width = '100%';
          answerCell.appendChild(inputClone);
          pruneDuplicateVisitReportAnswerTextInputs(answerCell, fieldCode);
        }

        // Comments cell
        const commentsCell = document.createElement('div');
        commentsCell.className = 'crf-comments-cell';
        commentsCell.style.display = 'table-cell';
        commentsCell.style.width = '25%';
        commentsCell.style.padding = '12px';
        commentsCell.style.verticalAlign = 'top';
        commentsCell.style.border = '1px solid #ddd';
        commentsCell.style.borderLeft = 'none';
        commentsCell.style.backgroundColor = 'white';
        commentsCell.appendChild(commentsField);

        tableRow.appendChild(questionCell);
        tableRow.appendChild(answerCell);
        tableRow.appendChild(commentsCell);
        if (tableContainer) tableContainer.appendChild(tableRow);
      });

      // STS AutoCapture: full-width blocks after the standard report table (same subgroup, rendered last)
      const stsBlocksToAppend = [];
      stsSections.forEach((section) => {
        const row = section.querySelector('.row') || section;
        const labelEl = row.querySelector('label.form-label, .crf-question-text');
        const controlGroup = row.querySelector('.form-group');
        const input = controlGroup?.querySelector('[data-sts-autocapture="true"]')
          || row.querySelector('[data-sts-autocapture="true"]');
        if (!input || !labelEl) return;

        const rawFieldId = (input.id || input.name || '').trim();
        const fieldCode = stsExtractFieldCodeFromControl(input, stsControlTypes);
        const commentsBase = rawFieldId.replace(/_answer$/i, '');
        const commentsId = `${commentsBase}_comments`;

        const list = savedFieldsList || [];
        const nCRFDtlNoFromMap =
          idToDtl[fieldCode] ??
          idToDtl[fieldCode.replace(/_answer$/i, '').trim()] ??
          (fieldCode && /^\d{1,5}$/.test(fieldCode) ? idToDtl[fieldCode.padStart(5, '0')] : undefined) ??
          null;
        const fcTrim = (fieldCode || '').trim();
        const fcNorm = fcTrim ? normalizeCrfFieldCodeForMatch(fcTrim) : '';
        const savedFieldByFieldId = fcTrim
          ? list.find((f) => {
              const fid = (f.vFieldId || f.VFieldId || '').trim();
              if (!fid) return false;
              if (fid.toLowerCase() === fcTrim.toLowerCase()) return true;
              return fcNorm ? normalizeCrfFieldCodeForMatch(fid) === fcNorm : false;
            })
          : null;
        const savedFieldByDtl =
          nCRFDtlNoFromMap != null
            ? list.find((f) => Number(f.nCRFDtlNo ?? f.NCRFDtlNo ?? f.ncrfDtlNo ?? 0) === nCRFDtlNoFromMap)
            : null;
        let savedField = savedFieldByFieldId || savedFieldByDtl;
        const nFromSaved = savedField != null ? Number(savedField.nCRFDtlNo ?? savedField.NCRFDtlNo ?? savedField.ncrfDtlNo ?? 0) : NaN;
        const nCRFDtlNoForRow = !Number.isNaN(nFromSaved) && nFromSaved > 0 ? nFromSaved : nCRFDtlNoFromMap;
        const medExCode =
          savedField && (savedField.vFieldId || savedField.VFieldId)
            ? String(savedField.vFieldId || savedField.VFieldId).trim()
            : fieldCode
              ? String(fieldCode).trim()
              : '';

        const block = document.createElement('div');
        block.className = 'crf-sts-autocapture-block';
        if (nCRFDtlNoForRow != null) block.setAttribute('data-n-crf-dtl-no', String(nCRFDtlNoForRow));
        if (medExCode) {
          block.setAttribute('data-medex-code', medExCode);
          block.setAttribute('data-field-id', medExCode);
        }

        const questionTextForAction = getCrfQuestionTextWithoutHintIcon(labelEl);

        const sideBtns = controlGroup?.querySelector('.col-sideBtns');
        const actionsWrap = document.createElement('div');
        actionsWrap.className = 'col-sideBtns crf-row-actions crf-sts-row-actions';
        if (sideBtns) {
          const sideBtnsClone = sideBtns.cloneNode(true);
          sideBtnsClone.querySelectorAll('.fieldDcfBtn, [class*="fieldDcfBtn"], [class*="DcfBtn"], [data-crf-action="dcf"]').forEach((el) => {
            el.setAttribute('data-crf-action', 'dcf');
            setReviewerCommentTooltip(el);
            el.setAttribute('role', 'button');
            el.style.pointerEvents = 'auto';
            el.style.cursor = 'pointer';
          });
          sideBtnsClone.querySelectorAll('.fieldauditBtn, [class*="fieldauditBtn"], [class*="auditBtn"]').forEach((el) => {
            if (el.getAttribute('data-crf-action') === 'dcf') return;
            el.setAttribute('data-crf-action', 'audit');
            el.setAttribute('title', 'View Audit');
            el.setAttribute('role', 'button');
            el.style.pointerEvents = 'auto';
            el.style.cursor = 'pointer';
          });
          const wrapperSelector = 'a, button, [role="button"], .icon-btn';
          const topLevelWrappers = Array.from(sideBtnsClone.querySelectorAll(wrapperSelector)).filter(
            (el) => el.closest(wrapperSelector) === el
          );
          const allClickables = sideBtnsClone.querySelectorAll('button, a, [role="button"], .icon-btn, i, span, svg');
          const btns = topLevelWrappers.length >= 2 ? topLevelWrappers : Array.from(allClickables).slice(0, 2);
          if (!sideBtnsClone.querySelector('[data-crf-action="dcf"]')) {
            btns[0]?.setAttribute('data-crf-action', 'dcf');
            setReviewerCommentTooltip(btns[0]);
            btns[0]?.setAttribute('role', 'button');
          }
          if (!sideBtnsClone.querySelector('[data-crf-action="audit"]') && btns[1]) {
            btns[1].setAttribute('data-crf-action', 'audit');
            btns[1].setAttribute('title', 'View Audit');
            btns[1].setAttribute('role', 'button');
          }
          actionsWrap.appendChild(sideBtnsClone);
          const actionItemsBtn = document.createElement('span');
          actionItemsBtn.setAttribute('style', 'color: #208094;');
          actionItemsBtn.setAttribute('data-crf-action', 'actionitems');
          actionItemsBtn.setAttribute('title', 'Issue Review');
          actionItemsBtn.setAttribute('aria-label', 'Issue Review');
          actionItemsBtn.setAttribute('role', 'button');
          actionItemsBtn.setAttribute('tabindex', '0');
          actionItemsBtn.innerHTML = crfActionIconSvg('actionitem');
          actionsWrap.appendChild(actionItemsBtn);
          if (canRaiseActionItem) {
            const createActionItemBtn = document.createElement('span');
            createActionItemBtn.setAttribute('style', 'color: #208094;');
            createActionItemBtn.setAttribute('data-crf-action', 'createactionitem');
            createActionItemBtn.setAttribute('title', 'Create Issue');
            createActionItemBtn.setAttribute('aria-label', 'Create Issue');
            createActionItemBtn.setAttribute('role', 'button');
            createActionItemBtn.setAttribute('tabindex', '0');
            createActionItemBtn.innerHTML = crfActionIconSvg('plus');
            actionsWrap.appendChild(createActionItemBtn);
          }
        } else {
          const dcfBtn = document.createElement('button');
          dcfBtn.type = 'button';
          dcfBtn.className = 'btn btn-sm btn-outline-secondary crf-action-btn';
          dcfBtn.setAttribute('data-crf-action', 'dcf');
          setReviewerCommentTooltip(dcfBtn);
          dcfBtn.textContent = "Reviewer's Comment";
          const auditBtn = document.createElement('button');
          auditBtn.type = 'button';
          auditBtn.className = 'btn btn-sm btn-outline-secondary crf-action-btn';
          auditBtn.setAttribute('data-crf-action', 'audit');
          auditBtn.setAttribute('title', 'View Audit');
          auditBtn.textContent = 'Audit';
          const actionItemsBtn = document.createElement('span');
          actionItemsBtn.className = 'btn btn-sm btn-outline-primary crf-action-btn crf-btn-icon';
          actionItemsBtn.setAttribute('data-crf-action', 'actionitems');
          actionItemsBtn.setAttribute('title', 'Issue Review');
          actionItemsBtn.setAttribute('aria-label', 'Issue Review');
          actionItemsBtn.setAttribute('role', 'button');
          actionItemsBtn.setAttribute('tabindex', '0');
          actionItemsBtn.innerHTML = crfActionIconSvg('actionitem');
          actionsWrap.appendChild(dcfBtn);
          actionsWrap.appendChild(auditBtn);
          actionsWrap.appendChild(actionItemsBtn);
          if (canRaiseActionItem) {
            const createActionItemBtn = document.createElement('span');
            createActionItemBtn.className = 'btn btn-sm btn-outline-primary crf-action-btn crf-btn-icon';
            createActionItemBtn.setAttribute('data-crf-action', 'createactionitem');
            createActionItemBtn.setAttribute('title', 'Create Issue');
            createActionItemBtn.setAttribute('aria-label', 'Create Issue');
            createActionItemBtn.setAttribute('role', 'button');
            createActionItemBtn.setAttribute('tabindex', '0');
            createActionItemBtn.innerHTML = crfActionIconSvg('plus');
            actionsWrap.appendChild(createActionItemBtn);
          }
        }

        const questionRow = document.createElement('div');
        questionRow.className = 'crf-sts-field-row crf-sts-question-row';
        const questionLabel = document.createElement('div');
        questionLabel.className = 'crf-sts-field-label';
        questionLabel.textContent = 'Question';
        const questionBody = document.createElement('div');
        questionBody.className = 'crf-sts-field-body';
        const questionTextEl = document.createElement('div');
        questionTextEl.className = 'crf-sts-question-text';
        const labelClone = sanitizeQuestionLabelNode(labelEl);
        if (labelClone) questionTextEl.appendChild(labelClone);
        else questionTextEl.textContent = questionTextForAction || labelEl.textContent || '';
        questionBody.appendChild(questionTextEl);
        questionBody.appendChild(actionsWrap);
        questionRow.appendChild(questionLabel);
        questionRow.appendChild(questionBody);
        const dcfBtnEl = actionsWrap.querySelector('[data-crf-action="dcf"]');
        const auditBtnEl = actionsWrap.querySelector('[data-crf-action="audit"]');
        const actionItemsBtnEl = actionsWrap.querySelector('[data-crf-action="actionitems"]');
        const createActionItemBtnEl = actionsWrap.querySelector('[data-crf-action="createactionitem"]');
        [dcfBtnEl, auditBtnEl, actionItemsBtnEl, createActionItemBtnEl].forEach((el) => {
          if (!el) return;
          el.removeAttribute('disabled');
          el.setAttribute('tabindex', '0');
          el.style.pointerEvents = 'auto';
          el.style.cursor = 'pointer';
        });
        actionsWrap.style.pointerEvents = 'auto';
        const wireStsDcfIconClick = (el) => {
          if (!el || !actionHandlerRef?.current) return;
          el.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            const blockMsg = actionHandlerRef.current.getDcfRaiseBlockMessage?.(
              medExCode || fieldCode,
              block,
              nCRFDtlNoForRow,
              true
            );
            if (blockMsg) {
              actionHandlerRef.current.showDcfRaiseBlockToast?.(blockMsg);
              return;
            }
            const nFromRow = block.getAttribute?.('data-n-crf-dtl-no');
            const nCRFDtlNo = (nFromRow != null && nFromRow !== '') ? Number(nFromRow) : nCRFDtlNoForRow;
            actionHandlerRef.current.openDcf({
              nCRFDtlNo: nCRFDtlNo ?? null,
              fieldId: medExCode || fieldCode,
              questionText: questionTextForAction,
            });
          });
        };
        actionsWrap.querySelectorAll('[data-crf-action="dcf"]').forEach(wireStsDcfIconClick);
        if (auditBtnEl && actionHandlerRef?.current) {
          auditBtnEl.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            const nFromRow = block.getAttribute?.('data-n-crf-dtl-no');
            const nCRFDtlNo = (nFromRow != null && nFromRow !== '') ? Number(nFromRow) : nCRFDtlNoForRow;
            actionHandlerRef.current.openAudit({ nCRFDtlNo: nCRFDtlNo ?? null, fieldId: fieldCode, questionText: questionTextForAction });
          });
        }
        if (actionItemsBtnEl && actionHandlerRef?.current) {
          actionItemsBtnEl.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            const nFromRow = block.getAttribute?.('data-n-crf-dtl-no');
            const nCRFDtlNo = (nFromRow != null && nFromRow !== '') ? Number(nFromRow) : nCRFDtlNoForRow;
            actionHandlerRef.current.openActionItems({ nCRFDtlNo: nCRFDtlNo ?? null, fieldId: fieldCode, questionText: questionTextForAction });
          });
        }
        if (createActionItemBtnEl && actionHandlerRef?.current) {
          createActionItemBtnEl.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            const nFromRow = block.getAttribute?.('data-n-crf-dtl-no');
            const nCRFDtlNo = (nFromRow != null && nFromRow !== '') ? Number(nFromRow) : nCRFDtlNoForRow;
            const vMedExCode = medExCode || fieldCode || '';
            actionHandlerRef.current.openCreateActionItem({ fieldCode: vMedExCode || null, nCRFDtlNo: nCRFDtlNo ?? null });
          });
        }

        const answerRow = document.createElement('div');
        answerRow.className = 'crf-sts-field-row crf-sts-answer-row';
        const answerLabel = document.createElement('div');
        answerLabel.className = 'crf-sts-field-label';
        answerLabel.textContent = 'Answer';
        const answerBody = document.createElement('div');
        answerBody.className = 'crf-sts-field-body crf-sts-autocapture-table-strip';
        const displayEl = document.createElement('div');
        displayEl.className = 'sts-autocapture-display';
        displayEl.setAttribute('data-sts-autocapture-display', 'true');
        answerBody.appendChild(displayEl);
        const inputClone = input.cloneNode(true);
        inputClone.setAttribute('data-field-id', fieldCode);
        answerBody.appendChild(inputClone);
        answerRow.appendChild(answerLabel);
        answerRow.appendChild(answerBody);

        const commentRow = document.createElement('div');
        commentRow.className = 'crf-sts-field-row crf-sts-comment-row';
        const commentLabel = document.createElement('div');
        commentLabel.className = 'crf-sts-field-label';
        commentLabel.textContent = 'Comment';
        const commentBody = document.createElement('div');
        commentBody.className = 'crf-sts-field-body crf-sts-autocapture-comments';
        const commentsField = document.createElement('textarea');
        commentsField.id = commentsId;
        commentsField.name = commentsId;
        commentsField.setAttribute('data-field-id', fieldCode);
        commentsField.className = 'form-control crf-comments-field crf-sts-comments-field';
        commentsField.placeholder = 'Enter the Comments';
        commentsField.rows = 2;
        commentsField.maxLength = 500;
        const existingComments = row.querySelector('[id$="_comments"], [name$="_comments"]');
        if (existingComments) {
          commentsField.value = (existingComments.value !== undefined ? existingComments.value : existingComments.textContent) || '';
        }
        commentBody.appendChild(commentsField);
        commentRow.appendChild(commentLabel);
        commentRow.appendChild(commentBody);

        block.appendChild(questionRow);
        block.appendChild(answerRow);
        block.appendChild(commentRow);
        stsBlocksToAppend.push(block);
      });

      // Replace entire subgroup content with the table (so no duplicate rows remain)
      while (subGroup.firstChild) {
        subGroup.removeChild(subGroup.firstChild);
      }
      if (tableContainer) {
        subGroup.appendChild(tableContainer);
      } else if (stsBlocksToAppend.length > 0) {
        subGroup.classList.add('crf-subgroup-sts-only');
      }
      stsBlocksToAppend.forEach((b) => {
        subGroup.appendChild(b);
      });

      // Mark as transformed
      subGroup.classList.add('crf-report-transformed');
    });
  };

  // Control-type words that must not be used as field codes (backend expects vMedExCode e.g. 00005)
  const CONTROL_TYPE_NAMES = ['radio', 'checkbox', 'textbox', 'textarea', 'combobox', 'label', 'formula', 'file', 'reportfield', 'datetime', 'time', 'standarddate'];

  const isControlTypeName = (id) => typeof id === 'string' && CONTROL_TYPE_NAMES.includes(id.trim().toLowerCase());

  // Extract 5-char field code (vMedExCode) from HTML id/name (e.g. reportfield_IB001_answer -> IB001, textbox_IB001 -> IB001, radio_00005_0 -> 00005)
  const extractFieldCode = React.useCallback((raw) => {
    if (!raw || typeof raw !== 'string') return '';
    const s = raw.trim();
    if (!s) return '';
    // Do not use control-type words as field codes (e.g. "radio" would break save)
    const lower = s.toLowerCase();
    if (CONTROL_TYPE_NAMES.some((t) => lower === t || lower.startsWith(t + '_'))) {
      // Parse from pattern like radio_00005_0 -> 00005
      const parts = s.split('_');
      const fiveChar = parts.find((p) => p.length === 5 && /^[A-Za-z0-9]+$/.test(p));
      if (fiveChar) return fiveChar;
      const numericPart = parts.find((p) => /^\d{4,5}$/.test(p));
      if (numericPart) return numericPart;
      return '';
    }
    // Short code: accept only if not a control-type name
    if (s.length <= 5 && /^[A-Za-z0-9]+$/.test(s)) {
      if (CONTROL_TYPE_NAMES.includes(lower)) return '';
      return s;
    }
    const parts = s.split('_');
    // Find 5-char alphanumeric segment (field codes like IB001, 00001)
    const fiveChar = parts.find((p) => p.length === 5 && /^[A-Za-z0-9]+$/.test(p));
    if (fiveChar) return fiveChar;
    // Fallback: last segment before _answer or _comments (e.g. reportfield_IB001_answer -> IB001)
    const beforeAnswer = s.replace(/_answer$/i, '').replace(/_comments$/i, '');
    const lastPart = beforeAnswer.split('_').pop();
    if (lastPart && lastPart.length <= 5 && /^[A-Za-z0-9]+$/.test(lastPart)) {
      if (CONTROL_TYPE_NAMES.includes(lastPart.toLowerCase())) return '';
      return lastPart;
    }
    // Last resort: first 5 chars if alphanumeric and not a control type
    const code = s.substring(0, 5);
    if (/^[A-Za-z0-9]+$/.test(code) && !CONTROL_TYPE_NAMES.includes(code.toLowerCase())) return code;
    return '';
  }, []);

  // Format StandardDate/StandardDateTime for vFieldValue (DD-MMM-YYYY and DD-MMM-YYYY HH:MM). Used by collectFormData and getCurrentFieldValueFromForm.
  // Preserve UK (day) and UNK (month) as-is in saved value.
  const formatStandardDateValueForSave = React.useCallback((raw) => {
    const s = (raw || '').trim();
    if (!s || s.length < 9) return s;
    const dd = s.substring(0, 2);
    const mmm = s.substring(2, 5).toUpperCase();
    const yyyy = s.substring(5, 9);
    const dayDisplay = dd === '00' ? 'UK' : dd; // save UK when day dropdown value is 00
    const monthDisplay = mmm === 'UNK' ? 'UNK' : mmm.charAt(0) + mmm.slice(1).toLowerCase();
    return `${dayDisplay}-${monthDisplay}-${yyyy}`;
  }, []);
  const formatStandardDateTimeValueForSave = React.useCallback((raw) => {
    const s = (raw || '').trim();
    if (!s || s.length < 13) return s;
    const dd = s.substring(0, 2);
    const mmm = s.substring(2, 5).toUpperCase();
    const yyyy = s.substring(5, 9);
    const hh = s.substring(9, 11);
    const mm = s.substring(11, 13);
    const dayDisplay = dd === '00' ? 'UK' : dd;
    const monthDisplay = mmm === 'UNK' ? 'UNK' : mmm.charAt(0) + mmm.slice(1).toLowerCase();
    return `${dayDisplay}-${monthDisplay}-${yyyy} ${hh}:${mm}`;
  }, []);

  const collectFormData = (contentRoot = null) => {
    const content = contentRoot ?? crfContainerRef.current?.querySelector('.crf-generated-content') ?? document.querySelector('.crf-generated-content');
    if (!content) return [];
    flushStsAutoCaptureHiddenFieldsFromTables(content);
    const fields = [];
    const seen = new Set();
    const isControlVisibleForSave = (el) => {
      if (!el || !content) return false;
      if (el.getAttribute?.('data-sts-autocapture') === 'true') return true;
      let node = el;
      while (node && node !== content) {
        if (typeof window !== 'undefined' && typeof window.getComputedStyle === 'function') {
          const cs = window.getComputedStyle(node);
          if (cs.display === 'none' || cs.visibility === 'hidden') return false;
        }
        node = node.parentElement;
      }
      return true;
    };

    // Answer vs Comment: isCrfCommentFieldControl (module) — all control types, not textarea-only.
    const rowCommentsTextareaSel = CRF_COMMENTS_TEXTAREA_SEL;

    // Helper: derive 5-char field code from backend name format (e.g. radio_00005, checkbox_00005, file_00005)
    const fieldIdFromName = (nameStr) => {
      if (!nameStr || typeof nameStr !== 'string') return '';
      const parts = nameStr.trim().split('_');
      const five = parts.find((p) => p.length === 5 && /^[A-Za-z0-9]+$/.test(p) && !isControlTypeName(p));
      if (five) return five;
      const four = parts.find((p) => p.length === 4 && /^[A-Za-z0-9]+$/.test(p) && !isControlTypeName(p));
      if (four) return four;
      return extractFieldCode(nameStr);
    };

    // First pass: collect all radio groups by fieldId and push one entry per group (checked value)
    // Parse id/name first so we never use "radio" as fieldId (e.g. radio_00001_Yes -> 00001); then data-field-id
    const radioInputs = content.querySelectorAll('input[type="radio"]');
    const radioGroupsByFieldId = new Map();
    radioInputs.forEach((el) => {
      if (!isControlVisibleForSave(el)) return;
      if (isCrfCommentFieldControl(el)) return;
      const rawId = (el.id || el.name || '').trim();
      let fieldId = '';
      // 1) From id like radio_00001_Yes or radio_00005_No: take parts[1] (never use "radio" as fieldId)
      if (el.id && typeof el.id === 'string' && el.id.toLowerCase().startsWith('radio_')) {
        const parts = el.id.split('_');
        const candidate = parts.length >= 2 && parts[1] ? parts[1].trim() : '';
        if (candidate && candidate.length <= 5 && !isControlTypeName(candidate)) fieldId = candidate;
        if (!fieldId && parts.length >= 3 && parts[2] && /^[A-Za-z0-9]{4,5}$/.test(parts[2]) && !isControlTypeName(parts[2])) fieldId = parts[2].trim();
      }
      // 2) From name (e.g. radio_00001) or data-field-id
      if (!fieldId && el.name) fieldId = fieldIdFromName(el.name) || extractFieldCode(el.name.trim());
      if (!fieldId && el.name && typeof el.name === 'string') {
        const afterPrefix = el.name.replace(/^radio_/i, '').trim();
        const segments = afterPrefix.split('_').filter(Boolean);
        const last = segments[segments.length - 1];
        if (last && /^[A-Za-z0-9]{4,5}$/.test(last) && !isControlTypeName(last)) fieldId = last;
        else if (/^[A-Za-z0-9]{4,5}$/.test(afterPrefix) && !isControlTypeName(afterPrefix)) fieldId = afterPrefix;
      }
      const dataFieldIdAttr = el.getAttribute?.('data-field-id');
      if (!fieldId && dataFieldIdAttr && dataFieldIdAttr.trim() && !isControlTypeName(dataFieldIdAttr.trim())) fieldId = dataFieldIdAttr.trim();
      if (!fieldId) fieldId = extractFieldCode(rawId);
      // Reject control-type names (e.g. "radio") so we never group by them
      if (fieldId && isControlTypeName(fieldId)) fieldId = '';
      // Backend CRFDtl.VFieldId is max 5 chars; normalize so save succeeds
      if (fieldId && fieldId.length > 5) fieldId = fieldId.substring(0, 5);
      if (!fieldId || isControlTypeName(fieldId)) return;
      if (!radioGroupsByFieldId.has(fieldId)) radioGroupsByFieldId.set(fieldId, []);
      radioGroupsByFieldId.get(fieldId).push(el);
    });
    radioGroupsByFieldId.forEach((inputs, fieldId) => {
      if (isControlTypeName(fieldId)) return;
      const checked = inputs.find((r) => r.checked);
      seen.add(fieldId);
      const row = inputs[0]?.closest?.('.crf-report-row');
      const commentsEl = (row ? row.querySelector(rowCommentsTextareaSel) : null) ||
        content.querySelector(`[id="${fieldId}_comments"], [name="${fieldId}_comments"]`) ||
        content.querySelector(`[id="${(inputs[0].name || '').replace(/_\d+$/, '')}_comments"]`) ||
        content.querySelector(`[id="reportfield_${fieldId}_comments"], [name="reportfield_${fieldId}_comments"]`) ||
        content.querySelector(`[id="radio_${fieldId}_comments"], [name="radio_${fieldId}_comments"]`);
      const comment = commentsEl ? (commentsEl.value || '').trim() : '';
      const radioValue = checked ? ((checked.value || '').trim() || 'Yes') : '';
      fields.push({ vFieldId: fieldId, vFieldValue: radioValue, vComment: comment || null });
    });

    // Second pass (RAMP-style): collect checkboxes by group (same name = one field), then other controls
    // Checkboxes: one entry per field with vFieldValue = comma-separated checked values (RAMP CheckList.jsx)
    // Prefer data-field-id so checkbox values are collected and saved reliably
    const checkboxInputs = content.querySelectorAll('input[type="checkbox"]');
    const checkboxGroupsByFieldId = new Map();
    checkboxInputs.forEach((el) => {
      if (!isControlVisibleForSave(el)) return;
      if (isCrfCommentFieldControl(el)) return;
      const dataFieldIdAttr = el.getAttribute?.('data-field-id');
      const rawId = (el.id || el.name || '').trim();
      let fieldId = (dataFieldIdAttr && dataFieldIdAttr.trim() && !isControlTypeName(dataFieldIdAttr.trim())) ? dataFieldIdAttr.trim() : extractFieldCode(rawId);
      if (!fieldId && el.id && typeof el.id === 'string' && el.id.toLowerCase().startsWith('checkbox_')) {
        const parts = el.id.split('_');
        const candidate = parts.length >= 2 && parts[1] ? parts[1].trim() : '';
        if (candidate && candidate.length <= 5 && !isControlTypeName(candidate)) fieldId = candidate;
        if (!fieldId && parts.length >= 3 && parts[2] && /^[A-Za-z0-9]{4,5}$/.test(parts[2]) && !isControlTypeName(parts[2])) fieldId = parts[2].trim();
      }
      if (!fieldId && el.name) fieldId = fieldIdFromName(el.name) || extractFieldCode(el.name.trim());
      if (fieldId && fieldId.length > 5) fieldId = fieldId.substring(0, 5);
      if (!fieldId || seen.has(fieldId) || isControlTypeName(fieldId)) return;
      if (!checkboxGroupsByFieldId.has(fieldId)) checkboxGroupsByFieldId.set(fieldId, []);
      checkboxGroupsByFieldId.get(fieldId).push(el);
    });
    checkboxGroupsByFieldId.forEach((inputs, fieldId) => {
      if (isControlTypeName(fieldId)) return;
      seen.add(fieldId);
      const checkedValues = inputs.filter((c) => c.checked).map((c) => (c.value || '').trim()).filter(Boolean);
      let value = checkedValues.length ? checkedValues.join(',') : '';
      const firstInput = inputs[0];
      const rawId = (firstInput.id || firstInput.name || '').trim();
      const commentsBase = rawId.replace(/_answer$/i, '');
      const row = firstInput?.closest?.('.crf-report-row');
      const commentsEl = (row ? row.querySelector(rowCommentsTextareaSel) : null) ||
        content.querySelector(`[id="${commentsBase}_comments"], [id="${fieldId}_comments"], [name="${commentsBase}_comments"]`) ||
        content.querySelector(`[id="reportfield_${fieldId}_comments"], [name="reportfield_${fieldId}_comments"]`) ||
        content.querySelector(`[id="checkbox_${fieldId}_comments"], [name="checkbox_${fieldId}_comments"]`);
      const comment = commentsEl ? (commentsEl.value || '').trim() : '';
      fields.push({ vFieldId: fieldId, vFieldValue: value, vComment: comment || null });
    });

    // Third pass: text, select, textarea, file (non-radio, non-checkbox) — excludes hidden and StandardDate/StandardDateTime part dropdowns (collected in fourth pass)
    const controls = Array.from(
      content.querySelectorAll(
        'input:not([type="hidden"]):not([type="radio"]):not([type="checkbox"]), select:not(.standarddate-select):not(.standarddatetime-select), textarea'
      )
    ).filter((el) => !isCrfCommentFieldControl(el) && isControlVisibleForSave(el));
    controls.forEach((el) => {
      const dataFieldId = el.getAttribute?.('data-field-id');
      const rawId = (el.id || el.name || '').trim();
      let fieldId = (dataFieldId?.trim() && !isControlTypeName(dataFieldId)) ? dataFieldId.trim() : extractFieldCode(rawId);
      if (!fieldId && el.name && el.type === 'file') {
        fieldId = fieldIdFromName(el.name) || extractFieldCode(el.name.trim());
      }
      if (!fieldId || fieldId.length > 5 || seen.has(fieldId) || isControlTypeName(fieldId)) return;
      seen.add(fieldId);
      let value = '';
      // STS AutoCapture: never save placeholder/loading/error into CRF values.
      if (el.getAttribute?.('data-sts-autocapture') === 'true') {
        const isLoading = el.getAttribute?.(STS_AUTOCAPTURE_LOADING_ATTR) === 'true';
        const isError = el.getAttribute?.(STS_AUTOCAPTURE_ERROR_ATTR) === 'true';
        if (isLoading || isError) value = '';
        else value = getStsAutoCapturePersistedValue(el);
      } else if (el.type === 'file') {
        value = (el.getAttribute('data-uploaded-path') || (el.value || '').trim()) || '';
      } else {
        value = (el.value || '').trim();
      }
      const commentsBase = rawId.replace(/_answer$/i, '');
      const row = el.closest?.('.crf-report-row');
      const commentsEl = (row ? row.querySelector(rowCommentsTextareaSel) : null) ||
        content.querySelector(`[id="${commentsBase}_comments"], [id="${fieldId}_comments"], [name="${commentsBase}_comments"]`) ||
        content.querySelector(`[id="reportfield_${fieldId}_comments"], [name="reportfield_${fieldId}_comments"]`);
      const comment = commentsEl ? (commentsEl.value || '').trim() : '';
      fields.push({ vFieldId: fieldId, vFieldValue: value, vComment: comment || null });
    });

    // STS AutoCapture pass: hidden value holders are excluded from the generic input selector above.
    content.querySelectorAll(STS_AUTOCAPTURE_SEL).forEach((el) => {
      const fieldId = (el.getAttribute('data-field-id') || '').trim() || resolveStsAutoCaptureFieldCode(el);
      if (!fieldId || fieldId.length > 5 || seen.has(fieldId) || isControlTypeName(fieldId)) return;
      seen.add(fieldId);
      let value = '';
      if (el.getAttribute(STS_AUTOCAPTURE_LOADING_ATTR) !== 'true' && el.getAttribute(STS_AUTOCAPTURE_ERROR_ATTR) !== 'true') {
        value = getStsAutoCapturePersistedValue(el);
      }
      const block = el.closest(STS_AUTOCAPTURE_BLOCK_SEL);
      const commentsEl = block?.querySelector('.crf-sts-comments-field')
        || block?.querySelector('textarea[id$="_comments"], textarea[name$="_comments"]');
      const comment = commentsEl ? (commentsEl.value || '').trim() : '';
      fields.push({ vFieldId: fieldId, vFieldValue: value, vComment: comment || null });
    });

    // Fourth pass: StandardDate and StandardDateTime — placeholder-only values saved as '' (see computeStandardDateFieldValueForSave).
    content.querySelectorAll('input.standarddate-text, input.standarddatetime-text').forEach((el) => {
      if (isCrfCommentFieldControl(el)) return;
      const rawId = (el.id || el.name || '').trim();
      const fieldId = extractFieldCode(rawId);
      if (!fieldId || fieldId.length > 5 || seen.has(fieldId) || isControlTypeName(fieldId)) return;
      seen.add(fieldId);
      let value = computeStandardDateFieldValueForSave(content, el, formatStandardDateValueForSave, formatStandardDateTimeValueForSave);
      const row = el.closest?.('.crf-report-row');
      const commentsEl = (row ? row.querySelector(rowCommentsTextareaSel) : null) ||
        content.querySelector(`[id="${fieldId}_comments"], [name="${fieldId}_comments"]`) ||
        content.querySelector(`[id="reportfield_${fieldId}_comments"], [name="reportfield_${fieldId}_comments"]`) ||
        content.querySelector(`[id="textbox_${fieldId}_comments"], [name="textbox_${fieldId}_comments"]`) ||
        content.querySelector(`[id="textarea_${fieldId}_comments"], [name="textarea_${fieldId}_comments"]`);
      const comment = commentsEl ? (commentsEl.value || '').trim() : '';
      fields.push({ vFieldId: fieldId, vFieldValue: value, vComment: comment || null });
    });

    return fields;
  };

  // Get current value for a single field from the form DOM (for DCF validation: compare with saved value).
  const getCurrentFieldValueFromForm = React.useCallback((fieldId) => {
    const content = crfContainerRef.current?.querySelector?.('.crf-generated-content') ?? document.querySelector('.crf-generated-content');
    if (!content || !fieldId) return '';
    const vFieldId = String(fieldId).trim();
    const stdInAnswer = content.querySelector(
      `.crf-answer-cell input.standarddate-text[id*="${CSS.escape(vFieldId)}"], .crf-answer-col input.standarddate-text[id*="${CSS.escape(vFieldId)}"], .crf-answer-cell input.standarddatetime-text[id*="${CSS.escape(vFieldId)}"], .crf-answer-col input.standarddatetime-text[id*="${CSS.escape(vFieldId)}"]`
    );
    const stdHidden =
      stdInAnswer ||
      content.querySelector(`input.standarddate-text[id*="${CSS.escape(vFieldId)}"], input.standarddatetime-text[id*="${CSS.escape(vFieldId)}"]`);
    if (stdHidden) {
      return computeStandardDateFieldValueForSave(content, stdHidden, formatStandardDateValueForSave, formatStandardDateTimeValueForSave);
    }
    const input = content.querySelector(`.crf-answer-cell [data-field-id="${vFieldId}"]`) ||
      content.querySelector(`.crf-answer-col [data-field-id="${vFieldId}"]`) ||
      content.querySelector(`[data-field-id="${vFieldId}"]`) ||
      content.querySelector(`[id="reportfield_${vFieldId}_answer"]`) ||
      content.querySelector(`[id="textbox_${vFieldId}"]`) ||
      content.querySelector(`[id="formula_${vFieldId}"]`) ||
      content.querySelector(`[id="textarea_${vFieldId}"]`) ||
      content.querySelector(`[id="combobox_${vFieldId}"]`) ||
      content.querySelector(`[id="datetime_${vFieldId}"]`) ||
      content.querySelector(`[id="time_${vFieldId}"]`) ||
      content.querySelector(`[id="file_${vFieldId}"]`) ||
      content.querySelector(`[name="reportfield_${vFieldId}_answer"]`) ||
      content.querySelector(`[name="textbox_${vFieldId}"]`) ||
      content.querySelector(`[name="textarea_${vFieldId}"]`) ||
      content.querySelector(`input[name="radio_${vFieldId}"], input[name^="radio_${vFieldId}_"]`) ||
      content.querySelector(`input[id^="radio_${vFieldId}_"], input[id="radio_${vFieldId}"]`) ||
      content.querySelector(`input[name="checkbox_${vFieldId}"], input[name^="checkbox_${vFieldId}_"]`) ||
      content.querySelector(`input[id="checkbox_${vFieldId}"]`) ||
      content.querySelector(`.crf-answer-cell input[name="radio_${vFieldId}"], .crf-answer-col input[name="radio_${vFieldId}"], .crf-answer-cell input[name^="radio_${vFieldId}_"], .crf-answer-col input[name^="radio_${vFieldId}_"]`) ||
      content.querySelector(`.crf-answer-cell input[name="checkbox_${vFieldId}"], .crf-answer-col input[name="checkbox_${vFieldId}"], .crf-answer-cell input[name^="checkbox_${vFieldId}_"], .crf-answer-col input[name^="checkbox_${vFieldId}_"]`) ||
      content.querySelector(`.crf-answer-cell input[id*="${vFieldId}"], .crf-answer-cell select[id*="${vFieldId}"], .crf-answer-cell textarea[id*="${vFieldId}"]`) ||
      content.querySelector(`.crf-answer-col input[id*="${vFieldId}"], .crf-answer-col select[id*="${vFieldId}"], .crf-answer-col textarea[id*="${vFieldId}"]`) ||
      content.querySelector(`[id$="_answer"][id*="${vFieldId}"]`) ||
      content.querySelector(`.crf-answer-cell input[type="file"][id*="${vFieldId}"], .crf-answer-col input[type="file"][id*="${vFieldId}"]`) ||
      content.querySelector(`.crf-answer-cell input[type="date"][id*="${vFieldId}"], .crf-answer-col input[type="date"][id*="${vFieldId}"]`) ||
      content.querySelector(`.crf-answer-cell input[type="datetime-local"][id*="${vFieldId}"], .crf-answer-col input[type="datetime-local"][id*="${vFieldId}"]`) ||
      content.querySelector(`input[id*="${vFieldId}"][id$="_answer"]:not([id*="_comments"]), select[id*="${vFieldId}"], textarea[id*="${vFieldId}"]:not([id*="_comments"])`) ||
      content.querySelector(`input[name*="${vFieldId}"]:not([name*="_comments"]), textarea[name*="${vFieldId}"]:not([name*="_comments"])`) ||
      content.querySelector(`input[type="date"][id*="${vFieldId}"]`) ||
      content.querySelector(`input[type="datetime-local"][id*="${vFieldId}"]`);
    if (!input || isCrfCommentRegionElement(input)) return '';
    if (input.type === 'checkbox') {
      // RAMP-style: one field can have multiple checkboxes (same name); return comma-separated checked values
      const checkboxes = content.querySelectorAll(`input[type="checkbox"][name="${input.name}"]`);
      if (checkboxes.length > 1) {
        const checkedValues = Array.from(checkboxes).filter((cb) => cb.checked).map((cb) => (cb.value || '').trim()).filter(Boolean);
        return checkedValues.join(',');
      }
      return input.checked ? (input.value || 'Yes').trim() : '';
    }
    if (input.type === 'radio') {
      const radios = content.querySelectorAll(`input[name="${input.name}"]`);
      const checked = Array.from(radios).find((r) => r.checked);
      return (checked?.value || '').trim() || 'Yes';
    }
    let val = (input.value || '').trim();
    if (input.classList?.contains('standarddate-text') && val) {
      const alreadySaved = /^(.{2})-([A-Za-z]{3})-(\d{4})$/.test(val);
      if (!alreadySaved) val = formatStandardDateValueForSave(val);
    } else if (input.classList?.contains('standarddatetime-text') && val) {
      const alreadySaved = /^(.{2})-([A-Za-z]{3})-(\d{4})\s+(\d{2}):(\d{2})$/.test(val);
      if (!alreadySaved) val = formatStandardDateTimeValueForSave(val);
    }
    if (input.classList?.contains('standarddate-text') || input.classList?.contains('standarddatetime-text')) {
      if (isPlaceholderStandardDateValue(val)) return '';
    }
    return val;
  }, [formatStandardDateValueForSave, formatStandardDateTimeValueForSave]);

  const hasAtLeastOneValue = (fields) => {
    return Array.isArray(fields) && fields.some(
      f => ((f.vFieldValue || '').trim() !== '' || (f.vComment || '').trim() !== '')
    );
  };

  const hasDataChange = (currentFields, savedFieldsList) => {
    if (!Array.isArray(savedFieldsList) || savedFieldsList.length === 0) return true;
    const savedMap = new Map();
    savedFieldsList.forEach((f) => {
      const id = (f.vFieldId ?? f.VFieldId ?? '').toString().trim();
      if (!id) return;
      const val = (f.vFieldValue ?? f.VFieldValue ?? '').toString().trim();
      const comment = (f.vComment ?? f.VComment ?? '').toString().trim();
      savedMap.set(id.toLowerCase(), { val, comment });
    });
    for (const f of currentFields) {
      const id = (f.vFieldId ?? '').toString().trim();
      if (!id) continue;
      const currentVal = (f.vFieldValue ?? '').toString().trim();
      const currentComment = (f.vComment ?? '').toString().trim();
      const saved = savedMap.get(id.toLowerCase());
      if (!saved) return true;
      if (currentVal !== saved.val || currentComment !== saved.comment) return true;
    }
    return false;
  };

  const handleDraftSave = async () => {
    if (!effectiveTemplateId || !visitId) {
      setToast({ type: 'error', message: 'Template and Visit are required to save.' });
      return;
    }
    const contentForSts = crfContainerRef.current?.querySelector('.crf-generated-content');
    const stsSizeCheck = validateStsAutoCaptureFieldsBeforeSave(contentForSts);
    if (!stsSizeCheck.ok) {
      setToast({ type: 'error', message: stsSizeCheck.message });
      return;
    }
    const fields = collectFormData();
    if (nCRFHdrNo != null && savedFields?.length > 0 && !hasDataChange(fields, savedFields)) {
      setToast({ type: 'error', message: reportMessages.noDataToSave });
      return;
    }
    // Allow empty draft on first save (Start Report → Draft Save with no data) so user can save and continue later
    setSaving(true);
    try {
      const nCRFHdrNoSafe = (nCRFHdrNo != null && nCRFHdrNo !== undefined && !Number.isNaN(Number(nCRFHdrNo))) ? Number(nCRFHdrNo) : null;
      const payload = {
        nCRFHdrNo: nCRFHdrNoSafe,
        vTemplateNo: effectiveTemplateId,
        vVisitType: visitType,
        iVisitNodeId: parseInt(visitId, 10),
        isSubmit: false,
        fields,
        viewingStage: visitUiStageLabel || null
      };
      const res = await api.post('/crf/page/save', payload);
      if (res.data?.nCRFHdrNo) setNCRFHdrNo(res.data.nCRFHdrNo);
      if (typeof onDraftSaved === 'function' && visitId) onDraftSaved(visitId, res.data?.nCRFHdrNo);
      // Refetch page so savedFields get nCRFDtlNo (needed for Raise DCF)
      try {
        const pageRes = await api.get(buildCrfPageUrl(res.data.nCRFHdrNo));
        const fields = pageRes.data?.fields ?? pageRes.data?.Fields ?? [];
        setSavedFields(Array.isArray(fields) ? fields : []);
        applyCrfPageDcfFlags(pageRes.data);
        applyCrfPageSaveType(pageRes.data);
        applyCrfPageAssociatedInactiveChildren(pageRes.data);
        applyCrfPageUploadLimit(pageRes.data);
        setWorkflowHeader(pageRes.data?.workflowHeader ?? pageRes.data?.WorkflowHeader ?? null);
        const irsDraft = readPageIsReviewStage(pageRes.data);
        if (irsDraft !== null) setPageIsReviewStage(irsDraft);
        const cscDraft = pickCurrentStageCodeUpdate(pageRes.data);
        if (cscDraft !== undefined) setCrfCurrentStageCode(cscDraft);
      } catch (_) { /* non-fatal */ }
      setToast({ type: 'success', message: commonMessages.authSuccess });
    } catch (err) {
      const msg = err.response?.data?.error || err.response?.data?.message || err.message || 'Failed to save draft';
      setToast({ type: 'error', message: msg });
    } finally {
      setSaving(false);
    }
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    // Form submit (e.g. Enter key): do not auto-submit to First Review; require explicit Submit button with e-sign
    if (!effectiveTemplateId || !visitId) {
      setToast({ type: 'error', message: 'Template and Visit are required.' });
      return;
    }
  };

  // CRA Submit Report: client checks, then server-side submit validation (before e-sign), then e-sign + real save
  const handleSubmitReport = async () => {
    if (!effectiveTemplateId || !visitId) {
      setToast({ type: 'error', message: 'Template and Visit are required to submit.' });
      return;
    }
    // Reviewer profiles (PM/SRM/etc.) must close active Reviewer's Comments before submit.
    // Open rows already batch-released to CRA (iSubmitDCFToCRA === 1) are CRA's queue — do not block reviewer Submit (matches server submit rules).
    // CRA keeps the full Open-row answer/remark check below.
    const fieldRowReleasedToCra = (f) => {
      const raw = f?.iSubmitDCFToCRA ?? f?.ISubmitDCFToCRA ?? f?.isubmitDCFToCRA;
      return raw === 1 || raw === '1' || raw === true;
    };
    const hasAnyOpenDcfBlockingReviewerSubmit = (savedFields || []).some((f) => {
      const status = (f?.vDCFStatus ?? f?.VDCFStatus ?? '').toString().trim().toLowerCase();
      return status === 'open' && !fieldRowReleasedToCra(f);
    });
    if (!isCraUser && hasAnyOpenDcfBlockingReviewerSubmit) {
      setToast({ type: 'error', message: dcfMessages.closeAllBeforeApproveReject });
      return;
    }
    if (!isCraUser && (savedFields || []).some((f) => isNonClosedDcfOwnedByCurrentProfile(f))) {
      setToast({ type: 'error', message: dcfMessages.closeAllBeforeApproveReject });
      return;
    }
    if (pmAtReviewLaneWithOwnDcfToClose) {
      setToast({ type: 'error', message: dcfMessages.closeAllBeforeApproveReject });
      return;
    }
    const contentForSts = crfContainerRef.current?.querySelector('.crf-generated-content');
    const stsSizeCheck = validateStsAutoCaptureFieldsBeforeSave(contentForSts);
    if (!stsSizeCheck.ok) {
      setToast({ type: 'error', message: stsSizeCheck.message });
      return;
    }
    const fields = collectFormData();
    const fieldByIdLower = {};
    fields.forEach((f) => {
      const id = (f.vFieldId || '').trim();
      if (id) fieldByIdLower[id.toLowerCase()] = f;
    });
    const fieldForCode = (code) => fieldByIdLower[String(code || '').trim().toLowerCase()];
    // Label-only rows (template type Label) have no input — exclude them from required validation
    const content = crfContainerRef.current?.querySelector('.crf-generated-content');
    const labelOnlyCodes = content
      ? new Set(
        Array.from(content.querySelectorAll('.crf-report-row-label-only[data-medex-code]'))
          .map((row) => (row.getAttribute('data-medex-code') || '').trim())
          .filter(Boolean)
      )
      : new Set();
    // Treat dropdown placeholder values as empty so "All fields are required" when user leaves SelectBox on "Select" (value -1 or __default_combobox__value__).
    const isPlaceholderSelectValue = (val) => {
      const v = (val || '').trim();
      if (!v) return true;
      if (v === '-1') return true;
      if (v.toLowerCase() === 'select') return true;
      if (v === '__default_combobox__value__') return true;
      return false;
    };
    const requiredToCheck = (requiredFieldCodes || []).filter(
      (code) => !labelOnlyCodes.has(String(code || '').trim())
        && !isStsAutoCaptureFieldTarget(content, code)
    );
    const isVisibleForRequiredValidation = (code) => {
      const c = String(code || '').trim();
      if (!c || !content) return true;
      const sec = findCrfFieldSection(content, c);
      // If the field cannot be resolved in current transformed DOM, treat it as not visible
      // (common for associated branch rows hidden by stage/branch gating).
      if (!sec) return false;
      let node = sec;
      while (node && node !== content) {
        if (typeof window !== 'undefined' && typeof window.getComputedStyle === 'function') {
          const cs = window.getComputedStyle(node);
          if (cs.display === 'none' || cs.visibility === 'hidden') return false;
        }
        node = node.parentElement;
      }
      return true;
    };
    const missing = requiredToCheck.filter((code) => {
      if (!isVisibleForRequiredValidation(code)) return false;
      const f = fieldForCode(code);
      const val = (f?.vFieldValue || '').trim();
      const com = (f?.vComment || '').trim();
      if (!val && !com) return true;
      if (val) {
        if (isPlaceholderSelectValue(val)) return !com;
        if (isPlaceholderOnlyStandardDateOrDateTime(val)) return !com;
      }
      return false;
    });
    if (missing.length > 0) {
      setToast({ type: 'error', message: reportMessages.allFieldsRequired });
      return;
    }
    // Open DCF rows: align with API — before e-sign, require normalized Answer value to differ from savedFields (Comments optional; DCF remarks alone do not satisfy).
    const normalizeSubmitAnswer = (raw) => {
      let v = (raw ?? '').toString().trim();
      if (!v) return '';
      if (isPlaceholderSelectValue(v)) return '';
      if (isPlaceholderOnlyStandardDateOrDateTime(v)) return '';
      return v;
    };
    const dcfOpenFields = (savedFields || []).filter((f) => {
      const status = (f.vDCFStatus ?? f.VDCFStatus ?? '').toString().trim().toLowerCase();
      if (status !== 'open') return false;
      if (!isCraUser && fieldRowReleasedToCra(f)) return false;
      return true;
    });
    // Match CRFPageService.ValidateSubmitOnly: skip tier-routed rows not resolvable at current header (same as openDcfResolutionAllowedAtHeaderStage).
    const headerStageForOpenDcfCheck =
      String(getAnyCase(workflowHeader, 'vStage', 'VStage', 'stage', 'Stage') || '').trim() ||
      (crfStage || '').trim();
    const dcfOpenFieldsForSubmitCheck = dcfOpenFields.filter((f) =>
      openDcfResolutionAllowedAtHeaderStage(
        f,
        headerStageForOpenDcfCheck,
        crfCurrentStageCode,
        pageHeaderStageInProgress === true
      )
    );
    if (dcfOpenFieldsForSubmitCheck.length > 0) {
      const anyOpenDcfUnsatisfied = dcfOpenFieldsForSubmitCheck.some((f) => {
        const id = (f.vFieldId ?? f.VFieldId ?? '').toString().trim();
        if (!id) return true;
        const dom = fieldForCode(id);
        return !openDcfRowSubmitSatisfiedClient(f, dom, normalizeSubmitAnswer);
      });
      if (anyOpenDcfUnsatisfied) {
        setToast({ type: 'error', message: reportMessages.dcfAnswerOrRemarkRequired });
        return;
      }
    }
    const nCRFHdrNoSafe = (nCRFHdrNo != null && nCRFHdrNo !== undefined && !Number.isNaN(Number(nCRFHdrNo))) ? Number(nCRFHdrNo) : null;
    setSaving(true);
    try {
      await api.post('/crf/page/save', {
        nCRFHdrNo: nCRFHdrNoSafe,
        vTemplateNo: effectiveTemplateId,
        vVisitType: visitType,
        iVisitNodeId: parseInt(visitId, 10),
        isSubmit: true,
        validateSubmitOnly: true,
        fields,
        viewingStage: visitUiStageLabel || null,
      });
    } catch (err) {
      const msg = err.response?.data?.error || err.response?.data?.message || err.message || 'Submit validation failed.';
      setToast({ type: 'error', message: msg });
      return;
    } finally {
      setSaving(false);
    }
    eSignRemarksRef.current = '';
    setESignPendingAction('submit');
    setShowESignModal(true);
  };

  const doSubmitReport = async (vRemarks) => {
    if (!effectiveTemplateId || !visitId) return;
    const contentForSts = crfContainerRef.current?.querySelector('.crf-generated-content');
    const stsSizeCheck = validateStsAutoCaptureFieldsBeforeSave(contentForSts);
    if (!stsSizeCheck.ok) {
      setToast({ type: 'error', message: stsSizeCheck.message });
      return;
    }
    const fields = collectFormData();
    setSaving(true);
    try {
      const nCRFHdrNoSafe = (nCRFHdrNo != null && nCRFHdrNo !== undefined && !Number.isNaN(Number(nCRFHdrNo))) ? Number(nCRFHdrNo) : null;
      const payload = {
        nCRFHdrNo: nCRFHdrNoSafe,
        vTemplateNo: effectiveTemplateId,
        vVisitType: visitType,
        iVisitNodeId: parseInt(visitId, 10),
        isSubmit: true,
        fields,
        vRemarks: vRemarks || null,
        viewingStage: visitUiStageLabel || null
      };
      const res = await api.post('/crf/page/save', payload);
      if (res.data?.nCRFHdrNo) setNCRFHdrNo(res.data.nCRFHdrNo);
      try {
        const hdrNo = res.data?.nCRFHdrNo;
        if (hdrNo) {
          const pageRes = await api.get(buildCrfPageUrl(hdrNo));
          applyCrfPageSaveType(pageRes.data);
          applyCrfPageDcfFlags(pageRes.data);
          applyCrfPageAssociatedInactiveChildren(pageRes.data);
          applyCrfPageUploadLimit(pageRes.data);
          const pf = pageRes.data?.fields ?? pageRes.data?.Fields ?? [];
          setSavedFields(Array.isArray(pf) ? pf : []);
          setWorkflowHeader(pageRes.data?.workflowHeader ?? pageRes.data?.WorkflowHeader ?? null);
          const irsSub = readPageIsReviewStage(pageRes.data);
          if (irsSub !== null) setPageIsReviewStage(irsSub);
          const cscSub = pickCurrentStageCodeUpdate(pageRes.data);
          if (cscSub !== undefined) setCrfCurrentStageCode(cscSub);
          const actRes = await api.get(`/crf/page/${hdrNo}/actions`);
          if (actRes.data) {
            applyCrfPageDcfFlags(actRes.data);
            applyCrfPageSaveType(actRes.data);
            const st = actRes.data?.stage ?? actRes.data?.Stage;
            if (st) setCrfStage(st);
            const irsA = readPageIsReviewStage(actRes.data);
            if (irsA !== null) setPageIsReviewStage(irsA);
            const cscA = pickCurrentStageCodeUpdate(actRes.data);
            if (cscA !== undefined) setCrfCurrentStageCode(cscA);
            const actions = actRes.data?.actions ?? actRes.data?.Actions;
            setAvailableActions(Array.isArray(actions) ? actions : []);
          }
          setPermissionsRefreshKey((k) => k + 1);
        }
      } catch (_) { /* non-fatal */ }
      setToast({ type: 'success', message: commonMessages.authSuccess });
      if (typeof onSubmitSuccess === 'function') onSubmitSuccess();
      if (onClose) onClose();
    } catch (err) {
      const msg = err.response?.data?.error || err.response?.data?.message || err.message || 'Failed to submit';
      setToast({ type: 'error', message: msg });
    } finally {
      setSaving(false);
    }
  };

  const doApprove = async (vRemarks) => {
    if (!nCRFHdrNo) return;
    const mustSubmitFirst = await shouldRunReviewValidationForAssociated();
    const submitFlowVisible = availableActions.includes('Submit') || availableActions.includes('DraftSave');
    if (mustSubmitFirst && submitFlowVisible) {
      setToast({ type: 'error', message: 'Please Submit first, then Approve.' });
      return;
    }
    setTransitioning(true);
    try {
      await api.post(`/crf/page/${nCRFHdrNo}/transition`, { action: 'Approve', vRemarks: vRemarks || undefined });
      setToast({ type: 'success', message: commonMessages.authSuccess });
      if (typeof onApproveSendbackSuccess === 'function') onApproveSendbackSuccess();
      if (onClose) onClose();
    } catch (err) {
      const msg = err.response?.data?.error || err.response?.data?.message || err.message || 'Approve failed.';
      setToast({ type: 'error', message: msg });
    } finally {
      setTransitioning(false);
    }
  };

  const handleApprove = async () => {
    if (!nCRFHdrNo) return;
    const mustSubmitFirst = await shouldRunReviewValidationForAssociated();
    const submitFlowVisible = availableActions.includes('Submit') || availableActions.includes('DraftSave');
    if (mustSubmitFirst && submitFlowVisible) {
      setToast({ type: 'error', message: 'Please Submit first, then Approve.' });
      return;
    }
    if (hasActiveDCF) {
      setToast({ type: 'error', message: dcfMessages.closeAllBeforeApproveReject });
      return;
    }
    eSignRemarksRef.current = '';
    setESignPendingAction('approve');
    setShowESignModal(true);
  };

  const doReject = async (vRemarks) => {
    if (!nCRFHdrNo) return;
    setTransitioning(true);
    try {
      const mustValidateAssoc = await shouldRunReviewValidationForAssociated();
      const submitFlowVisibleReject =
        Array.isArray(availableActions) &&
        (availableActions.includes('Submit') || availableActions.includes('DraftSave'));
      if (mustValidateAssoc && submitFlowVisibleReject) {
        const ok = await validateAndDraftSaveBeforeReviewTransition();
        if (!ok) return;
      }
      await api.post(`/crf/page/${nCRFHdrNo}/transition`, { action: 'Reject', vRemarks: vRemarks || undefined });
      setToast({ type: 'success', message: commonMessages.authSuccess });
      if (typeof onApproveSendbackSuccess === 'function') onApproveSendbackSuccess();
      if (onClose) onClose();
    } catch (err) {
      const msg = err.response?.data?.error || err.response?.data?.message || err.message || 'Send Back failed.';
      setToast({ type: 'error', message: msg });
    } finally {
      setTransitioning(false);
    }
  };

  const handleReject = () => {
    if (!nCRFHdrNo) return;
    if (hasActiveDCF) {
      setToast({ type: 'error', message: dcfMessages.closeAllBeforeApproveReject });
      return;
    }
    eSignRemarksRef.current = '';
    setESignPendingAction('reject');
    setShowESignModal(true);
  };

  const shouldRunReviewValidationForAssociated = async () => {
    if (!isReviewStage) return false;
    const content = crfContainerRef.current?.querySelector('.crf-generated-content');
    if (!content) return false;
    const markers = queryCrfAssociatedMarkerElements(content);
    if (!markers || markers.length === 0) return false;

    let stageList = [];
    try {
      const pc = auditProjectCode || (formData.protocolNo || '').trim() || undefined;
      stageList = await stageService.getStagesForCrFAssociation(pc);
    } catch {
      stageList = [];
    }

    for (const wrap of markers) {
      const raw = wrap.getAttribute('data-associated-config') || '';
      let cfg = {};
      try {
        cfg = JSON.parse(decodeAssociatedConfigAttribute(raw) || '{}');
      } catch {
        cfg = {};
      }
      const wantStage = resolveAssociatedStageGate(cfg);
      // Rule applies only when Associated is explicitly mapped with stage.
      if (wantStage == null || wantStage === '') continue;
      const blocked = isAssociatedStageMismatch(
        wantStage,
        Array.isArray(stageList) ? stageList : [],
        crfCurrentStageCode,
        crfStage
      );
      if (!blocked) return true;
    }
    return false;
  };

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (String(crfCSaveType || '').trim().toUpperCase() === 'E') {
        if (!cancelled) setReviewAssociatedRequiresSaveFlow(false);
        return;
      }
      const must = await shouldRunReviewValidationForAssociated();
      // Backend omits DraftSave/Submit in review when no Associated branch child applies (e.g. driver "No"); keep UI in sync with GET …/actions.
      const apiAllowsAssociatedSubmitFlow =
        Array.isArray(availableActions) &&
        (availableActions.includes('DraftSave') || availableActions.includes('Submit'));
      if (!cancelled) setReviewAssociatedRequiresSaveFlow(!!must && apiAllowsAssociatedSubmitFlow);
    };
    void run();
    return () => { cancelled = true; };
  }, [htmlContent, savedFields, crfStage, crfCurrentStageCode, isReviewStage, auditProjectCode, formData.protocolNo, isPreviewInNewTab, crfCSaveType, availableActions]);

  const validateAndDraftSaveBeforeReviewTransition = async () => {
    const contentForSts = crfContainerRef.current?.querySelector('.crf-generated-content');
    const stsSizeCheck = validateStsAutoCaptureFieldsBeforeSave(contentForSts);
    if (!stsSizeCheck.ok) {
      setToast({ type: 'error', message: stsSizeCheck.message });
      return false;
    }
    const fields = collectFormData();
    const fieldByIdLower = {};
    fields.forEach((f) => {
      const id = (f.vFieldId || '').trim();
      if (id) fieldByIdLower[id.toLowerCase()] = f;
    });
    const fieldForCode = (code) => fieldByIdLower[String(code || '').trim().toLowerCase()];
    const content = crfContainerRef.current?.querySelector('.crf-generated-content');
    const labelOnlyCodes = content
      ? new Set(
        Array.from(content.querySelectorAll('.crf-report-row-label-only[data-medex-code]'))
          .map((row) => (row.getAttribute('data-medex-code') || '').trim())
          .filter(Boolean)
      )
      : new Set();
    const isVisibleForRequiredValidation = (code) => {
      const c = String(code || '').trim();
      if (!c || !content) return true;
      const sec = findCrfFieldSection(content, c);
      if (!sec) return false;
      let node = sec;
      while (node && node !== content) {
        if (typeof window !== 'undefined' && typeof window.getComputedStyle === 'function') {
          const cs = window.getComputedStyle(node);
          if (cs.display === 'none' || cs.visibility === 'hidden') return false;
        }
        node = node.parentElement;
      }
      return true;
    };
    const requiredToCheck = (requiredFieldCodes || []).filter(
      (code) => !labelOnlyCodes.has(String(code || '').trim())
        && !isStsAutoCaptureFieldTarget(content, code)
    );
    const missing = requiredToCheck.filter((code) => {
      if (!isVisibleForRequiredValidation(code)) return false;
      const f = fieldForCode(code);
      const val = (f?.vFieldValue || '').trim();
      const com = (f?.vComment || '').trim();
      if (!val && !com) return true;
      if (val) {
        if (val.trim() === '-1' || val.trim().toLowerCase() === 'select' || val.trim() === '__default_combobox__value__') return !com;
        if (isPlaceholderOnlyStandardDateOrDateTime(val)) return !com;
      }
      return false;
    });
    if (missing.length > 0) {
      setToast({ type: 'error', message: reportMessages.allFieldsRequired });
      return false;
    }

    try {
      await api.post('/crf/page/save', {
        nCRFHdrNo,
        vTemplateNo: effectiveTemplateId,
        vVisitType: visitType,
        iVisitNodeId: parseInt(visitId, 10),
        isSubmit: false,
        fields,
        viewingStage: visitUiStageLabel || null,
      });
      return true;
    } catch (err) {
      const msg = err.response?.data?.error || err.response?.data?.message || err.message || 'Draft Save failed.';
      setToast({ type: 'error', message: msg });
      return false;
    }
  };

  // Optimistically update savedFields state so UI colors/icons reflect change immediately
  const updateLocalFieldStatus = (nCRFDtlNo, status, remark, response) => {
    setSavedFields(prev => {
      return prev.map(f => {
        const fNo = f.nCRFDtlNo ?? f.NCRFDtlNo ?? f.ncrfDtlNo;
        if (Number(fNo) === Number(nCRFDtlNo)) {
          const out = { ...f, vDCFStatus: status, vDCFRemark: remark ?? f.vDCFRemark, VDCFStatus: status, VDCFRemark: remark ?? f.VDCFRemark };
          if (response !== undefined) {
            out.vDCFResponse = response ?? f.vDCFResponse;
            out.VDCFResponse = response ?? f.VDCFResponse;
          }
          return out;
        }
        return f;
      });
    });
  };

  // Helper to check DCF Status (for the opened field row). Prefer nCRFDtlNo so we always get the correct row when opening details.
  const getDCFInfo = () => {
    if (!savedFields) {
      return {
        status: null,
        remarks: '',
        sentToReviewStage: null,
        raisedStage: null,
        batchDisplayStage: null,
        releasedToCra: false,
        selectedFieldRow: null,
      };
    }
    let f = null;
    if (dcfSelected.nCRFDtlNo != null && dcfSelected.nCRFDtlNo !== 0) {
      const no = Number(dcfSelected.nCRFDtlNo);
      f = savedFields.find(x => Number(x.nCRFDtlNo ?? x.NCRFDtlNo ?? x.ncrfDtlNo ?? 0) === no);
    }
    if (!f && dcfSelected.fieldId) {
      f = savedFields.find(x => (x.vFieldId || x.VFieldId) === dcfSelected.fieldId);
    }
    if (!f && dcfSelected.fieldId) {
      f = savedFields.find(x => (x.vFieldId || x.VFieldId || '').toLowerCase() === dcfSelected.fieldId.toLowerCase());
    }
    const status = f?.vDCFStatus ?? f?.VDCFStatus ?? null;
    const sentToReviewStage = (f?.vDCFSentToReviewStage ?? f?.VDCFSentToReviewStage ?? '').toString().trim() || null;
    const raisedStage = (f?.vDCFStageRaised ?? f?.VDCFStageRaised ?? '').toString().trim() || null;
    const batchDisplayStage = sentToReviewStage || raisedStage;
    const submitCraRaw = f?.iSubmitDCFToCRA ?? f?.ISubmitDCFToCRA;
    const releasedToCra = Number(submitCraRaw) === 1;
    return {
      status,
      remarks: f?.vDCFRemark || f?.VDCFRemark,
      raisedByProfile: f?.vDCFRaisedByProfileCode || f?.VDCFRaisedByProfileCode,
      response: f?.vDCFResponse || f?.VDCFResponse || '',
      sentToReviewStage,
      raisedStage,
      batchDisplayStage,
      releasedToCra,
      isResolved: (status || '').toLowerCase() === 'resolved',
      /** Same persisted row as status/routing — server IsEditable matches Visit Report cell enablement. */
      selectedFieldRow: f ?? null,
    };
  };

  const dcfInfo = getDCFInfo();
  /** Persisted workflow stage (matches CRFHdr.VStage) — use for CRA unreleased sent-to gate; prefer header over crfStage state order. */
  const headerStageLabelForDcfGate =
    String(getAnyCase(workflowHeader, 'vStage', 'VStage', 'stage', 'Stage') || '').trim() ||
    (crfStage || '').trim();
  /** Reviewer DCF modal / release: prefer visit list tab label when embedded so tier matches user context during sequential CRA → review transitions. */
  const dcfReviewerContextStageLabel = (visitUiStageLabel || headerStageLabelForDcfGate).trim();
  /** Review-tier batch routing only; null/empty = In Progress path to CRA (no review-stage mismatch gate). */
  const dcfSentToReviewStageTrim = (dcfInfo.sentToReviewStage || '').trim();
  /** Matches API GetDcfResolveStageMismatchMessage (sent-to stage vs header) for unreleased rows. Released-to-CRA follows the same server checks. */
  const craDcfResponseBlockedByStage =
    isCraUser &&
    !dcfInfo.releasedToCra &&
    dcfSentToReviewStageTrim.length > 0 &&
    headerStageLabelForDcfGate.length > 0 &&
    normalizeStageLabelForMatch(dcfSentToReviewStageTrim) !== normalizeStageLabelForMatch(headerStageLabelForDcfGate);
  const craDcfStageBlockDetailText = craDcfResponseBlockedByStage
    ? dcfMessages.craResponseWrongReviewStageDetail
      .replace('{dcfStage}', dcfSentToReviewStageTrim)
      .replace('{currentStage}', headerStageLabelForDcfGate)
    : '';

  /** CRA: unreleased tier-routed Open DCF — remarks stay with reviewer lane until Submit All to CRA; after release, CRA may enter vDCFResponse. */
  const dcfOpenForCraTierGate = ((dcfInfo.status || '').trim().toLowerCase() === 'open');
  const craDcfTierRoutedResponseBlocked =
    isCraUser &&
    !dcfInfo.releasedToCra &&
    dcfSentToReviewStageTrim.length > 0 &&
    dcfOpenForCraTierGate;
  const craDcfModalCraResponseBlocked = craDcfResponseBlockedByStage || craDcfTierRoutedResponseBlocked;
  const craDcfModalCraResponseBlockedDetail = craDcfTierRoutedResponseBlocked
    ? dcfMessages.craTierRoutedReviewerOnlyDetail.replace('{stage}', dcfSentToReviewStageTrim)
    : craDcfStageBlockDetailText;

  const doRaiseDCF = async (vRemarks) => {
    if (!nCRFHdrNo || dcfSelected.nCRFDtlNo == null) return;
    setDcfSubmitting(true);
    try {
      await dcfRaise({
        nCRFHdrNo,
        nCRFDtlNo: dcfSelected.nCRFDtlNo,
        vDCFRemark: dcfRemark.trim(),
        vDCFRaisedByProfileCode: currentUserProfile || user?.role || 'Unknown',
        vRemarks: vRemarks || null,
      });
      updateLocalFieldStatus(dcfSelected.nCRFDtlNo, 'Open', dcfRemark.trim());
      setDcfRemark('');
      setDcfSelected({ nCRFDtlNo: null, fieldId: '', questionText: '' });
      setDcfModalOpen(false);
      if (nCRFHdrNo) {
        const pageRes = await api.get(buildCrfPageUrl(nCRFHdrNo));
        const fields = pageRes.data?.fields ?? pageRes.data?.Fields ?? [];
        if (Array.isArray(fields)) setSavedFields(fields);
        applyCrfPageDcfFlags(pageRes.data);
        applyCrfPageSaveType(pageRes.data);
        applyCrfPageAssociatedInactiveChildren(pageRes.data);
        applyCrfPageUploadLimit(pageRes.data);
        setWorkflowHeader(pageRes.data?.workflowHeader ?? pageRes.data?.WorkflowHeader ?? null);
        const irsRaise = readPageIsReviewStage(pageRes.data);
        if (irsRaise !== null) setPageIsReviewStage(irsRaise);
        const cscRaise = pickCurrentStageCodeUpdate(pageRes.data);
        if (cscRaise !== undefined) setCrfCurrentStageCode(cscRaise);
        setPermissionsRefreshKey((k) => k + 1); // Re-apply field enable/disable without page refresh
        // Also refresh availableActions so buttons (Submit, Approve, etc.) stay visible
        try {
          const actRes = await api.get(`/crf/page/${nCRFHdrNo}/actions`);
          const stage = actRes.data?.stage ?? actRes.data?.Stage ?? null;
          if (stage) setCrfStage(stage);
          applyCrfPageDcfFlags(actRes.data);
          applyCrfPageSaveType(actRes.data);
          const actions = actRes.data?.actions ?? actRes.data?.Actions;
          if (Array.isArray(actions)) setAvailableActions(actions);
        } catch (_) { /* non-fatal: keep existing actions */ }
      }
      setToast({ type: 'success', message: commonMessages.authSuccess });
    } catch (err) {
      const msg = err.response?.data?.error || err.response?.data?.message || err.message || "Failed to create Reviewer's Comment.";
      setToast({ type: 'error', message: msg });
    } finally {
      setDcfSubmitting(false);
    }
  };

  const handleCRFDCFSubmit = async (e) => {
    e.preventDefault();
    const info = getDCFInfo();
    const statusLower = (info.status || '').trim().toLowerCase();
    const alreadyRaised = statusLower === 'open' || statusLower === 'resolved';
    if (alreadyRaised) {
      setToast({ type: 'error', message: dcfMessages.raiseBlockedActiveThread });
      return;
    }
    // Allow in First/Second Review, or when there are already open DCFs (Reviewer adding more on another field)
    const canRaiseDCF = isReviewStage || hasOpenDCF;
    if (!canRaiseDCF) {
      setToast({ type: 'error', message: "Raise Reviewer's Comment is only available in a review stage." });
      return;
    }
    if (!dcfRemark.trim()) {
      setDcfRemarkError('Please Enter Remarks.');
      return;
    }
    if (dcfSelected.nCRFDtlNo == null || dcfSelected.nCRFDtlNo === undefined || dcfSelected.nCRFDtlNo === 0) {
      setToast({ type: 'error', message: "Cannot create Reviewer's Comment: this field is not linked to a saved row. Save the form first." });
      return;
    }
    const dcfRaiseBlock = getDcfRaiseBlockMessageForFieldId(dcfSelected.fieldId);
    if (dcfRaiseBlock) {
      setToast({ type: 'error', message: dcfRaiseBlock });
      return;
    }
    void doRaiseDCF(null);
  };

  const doSubmitDCFResponse = async (vRemarks) => {
    if (!dcfSelected.nCRFDtlNo) return;
    if (craDcfResponseBlockedByStage) {
      setToast({ type: 'error', message: dcfMessages.craResponseWrongReviewStage });
      return;
    }
    if (craDcfTierRoutedResponseBlocked) {
      setToast({ type: 'error', message: dcfMessages.craTierRoutedReviewerOnly });
      return;
    }
    setDcfSubmitting(true);
    try {
      await dcfResolve(dcfSelected.nCRFDtlNo, {
        vDCFResponse: dcfResponse.trim() || null,
        vRemarks: vRemarks || null,
        finalizeResolve: false,
        viewingStage: visitUiStageLabel || null,
      });
      updateLocalFieldStatus(dcfSelected.nCRFDtlNo, 'Resolved', null, dcfResponse.trim() || null);
      setDcfModalOpen(false);
      setDcfRemark('');
      setDcfRemarkError('');
      setDcfResponse('');
      setDcfSelected({ nCRFDtlNo: null, fieldId: '', questionText: '' });
      if (nCRFHdrNo) {
        const pageRes = await api.get(buildCrfPageUrl(nCRFHdrNo));
        const fields = pageRes.data?.fields ?? pageRes.data?.Fields ?? [];
        if (Array.isArray(fields)) setSavedFields(fields);
        applyCrfPageDcfFlags(pageRes.data);
        applyCrfPageSaveType(pageRes.data);
        applyCrfPageAssociatedInactiveChildren(pageRes.data);
        applyCrfPageUploadLimit(pageRes.data);
        setWorkflowHeader(pageRes.data?.workflowHeader ?? pageRes.data?.WorkflowHeader ?? null);
        const irsResp = readPageIsReviewStage(pageRes.data);
        if (irsResp !== null) setPageIsReviewStage(irsResp);
        const cscResp = pickCurrentStageCodeUpdate(pageRes.data);
        if (cscResp !== undefined) setCrfCurrentStageCode(cscResp);
        setPermissionsRefreshKey((k) => k + 1);
        try {
          const actRes = await api.get(`/crf/page/${nCRFHdrNo}/actions`);
          const stage = actRes.data?.stage ?? actRes.data?.Stage ?? null;
          if (stage) setCrfStage(stage);
          applyCrfPageDcfFlags(actRes.data);
          applyCrfPageSaveType(actRes.data);
          const actions = actRes.data?.actions ?? actRes.data?.Actions;
          if (Array.isArray(actions)) setAvailableActions(actions);
        } catch (_) { /* non-fatal */ }
      }
      if (typeof onDcfSaveSuccess === 'function') onDcfSaveSuccess();
      else setToast({ type: 'success', message: commonMessages.authSuccess });
    } catch (err) {
      const msg = err.response?.data?.error || err.response?.data?.message || err.message || 'Failed to save comment.';
      setToast({ type: 'error', message: msg });
    } finally {
      setDcfSubmitting(false);
    }
  };

  const doResolveDCF = async (vRemarks) => {
    if (!dcfSelected.nCRFDtlNo) return;
    if (craDcfResponseBlockedByStage) {
      setToast({ type: 'error', message: dcfMessages.craResponseWrongReviewStage });
      return;
    }
    setDcfSubmitting(true);
    try {
      const body = { vRemarks: vRemarks || null, finalizeResolve: true, viewingStage: visitUiStageLabel || null };
      if (dcfResponse.trim()) body.vDCFResponse = dcfResponse.trim();
      await dcfResolve(dcfSelected.nCRFDtlNo, body);
      updateLocalFieldStatus(dcfSelected.nCRFDtlNo, 'Resolved', null, dcfResponse.trim() || undefined);
      setDcfModalOpen(false);
      setDcfRemark('');
      setDcfRemarkError('');
      setDcfResponse('');
      setDcfSelected({ nCRFDtlNo: null, fieldId: '', questionText: '' });
      if (nCRFHdrNo) {
        const pageRes = await api.get(buildCrfPageUrl(nCRFHdrNo));
        const fields = pageRes.data?.fields ?? pageRes.data?.Fields ?? [];
        if (Array.isArray(fields)) setSavedFields(fields);
        applyCrfPageDcfFlags(pageRes.data);
        applyCrfPageSaveType(pageRes.data);
        applyCrfPageAssociatedInactiveChildren(pageRes.data);
        applyCrfPageUploadLimit(pageRes.data);
        setWorkflowHeader(pageRes.data?.workflowHeader ?? pageRes.data?.WorkflowHeader ?? null);
        const irsRes = readPageIsReviewStage(pageRes.data);
        if (irsRes !== null) setPageIsReviewStage(irsRes);
        const cscRes = pickCurrentStageCodeUpdate(pageRes.data);
        if (cscRes !== undefined) setCrfCurrentStageCode(cscRes);
        setPermissionsRefreshKey((k) => k + 1);
        try {
          const actRes = await api.get(`/crf/page/${nCRFHdrNo}/actions`);
          const stage = actRes.data?.stage ?? actRes.data?.Stage ?? null;
          if (stage) setCrfStage(stage);
          applyCrfPageDcfFlags(actRes.data);
          applyCrfPageSaveType(actRes.data);
          const actions = actRes.data?.actions ?? actRes.data?.Actions;
          if (Array.isArray(actions)) setAvailableActions(actions);
        } catch (_) { /* non-fatal */ }
      }
      if (typeof onDcfSaveSuccess === 'function') onDcfSaveSuccess();
      else setToast({ type: 'success', message: commonMessages.authSuccess });
    } catch (err) {
      const msg = err.response?.data?.error || err.response?.data?.message || err.message || "Failed to resolve Reviewer's Comment.";
      setToast({ type: 'error', message: msg });
    } finally {
      setDcfSubmitting(false);
    }
  };

  const doCloseDCF = async (vRemarks) => {
    if (!dcfSelected.nCRFDtlNo) return;
    setDcfSubmitting(true);
    try {
      await dcfClose(dcfSelected.nCRFDtlNo, {
        vRemarks: vRemarks || null,
        viewingStage: visitUiStageLabel || null,
      });
      updateLocalFieldStatus(dcfSelected.nCRFDtlNo, 'Closed');
      setDcfModalOpen(false);
      setDcfRemark('');
      setDcfSelected({ nCRFDtlNo: null, fieldId: '', questionText: '' });
      if (nCRFHdrNo) {
        const pageRes = await api.get(buildCrfPageUrl(nCRFHdrNo));
        const fields = pageRes.data?.fields ?? pageRes.data?.Fields ?? [];
        if (Array.isArray(fields)) setSavedFields(fields);
        applyCrfPageDcfFlags(pageRes.data);
        applyCrfPageSaveType(pageRes.data);
        applyCrfPageAssociatedInactiveChildren(pageRes.data);
        applyCrfPageUploadLimit(pageRes.data);
        setWorkflowHeader(pageRes.data?.workflowHeader ?? pageRes.data?.WorkflowHeader ?? null);
        const irsClose = readPageIsReviewStage(pageRes.data);
        if (irsClose !== null) setPageIsReviewStage(irsClose);
        const cscClose = pickCurrentStageCodeUpdate(pageRes.data);
        if (cscClose !== undefined) setCrfCurrentStageCode(cscClose);
        setPermissionsRefreshKey((k) => k + 1);
        // Also refresh availableActions so buttons stay properly visible
        try {
          const actRes = await api.get(`/crf/page/${nCRFHdrNo}/actions`);
          const stage = actRes.data?.stage ?? actRes.data?.Stage ?? null;
          if (stage) setCrfStage(stage);
          applyCrfPageDcfFlags(actRes.data);
          applyCrfPageSaveType(actRes.data);
          const actions = actRes.data?.actions ?? actRes.data?.Actions;
          if (Array.isArray(actions)) setAvailableActions(actions);
        } catch (_) { /* non-fatal: keep existing actions */ }
      }
      setToast({ type: 'success', message: commonMessages.authSuccess });
    } catch (err) {
      const msg = err.response?.data?.error || err.response?.data?.message || err.message || "Failed to close Reviewer's Comment.";
      setToast({ type: 'error', message: msg });
    } finally {
      setDcfSubmitting(false);
    }
  };

  const handleSubmitAllDcfsToCra = async () => {
    if (!nCRFHdrNo) return;
    setSubmitAllDcfSubmitting(true);
    try {
      await dcfSubmitAllToCra(nCRFHdrNo);
      // Instant UI response: reviewer lane should stop showing Submit once batch release is done.
      setReviewerSubmitHiddenAfterBatchRelease(true);
      setHasUnreleasedOpenDcf(false);
      const pageRes = await api.get(buildCrfPageUrl(nCRFHdrNo));
      const fields = pageRes.data?.fields ?? pageRes.data?.Fields ?? [];
      if (Array.isArray(fields)) setSavedFields(fields);
      applyCrfPageDcfFlags(pageRes.data);
      applyCrfPageSaveType(pageRes.data);
      applyCrfPageAssociatedInactiveChildren(pageRes.data);
      applyCrfPageUploadLimit(pageRes.data);
      setWorkflowHeader(pageRes.data?.workflowHeader ?? pageRes.data?.WorkflowHeader ?? null);
      const irsS = readPageIsReviewStage(pageRes.data);
      if (irsS !== null) setPageIsReviewStage(irsS);
      const cscS = pickCurrentStageCodeUpdate(pageRes.data);
      if (cscS !== undefined) setCrfCurrentStageCode(cscS);
      const actRes = await api.get(`/crf/page/${nCRFHdrNo}/actions`);
      const stage = actRes.data?.stage ?? actRes.data?.Stage ?? null;
      if (stage) setCrfStage(stage);
      applyCrfPageDcfFlags(actRes.data);
      applyCrfPageSaveType(actRes.data);
      const actions = actRes.data?.actions ?? actRes.data?.Actions;
      setAvailableActions(Array.isArray(actions) ? actions : []);
      setPermissionsRefreshKey((k) => k + 1);
      setToast({ type: 'success', message: commonMessages.authSuccess });
    } catch (err) {
      const msg = err.response?.data?.error || err.response?.data?.message || err.message || "Failed to submit Reviewer's Comments to CRA.";
      setToast({ type: 'error', message: msg });
    } finally {
      setSubmitAllDcfSubmitting(false);
    }
  };

  useEffect(() => {
    // New unreleased Open DCF at reviewer lane should allow reviewer Submit again.
    if (hasUnreleasedOpenDcf) setReviewerSubmitHiddenAfterBatchRelease(false);
  }, [hasUnreleasedOpenDcf]);

  const runAfterESign = () => {
    const vRemarks = eSignRemarksRef.current || '';
    const action = eSignPendingAction;
    setShowESignModal(false);
    setESignPendingAction(null);
    eSignRemarksRef.current = '';
    if (action === 'approve') doApprove(vRemarks);
    else if (action === 'reject') doReject(vRemarks);
    else if (action === 'submit') doSubmitReport(vRemarks);
    else if (action === 'closeActionItem') doCloseActionItem(vRemarks);
  };

  const doUpdateActionItemRemark = async () => {
    const payload = actionItemUpdateRef.current;
    actionItemUpdateRef.current = null;
    if (!payload?.actionItemID) return;
    const draft = (payload.replyRemark ?? '').toString().trim();
    const submitToCreator = payload.submitToCreator === true;
    if (!draft && !submitToCreator) return;
    if (!draft && submitToCreator && !(actionReplyRemark || '').toString().trim()) return;
    setActionItemSaving(true);
    try {
      const body = { submitToCreator };
      if (draft) body.replyRemark = draft;
      const res = await api.post(`/action-items/${payload.actionItemID}/update`, body);
      const nextStatus = (res.data?.status ?? (submitToCreator ? 'Resolved' : 'Open')).toString();
      const savedRemark = (res.data?.replyRemark ?? '').toString();
      const remarkForList = savedRemark || draft || (actionReplyRemark || '').toString().trim();
      setActionItemsList((prev) =>
        prev.map((x) =>
          (x.actionItemID ?? x.nCRFActionItemNo) === payload.actionItemID
            ? { ...x, status: nextStatus, replyRemark: remarkForList }
            : x
        )
      );
      if (savedRemark) {
        actionItemReplyRemarkOriginalRef.current = savedRemark;
        setActionReplyRemark(savedRemark);
      }
      setActionReplyDraft('');
      if (submitToCreator) {
        setToast({ type: 'success', message: actionItemMessages.issueSubmittedToCreatorSuccess });
        dispatchNotificationsRefresh();
        setActionItemsModalOpen(false);
        setActionItemsSelected({ nCRFDtlNo: null, fieldId: '', questionText: '' });
        setActionItemsList([]);
        setSelectedActionItemIndex(0);
        setActionReplyRemark('');
        setActionReplyDraft('');
        refreshCrfPageFieldsForActionItems();
      } else {
        setToast({ type: 'success', message: actionItemMessages.remarkSavedSuccess });
      }
    } catch (err) {
      const msg = err.response?.data?.error || err.response?.data?.message || err.message || 'Failed to update remark.';
      setToast({ type: 'error', message: msg });
    } finally {
      setActionItemSaving(false);
    }
  };

  const doCloseActionItem = async (vRemarks) => {
    const payload = actionItemCloseRef.current;
    actionItemCloseRef.current = null;
    if (!payload?.actionItemID) return;
    setActionItemSaving(true);
    try {
      await api.post(`/action-items/${payload.actionItemID}/close`, {
        remark: (vRemarks || '').trim() || undefined,
        closedDate: payload.closedDate || undefined,
      });
      setActionItemsList((prev) => prev.map((x) => (x.actionItemID === payload.actionItemID
        ? { ...x, status: 'Closed', closedDate: payload.closedDate ?? x.closedDate }
        : x)));
      setToast({ type: 'success', message: commonMessages.authSuccess });
      dispatchNotificationsRefresh();
      setActionItemsModalOpen(false);
      setActionItemsSelected({ nCRFDtlNo: null, fieldId: '', questionText: '' });
      setActionItemsList([]);
      setSelectedActionItemIndex(0);
      setActionReplyRemark('');
      setActionReplyDraft('');
      setActionItemClosingDate('');
      refreshCrfPageFieldsForActionItems();
    } catch (err) {
      const msg = err.response?.data?.error || err.response?.data?.message || err.message || 'Failed to close issue.';
      setToast({ type: 'error', message: msg });
    } finally {
      setActionItemSaving(false);
    }
  };

  const doReopenActionItem = async () => {
    const payload = actionItemReopenRef.current;
    actionItemReopenRef.current = null;
    const reopenId = payload?.actionItemID ?? payload?.nCRFActionItemNo;
    if (!reopenId) return;
    setActionItemSaving(true);
    try {
      const full = (payload?.replyRemark ?? '').toString().trim();
      const reopenBody = full ? { replyRemark: full } : {};
      const res = await api.post(`/action-items/${reopenId}/reopen`, reopenBody);
      const nextStatus = (res.data?.status ?? 'Open').toString();
      const savedRemark = (res.data?.replyRemark ?? (full || actionReplyRemark || '')).toString();
      setActionItemsList((prev) =>
        prev.map((x) =>
          (x.actionItemID ?? x.nCRFActionItemNo) === reopenId
            ? { ...x, status: nextStatus, replyRemark: savedRemark }
            : x
        )
      );
      actionItemReplyRemarkOriginalRef.current = savedRemark;
      setActionReplyRemark(savedRemark);
      setActionReplyDraft('');
      setToast({ type: 'success', message: actionItemMessages.issueReopenedSuccess });
      dispatchNotificationsRefresh();
      refreshCrfPageFieldsForActionItems();
    } catch (err) {
      const msg = err.response?.data?.error || err.response?.data?.message || err.message || 'Failed to reopen issue.';
      setToast({ type: 'error', message: msg });
    } finally {
      setActionItemSaving(false);
    }
  };

  const handleSubmitDCFResponse = () => {
    if (!dcfSelected.nCRFDtlNo) return;
    if (craDcfResponseBlockedByStage) {
      setToast({ type: 'error', message: dcfMessages.craResponseWrongReviewStage });
      return;
    }
    if (craDcfTierRoutedResponseBlocked) {
      setToast({ type: 'error', message: dcfMessages.craTierRoutedReviewerOnly });
      return;
    }
    if (!dcfResponse.trim()) {
      setToast({ type: 'error', message: dcfMessages.remarksOrUpdateRequired });
      return;
    }
    void doSubmitDCFResponse(null);
  };

  const handleResolveDCF = () => {
    if (!dcfSelected.nCRFDtlNo) {
      setDcfModalOpen(false);
      setDcfRemark('');
      setDcfResponse('');
      setDcfSelected({ nCRFDtlNo: null, fieldId: '', questionText: '' });
      return;
    }
    if (craDcfResponseBlockedByStage) {
      setToast({ type: 'error', message: dcfMessages.craResponseWrongReviewStage });
      return;
    }
    const no = Number(dcfSelected.nCRFDtlNo);
    const savedField = savedFields?.find(x => Number(x.nCRFDtlNo ?? x.NCRFDtlNo ?? x.ncrfDtlNo ?? 0) === no)
      || savedFields?.find(x => (x.vFieldId || x.VFieldId) === dcfSelected.fieldId)
      || savedFields?.find(x => ((x.vFieldId || x.VFieldId) || '').toLowerCase() === (dcfSelected.fieldId || '').toLowerCase());
    const savedValue = (savedField?.vFieldValue ?? savedField?.VFieldValue ?? '').trim();
    const currentValue = getCurrentFieldValueFromForm(dcfSelected.fieldId);
    const fieldUnchanged = (currentValue === savedValue) || (currentValue === (savedField?.vFieldValue ?? savedField?.VFieldValue ?? ''));
    if (fieldUnchanged && !dcfResponse.trim()) {
      setToast({ type: 'error', message: dcfMessages.remarksOrUpdateRequired });
      return;
    }
    void doResolveDCF(null);
  };

  // When savedFields is stale, derive Resolved from DCF history (latest action "Resolved DCF" or "CRA Response")
  const latestAction = (dcfHistory && dcfHistory.length > 0)
    ? (dcfHistory[0].action || dcfHistory[0].Action || '').toLowerCase()
    : '';
  const isResolvedFromHistory =
    latestAction === "resolved reviewer's comment" ||
    latestAction === 'resolved dcf' ||
    latestAction === 'cra response';
  const isResolved = dcfInfo.isResolved || isResolvedFromHistory;

  // Allow "Raise DCF" only when no DCF is open on this field (status empty/null or closed). When Open or Resolved, show message instead.
  const dcfStatusLower = (dcfInfo.status || '').trim().toLowerCase();
  const hasRaisedInHistory =
    dcfHistory.length > 0 &&
    (latestAction === "raised reviewer's comment" ||
      latestAction === 'raised dcf' ||
      (dcfHistory[0].action || dcfHistory[0].Action || '').toLowerCase().includes('raised'));
  const dcfAlreadyRaisedOnField = dcfStatusLower === 'open' || isResolved || (hasRaisedInHistory && dcfStatusLower !== 'closed');
  const canRaiseNewDcf = !dcfAlreadyRaisedOnField;

  // Show Close DCF when Resolved, raiser profile matches, and review lane matches raised/sent-to stage (API GetDcfCloseStageMismatchMessage).
  const dcfRaiserMatchesCurrentUser = (() => {
    const raisedBy = (dcfInfo.raisedByProfile || '').toString().trim().toLowerCase();
    if (!raisedBy) return true;
    if (currentUserProfileAliases.has(raisedBy)) return true;
    return (currentUserProfile || '').trim().toLowerCase() === raisedBy;
  })();
  const closeStageAllowedAtLane = dcfCloseAllowedAtViewingStage(
    dcfInfo,
    dcfReviewerContextStageLabel,
    headerStageLabelForDcfGate
  );
  const canCloseDCF = isResolved && dcfRaiserMatchesCurrentUser && closeStageAllowedAtLane;
  const getDcfCloseBlockMessage = () => {
    if (!isResolved) return dcfMessages.closeBlockedNotResolved;
    if (!dcfRaiserMatchesCurrentUser) return dcfMessages.closeBlockedNotRaiser;
    if (!closeStageAllowedAtLane) {
      const ip = normalizeStageLabelForMatch('In Progress');
      const tierStage =
        dcfSentToReviewStageTrim &&
        normalizeStageLabelForMatch(dcfSentToReviewStageTrim) !== ip
          ? dcfSentToReviewStageTrim
          : (dcfInfo.raisedStage || '').trim();
      return dcfMessages.closeBlockedWrongStage
        .replace('{dcfStage}', tierStage || dcfReviewerContextStageLabel || headerStageLabelForDcfGate)
        .replace('{currentStage}', dcfReviewerContextStageLabel || headerStageLabelForDcfGate || '—');
    }
    return null;
  };
  const dcfCloseBlockMessage = isResolved && !canCloseDCF ? getDcfCloseBlockMessage() : null;

  const handleFinalCloseDCF = () => {
    if (!dcfSelected.nCRFDtlNo) return;
    const block = getDcfCloseBlockMessage();
    if (block) {
      setToast({ type: 'error', message: block });
      return;
    }
    void doCloseDCF(null);
  };

  // When CRA has DCF-generated fields (Open or Resolved) to fill, hide Draft Save so they must Submit after updating (avoids "Please update field" after draft save).
  const hasDcfGeneratedFieldsToFill = (savedFields || []).filter((f) => {
    const status = (f.vDCFStatus ?? f.VDCFStatus ?? '').toString().trim().toLowerCase();
    return status === 'open' || status === 'resolved';
  }).length > 0;
  // Keep Submit visible only when at least one Open DCF is routed to the current header/viewing stage.
  const showSubmitForAnyOpenDcf = (savedFields || []).some((f) => {
    if (!isFieldDcfOpen(f)) return false;
    return openDcfResolutionAllowedAtHeaderStage(
      f,
      headerStageLabelForDcfGate,
      crfCurrentStageCode,
      pageHeaderStageInProgress === true
    );
  });

  /** Parent Visit Report / closed visit, or server viewer flag: no DCF/Issue/row transactions; modals are details-only. */
  const crfVisitReportNonTransactional = !!(props.readOnly || isViewOnly);

  /** DCF batch tier (sent-to or raised) matches the review lane (visit tab or persisted header, normalized). */
  const dcfBatchTierForReviewGate = (dcfInfo.batchDisplayStage || dcfInfo.sentToReviewStage || dcfInfo.raisedStage || '').trim();
  const reviewerAtDcfBatchTier =
    dcfBatchTierForReviewGate.length > 0 &&
    dcfReviewerContextStageLabel.length > 0 &&
    normalizeStageLabelForMatch(dcfBatchTierForReviewGate) === normalizeStageLabelForMatch(dcfReviewerContextStageLabel);
  /** True when API says review OR embedded from a visit review lane while hdr may still be In Progress (sequential CRA handoff). */
  const isReviewLaneForDcfRelease =
    isReviewStage || (embedInModal && visitUiStageLabel.length > 0);
  /** UI-only fallback: unreleased branch-routed Open DCF may exist but backend flag can be false when created from a non-assigned stage. */
  const hasUnreleasedBranchRoutedOpenDcf = (savedFields || []).some((f) => {
    if (!isFieldDcfOpen(f)) return false;
    const submitRaw = f.iSubmitDCFToCRA ?? f.ISubmitDCFToCRA ?? f.isubmitDCFToCRA;
    const releasedToCra = submitRaw === 1 || submitRaw === '1' || submitRaw === true;
    if (releasedToCra) return false;
    const sentTo = (f.vDCFSentToReviewStage ?? f.VDCFSentToReviewStage ?? '').toString().trim();
    if (!sentTo) return false;
    return normalizeStageLabelForMatch(sentTo) !== normalizeStageLabelForMatch('In Progress');
  });
  const showReleaseDcfsToCraInModal =
    !crfVisitReportNonTransactional &&
    canApproveReject &&
    isReviewLaneForDcfRelease &&
    hasUnreleasedOpenDcf &&
    !!nCRFHdrNo &&
    canRaiseDCFFromServer &&
    dcfStatusLower === 'open' &&
    !dcfInfo.releasedToCra &&
    reviewerAtDcfBatchTier;
  // Keep footer actions mutually exclusive: when batch release is shown, hide normal Submit.
  const showSubmitAllToCraButton =
    !isViewOnly &&
    canApproveReject &&
    isReviewLaneForDcfRelease &&
    (hasUnreleasedOpenDcf || hasUnreleasedBranchRoutedOpenDcf) &&
    !props.readOnly &&
    !!nCRFHdrNo;
  // Resolve-only mode: when only routed Open DCF correction is being handled at this stage, hide Draft Save.
  const hideDraftSaveForResolveOnly =
    isReviewStage &&
    showSubmitForAnyOpenDcf &&
    !reviewAssociatedRequiresSaveFlow &&
    !showSubmitAllToCraButton;

  /** In-modal resolve: same master gate as grid — API IsEditable on the opened detail row (avoids mismatch with released/tier heuristics). */
  const dcfModalSelectedRowEditable =
    dcfInfo.selectedFieldRow != null &&
    (dcfInfo.selectedFieldRow.isEditable === true || dcfInfo.selectedFieldRow.IsEditable === true);
  const showReviewerResolveOpenDcf =
    !crfVisitReportNonTransactional &&
    canApproveReject &&
    dcfStatusLower === 'open' &&
    dcfModalSelectedRowEditable;

  /** CRA: show response UI even if audit history is still empty (same field row already has Open/Resolved). */
  const craDcfModalShowResponseUi =
    isCraUser &&
    (dcfHistory.length > 0 ||
      dcfStatusLower === 'open' ||
      dcfStatusLower === 'resolved');

  if (loading) {
    return (
      <div className="crf-form-container">
        <Spinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="crf-form-container">
        <div className="alert alert-danger">
          <strong>Error:</strong> {error}
        </div>
      </div>
    );
  }

  const handleClose = () => {
    if (isPreviewInNewTab) {
      window.close();
      return;
    }
    if (onClose) onClose();
    else window.history.back();
  };

  return (
    <div ref={crfContainerRef} className={`crf-form-container${isReviewStage ? ' crf-form-review-stage' : ''}`}>
      <div className="crf-form-wrapper">
        {/* Form Header */}
        <div className="crf-form-header">
          <div className="crf-form-title">
            <h2>Report : {reportTitle}</h2>
            <button className="crf-close-btn" onClick={handleClose}>
              <i className="fas fa-times"></i>
            </button>
          </div>
        </div>

        {/* Form Content */}
        <form className="crf-form" onSubmit={handleFormSubmit}>
          {/* Metadata Section */}
          <div className="crf-metadata-section">
            <div className="crf-metadata-grid">
              <div className="crf-metadata-item">
                <label className="crf-metadata-label">Project:</label>
                <input
                  type="text"
                  className="crf-metadata-input"
                  value={formData.protocolNo}
                  readOnly
                />
              </div>
              <div className="crf-metadata-item">
                <label className="crf-metadata-label">Actual Start Date:</label>
                <input
                  type="text"
                  className="crf-metadata-input"
                  value={formData.actualStartDate}
                  readOnly
                />
              </div>
              <div className="crf-metadata-item">
                <label className="crf-metadata-label">Actual End Date:</label>
                <input
                  type="text"
                  className="crf-metadata-input"
                  value={formData.actualEndDate}
                  readOnly
                />
              </div>
              <div className="crf-metadata-item">
                <label className="crf-metadata-label">Visit Mode:</label>
                <input
                  type="text"
                  className="crf-metadata-input"
                  value={formData.visitMode}
                  readOnly
                />
              </div>
              <div className="crf-metadata-item crf-metadata-item-half">
                <label className="crf-metadata-label">PI Name:</label>
                <input
                  type="text"
                  className="crf-metadata-input"
                  value={formData.piName}
                  readOnly
                />
              </div>
              <div className="crf-metadata-item crf-metadata-item-half">
                <label className="crf-metadata-label">Sponsor:</label>
                <input
                  type="text"
                  className="crf-metadata-input"
                  value={formData.sponsor}
                  readOnly
                />
              </div>
              <div className="crf-metadata-item crf-metadata-item-site">
                <label className="crf-metadata-label">Site:</label>
                <input
                  type="text"
                  className="crf-metadata-input"
                  value={formData.siteNumber}
                  readOnly
                />
              </div>
            </div>
          </div>

          {/* Generated CRF Content - use memoized __html so opening DCF/Audit modals does not wipe answers/comments */}
          <div
            className="crf-generated-content"
            dangerouslySetInnerHTML={dangerousContent}
          />

          {/* Report Signer Blocks — displayed at the bottom after CRF content */}
          {(() => {
            const submittedBy = workflowHeader?.submittedBy || workflowHeader?.SubmittedBy || '';
            const submittedAtUtc = workflowHeader?.submittedAtUtc || workflowHeader?.SubmittedAtUtc || null;
            const submittedAtOffset = workflowHeader?.submittedAtOffset || workflowHeader?.SubmittedAtOffset || workflowHeader?.dateTimeOffset || workflowHeader?.DateTimeOffset || null;
            const submitParsed = parseSignerNameAndRole(submittedBy);
            const reviewerBlocks = headerReviewerCols
              ? headerReviewerCols.filter((col) => col.signedBy && col.signedBy.trim())
              : [];
            const hasAnyBlock = (submittedBy && submittedBy.trim()) || reviewerBlocks.length > 0;
            if (!hasAnyBlock) return null;
            return (
              <div className="crf-report-approved-section">
                {submittedBy && submittedBy.trim() && (
                  <table className="crf-report-approved-table">
                    <thead>
                      <tr><th colSpan="4" className="crf-report-approved-header">Report Submit By</th></tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td className="crf-report-approved-label">Signature</td>
                        <td>{submitParsed.name || '\u00A0'}</td>
                        <td className="crf-report-approved-label">Role in the Study</td>
                        <td>{submitParsed.role || '\u00A0'}</td>
                      </tr>
                      <tr>
                        <td className="crf-report-approved-label">Name</td>
                        <td>{submitParsed.name || '\u00A0'}</td>
                        <td className="crf-report-approved-label">Date</td>
                        <td>{submittedAtUtc ? formatPerformedOnUtcToLocalWithOffset(submittedAtUtc, submittedAtOffset) : '\u00A0'}</td>
                      </tr>
                    </tbody>
                  </table>
                )}
                {reviewerBlocks.map((col, idx) => {
                  const parsed = parseSignerNameAndRole(col.signedBy);
                  const dateDisplay = col.performedAtUtc
                    ? formatPerformedOnUtcToLocalWithOffset(col.performedAtUtc, col.performedAtOffset || workflowHeader?.dateTimeOffset || workflowHeader?.DateTimeOffset)
                    : '\u00A0';
                  return (
                    <table className="crf-report-approved-table" key={`signer-${idx}-${col.label || 'r'}`}>
                      <thead>
                        <tr><th colSpan="4" className="crf-report-approved-header">Report {col.label || 'Review'} By</th></tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td className="crf-report-approved-label">Signature</td>
                          <td>{parsed.name || '\u00A0'}</td>
                          <td className="crf-report-approved-label">Role in the Study</td>
                          <td>{parsed.role || '\u00A0'}</td>
                        </tr>
                        <tr>
                          <td className="crf-report-approved-label">Name</td>
                          <td>{parsed.name || '\u00A0'}</td>
                          <td className="crf-report-approved-label">Date</td>
                          <td>{dateDisplay}</td>
                        </tr>
                      </tbody>
                    </table>
                  );
                })}
                <p className="crf-report-approved-note">
                  <strong>Note:</strong> This is electronically signed document and manual signature is not required.
                </p>
              </div>
            );
          })()}

          {/* Form Footer */}
          <div className="crf-form-footer">
            <div className="crf-form-actions">
              {/* Draft Save / Submit: CRA or Associated review flow, or reviewer when API returns those actions (e.g. Open DCF in review). Approve/Reject follow availableActions only — server omits them while active DCF exists. */}
              {(isCraUser ||
                reviewAssociatedRequiresSaveFlow ||
                (isReviewStage && (canApproveReject || canRaiseDCFFromServer) && availableActions.includes('DraftSave'))) &&
                !isViewOnly &&
                (availableActions.includes('DraftSave') || reviewAssociatedRequiresSaveFlow) &&
                !props.readOnly &&
                !hideDraftSaveForResolveOnly &&
                !hasDcfGeneratedFieldsToFill && (
                <button type="button" className="btn btn-primary" onClick={handleDraftSave} disabled={saving}>
                  {saving ? 'Saving...' : 'Draft Save'}
                </button>
              )}
              {(isCraUser ||
                reviewAssociatedRequiresSaveFlow ||
                (isReviewStage && (canApproveReject || canRaiseDCFFromServer) && availableActions.includes('Submit')) ||
                showSubmitForAnyOpenDcf ||
                (hasOpenDCF && !hasUnreleasedOpenDcf && isCraUser)) && // CRA sees Submit when DCFs are released to them
                !isViewOnly &&
                (availableActions.includes('Submit') || reviewAssociatedRequiresSaveFlow || showSubmitForAnyOpenDcf) &&
                !props.readOnly &&
                !(reviewerSubmitHiddenAfterBatchRelease && !isCraUser) &&
                !showSubmitAllToCraButton &&
                (!submitBlockedByOwnOpenDcf || pmAtReviewLaneWithOwnDcfToClose) && (
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleSubmitReport}
                  disabled={saving}
                >
                  {saving ? 'Submitting...' : 'Submit'}
                </button>
              )}
              {!isViewOnly && canApproveReject && availableActions.includes('Approve') && (
                <button type="button" className="btn btn-success" onClick={handleApprove} disabled={transitioning}>
                  {transitioning ? 'Processing...' : 'Approve'}
                </button>
              )}
              {!isViewOnly && canApproveReject && availableActions.includes('Reject') && (
                <button type="button" className="btn btn-danger" onClick={handleReject} disabled={transitioning}>
                  {transitioning ? 'Processing...' : 'Send Back'}
                </button>
              )}
              <SubmitAllDcfButton
                visible={showSubmitAllToCraButton}
                disabled={transitioning || saving}
                submitting={submitAllDcfSubmitting}
                onClick={handleSubmitAllDcfsToCra}
              />
              {!isPreviewInNewTab && (
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => setShowPreviewChoiceModal(true)}
                  disabled={!nCRFHdrNo}
                >
                  Preview
                </button>
              )}
              <button type="button" className="btn btn-primary" onClick={handleClose}>
                Close
              </button>
            </div>
          </div>
        </form>

        {/* Create Action Item modal (same as Action Items tab in Visit Management) */}
        <CreateActionItemModal
          isOpen={isCreateActionItemModalOpen}
          onClose={() => {
            setIsCreateActionItemModalOpen(false);
            setCreateActionItemFieldCode(null);
            setCreateActionItemNCRFDtlNo(null);
            createActionItemPayloadRef.current = null;
          }}
          onSave={(payload) => {
            const fromPayload = payload?.nCRFDtlNo != null && Number(payload.nCRFDtlNo) > 0 ? Number(payload.nCRFDtlNo) : null;
            const fromOpenRow = createActionItemNCRFDtlNo != null && Number(createActionItemNCRFDtlNo) > 0 ? Number(createActionItemNCRFDtlNo) : null;
            const detailNo = fromPayload ?? fromOpenRow;
            if (detailNo != null) {
              setSavedFields((prev) => {
                if (!Array.isArray(prev) || prev.length === 0) return prev;
                let touched = false;
                const next = prev.map((f) => {
                  const no = Number(f.nCRFDtlNo ?? f.NCRFDtlNo ?? f.ncrfDtlNo ?? 0);
                  if (no !== detailNo) return f;
                  touched = true;
                  return { ...f, hasOpenOrResolvedActionItem: true, HasOpenOrResolvedActionItem: true };
                });
                return touched ? next : prev;
              });
              setPermissionsRefreshKey((k) => k + 1);
            }
            setIsCreateActionItemModalOpen(false);
            setCreateActionItemFieldCode(null);
            setCreateActionItemNCRFDtlNo(null);
            createActionItemPayloadRef.current = null;
            refreshCrfPageFieldsForActionItems();
            if (typeof onActionItemCreated === 'function') onActionItemCreated();
          }}
          defaultProject={defaultProjectForActionItem}
          defaultSiteId={defaultSiteIdForActionItem}
          defaultSiteName={defaultSiteNameForActionItem}
          defaultVisitId={visitId != null && visitId !== '' ? String(visitId) : ''}
          defaultVisitType={visitType}
          defaultAssignActionItemTo="Section Attributes"
          defaultNCRFDtlNo={createActionItemNCRFDtlNo != null ? Number(createActionItemNCRFDtlNo) : null}
          defaultFieldCode={createActionItemFieldCode ?? null}
          openPayloadRef={createActionItemPayloadRef}
          openedFromCRF={true}
          stageVisits={stageVisitsForActionItem}
          visitReportViewOnly={crfVisitReportNonTransactional}
        />

        {/* CRF Report Preview: first choose With (filled) or Without (blank) answers and comments */}
        <Modal
          isOpen={showPreviewChoiceModal}
          onClose={() => setShowPreviewChoiceModal(false)}
          title="Preview Visit Report"
          size="small"
          className="crf-preview-choice-modal"
        >
          <p className="crf-preview-choice-prompt">Choose how to preview the report:</p>
          <div className="crf-preview-choice-attachments">
            <label className="crf-preview-choice-attachments-label" htmlFor="crf-preview-attachments-select">
              Attachments
            </label>
            <select
              id="crf-preview-attachments-select"
              className="form-select crf-preview-attachments-select"
              value={previewAttachmentsChoice}
              onChange={(e) => setPreviewAttachmentsChoice(e.target.value)}
            >
              <option value="without">Without attachments</option>
              <option value="with">With attachments</option>
            </select>
            
          </div>
          <div className="crf-preview-choice-actions">
            <button
              type="button"
              className="btn btn-primary"
              onClick={handlePreviewWith}
            >
              View Filled Responses
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handlePreviewWithout}
            >
              View Blank Responses
            </button>
            <button
              type="button"
              className="btn btn-secondary crf-preview-choice-btn crf-preview-choice-cancel"
              onClick={() => setShowPreviewChoiceModal(false)}
            >
              Cancel
            </button>
          </div>
        </Modal>

        {/* CRF Report Preview display modal */}
        <Modal
          isOpen={showPreviewModal}
          onClose={closePreviewModal}
          title="Visit Report Preview"
          size="large"
          className="crf-report-preview-modal"
          footer={
            <>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleDownloadPreviewPdf}
                disabled={pdfDownloading || openIssuesLoading || !previewHtml}
              >
                {pdfDownloading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                    Generating PDF...
                  </>
                ) : 'Download PDF'}
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={closePreviewModal}
                disabled={pdfDownloading}
              >
                Close
              </button>
            </>
          }
        >
          {pdfDownloading && (
            <div className="crf-report-pdf-progress-overlay" role="status" aria-live="polite">
              <div className="crf-report-pdf-progress">
                <div className="crf-report-pdf-progress-label">
                  <span>{pdfProgressLabel || 'Generating PDF...'}</span>
                  <span>{Math.round(pdfProgressPercent)}%</span>
                </div>
                <div className="crf-report-pdf-progress-track" aria-hidden="true">
                  <div
                    className="crf-report-pdf-progress-bar"
                    style={{ width: `${Math.max(0, Math.min(100, pdfProgressPercent))}%` }}
                  />
                </div>
              </div>
            </div>
          )}
          <div ref={previewContentRef} className="crf-report-preview-content-wrapper">
            <div className="crf-report-preview-header">
              <h2>Report : {reportTitle}</h2>
            </div>
            <div className="crf-metadata-section">
              <div className="crf-metadata-grid">
                <div className="crf-metadata-item">
                  <label className="crf-metadata-label">Project:</label>
                  <div className="crf-preview-field-value crf-metadata-value">{formData.protocolNo || '\u00A0'}</div>
                </div>
                <div className="crf-metadata-item">
                  <label className="crf-metadata-label">Actual Start Date:</label>
                  <div className="crf-preview-field-value crf-metadata-value">{formData.actualStartDate || '\u00A0'}</div>
                </div>
                <div className="crf-metadata-item">
                  <label className="crf-metadata-label">Actual End Date:</label>
                  <div className="crf-preview-field-value crf-metadata-value">{formData.actualEndDate || '\u00A0'}</div>
                </div>
                <div className="crf-metadata-item">
                  <label className="crf-metadata-label">Visit Mode:</label>
                  <div className="crf-preview-field-value crf-metadata-value">{formData.visitMode || '\u00A0'}</div>
                </div>
                <div className="crf-metadata-item crf-metadata-item-half">
                  <label className="crf-metadata-label">PI Name:</label>
                  <div className="crf-preview-field-value crf-metadata-value">{formData.piName || '\u00A0'}</div>
                </div>
                <div className="crf-metadata-item crf-metadata-item-half">
                  <label className="crf-metadata-label">Sponsor:</label>
                  <div className="crf-preview-field-value crf-metadata-value">{formData.sponsor || '\u00A0'}</div>
                </div>
                <div className="crf-metadata-item crf-metadata-item-site">
                  <label className="crf-metadata-label">Site:</label>
                  <div className="crf-preview-field-value crf-metadata-value">{formData.siteNumber || '\u00A0'}</div>
                </div>
                <div className="crf-metadata-item">
                  <label className="crf-metadata-label">Exported On:</label>
                  <div className="crf-preview-field-value crf-metadata-value">{exportedOnDisplay || '\u00A0'}</div>
                </div>
              </div>
            </div>
            <div className="crf-generated-content crf-report-preview-content" dangerouslySetInnerHTML={{ __html: previewHtml }} />
            {includeOpenIssuesInPreview && (
              <div className="crf-open-issues-section">
                <h3 className="crf-open-issues-title">Issues</h3>
                <div className="table-responsive">
                  <table className="crf-open-issues-table">
                    <thead>
                      <tr>
                        <th>Reference No</th>
                        <th>Site</th>
                        <th>Visit Date</th>
                        <th>Field Name</th>
                        <th>Issue Description</th>
                        <th>Expected Closure Date</th>
                        <th>Assigned To</th>
                        <th>Current Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {openIssuesLoading ? (
                        <tr>
                          <td colSpan="10" className="crf-open-issues-empty">Loading issues...</td>
                        </tr>
                      ) : openIssuesForReport.length > 0 ? (
                        openIssuesForReport.map((item) => {
                          const siteCell = [item.siteCode, item.siteDescription].filter(Boolean).join(' – ') || formData.siteNumber || '-';
                          const vt = item.visitType ?? item.VisitType ?? '';
                          const vd = item.visitDate ?? item.VisitDate ?? item.plannedStartDate;
                          return (
                            <tr key={item.actionItemID ?? item.nCRFActionItemNo ?? item.actionItemNumber}>
                              <td>{item.actionItemNumber ?? item.vActionItemNumber ?? '-'}</td>
                              <td>{siteCell}</td>
                              <td>{vd ? formatDateDDMMMYYYY(vd) : '-'}</td>
                              <td>{item.actionItemOn || '-'}</td>
                              <td>{item.findingDescription || '-'}</td>
                              <td>{item.dueDate ? formatDateDDMMMYYYY(item.dueDate) : '-'}</td>
                              <td>{item.vAssignTo ?? item.VAssignTo ?? item.assignedToName ?? '-'}</td>
                              <td>{formatActionItemStatusForDisplay(item.status)}</td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td colSpan="10" className="crf-open-issues-empty">No issues for this site.</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            {/* Report Signer Blocks — after Issues, before Report Attachments */}
            {!previewOmitWorkflowSignerFields && (() => {
              const submittedBy = workflowHeader?.submittedBy || workflowHeader?.SubmittedBy || '';
              const submittedAtUtc = workflowHeader?.submittedAtUtc || workflowHeader?.SubmittedAtUtc || null;
              const submittedAtOffset = workflowHeader?.submittedAtOffset || workflowHeader?.SubmittedAtOffset || workflowHeader?.dateTimeOffset || workflowHeader?.DateTimeOffset || null;
              const submitParsed = parseSignerNameAndRole(submittedBy);
              const reviewerBlocks = headerReviewerCols
                ? headerReviewerCols.filter((col) => col.signedBy && col.signedBy.trim())
                : [];
              const hasAnyBlock = (submittedBy && submittedBy.trim()) || reviewerBlocks.length > 0;
              if (!hasAnyBlock) return null;
              return (
                <div className="crf-report-approved-section">
                  {submittedBy && submittedBy.trim() && (
                    <table className="crf-report-approved-table">
                      <thead>
                        <tr><th colSpan="4" className="crf-report-approved-header">Report Submit By</th></tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td className="crf-report-approved-label">Signature</td>
                          <td>{submitParsed.name || '\u00A0'}</td>
                          <td className="crf-report-approved-label">Role in the Study</td>
                          <td>{submitParsed.role || '\u00A0'}</td>
                        </tr>
                        <tr>
                          <td className="crf-report-approved-label">Name</td>
                          <td>{submitParsed.name || '\u00A0'}</td>
                          <td className="crf-report-approved-label">Date</td>
                          <td>{submittedAtUtc ? formatPerformedOnUtcToLocalWithOffset(submittedAtUtc, submittedAtOffset) : '\u00A0'}</td>
                        </tr>
                      </tbody>
                    </table>
                  )}
                  {reviewerBlocks.map((col, idx) => {
                    const parsed = parseSignerNameAndRole(col.signedBy);
                    const dateDisplay = col.performedAtUtc
                      ? formatPerformedOnUtcToLocalWithOffset(col.performedAtUtc, col.performedAtOffset || workflowHeader?.dateTimeOffset || workflowHeader?.DateTimeOffset)
                      : '\u00A0';
                    return (
                      <table className="crf-report-approved-table" key={`prev-signer-${idx}-${col.label || 'r'}`}>
                        <thead>
                          <tr><th colSpan="4" className="crf-report-approved-header">Report {col.label || 'Review'} By</th></tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td className="crf-report-approved-label">Signature</td>
                            <td>{parsed.name || '\u00A0'}</td>
                            <td className="crf-report-approved-label">Role in the Study</td>
                            <td>{parsed.role || '\u00A0'}</td>
                          </tr>
                          <tr>
                            <td className="crf-report-approved-label">Name</td>
                            <td>{parsed.name || '\u00A0'}</td>
                            <td className="crf-report-approved-label">Date</td>
                            <td>{dateDisplay}</td>
                          </tr>
                        </tbody>
                      </table>
                    );
                  })}
                  <p className="crf-report-approved-note">
                    <strong>Note:</strong> This is electronically signed document and manual signature is not required.
                  </p>
                </div>
              );
            })()}
            {includeAttachmentsInPreview && (
              <div className="crf-report-attachments-appendix">
                <h3 className="crf-report-attachments-appendix-title">Report Attachments</h3>
                {previewAttachmentsLoading && (
                  <p className="crf-report-attachments-loading text-muted">Loading attachments…</p>
                )}
                {previewAttachmentsFetchError && (
                  <p className="crf-report-attachments-error text-danger">{previewAttachmentsFetchError}</p>
                )}
                {!previewAttachmentsLoading && !previewAttachmentsFetchError && previewAttachmentAppendixItems.length === 0 && (
                  <p className="crf-report-attachments-empty text-muted">No file uploads on this report.</p>
                )}
                {previewAttachmentAppendixItems.map((a) => (
                  <div key={a.path} className="crf-report-attachment-item">
                    <div className="crf-report-attachment-filename">{a.filename}</div>
                    {a.kind === 'image' && a.objectUrl && (
                      <img
                        src={a.objectUrl}
                        alt={a.filename}
                        className="crf-report-attachment-image"
                      />
                    )}
                    {a.kind === 'pdf' && a.objectUrl && (
                      <div className="crf-report-attachment-pdf-wrap">
                        <iframe
                          className="crf-report-attachment-pdf-frame"
                          title={a.filename}
                          src={a.objectUrl}
                        />
                      </div>
                    )}
                    {a.kind === 'other' && (
                      <p className="crf-report-attachment-other small text-muted">
                        Preview not available for this file type. The file remains available from the Visit Report.
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </Modal>

        <DcfVisitReportModal
          dcfModalOpen={dcfModalOpen}
          onCloseModal={() => {
            setDcfModalOpen(false);
            setDcfRemark('');
            setDcfRemarkError('');
            setDcfResponse('');
            setDcfSelected({ nCRFDtlNo: null, fieldId: '', questionText: '' });
          }}
          dcfInfo={dcfInfo}
          loadingHistory={loadingHistory}
          dcfHistory={dcfHistory}
          dcfRemark={dcfRemark}
          setDcfRemark={setDcfRemark}
          dcfRemarkError={dcfRemarkError}
          setDcfRemarkError={setDcfRemarkError}
          dcfResponse={dcfResponse}
          setDcfResponse={setDcfResponse}
          dcfSubmitting={dcfSubmitting}
          submitAllDcfSubmitting={submitAllDcfSubmitting}
          crfVisitReportNonTransactional={crfVisitReportNonTransactional}
          dcfStatusLower={dcfStatusLower}
          showReleaseDcfsToCraInModal={showReleaseDcfsToCraInModal}
          showReviewerResolveOpenDcf={showReviewerResolveOpenDcf}
          dcfMessages={dcfMessages}
          dcfAlreadyRaisedOnField={dcfAlreadyRaisedOnField}
          canRaiseDCFFromServer={canRaiseDCFFromServer}
          canRaiseNewDcf={canRaiseNewDcf}
          canCloseDCF={canCloseDCF}
          dcfCloseBlockMessage={dcfCloseBlockMessage}
          isResolved={isResolved}
          isCraUser={isCraUser}
          craDcfModalShowResponseUi={craDcfModalShowResponseUi}
          craDcfModalCraResponseBlocked={craDcfModalCraResponseBlocked}
          craDcfModalCraResponseBlockedDetail={craDcfModalCraResponseBlockedDetail}
          showReviewerDcfView={showReviewerDcfView}
          dcfBatchTierForReviewGate={dcfBatchTierForReviewGate}
          reviewerAtDcfBatchTier={reviewerAtDcfBatchTier}
          handleCRFDCFSubmit={handleCRFDCFSubmit}
          handleFinalCloseDCF={handleFinalCloseDCF}
          handleSubmitAllDcfsToCra={handleSubmitAllDcfsToCra}
          handleResolveDCF={handleResolveDCF}
          handleSubmitDCFResponse={handleSubmitDCFResponse}
          formatPerformedOnUtcToLocal={formatPerformedOnUtcToLocal}
          formatOffsetForDisplay={formatOffsetForDisplay}
        />

        {/* E-sign modal: before DCF/Visit actions that impact transaction tables */}
        {showESignModal && createPortal(
          <div className="crf-nested-modal-wrapper" style={{ position: 'fixed', inset: 0, zIndex: 1300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <ElectronicSign
              show={true}
              onClose={() => {
                setShowESignModal(false);
                setESignPendingAction(null);
                eSignRemarksRef.current = '';
                actionItemUpdateRef.current = null;
              }}
              onAuthSuccess={(vRemarks) => { eSignRemarksRef.current = vRemarks || ''; }}
              onSave={runAfterESign}
              title={eSignPendingAction === 'closeActionItem' ? 'Enter Remark' : 'Authenticate'}
              primaryActionLabel={eSignPendingAction === 'closeActionItem' ? 'Save' : 'Authenticate'}
              remarkOnlyMode={eSignPendingAction === 'closeActionItem'}
              showConfirmText={eSignPendingAction !== 'closeActionItem'}
              onValidationError={(message) => setToast({ type: 'error', message })}
            />
          </div>,
          document.body
        )}

        {/* View Audit Modal - portaled to body in high-z wrapper (First/Second Review) */}
        {auditModalOpen && createPortal(
          <div className="crf-nested-modal-wrapper" style={{ position: 'fixed', inset: 0, zIndex: 1300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Modal
              isOpen={true}
              onClose={() => {
                setAuditModalOpen(false);
                setAuditSelected({
                  fieldId: '',
                  questionText: '',
                  nCRFDtlNo: null,
                  nCRFActionItemNo: null,
                  actionItemAuditFieldName: null,
                });
                setAuditTrailList([]);
                setAuditTrailError(null);
              }}
              title="Audit Details"
              showCloseButton={true}
              size="fullscreen"
              className="crf-audit-modal"
              overlayClassName="crf-nested-modal-overlay"
            >
              {auditTrailLoading && <div className="mb-3"><Spinner /></div>}
              {!auditTrailLoading && auditTrailError && (
                <div className="alert alert-danger mb-3">{auditTrailError}</div>
              )}
              {!auditTrailLoading && !auditTrailError && auditTrailList.length === 0 && (
                <p className="text-muted mb-3">No audit entries found for this field.</p>
              )}
              {!auditTrailLoading && !auditTrailError && auditTrailList.length > 0 && (() => {
                const isCrfActionItemAudit = auditSelected.nCRFActionItemNo != null && Number(auditSelected.nCRFActionItemNo) > 0;
                const aiField = (auditSelected.actionItemAuditFieldName || '').toString().toLowerCase();
                // CRFDtl: VFieldValue; CRFActionItem from Issue Review: VReplyRemark or VStatus
                const answerFieldName = isCrfActionItemAudit
                  ? (aiField === 'vstatus' ? 'vstatus' : 'vreplyremark')
                  : 'vfieldvalue';
                const selectedFieldName = String(auditSelected.questionText || '')
                  .replace(/\s*DCF View Audit\s*$/i, '')
                  .replace(/\?\s*i\s*$/i, '?')
                  .trim() || '—';
                const commentFieldName = 'vcomment';
                const auditDetailFieldKey = (d) => (d.vFieldName || d.VFieldName || '').toLowerCase();
                const auditContent = crfContainerRef.current?.querySelector('.crf-generated-content');
                const isStsAuditField = !isCrfActionItemAudit
                  && isStsAutoCaptureFieldTarget(auditContent, auditSelected.fieldId);
                const toAuditCell = (raw) => {
                  let v = raw;
                  if (isCrfActionItemAudit && aiField === 'vreplyremark') {
                    v = formatReplyRemarkPlainForAuditDisplay(raw);
                  }
                  if (isStsAuditField) {
                    const stsFmt = formatStsAutoCaptureAuditValueForDisplay(v);
                    if (stsFmt != null && String(stsFmt).trim() !== '') return stsFmt;
                  }
                  const fmt = formatAuditValueForDisplay(v);
                  if (fmt == null || String(fmt).trim() === '') return '—';
                  return fmt;
                };
                // Comment columns apply only to Visit Report / CRFDtl row audit (Answer + vComment in AuditDtl). Hide for CRFActionItem (Status / Description) audits.
                const showCommentAuditColumns = !isCrfActionItemAudit;
                const filteredRows = auditTrailList.flatMap((hdr, hi) => {
                  const details = hdr.details || hdr.Details || [];
                  const recordedOn = hdr.dRecordedOnUTC || hdr.dRecordedOnUtc;
                  const sign = hdr.performedBy || hdr.vRecordedSign || '—';
                  const offset = hdr.vRecordedAtOffSet || hdr.vRecordedAtOffset || '—';
                  const mapStatusAuditLabel = (fmt) => {
                    if (fmt == null || String(fmt).trim() === '' || fmt === '—') return '—';
                    const s = String(fmt);
                    return isCrfActionItemAudit && aiField === 'vstatus' ? formatActionItemStatusForDisplay(s) : s;
                  };
                  if (isCrfActionItemAudit) {
                    const answerDetails = details.filter((d) => auditDetailFieldKey(d) === answerFieldName);
                    if (answerDetails.length === 0) return [];
                    return answerDetails.map((dtl, di) => {
                      const oldFmt = toAuditCell(dtl.vOldValue ?? dtl.VOldValue);
                      const newFmt = toAuditCell(dtl.vNewValue ?? dtl.VNewValue);
                      return {
                        key: `${hdr.nAuditHdrNo ?? hi}-${di}`,
                        fieldName: selectedFieldName,
                        oldV: mapStatusAuditLabel(oldFmt),
                        newV: mapStatusAuditLabel(newFmt),
                        recordedOn,
                        sign,
                        offset,
                      };
                    });
                  }
                  const valueDtl = details.find((d) => auditDetailFieldKey(d) === answerFieldName);
                  const commentDtl = details.find((d) => auditDetailFieldKey(d) === commentFieldName);
                  if (!valueDtl && !commentDtl) return [];
                  return [{
                    key: `${hdr.nAuditHdrNo ?? hi}`,
                    fieldName: selectedFieldName,
                    oldV: valueDtl ? toAuditCell(valueDtl.vOldValue ?? valueDtl.VOldValue) : '—',
                    newV: valueDtl ? toAuditCell(valueDtl.vNewValue ?? valueDtl.VNewValue) : '—',
                    oldComment: commentDtl ? toAuditCell(commentDtl.vOldValue ?? commentDtl.VOldValue) : '—',
                    newComment: commentDtl ? toAuditCell(commentDtl.vNewValue ?? commentDtl.VNewValue) : '—',
                    recordedOn,
                    sign,
                    offset,
                  }];
                });
                const emptyColSpan = showCommentAuditColumns ? 8 : 6;
                return (
                  <div className="table-responsive mb-3">
                    <table
                      className={`table table-sm table-bordered crf-audit-modal-table${
                        showCommentAuditColumns ? ' crf-audit-modal-table--cols8' : ' crf-audit-modal-table--cols6'
                      }`}
                    >
                      <colgroup>
                        <col className="crf-audit-col-field-name" />
                        <col className="crf-audit-col-old-value" />
                        <col className="crf-audit-col-new-value" />
                        {showCommentAuditColumns ? (
                          <>
                            <col className="crf-audit-col-old-comment" />
                            <col className="crf-audit-col-new-comment" />
                          </>
                        ) : null}
                        <col className="crf-audit-col-performed-by" />
                        <col className="crf-audit-col-utc" />
                        <col className="crf-audit-col-offset" />
                      </colgroup>
                      <thead>
                        <tr>
                          <th>Field Name</th>
                          <th>Old Value</th>
                          <th>New Value</th>
                          {showCommentAuditColumns ? (
                            <>
                              <th>Old Comment Value</th>
                              <th>New Comment Value</th>
                            </>
                          ) : null}
                          <th>Performed By</th>
                          <th>Performed on UTC</th>
                          <th>Performed At Offset</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredRows.length === 0 ? (
                          <tr><td colSpan={emptyColSpan} className="text-muted">No audit entries for this field.</td></tr>
                        ) : (
                          filteredRows.map((row) => (
                            <tr key={row.key}>
                              <td className="crf-audit-cell-value">{row.fieldName}</td>
                              <td className="crf-audit-cell-value">{row.oldV}</td>
                              <td className="crf-audit-cell-value">{row.newV}</td>
                              {showCommentAuditColumns ? (
                                <>
                                  <td className="crf-audit-cell-value">{row.oldComment}</td>
                                  <td className="crf-audit-cell-value">{row.newComment}</td>
                                </>
                              ) : null}
                              <td className="crf-audit-cell-performed-by">{row.sign}</td>
                              <td className="crf-audit-cell-meta crf-audit-cell-utc">{formatPerformedOnUtcToLocal(row.recordedOn, row.offset)}</td>
                              <td className="crf-audit-cell-meta crf-audit-cell-offset">{formatOffsetForDisplay(row.offset)}</td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                );
              })()}
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => {
                  setAuditModalOpen(false);
                  setAuditSelected({
                    fieldId: '',
                    questionText: '',
                    nCRFDtlNo: null,
                    nCRFActionItemNo: null,
                    actionItemAuditFieldName: null,
                  });
                  setAuditTrailList([]);
                  setAuditTrailError(null);
                }}
              >
                Close
              </button>
            </Modal>
          </div>,
          document.body
        )}

        {/* Action Item Review modal - single action item view matching design (this question only, by nCRFDtlNo) */}
        {actionItemsModalOpen && createPortal(
          <div className="crf-nested-modal-wrapper" style={{ position: 'fixed', inset: 0, zIndex: 1200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Modal
              isOpen={true}
              onClose={() => {
                setActionItemsModalOpen(false);
                setActionItemsSelected({ nCRFDtlNo: null, fieldId: '', questionText: '' });
                setActionItemsList([]);
                setSelectedActionItemIndex(0);
                setActionReplyRemark('');
                setActionReplyHistoryEntries([]);
                setActionReplyDraft('');
                setActionItemReopenIntent('Closed');
                setActionItemClosingDate('');
              }}
              title="Issue Review"
              showCloseButton={true}
              className="crf-action-item-review-modal create-action-item-modal"
              overlayClassName="crf-nested-modal-overlay"
              size="large"
            >
              {actionItemsLoading ? (
                <Spinner />
              ) : actionItemsList.length === 0 ? (
                <p className="text-muted mb-0">No issues for this question.</p>
              ) : (
                <>
                  {(() => {
                    const item = actionItemsList[Math.min(selectedActionItemIndex, actionItemsList.length - 1)];
                    const currentProfile = (user?.profileCode || user?.vUserTypeCode || user?.role || '').toString().toUpperCase().trim();
                    const assignedCode = (item.assignedToProfileCode || '').toString().toUpperCase().trim();
                    const raisedByProfile = (item.createdByProfileCode || item.CreatedByProfileCode || '').toString().toUpperCase().trim();
                    const sessionMid = Number(sessionRecordedByMappingId ?? 0) || 0;
                    const canReply = (item.status || '').toString().toLowerCase() === 'open' && currentProfile && assignedCode && currentProfile === assignedCode;
                    const stLower = (item.status || '').toString().toLowerCase();
                    const canCreatorClose =
                      stLower === 'resolved'
                      && sessionMid > 0
                      && Number(item.createdBy || 0) === sessionMid
                      && (!raisedByProfile || (Boolean(currentProfile) && currentProfile === raisedByProfile));
                    const issueReopenCloseWorkflow = stLower === 'resolved' || stLower === 'closed';
                    const actionItemDescriptionEditable =
                      (canReply && !actionItemSaving && !crfVisitReportNonTransactional)
                      || (issueReopenCloseWorkflow && actionItemReopenIntent === 'Open' && !actionItemSaving && !crfVisitReportNonTransactional);
                    const siteDisplay = [item.siteCode, item.siteDescription].filter(Boolean).join(' - ') || formData.siteNumber || '-';
                    const visitTypeDisplay = item.visitType || visitType || '-';
                    const visitDisplay = (() => {
                      const displayCode = (item.displayVisitCode ?? item.DisplayVisitCode ?? '').toString().trim();
                      const siteName = (item.siteDescription ?? item.SiteDescription ?? '').toString().trim();
                      if (displayCode) return siteName ? `${displayCode} - ${siteName}` : displayCode;
                      const fallbackCode = (item.visitCode ?? item.VisitCode ?? '').toString().trim();
                      if (fallbackCode) return siteName ? `${fallbackCode} - ${siteName}` : fallbackCode;
                      return visitId != null && `${visitId}`.trim() !== '' ? `${visitId}` : '-';
                    })();
                    const issueFieldDisplay = (() => {
                      const raw = actionItemsSelected.questionText || item.actionItemOn || '';
                      const nameWithoutSuffix = raw.replace(/\s+DCF\s+View\s+Audit$/i, '').trim();
                      return nameWithoutSuffix || actionItemsSelected.fieldId || item.findingDescription || '-';
                    })();
                    return (
                      <>
                        <div className="create-action-item-form crf-action-item-review-form">
                        <div className="form-group crf-action-item-review-field">
                          <label className="form-label crf-action-item-review-label">Project</label>
                          <input
                            type="text"
                            className="form-control form-input crf-action-item-review-readonly"
                            value={(formData.protocolNo || '').trim() || '-'}
                            readOnly
                            disabled
                          />
                        </div>
                        <div className="form-group crf-action-item-review-field">
                          <label className="form-label crf-action-item-review-label">Site</label>
                          <input
                            type="text"
                            className="form-control form-input crf-action-item-review-readonly"
                            value={siteDisplay}
                            readOnly
                            disabled
                          />
                        </div>
                        <div className="form-group crf-action-item-review-field">
                          <label className="form-label crf-action-item-review-label">Visit Type</label>
                          <input
                            type="text"
                            className="form-control form-input crf-action-item-review-readonly"
                            value={visitTypeDisplay}
                            readOnly
                            disabled
                          />
                        </div>
                        <div className="form-group crf-action-item-review-field">
                          <label className="form-label crf-action-item-review-label">Visit</label>
                          <input
                            type="text"
                            className="form-control form-input crf-action-item-review-readonly"
                            value={visitDisplay}
                            readOnly
                            disabled
                          />
                        </div>
                        <div className="form-group crf-action-item-review-field crf-action-item-review-field--wide">
                          <label className="form-label crf-action-item-review-label">Field Name</label>
                          <input
                            type="text"
                            className="form-control form-input crf-action-item-review-readonly"
                            value={issueFieldDisplay}
                            readOnly
                            disabled
                          />
                        </div>
                        <div className="form-group crf-action-item-review-field">
                          <label className="form-label crf-action-item-review-label">Reference No</label>
                          <input
                            type="text"
                            className="form-control form-input crf-action-item-review-readonly"
                            value={item.actionItemNumber ?? ''}
                            readOnly
                          />
                        </div>
                        <div className="form-group crf-action-item-review-field">
                          <label className="form-label crf-action-item-review-label">Field Name</label>
                          <div className="crf-action-item-review-readonly crf-action-item-review-textarea-readonly">
                            {issueFieldDisplay}
                          </div>
                        </div>
                        <div className="form-group crf-action-item-review-field crf-action-item-review-field--wide">
                          <label className="form-label crf-action-item-review-label">Issue Description</label>
                          <textarea
                            className="form-control form-textarea crf-action-item-review-textarea-disabled"
                            value={actionDescriptionEdit}
                            readOnly
                            rows={4}
                            placeholder="Issue description..."
                          />
                        </div>
                        <div className="form-group crf-action-item-review-field">
                          <label className="form-label crf-action-item-review-label">Priority</label>
                          <select
                            className="form-select crf-action-item-review-readonly"
                            value={(item.priorityDescription ?? item.severity ?? item.priority ?? '').toString().trim()}
                            disabled
                          >
                            <option value="">Select priority</option>
                            <option value="Low">Low</option>
                            <option value="Medium">Medium</option>
                            <option value="High">High</option>
                            <option value="Critical">Critical</option>
                            {(() => {
                              const val = (item.priorityDescription ?? item.severity ?? item.priority ?? '').toString().trim();
                              if (val && !['Low', 'Medium', 'High', 'Critical'].includes(val)) {
                                return <option value={val}>{val}</option>;
                              }
                              return null;
                            })()}
                          </select>
                        </div>
                        <div className="form-group crf-action-item-review-field">
                          <label className="form-label crf-action-item-review-label">Assigned To</label>
                          <input
                            type="text"
                            className="form-control form-input crf-action-item-review-readonly"
                            value={item.vAssignTo ?? item.VAssignTo ?? item.assignedToName ?? item.assignedToProfileCode ?? '-'}
                            readOnly
                            disabled
                          />
                        </div>
                        <div className="form-group crf-action-item-review-field">
                          <label className="form-label crf-action-item-review-label">Date of Awareness</label>
                          <input
                            type="text"
                            className="form-control form-input crf-action-item-review-readonly"
                            value={(() => {
                              const iso = apiDateTimeToIsoDateLocal(getAnyCase(item, 'dateOfAwareness', 'DateOfAwareness', 'dDateOfAwareness', 'DDateOfAwareness'));
                              return (iso && /^\d{4}-\d{2}-\d{2}$/.test(iso) ? formatIsoDateCalendarDDMMMYYYY(iso) : '') || '-';
                            })()}
                            readOnly
                            disabled
                          />
                        </div>
                        <div className="form-group crf-action-item-review-field">
                          <label className="form-label crf-action-item-review-label">Date of Issue Creation</label>
                          <input
                            type="text"
                            className="form-control form-input crf-action-item-review-readonly"
                            value={(() => {
                              const iso = apiDateTimeToIsoDateLocal(getAnyCase(item, 'dateOfIssue', 'DateOfIssue', 'dDateOfIssue', 'DDateOfIssue'));
                              return (iso && /^\d{4}-\d{2}-\d{2}$/.test(iso) ? formatIsoDateCalendarDDMMMYYYY(iso) : '') || '-';
                            })()}
                            readOnly
                            disabled
                          />
                        </div>
                        <div className="form-group crf-action-item-review-field">
                          <label className="form-label crf-action-item-review-label">Expected Closure Date</label>
                          <input
                            type="text"
                            className="form-control form-input crf-action-item-review-readonly"
                            value={(() => {
                              const iso = apiDateTimeToIsoDateLocal(getAnyCase(item, 'dueDate', 'DueDate', 'dDueDate', 'DDueDate'));
                              return (iso && /^\d{4}-\d{2}-\d{2}$/.test(iso) ? formatIsoDateCalendarDDMMMYYYY(iso) : '') || '-';
                            })()}
                            readOnly
                            disabled
                          />
                        </div>
                        <div className="form-group crf-action-item-review-field">
                          <label className="form-label crf-action-item-review-label">Issue Category</label>
                          <input
                            type="text"
                            className="form-control form-input crf-action-item-review-readonly"
                            value={(getAnyCase(item, 'issueCategory', 'IssueCategory') || '').toString().trim() || '-'}
                            readOnly
                            disabled
                          />
                        </div>
                        {isIssueCategoryOthers(getAnyCase(item, 'issueCategory', 'IssueCategory')) ? (
                          <div className="form-group crf-action-item-review-field">
                            <label className="form-label crf-action-item-review-label">Specify Issue Category</label>
                            <input
                              type="text"
                              className="form-control form-input crf-action-item-review-readonly"
                              value={(getAnyCase(item, 'issueCategoryOther', 'IssueCategoryOther') || '').toString().trim() || '-'}
                              readOnly
                              disabled
                            />
                          </div>
                        ) : null}

                        <div className="form-group crf-action-item-review-field">
                          <div className="crf-action-item-audit-label-row">
                            <label className="form-label crf-action-item-review-label">Status</label>
                            {(() => {
                              const aid = Number(item.actionItemID ?? item.nCRFActionItemNo ?? 0);
                              if (!aid) return null;
                              return (
                                <button
                                  type="button"
                                  className="audit-icon-button"
                                  title="View Status audit"
                                  onClick={() => {
                                    setAuditSelected({
                                      fieldId: '',
                                      questionText: 'Status',
                                      nCRFDtlNo: null,
                                      nCRFActionItemNo: aid,
                                      actionItemAuditFieldName: 'vstatus',
                                    });
                                    setAuditModalOpen(true);
                                  }}
                                >
                                  <FiFileText size={14} />
                                </button>
                              );
                            })()}
                          </div>
                          <select
                            className={`form-select ${issueReopenCloseWorkflow && !actionItemSaving && !crfVisitReportNonTransactional ? 'crf-action-item-review-editable' : 'crf-action-item-review-readonly'}`}
                            value={
                              issueReopenCloseWorkflow
                                ? actionItemReopenIntent
                                : normalizeActionItemStatusSelectValue(item.status)
                            }
                            disabled={crfVisitReportNonTransactional || !issueReopenCloseWorkflow || actionItemSaving}
                            onChange={
                              issueReopenCloseWorkflow && !crfVisitReportNonTransactional
                                ? (e) => {
                                  const v = e.target.value;
                                  setActionItemReopenIntent(v);
                                  if (v === 'Closed') {
                                    setActionReplyRemark(actionItemReplyRemarkOriginalRef.current);
                                    setActionReplyDraft('');
                                    const { minIso, maxIso } = getActionItemClosingDateBounds(
                                      getAnyCase(item, 'dateOfIssue', 'DateOfIssue', 'dDateOfIssue', 'DDateOfIssue')
                                    );
                                    setActionItemClosingDate((prev) => {
                                      if (prev && /^\d{4}-\d{2}-\d{2}$/.test(prev)) {
                                        const c = clampIsoDateToMinMax(prev, minIso, maxIso);
                                        if (c === prev) return prev;
                                      }
                                      return clampIsoDateToMinMax(todayIsoDateLocal(), minIso, maxIso);
                                    });
                                  } else {
                                    setActionItemClosingDate('');
                                  }
                                }
                                : undefined
                            }
                          >
                            {issueReopenCloseWorkflow ? (
                              <>
                                <option value="Closed">Close</option>
                                <option value="Open">Open</option>
                              </>
                            ) : (
                              <>
                                <option value="Open">Open</option>
                                <option value="Resolved">Open</option>
                                <option value="Closed">Close</option>
                              </>
                            )}
                          </select>
                        </div>
                        {stLower === 'closed' && actionItemReopenIntent === 'Closed' ? (
                          <div className="form-group crf-action-item-review-field">
                            <label className="form-label crf-action-item-review-label">Closing Date</label>
                            <input
                              type="text"
                              className="form-control crf-action-item-review-readonly crf-issue-closing-date-readonly"
                              readOnly
                              disabled
                              value={(() => {
                                const iso = apiDateTimeToIsoDateLocal(
                                  getAnyCase(item, 'closedDate', 'ClosedDate', 'dClosedDate', 'DClosedDate')
                                );
                                return (iso && /^\d{4}-\d{2}-\d{2}$/.test(iso) ? formatIsoDateCalendarDDMMMYYYY(iso) : '') || '—';
                              })()}
                              aria-label="Closing Date"
                            />
                          </div>
                        ) : null}
                        {!crfVisitReportNonTransactional && canCreatorClose && stLower === 'resolved' && actionItemReopenIntent === 'Closed' ? (() => {
                          const { minIso, maxIso } = getActionItemClosingDateBounds(
                            getAnyCase(item, 'dateOfIssue', 'DateOfIssue', 'dDateOfIssue', 'DDateOfIssue')
                          );
                          return (
                          <div className="form-group crf-action-item-review-field">
                            <label className="form-label crf-action-item-review-label" htmlFor="crf-issue-review-closing-date-picker">
                              Closing Date
                              <span className="required-asterisk">*</span>
                            </label>
                            <input
                              id="crf-issue-review-closing-date-picker"
                              type="date"
                              className={`form-control crf-action-item-review-editable crf-issue-closing-date-input${actionItemSaving ? ' crf-issue-closing-date-input--locked' : ''}`}
                              value={actionItemClosingDate}
                              min={minIso || undefined}
                              max={maxIso}
                              onChange={(e) => setActionItemClosingDate(clampIsoDateToMinMax(e.target.value, minIso, maxIso))}
                              disabled={actionItemSaving}
                              aria-label="Closing Date"
                            />
                          </div>
                          );
                        })() : null}
                        <div className="form-group crf-action-item-review-field crf-action-item-review-field--wide">
                          <div className="crf-action-item-audit-label-row">
                            <label className="form-label crf-action-item-review-label">
                              Description of Action
                              {canReply && !crfVisitReportNonTransactional ? <span className="required-asterisk">*</span> : null}
                            </label>
                            {(() => {
                              const aid = Number(item.actionItemID ?? item.nCRFActionItemNo ?? 0);
                              if (!aid) return null;
                              return (
                                <button
                                  type="button"
                                  className="audit-icon-button"
                                  title="View Description of Action audit"
                                  onClick={() => {
                                    setAuditSelected({
                                      fieldId: '',
                                      questionText: 'Description of Action',
                                      nCRFDtlNo: null,
                                      nCRFActionItemNo: aid,
                                      actionItemAuditFieldName: 'vreplyremark',
                                    });
                                    setAuditModalOpen(true);
                                  }}
                                >
                                  <FiFileText size={14} />
                                </button>
                              );
                            })()}
                          </div>
                          <ActionItemReplyThread
                            persistedRemark={actionReplyRemark}
                            historyEntries={actionReplyHistoryEntries}
                            draft={actionReplyDraft}
                            onDraftChange={setActionReplyDraft}
                            readOnly={!actionItemDescriptionEditable}
                            user={user}
                            fieldError=""
                            showRequiredStar={Boolean(
                              canReply
                              && !crfVisitReportNonTransactional
                              && !(actionReplyRemark || '').toString().trim()
                            )}
                          />
                        </div>
                        </div>
                        <div className="create-action-item-footer crf-action-item-review-footer">
                          {canReply && !crfVisitReportNonTransactional && (
                            <>
                              <button
                                type="button"
                                className="btn btn-primary me-2"
                                disabled={actionItemSaving}
                                onClick={() => {
                                  const d = actionReplyDraft.trim();
                                  if (!d) {
                                    setToast({ type: 'error', message: actionItemMessages.replyRemarkRequired });
                                    return;
                                  }
                                  const replyId = item.actionItemID ?? item.nCRFActionItemNo;
                                  if (!replyId) {
                                    setToast({ type: 'error', message: 'Issue id is missing; cannot save.' });
                                    return;
                                  }
                                  actionItemUpdateRef.current = {
                                    actionItemID: replyId,
                                    replyRemark: d,
                                    submitToCreator: false,
                                  };
                                  void doUpdateActionItemRemark();
                                }}
                              >
                                Update
                              </button>
                              <button
                                type="button"
                                className="btn btn-primary"
                                disabled={actionItemSaving}
                                onClick={() => {
                                  const d = actionReplyDraft.trim();
                                  const p = (actionReplyRemark || '').trim();
                                  if (!d && !p) {
                                    setToast({ type: 'error', message: actionItemMessages.replyRemarkRequired });
                                    return;
                                  }
                                  const replyId = item.actionItemID ?? item.nCRFActionItemNo;
                                  if (!replyId) {
                                    setToast({ type: 'error', message: 'Issue id is missing; cannot save.' });
                                    return;
                                  }
                                  actionItemUpdateRef.current = {
                                    actionItemID: replyId,
                                    replyRemark: d,
                                    submitToCreator: true,
                                  };
                                  void doUpdateActionItemRemark();
                                }}
                              >
                                Save
                              </button>
                            </>
                          )}
                          {issueReopenCloseWorkflow && actionItemReopenIntent === 'Open' && !crfVisitReportNonTransactional && (
                            <button
                              type="button"
                              className="btn btn-primary me-2"
                              disabled={actionItemSaving}
                              onClick={() => {
                                const reopenId = item.actionItemID ?? item.nCRFActionItemNo;
                                if (!reopenId) {
                                  setToast({ type: 'error', message: 'Issue id is missing; cannot reopen.' });
                                  return;
                                }
                                const d = actionReplyDraft.trim();
                                const p = (actionReplyRemark || '').trim();
                                if (!d && !p) {
                                  setToast({ type: 'error', message: actionItemMessages.replyRemarkRequired });
                                  return;
                                }
                                actionItemReopenRef.current = { actionItemID: reopenId, replyRemark: d || undefined };
                                void doReopenActionItem();
                              }}
                            >
                              Re Open
                            </button>
                          )}
                          {canCreatorClose && actionItemReopenIntent === 'Closed' && !crfVisitReportNonTransactional && (
                            <button
                              type="button"
                              className="btn btn-success"
                              disabled={actionItemSaving}
                              onClick={() => {
                                if (!actionItemClosingDate || !/^\d{4}-\d{2}-\d{2}$/.test(actionItemClosingDate)) {
                                  setToast({ type: 'error', message: actionItemMessages.closingDateRequired });
                                  return;
                                }
                                const bounds = getActionItemClosingDateBounds(
                                  getAnyCase(item, 'dateOfIssue', 'DateOfIssue', 'dDateOfIssue', 'DDateOfIssue')
                                );
                                if (bounds.minIso && actionItemClosingDate < bounds.minIso) {
                                  setToast({ type: 'error', message: actionItemMessages.closingDateBeforeIssue });
                                  return;
                                }
                                if (actionItemClosingDate > bounds.maxIso) {
                                  setToast({ type: 'error', message: actionItemMessages.closingDateFuture });
                                  return;
                                }
                                actionItemCloseRef.current = {
                                  actionItemID: item.actionItemID ?? item.nCRFActionItemNo,
                                  closedDate: actionItemClosingDate,
                                };
                                eSignRemarksRef.current = '';
                                setESignPendingAction('closeActionItem');
                                setShowESignModal(true);
                              }}
                            >
                              Close Issue
                            </button>
                          )}
                          <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={() => {
                              setActionItemsModalOpen(false);
                              setActionItemsSelected({ nCRFDtlNo: null, fieldId: '', questionText: '' });
                              setActionItemsList([]);
                              setSelectedActionItemIndex(0);
                              setActionReplyRemark('');
                              setActionReplyDraft('');
                              setActionItemReopenIntent('Closed');
                              setActionItemClosingDate('');
                            }}
                          >
                            Close
                          </button>
                        </div>
                      </>
                    );
                  })()}
                </>
              )}
            </Modal>
          </div>,
          document.body
        )}
      </div>
      {toast && (embedInModal
        ? createPortal(
          <div style={{ position: 'fixed', top: 16, right: 16, zIndex: 10000 }}>
            <Alert type={toast.type} message={toast.message} onClose={() => setToast(null)} />
          </div>,
          document.body
        )
        : (
          <div style={{ position: 'fixed', top: 16, right: 16, zIndex: 10000 }}>
            <Alert type={toast.type} message={toast.message} onClose={() => setToast(null)} />
          </div>
        )
      )}
    </div>
  );
};

export default CRFForm;
